/*!
 * @antv/g-plugin-device-renderer
 * @description A G plugin of renderer implementation with GPUDevice
 * @version 2.6.1
 * @date 12/24/2025, 11:56:30 AM
 * @author AntVis
 * @docs https://g.antv.antgroup.com/
 */
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import _callSuper from '@babel/runtime/helpers/callSuper';
import _inherits from '@babel/runtime/helpers/inherits';
import { DisplayObject, Shape, AABB, CanvasEvent, ElementEvent, parseColor, Rectangle, isCSSRGB, isPattern, isDisplayObject, parsePath, convertToPath, parsedTransformToMat4, parseTransform, AbstractRendererPlugin } from '@antv/g-lite';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _superPropGet from '@babel/runtime/helpers/superPropGet';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _regeneratorRuntime from '@babel/runtime/helpers/regeneratorRuntime';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import { isNil, clamp, isNumber, arcToCubic, isString } from '@antv/util';
import { nullify, assert, alignNonPowerOfTwo, assertExists, BufferUsage, BufferFrequencyHint, bindingsDescriptorCopy, renderPipelineDescriptorCopy, inputLayoutDescriptorCopy, preprocessProgram_GLSL, bindingsDescriptorEquals, renderPipelineDescriptorEquals, inputLayoutDescriptorEquals, samplerDescriptorEquals, TextureDimension, TextureUsage, fillArray, ClipSpaceNearZ, setMegaStateFlags, setBitFlagEnabled, nArray, copyMegaState, defaultMegaState, PrimitiveTopology, spliceBisectRight, colorNewFromRGBA, OpaqueBlack, OpaqueWhite, Format, fullscreenMegaState, VertexStepMode, TransparentWhite, setAttachmentStateSimple, TransparentBlack, BlendMode, BlendFactor, getUniforms, TextureEvent, CompareFunction, StencilOp, AddressMode, FilterMode, MipmapFilterMode, ChannelWriteMask, makeTextureDescriptor2D, CullMode } from '@antv/g-device-api';
import _createForOfIteratorHelper from '@babel/runtime/helpers/createForOfIteratorHelper';
import { mat4, vec3 } from 'gl-matrix';
import EventEmitter from 'eventemitter3';
import '@babel/runtime/helpers/readOnlyError';
import _arrayLikeToArray from '@babel/runtime/helpers/arrayLikeToArray';
import _toArray from '@babel/runtime/helpers/toArray';
import earcut from 'earcut';
import { quadLength, cubicLength } from '@antv/g-math';

var Renderable3D = /*#__PURE__*/_createClass(function Renderable3D() {
  _classCallCheck(this, Renderable3D);
  this.drawcalls = [];
});
Renderable3D.tag = 'c-renderable-3d';

var LightPool = /*#__PURE__*/function () {
  function LightPool() {
    _classCallCheck(this, LightPool);
    /**
     * lights
     */
    this.lights = [];
  }
  return _createClass(LightPool, [{
    key: "addLight",
    value:
    /**
     * support only 1 fog
     */

    function addLight(light) {
      this.lights.push(light);
      this.sortLights();
    }
  }, {
    key: "removeLight",
    value: function removeLight(light) {
      var i = this.lights.indexOf(light);
      this.lights.splice(i, 1);
      this.sortLights();
    }
  }, {
    key: "addFog",
    value: function addFog(fog) {
      this.fog = fog;
    }
  }, {
    key: "removeFog",
    value: function removeFog(fog) {
      this.fog = null;
    }
  }, {
    key: "getFog",
    value: function getFog() {
      return this.fog;
    }
  }, {
    key: "getAllLights",
    value: function getAllLights() {
      return this.lights;
    }

    /**
     * USE_LIGHT
     * NUM_AMBIENT_LIGHTS
     * NUM_DIR_LIGHTS
     */
  }, {
    key: "getDefines",
    value: function getDefines() {
      var defines = {
        USE_LIGHT: !!this.lights.length
      };
      this.lights.forEach(function (light) {
        if (!defines[light.define]) {
          defines[light.define] = 0;
        }
        defines[light.define]++;
      });
      return defines;
    }
  }, {
    key: "sortLights",
    value: function sortLights() {
      this.lights.sort(function (a, b) {
        return a.order - b.order;
      });
    }
  }]);
}();

var _excluded$2 = ["style"];
var Mesh = /*#__PURE__*/function (_DisplayObject) {
  function Mesh(_ref) {
    var _this;
    var style = _ref.style,
      rest = _objectWithoutProperties(_ref, _excluded$2);
    _classCallCheck(this, Mesh);
    _this = _callSuper(this, Mesh, [_objectSpread({
      type: Shape.MESH,
      style: _objectSpread({
        x: '',
        y: '',
        z: '',
        lineWidth: 0
      }, style)
    }, rest)]);
    _this.cullable.enable = false;
    _this.style.geometry.meshes.push(_this);
    _this.style.material.meshes.push(_this);
    return _this;
  }

  // getVertexBufferData(bufferIndex: number) {
  //   return this.style.geometry.vertexBuffers[bufferIndex];
  // }

  // setVertexBufferData(descriptor: {
  //   bufferIndex: number;
  //   byteOffset: number;
  //   data: ArrayBufferView;
  // }) {}
  _inherits(Mesh, _DisplayObject);
  return _createClass(Mesh, [{
    key: "destroy",
    value: function destroy() {
      _superPropGet(Mesh, "destroy", this, 3)([]);

      // detach from geometry
      var meshes = this.style.geometry.meshes;
      var index = meshes.indexOf(this);
      meshes.splice(index, 1);

      // detach from material
      meshes = this.style.material.meshes;
      index = meshes.indexOf(this);
      meshes.splice(index, 1);
    }
  }]);
}(DisplayObject);
Mesh.PARSED_STYLE_LIST = new Set([].concat(_toConsumableArray(DisplayObject.PARSED_STYLE_LIST), ['x', 'y', 'z', 'geometry', 'material']));

var MeshUpdater = /*#__PURE__*/function () {
  function MeshUpdater() {
    _classCallCheck(this, MeshUpdater);
  }
  return _createClass(MeshUpdater, [{
    key: "update",
    value: function update(parsedStyle) {
      var _parsedStyle$x = parsedStyle.x,
        x = _parsedStyle$x === void 0 ? 0 : _parsedStyle$x,
        _parsedStyle$y = parsedStyle.y,
        y = _parsedStyle$y === void 0 ? 0 : _parsedStyle$y,
        _parsedStyle$z = parsedStyle.z,
        z = _parsedStyle$z === void 0 ? 0 : _parsedStyle$z,
        geometry = parsedStyle.geometry;
      var aabb = geometry.computeBoundingBox();
      return {
        cx: x,
        cy: y,
        cz: z,
        hwidth: aabb.halfExtents[0],
        hheight: aabb.halfExtents[1],
        hdepth: aabb.halfExtents[2]
      };
    }
  }]);
}();

var PickingIdGenerator = /*#__PURE__*/function () {
  function PickingIdGenerator() {
    _classCallCheck(this, PickingIdGenerator);
    this.counter = 0;
    this.id2DisplayObjectMap = {};
  }
  return _createClass(PickingIdGenerator, [{
    key: "getId",
    value: function getId(displayObject) {
      var id = this.counter++;
      this.id2DisplayObjectMap[id] = displayObject;
      return id;
    }
  }, {
    key: "getById",
    value: function getById(id) {
      return this.id2DisplayObjectMap[id];
    }
  }, {
    key: "deleteById",
    value: function deleteById(id) {
      delete this.id2DisplayObjectMap[id];
    }
  }, {
    key: "reset",
    value: function reset() {
      this.counter = 0;
      this.id2DisplayObjectMap = {};
    }
  }, {
    key: "decodePickingColor",
    value: function decodePickingColor(color) {
      var _color = _slicedToArray(color, 3),
        i1 = _color[0],
        i2 = _color[1],
        i3 = _color[2];
      var index = i1 + i2 * 256 + i3 * 65536 - 1;
      return index;
    }
  }, {
    key: "encodePickingColor",
    value: function encodePickingColor(featureIdx) {
      return [featureIdx + 1 & 255, featureIdx + 1 >> 8 & 255, featureIdx + 1 >> 8 >> 8 & 255];
    }
  }]);
}();

var DeviceProgram = /*#__PURE__*/function () {
  function DeviceProgram() {
    _classCallCheck(this, DeviceProgram);
    this.name = '(unnamed)';
    // Compiled program.
    this.preprocessedVert = '';
    this.preprocessedFrag = '';
    // Inputs.
    this.both = '';
    this.vert = '';
    this.frag = '';
    this.defines = {};
  }
  return _createClass(DeviceProgram, [{
    key: "definesChanged",
    value: function definesChanged() {
      this.preprocessedVert = '';
      this.preprocessedFrag = '';
    }
  }, {
    key: "setDefineString",
    value: function setDefineString(name, v) {
      if (v !== null) {
        if (this.defines[name] === v) return false;
        this.defines[name] = v;
      } else {
        if (isNil(this.defines[name])) return false;
        delete this.defines[name];
      }
      this.definesChanged();
      return true;
    }
  }, {
    key: "setDefineBool",
    value: function setDefineBool(name, v) {
      return this.setDefineString(name, v ? '1' : null);
    }
  }, {
    key: "getDefineString",
    value: function getDefineString(name) {
      return nullify(this.defines[name]);
    }
  }, {
    key: "getDefineBool",
    value: function getDefineBool(name) {
      var str = this.getDefineString(name);
      if (str !== null) assert(str === '1');
      return str !== null;
    }
  }]);
}();

// This is a very basic linear allocator. We allocate offsets in-order.
var DynamicUniformBuffer = /*#__PURE__*/function () {
  function DynamicUniformBuffer(device) {
    _classCallCheck(this, DynamicUniformBuffer);
    /**
     * Word count, 4 bytes per word
     */
    this.currentBufferWordSize = -1;
    this.currentWordOffset = 0;
    this.buffer = null;
    this.shadowBufferF32 = null;
    this.shadowBufferU8 = null;
    this.device = device;
    var limits = device.queryLimits();
    this.uniformBufferWordAlignment = limits.uniformBufferWordAlignment;
    this.uniformBufferMaxPageWordSize = limits.uniformBufferMaxPageWordSize;
  }
  return _createClass(DynamicUniformBuffer, [{
    key: "isSupportedUBO",
    value: function isSupportedUBO() {
      // UBO not supported in WebGL1
      return this.device.queryVendorInfo().platformString !== 'WebGL1';
    }
  }, {
    key: "findPageIndex",
    value: function findPageIndex(wordOffset) {
      return wordOffset / this.uniformBufferMaxPageWordSize | 0;
    }
  }, {
    key: "allocateChunk",
    value: function allocateChunk(wordCount) {
      wordCount = alignNonPowerOfTwo(wordCount, this.uniformBufferWordAlignment);
      assert(wordCount < this.uniformBufferMaxPageWordSize);
      var wordOffset = this.currentWordOffset;

      // If we straddle the page, then put it at the start of the next one.
      if (this.findPageIndex(wordOffset) !== this.findPageIndex(wordOffset + wordCount - 1)) wordOffset = alignNonPowerOfTwo(wordOffset, this.uniformBufferMaxPageWordSize);
      this.currentWordOffset = wordOffset + wordCount;
      this.ensureShadowBuffer(wordOffset, wordCount);
      return wordOffset;
    }
  }, {
    key: "ensureShadowBuffer",
    value: function ensureShadowBuffer(wordOffset, wordCount) {
      if (this.shadowBufferU8 === null || this.shadowBufferF32 === null) {
        var newWordCount = alignNonPowerOfTwo(this.currentWordOffset, this.uniformBufferMaxPageWordSize);
        this.shadowBufferU8 = new Uint8Array(newWordCount * 4);
        this.shadowBufferF32 = new Float32Array(this.shadowBufferU8.buffer);
      } else if (wordOffset + wordCount >= this.shadowBufferF32.length) {
        assert(wordOffset < this.currentWordOffset && wordOffset + wordCount <= this.currentWordOffset);

        // Grow logarithmically, aligned to page size.
        var _newWordCount = alignNonPowerOfTwo(Math.max(this.currentWordOffset, this.shadowBufferF32.length * 2), this.uniformBufferMaxPageWordSize);
        var newBuffer = new Uint8Array(_newWordCount * 4);
        newBuffer.set(this.shadowBufferU8, 0);
        this.shadowBufferU8 = newBuffer;
        this.shadowBufferF32 = new Float32Array(this.shadowBufferU8.buffer);
        if (!(this.currentWordOffset <= _newWordCount)) throw new Error("Assert fail: this.currentWordOffset [".concat(this.currentWordOffset, "] <= newWordCount [").concat(_newWordCount, "]"));
      }
    }

    /**
     * Return the CPU data buffer used internally. Fill this in to submit data to the CPU. Write to
     * it with the offset that was returned from {@see allocateChunk}.
     */
  }, {
    key: "mapBufferF32",
    value: function mapBufferF32() {
      return assertExists(this.shadowBufferF32);
    }
  }, {
    key: "prepareToRender",
    value: function prepareToRender() {
      if (this.shadowBufferF32 === null) {
        return;
      }
      var shadowBufferF32 = assertExists(this.shadowBufferF32);
      if (shadowBufferF32.length !== this.currentBufferWordSize) {
        this.currentBufferWordSize = shadowBufferF32.length;
        if (this.buffer !== null) {
          this.buffer.destroy();
        }
        this.buffer = this.device.createBuffer({
          // in bytes length
          viewOrSize: this.currentBufferWordSize * 4,
          usage: BufferUsage.UNIFORM,
          hint: BufferFrequencyHint.DYNAMIC
        });
      }
      var wordCount = alignNonPowerOfTwo(this.currentWordOffset, this.uniformBufferMaxPageWordSize);
      if (!(wordCount <= this.currentBufferWordSize)) throw new Error("Assert fail: wordCount [".concat(wordCount, "] (").concat(this.currentWordOffset, " aligned ").concat(this.uniformBufferMaxPageWordSize, ") <= this.currentBufferWordSize [").concat(this.currentBufferWordSize, "]"));
      if (this.isSupportedUBO()) {
        var buffer = assertExists(this.buffer);
        buffer.setSubData(0, this.shadowBufferU8, 0, wordCount * 4);
      }

      // Reset the offset for next frame.
      this.currentWordOffset = 0;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      if (this.buffer !== null) this.buffer.destroy();
      this.shadowBufferF32 = null;
      this.shadowBufferU8 = null;
    }
  }]);
}();

/* eslint-disable max-classes-per-file */
// Jenkins One-at-a-Time hash from http://www.burtleburtle.net/bob/hash/doobs.html
function hashCodeNumberUpdate(hash, v) {
  hash += v;
  hash += hash << 10;
  hash += hash >>> 6;
  return hash >>> 0;
}
function hashCodeNumberFinish(hash) {
  hash += hash << 3;
  hash ^= hash >>> 11;
  hash += hash << 15;
  return hash >>> 0;
}

// Pass this as a hash function to use a one-bucket HashMap (equivalent to linear search in an array),
// which can be efficient for small numbers of items.
function nullHashFunc(k) {
  return 0;
}
var HashBucket = /*#__PURE__*/_createClass(function HashBucket() {
  _classCallCheck(this, HashBucket);
  this.keys = [];
  this.values = [];
});
var HashMap = /*#__PURE__*/function () {
  function HashMap(keyEqualFunc, keyHashFunc) {
    _classCallCheck(this, HashMap);
    this.buckets = new Map();
    this.keyEqualFunc = keyEqualFunc;
    this.keyHashFunc = keyHashFunc;
  }
  return _createClass(HashMap, [{
    key: "findBucketIndex",
    value: function findBucketIndex(bucket, k) {
      for (var i = 0; i < bucket.keys.length; i++) if (this.keyEqualFunc(k, bucket.keys[i])) return i;
      return -1;
    }
  }, {
    key: "findBucket",
    value: function findBucket(k) {
      var bw = this.keyHashFunc(k);
      return this.buckets.get(bw);
    }
  }, {
    key: "get",
    value: function get(k) {
      var bucket = this.findBucket(k);
      if (bucket === undefined) return null;
      var bi = this.findBucketIndex(bucket, k);
      if (bi < 0) return null;
      return bucket.values[bi];
    }
  }, {
    key: "add",
    value: function add(k, v) {
      var bw = this.keyHashFunc(k);
      if (this.buckets.get(bw) === undefined) this.buckets.set(bw, new HashBucket());
      var bucket = this.buckets.get(bw);
      bucket.keys.push(k);
      bucket.values.push(v);
    }
  }, {
    key: "delete",
    value: function _delete(k) {
      var bucket = this.findBucket(k);
      if (bucket === undefined) return;
      var bi = this.findBucketIndex(bucket, k);
      if (bi === -1) return;
      bucket.keys.splice(bi, 1);
      bucket.values.splice(bi, 1);
    }
  }, {
    key: "clear",
    value: function clear() {
      this.buckets.clear();
    }
  }, {
    key: "size",
    value: function size() {
      var acc = 0;
      var _iterator = _createForOfIteratorHelper(this.buckets.values()),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var bucket = _step.value;
          acc += bucket.values.length;
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      return acc;
    }
  }, {
    key: "values",
    value: /*#__PURE__*/_regeneratorRuntime().mark(function values() {
      var _iterator2, _step2, bucket, j, _t;
      return _regeneratorRuntime().wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _iterator2 = _createForOfIteratorHelper(this.buckets.values());
            _context.prev = 1;
            _iterator2.s();
          case 2:
            if ((_step2 = _iterator2.n()).done) {
              _context.next = 6;
              break;
            }
            bucket = _step2.value;
            j = bucket.values.length - 1;
          case 3:
            if (!(j >= 0)) {
              _context.next = 5;
              break;
            }
            _context.next = 4;
            return bucket.values[j];
          case 4:
            j--;
            _context.next = 3;
            break;
          case 5:
            _context.next = 2;
            break;
          case 6:
            _context.next = 8;
            break;
          case 7:
            _context.prev = 7;
            _t = _context["catch"](1);
            _iterator2.e(_t);
          case 8:
            _context.prev = 8;
            _iterator2.f();
            return _context.finish(8);
          case 9:
          case "end":
            return _context.stop();
        }
      }, values, this, [[1, 7, 8, 9]]);
    })
  }]);
}();

function preprocessProgramObj_GLSL(device, obj) {
  var defines = obj.defines !== undefined ? obj.defines : null;
  var vert = obj.both !== undefined ? obj.both + obj.vert : obj.vert;
  var frag = obj.both !== undefined ? obj.both + obj.frag : obj.frag;
  return preprocessProgram_GLSL(device.queryVendorInfo(), vert, frag, defines);
}
function programDescriptorSimpleEquals(a, b) {
  assert(a.preprocessedVert !== '' && b.preprocessedVert !== '');
  assert(a.preprocessedFrag !== '' && b.preprocessedFrag !== '');
  return a.preprocessedVert === b.preprocessedVert && a.preprocessedFrag === b.preprocessedFrag;
}
function programDescriptorSimpleCopy(a) {
  var preprocessedVert = a.preprocessedVert;
  var preprocessedFrag = a.preprocessedFrag;
  var vert = a.vert;
  var frag = a.frag;
  return {
    preprocessedVert: preprocessedVert,
    preprocessedFrag: preprocessedFrag,
    vert: vert,
    frag: frag
  };
}
function blendStateHash(hash, a) {
  hash = hashCodeNumberUpdate(hash, a.blendMode);
  hash = hashCodeNumberUpdate(hash, a.blendSrcFactor);
  hash = hashCodeNumberUpdate(hash, a.blendDstFactor);
  return hash;
}
function attachmentStateHash(hash, a) {
  hash = blendStateHash(hash, a.rgbBlendState);
  hash = blendStateHash(hash, a.alphaBlendState);
  hash = hashCodeNumberUpdate(hash, a.channelWriteMask);
  return hash;
}
function colorHash(hash, a) {
  hash = hashCodeNumberUpdate(hash, a.r << 24 | a.g << 16 | a.b << 8 | a.a);
  return hash;
}
function megaStateDescriptorHash(hash, a) {
  var _a$stencilFront, _a$stencilFront2, _a$stencilFront3, _a$stencilFront4, _a$stencilBack, _a$stencilBack2, _a$stencilBack3, _a$stencilBack4;
  for (var i = 0; i < a.attachmentsState.length; i++) hash = attachmentStateHash(hash, a.attachmentsState[i]);
  hash = colorHash(hash, a.blendConstant);
  hash = hashCodeNumberUpdate(hash, a.depthCompare);
  hash = hashCodeNumberUpdate(hash, a.depthWrite ? 1 : 0);
  hash = hashCodeNumberUpdate(hash, (_a$stencilFront = a.stencilFront) === null || _a$stencilFront === void 0 ? void 0 : _a$stencilFront.compare);
  hash = hashCodeNumberUpdate(hash, (_a$stencilFront2 = a.stencilFront) === null || _a$stencilFront2 === void 0 ? void 0 : _a$stencilFront2.passOp);
  hash = hashCodeNumberUpdate(hash, (_a$stencilFront3 = a.stencilFront) === null || _a$stencilFront3 === void 0 ? void 0 : _a$stencilFront3.failOp);
  hash = hashCodeNumberUpdate(hash, (_a$stencilFront4 = a.stencilFront) === null || _a$stencilFront4 === void 0 ? void 0 : _a$stencilFront4.depthFailOp);
  hash = hashCodeNumberUpdate(hash, (_a$stencilBack = a.stencilBack) === null || _a$stencilBack === void 0 ? void 0 : _a$stencilBack.compare);
  hash = hashCodeNumberUpdate(hash, (_a$stencilBack2 = a.stencilBack) === null || _a$stencilBack2 === void 0 ? void 0 : _a$stencilBack2.passOp);
  hash = hashCodeNumberUpdate(hash, (_a$stencilBack3 = a.stencilBack) === null || _a$stencilBack3 === void 0 ? void 0 : _a$stencilBack3.failOp);
  hash = hashCodeNumberUpdate(hash, (_a$stencilBack4 = a.stencilBack) === null || _a$stencilBack4 === void 0 ? void 0 : _a$stencilBack4.depthFailOp);
  hash = hashCodeNumberUpdate(hash, a.stencilWrite ? 1 : 0);
  hash = hashCodeNumberUpdate(hash, a.cullMode);
  hash = hashCodeNumberUpdate(hash, a.frontFace ? 1 : 0);
  hash = hashCodeNumberUpdate(hash, a.polygonOffset ? 1 : 0);
  return hash;
}
function renderPipelineDescriptorHash(a) {
  var hash = 0;
  hash = hashCodeNumberUpdate(hash, a.program.id);
  if (a.inputLayout !== null) hash = hashCodeNumberUpdate(hash, a.inputLayout.id);
  hash = megaStateDescriptorHash(hash, a.megaStateDescriptor);
  for (var i = 0; i < a.colorAttachmentFormats.length; i++) hash = hashCodeNumberUpdate(hash, a.colorAttachmentFormats[i] || 0);
  hash = hashCodeNumberUpdate(hash, a.depthStencilAttachmentFormat || 0);
  return hashCodeNumberFinish(hash);
}
function bindingsDescriptorHash(a) {
  var hash = 0;
  for (var i = 0; i < a.samplerBindings.length; i++) {
    var binding = a.samplerBindings[i];
    if (binding !== null && binding.texture !== null) hash = hashCodeNumberUpdate(hash, binding.texture.id);
  }
  for (var _i = 0; _i < a.uniformBufferBindings.length; _i++) {
    var _binding = a.uniformBufferBindings[_i];
    if (_binding !== null && _binding.buffer !== null) {
      hash = hashCodeNumberUpdate(hash, _binding.buffer.id);
      hash = hashCodeNumberUpdate(hash, _binding.binding);
      hash = hashCodeNumberUpdate(hash, _binding.offset);
      hash = hashCodeNumberUpdate(hash, _binding.size);
    }
  }
  return hashCodeNumberFinish(hash);
}
var RenderCache = /*#__PURE__*/function () {
  function RenderCache(device) {
    _classCallCheck(this, RenderCache);
    this.bindingsCache = new HashMap(bindingsDescriptorEquals, bindingsDescriptorHash);
    this.renderPipelinesCache = new HashMap(renderPipelineDescriptorEquals, renderPipelineDescriptorHash);
    this.inputLayoutsCache = new HashMap(inputLayoutDescriptorEquals, nullHashFunc);
    this.programCache = new HashMap(programDescriptorSimpleEquals, nullHashFunc);
    this.samplerCache = new HashMap(samplerDescriptorEquals, nullHashFunc);
    this.device = device;
  }
  return _createClass(RenderCache, [{
    key: "createBindings",
    value: function createBindings(descriptor) {
      var bindings = this.bindingsCache.get(descriptor);
      if (bindings === null) {
        var descriptorCopy = bindingsDescriptorCopy(descriptor);
        descriptorCopy.uniformBufferBindings = descriptorCopy.uniformBufferBindings.filter(function (_ref) {
          var size = _ref.size;
          return size > 0;
        });
        bindings = this.device.createBindings(descriptorCopy);
        this.bindingsCache.add(descriptorCopy, bindings);
      }
      return bindings;
    }
  }, {
    key: "createRenderPipeline",
    value: function createRenderPipeline(descriptor) {
      var renderPipeline = this.renderPipelinesCache.get(descriptor);
      if (renderPipeline === null) {
        var descriptorCopy = renderPipelineDescriptorCopy(descriptor);
        renderPipeline = this.device.createRenderPipeline(_objectSpread(_objectSpread({}, descriptorCopy), {}, {
          colorAttachmentFormats: descriptor.colorAttachmentFormats.filter(function (f) {
            return f;
          })
        }));
        this.renderPipelinesCache.add(descriptorCopy, renderPipeline);
      }
      return renderPipeline;
    }
  }, {
    key: "createInputLayout",
    value: function createInputLayout(descriptor) {
      // remove hollows
      descriptor.vertexBufferDescriptors = descriptor.vertexBufferDescriptors.filter(function (d) {
        return !!d;
      });
      var inputLayout = this.inputLayoutsCache.get(descriptor);
      if (inputLayout === null) {
        var descriptorCopy = inputLayoutDescriptorCopy(descriptor);
        inputLayout = this.device.createInputLayout(descriptorCopy);
        this.inputLayoutsCache.add(descriptorCopy, inputLayout);
      }
      return inputLayout;
    }
  }, {
    key: "createProgramSimple",
    value: function createProgramSimple(deviceProgram) {
      var vert = deviceProgram.vert,
        frag = deviceProgram.frag,
        preprocessedFrag = deviceProgram.preprocessedFrag,
        preprocessedVert = deviceProgram.preprocessedVert;
      var program = null;
      if (preprocessedVert && preprocessedFrag) {
        program = this.programCache.get({
          vert: vert,
          frag: frag,
          preprocessedFrag: preprocessedFrag,
          preprocessedVert: preprocessedVert
        });
      }
      if (program === null) {
        var _preprocessProgramObj = preprocessProgramObj_GLSL(this.device, deviceProgram),
          _preprocessedVert = _preprocessProgramObj.preprocessedVert,
          _preprocessedFrag = _preprocessProgramObj.preprocessedFrag;
        deviceProgram.preprocessedVert = _preprocessedVert;
        deviceProgram.preprocessedFrag = _preprocessedFrag;
        var descriptorCopy = programDescriptorSimpleCopy(deviceProgram);
        program = this.device.createProgramSimple({
          vertex: {
            glsl: _preprocessedVert
          },
          fragment: {
            glsl: _preprocessedFrag
          }
        }, vert);
        this.programCache.add(descriptorCopy, program);
      }
      return program;
    }
  }, {
    key: "createSampler",
    value: function createSampler(descriptor) {
      var sampler = this.samplerCache.get(descriptor);
      if (sampler === null) {
        sampler = this.device.createSampler(descriptor);
        this.samplerCache.add(descriptor, sampler);
      }
      return sampler;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      var _iterator = _createForOfIteratorHelper(this.bindingsCache.values()),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var bindings = _step.value;
          bindings.destroy();
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      var _iterator2 = _createForOfIteratorHelper(this.renderPipelinesCache.values()),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var renderPipeline = _step2.value;
          renderPipeline.destroy();
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
      var _iterator3 = _createForOfIteratorHelper(this.inputLayoutsCache.values()),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var inputLayout = _step3.value;
          inputLayout.destroy();
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
      var _iterator4 = _createForOfIteratorHelper(this.programCache.values()),
        _step4;
      try {
        for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
          var program = _step4.value;
          program.destroy();
        }
      } catch (err) {
        _iterator4.e(err);
      } finally {
        _iterator4.f();
      }
      var _iterator5 = _createForOfIteratorHelper(this.samplerCache.values()),
        _step5;
      try {
        for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
          var sampler = _step5.value;
          sampler.destroy();
        }
      } catch (err) {
        _iterator5.e(err);
      } finally {
        _iterator5.f();
      }
      this.bindingsCache.clear();
      this.renderPipelinesCache.clear();
      this.inputLayoutsCache.clear();
      this.programCache.clear();
      this.samplerCache.clear();
    }
  }]);
}();

var ToneMapping = /*#__PURE__*/function (ToneMapping) {
  ToneMapping["NONE"] = "none";
  ToneMapping["LINEAR"] = "LinearToneMapping";
  ToneMapping["REINHARD"] = "ReinhardToneMapping";
  ToneMapping["CINEON"] = "OptimizedCineonToneMapping";
  ToneMapping["ACES_FILMIC"] = "ACESFilmicToneMapping";
  ToneMapping["CUSTOM"] = "CustomToneMapping";
  return ToneMapping;
}({});

var RGAttachmentSlot = /*#__PURE__*/function (RGAttachmentSlot) {
  RGAttachmentSlot[RGAttachmentSlot["Color0"] = 0] = "Color0";
  RGAttachmentSlot[RGAttachmentSlot["Color1"] = 1] = "Color1";
  RGAttachmentSlot[RGAttachmentSlot["Color2"] = 2] = "Color2";
  RGAttachmentSlot[RGAttachmentSlot["Color3"] = 3] = "Color3";
  RGAttachmentSlot[RGAttachmentSlot["ColorMax"] = 3] = "ColorMax";
  RGAttachmentSlot[RGAttachmentSlot["DepthStencil"] = 4] = "DepthStencil";
  return RGAttachmentSlot;
}({});

var RenderGraphPass = /*#__PURE__*/function () {
  function RenderGraphPass() {
    _classCallCheck(this, RenderGraphPass);
    // RenderTargetAttachmentSlot => renderTargetID
    this.renderTargetIDs = [];
    this.renderTargetLevels = [];
    // RenderTargetAttachmentSlot => resolveTextureID
    this.resolveTextureOutputIDs = [];
    // RenderTargetAttachmentSlot => Texture
    this.resolveTextureOutputExternalTextures = [];
    this.resolveTextureOutputExternalTextureLevel = [];
    // List of resolveTextureIDs that we have a reference to.
    this.resolveTextureInputIDs = [];
    // RGAttachmentSlot => refcount.
    this.renderTargetExtraRefs = [];
    this.resolveTextureInputTextures = [];
    this.renderTargets = [];
    // Execution state computed by scheduling.
    this.descriptor = {
      colorAttachment: [],
      colorAttachmentLevel: [],
      colorResolveTo: [],
      colorResolveToLevel: [],
      colorStore: [],
      depthStencilAttachment: null,
      depthStencilResolveTo: null,
      depthStencilStore: true,
      colorClearColor: ['load'],
      depthClearValue: 'load',
      stencilClearValue: 'load',
      occlusionQueryPool: null
    };
    this.viewportX = 0;
    this.viewportY = 0;
    this.viewportW = 1;
    this.viewportH = 1;
    // Execution callback from user.
    this.execFunc = null;
    this.postFunc = null;
    this.debugThumbnails = [];
  }
  return _createClass(RenderGraphPass, [{
    key: "setDebugName",
    value: function setDebugName(debugName) {
      this.debugName = debugName;
    }
  }, {
    key: "pushDebugThumbnail",
    value: function pushDebugThumbnail(attachmentSlot) {
      this.debugThumbnails[attachmentSlot] = true;
    }
  }, {
    key: "setViewport",
    value: function setViewport(x, y, w, h) {
      this.viewportX = x;
      this.viewportY = y;
      this.viewportW = w;
      this.viewportH = h;
    }
  }, {
    key: "attachRenderTargetID",
    value: function attachRenderTargetID(attachmentSlot, renderTargetID) {
      var level = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
      assert(this.renderTargetIDs[attachmentSlot] === undefined);
      this.renderTargetIDs[attachmentSlot] = renderTargetID;
      this.renderTargetLevels[attachmentSlot] = level;
    }
  }, {
    key: "attachResolveTexture",
    value: function attachResolveTexture(resolveTextureID) {
      this.resolveTextureInputIDs.push(resolveTextureID);
    }
  }, {
    key: "attachOcclusionQueryPool",
    value: function attachOcclusionQueryPool(queryPool) {
      this.descriptor.occlusionQueryPool = queryPool;
    }
  }, {
    key: "exec",
    value: function exec(func) {
      assert(this.execFunc === null);
      this.execFunc = func;
    }
  }, {
    key: "post",
    value: function post(func) {
      assert(this.postFunc === null);
      this.postFunc = func;
    }
  }, {
    key: "addExtraRef",
    value: function addExtraRef(slot) {
      this.renderTargetExtraRefs[slot] = true;
    }
  }]);
}();

var RGRenderTarget = /*#__PURE__*/function () {
  function RGRenderTarget(device, desc) {
    _classCallCheck(this, RGRenderTarget);
    this.dimension = TextureDimension.TEXTURE_2D;
    this.depthOrArrayLayers = 1;
    this.mipLevelCount = 1;
    this.width = 0;
    this.height = 0;
    this.sampleCount = 0;
    this.usage = TextureUsage.RENDER_TARGET;
    this.needsClear = true;
    this.texture = null;
    this.age = 0;
    this.format = desc.format;
    this.width = desc.width;
    this.height = desc.height;
    this.sampleCount = desc.sampleCount;
    assert(this.sampleCount >= 1);
    if (this.sampleCount > 1) {
      // MSAA render targets must be backed by attachments.
      this.attachment = device.createRenderTarget(this);
    } else {
      // Single-sampled textures can be backed by regular textures.
      this.texture = device.createTexture(this);
      this.attachment = device.createRenderTargetFromTexture(this.texture);
    }
  }
  return _createClass(RGRenderTarget, [{
    key: "setDebugName",
    value: function setDebugName(device, debugName) {
      this.debugName = debugName;
      if (this.texture !== null) {
        device.setResourceName(this.texture, this.debugName);
      }
      device.setResourceName(this.attachment, this.debugName);
    }
  }, {
    key: "matchesDescription",
    value: function matchesDescription(desc) {
      return this.format === desc.format && this.width === desc.width && this.height === desc.height && this.sampleCount === desc.sampleCount;
    }
  }, {
    key: "reset",
    value: function reset(desc) {
      assert(this.matchesDescription(desc));
      this.age = 0;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      this.attachment.destroy();
    }
  }]);
}();

// Whenever we need to resolve a multi-sampled render target to a single-sampled texture,
// we record an extra single-sampled texture here.
var SingleSampledTexture = /*#__PURE__*/function () {
  function SingleSampledTexture(device, desc) {
    _classCallCheck(this, SingleSampledTexture);
    this.dimension = TextureDimension.TEXTURE_2D;
    this.depthOrArrayLayers = 1;
    this.mipLevelCount = 1;
    this.usage = TextureUsage.RENDER_TARGET;
    this.width = 0;
    this.height = 0;
    this.age = 0;
    this.format = desc.format;
    this.width = desc.width;
    this.height = desc.height;
    this.texture = device.createTexture(this);
  }
  return _createClass(SingleSampledTexture, [{
    key: "matchesDescription",
    value: function matchesDescription(desc) {
      return this.format === desc.format && this.width === desc.width && this.height === desc.height;
    }
  }, {
    key: "reset",
    value: function reset(desc) {
      assert(this.matchesDescription(desc));
      this.age = 0;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      this.texture.destroy();
    }
  }]);
}();

var GraphImpl = /*#__PURE__*/_createClass(function GraphImpl() {
  _classCallCheck(this, GraphImpl);
  // [Symbol.species]?: 'RGGraph';
  // Used for determining scheduling.
  this.renderTargetDescriptions = [];
  this.resolveTextureRenderTargetIDs = [];
  this.passes = [];
  // Debugging.
  this.renderTargetDebugNames = [];
});
var RenderGraph = /*#__PURE__*/function () {
  function RenderGraph(device) {
    _classCallCheck(this, RenderGraph);
    // For scope callbacks.
    this.currentPass = null;
    // #region Resource Creation & Caching
    this.renderTargetDeadPool = [];
    this.singleSampledTextureDeadPool = [];
    // #endregion
    // #region Graph Builder
    this.currentGraph = null;
    // #endregion
    // #region Scheduling
    this.renderTargetOutputCount = [];
    this.renderTargetResolveCount = [];
    this.resolveTextureUseCount = [];
    this.renderTargetAliveForID = [];
    this.singleSampledTextureForResolveTextureID = [];
    this.device = device;
  }
  return _createClass(RenderGraph, [{
    key: "acquireRenderTargetForDescription",
    value: function acquireRenderTargetForDescription(desc) {
      for (var i = 0; i < this.renderTargetDeadPool.length; i++) {
        var freeRenderTarget = this.renderTargetDeadPool[i];
        if (freeRenderTarget.matchesDescription(desc)) {
          // Pop it off the list.
          freeRenderTarget.reset(desc);
          this.renderTargetDeadPool.splice(i--, 1);
          return freeRenderTarget;
        }
      }

      // Allocate a new render target.
      return new RGRenderTarget(this.device, desc);
    }
  }, {
    key: "acquireSingleSampledTextureForDescription",
    value: function acquireSingleSampledTextureForDescription(desc) {
      for (var i = 0; i < this.singleSampledTextureDeadPool.length; i++) {
        var freeSingleSampledTexture = this.singleSampledTextureDeadPool[i];
        if (freeSingleSampledTexture.matchesDescription(desc)) {
          // Pop it off the list.
          freeSingleSampledTexture.reset(desc);
          this.singleSampledTextureDeadPool.splice(i--, 1);
          return freeSingleSampledTexture;
        }
      }

      // Allocate a new resolve texture.
      return new SingleSampledTexture(this.device, desc);
    }
  }, {
    key: "beginGraphBuilder",
    value: function beginGraphBuilder() {
      assert(this.currentGraph === null);
      this.currentGraph = new GraphImpl();
    }
  }, {
    key: "pushPass",
    value: function pushPass(setupFunc) {
      var pass = new RenderGraphPass();
      setupFunc(pass);
      this.currentGraph.passes.push(pass);
    }
  }, {
    key: "createRenderTargetID",
    value: function createRenderTargetID(desc, debugName) {
      this.currentGraph.renderTargetDebugNames.push(debugName);
      return this.currentGraph.renderTargetDescriptions.push(desc) - 1;
    }
  }, {
    key: "createResolveTextureID",
    value: function createResolveTextureID(renderTargetID) {
      return this.currentGraph.resolveTextureRenderTargetIDs.push(renderTargetID) - 1;
    }

    /**
     * 查找最靠近输出的一个关联目标 RT 的 RGPass
     */
  }, {
    key: "findMostRecentPassThatAttachedRenderTarget",
    value: function findMostRecentPassThatAttachedRenderTarget(renderTargetID) {
      for (var i = this.currentGraph.passes.length - 1; i >= 0; i--) {
        var pass = this.currentGraph.passes[i];
        if (pass.renderTargetIDs.includes(renderTargetID)) return pass;
      }
      return null;
    }
  }, {
    key: "resolveRenderTargetPassAttachmentSlot",
    value: function resolveRenderTargetPassAttachmentSlot(pass, attachmentSlot) {
      var renderPass = pass;
      if (renderPass.resolveTextureOutputIDs[attachmentSlot] === undefined) {
        var renderTargetID = renderPass.renderTargetIDs[attachmentSlot];
        var resolveTextureID = this.createResolveTextureID(renderTargetID);
        renderPass.resolveTextureOutputIDs[attachmentSlot] = resolveTextureID;
      }
      return renderPass.resolveTextureOutputIDs[attachmentSlot];
    }
  }, {
    key: "findPassForResolveRenderTarget",
    value: function findPassForResolveRenderTarget(renderTargetID) {
      // Find the last pass that rendered to this render target, and resolve it now.

      // If you wanted a previous snapshot copy of it, you should have created a separate,
      // intermediate pass to copy that out. Perhaps we should have a helper for that use case?

      // If there was no pass that wrote to this RT, well there's no point in resolving it, is there?
      var renderPass = assertExists(this.findMostRecentPassThatAttachedRenderTarget(renderTargetID));

      // Check which attachment we're in. This could possibly be explicit from the user, but it's
      // easy enough to find...
      var attachmentSlot = renderPass.renderTargetIDs.indexOf(renderTargetID);

      // Check that the pass isn't resolving its attachment to another texture. Can't do both!
      assert(renderPass.resolveTextureOutputExternalTextures[attachmentSlot] === undefined);
      return renderPass;
    }
  }, {
    key: "resolveRenderTarget",
    value: function resolveRenderTarget(renderTargetID) {
      var renderPass = this.findPassForResolveRenderTarget(renderTargetID);
      var attachmentSlot = renderPass.renderTargetIDs.indexOf(renderTargetID);
      return this.resolveRenderTargetPassAttachmentSlot(renderPass, attachmentSlot);
    }
  }, {
    key: "resolveRenderTargetToExternalTexture",
    value: function resolveRenderTargetToExternalTexture(renderTargetID, texture) {
      var level = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
      var renderPass = this.findPassForResolveRenderTarget(renderTargetID);
      var attachmentSlot = renderPass.renderTargetIDs.indexOf(renderTargetID);
      // We shouldn't be resolving to a resolve texture ID in this case.
      assert(renderPass.resolveTextureOutputIDs[attachmentSlot] === undefined);
      renderPass.resolveTextureOutputExternalTextures[attachmentSlot] = texture;
      renderPass.resolveTextureOutputExternalTextureLevel[attachmentSlot] = level;
    }
  }, {
    key: "getRenderTargetDescription",
    value: function getRenderTargetDescription(renderTargetID) {
      return assertExists(this.currentGraph.renderTargetDescriptions[renderTargetID]);
    }
  }, {
    key: "scheduleAddUseCount",
    value: function scheduleAddUseCount(graph, pass) {
      for (var slot = 0; slot < pass.renderTargetIDs.length; slot++) {
        var renderTargetID = pass.renderTargetIDs[slot];
        if (renderTargetID === undefined) continue;
        this.renderTargetOutputCount[renderTargetID]++;
        if (pass.renderTargetExtraRefs[slot]) this.renderTargetOutputCount[renderTargetID]++;
      }
      for (var i = 0; i < pass.resolveTextureInputIDs.length; i++) {
        var resolveTextureID = pass.resolveTextureInputIDs[i];
        if (resolveTextureID === undefined) continue;
        this.resolveTextureUseCount[resolveTextureID]++;
        var _renderTargetID = graph.resolveTextureRenderTargetIDs[resolveTextureID];
        this.renderTargetResolveCount[_renderTargetID]++;
      }
    }
  }, {
    key: "acquireRenderTargetForID",
    value: function acquireRenderTargetForID(graph, renderTargetID) {
      if (renderTargetID === undefined) return null;
      assert(this.renderTargetOutputCount[renderTargetID] > 0);
      if (!this.renderTargetAliveForID[renderTargetID]) {
        var desc = graph.renderTargetDescriptions[renderTargetID];
        var newRenderTarget = this.acquireRenderTargetForDescription(desc);
        newRenderTarget.setDebugName(this.device, graph.renderTargetDebugNames[renderTargetID]);
        this.renderTargetAliveForID[renderTargetID] = newRenderTarget;
      }
      return this.renderTargetAliveForID[renderTargetID];
    }
  }, {
    key: "releaseRenderTargetForID",
    value: function releaseRenderTargetForID(renderTargetID, forOutput) {
      if (renderTargetID === undefined) return null;
      var renderTarget = assertExists(this.renderTargetAliveForID[renderTargetID]);
      if (forOutput) {
        assert(this.renderTargetOutputCount[renderTargetID] > 0);
        this.renderTargetOutputCount[renderTargetID]--;
      } else {
        assert(this.renderTargetResolveCount[renderTargetID] > 0);
        this.renderTargetResolveCount[renderTargetID]--;
      }
      if (this.renderTargetOutputCount[renderTargetID] === 0 && this.renderTargetResolveCount[renderTargetID] === 0) {
        // This was the last reference to this RT -- steal it from the alive list, and put it back into the pool.
        renderTarget.needsClear = true;
        this.renderTargetAliveForID[renderTargetID] = undefined;
        this.renderTargetDeadPool.push(renderTarget);
      }
      return renderTarget;
    }
  }, {
    key: "acquireResolveTextureInputTextureForID",
    value: function acquireResolveTextureInputTextureForID(graph, resolveTextureID) {
      var renderTargetID = graph.resolveTextureRenderTargetIDs[resolveTextureID];
      assert(this.resolveTextureUseCount[resolveTextureID] > 0);
      this.resolveTextureUseCount[resolveTextureID]--;
      var renderTarget = assertExists(this.releaseRenderTargetForID(renderTargetID, false));
      if (this.singleSampledTextureForResolveTextureID[resolveTextureID] !== undefined) {
        // The resolved texture belonging to this RT is backed by our own single-sampled texture.

        var singleSampledTexture = this.singleSampledTextureForResolveTextureID[resolveTextureID];
        if (this.resolveTextureUseCount[resolveTextureID] === 0) {
          // Release this single-sampled texture back to the pool, if this is the last use of it.
          this.singleSampledTextureDeadPool.push(singleSampledTexture);
        }
        return singleSampledTexture.texture;
      }
      // The resolved texture belonging to this RT is backed by our render target.
      return assertExists(renderTarget.texture);
    }
  }, {
    key: "determineResolveParam",
    value: function determineResolveParam(graph, pass, slot) {
      var renderTargetID = pass.renderTargetIDs[slot];
      var resolveTextureOutputID = pass.resolveTextureOutputIDs[slot];
      var externalTexture = pass.resolveTextureOutputExternalTextures[slot];

      // We should have either an output ID or an external texture, not both.
      var hasResolveTextureOutputID = resolveTextureOutputID !== undefined;
      var hasExternalTexture = externalTexture !== undefined;
      assert(!(hasResolveTextureOutputID && hasExternalTexture));
      var resolveTo = null;
      var store = false;
      var level = 0;
      if (this.renderTargetOutputCount[renderTargetID] > 1) {
        // A future pass is going to render to this RT, we need to store the results.
        store = true;
      }
      if (hasResolveTextureOutputID) {
        assert(graph.resolveTextureRenderTargetIDs[resolveTextureOutputID] === renderTargetID);
        assert(this.resolveTextureUseCount[resolveTextureOutputID] > 0);
        assert(this.renderTargetOutputCount[renderTargetID] > 0);
        var renderTarget = assertExists(this.renderTargetAliveForID[renderTargetID]);

        // If we're the last user of this RT, then we don't need to resolve -- the texture itself will be enough.
        // Note that this isn't exactly an exactly correct algorithm. If we have pass A writing to RenderTargetA,
        // pass B resolving RenderTargetA to ResolveTextureA, and pass C writing to RenderTargetA, then we don't
        // strictly need to copy, but in order to determine that at the time of pass A, we'd need a much fancier
        // schedule than just tracking refcounts...
        if (renderTarget.texture !== null && this.renderTargetOutputCount[renderTargetID] === 1) {
          resolveTo = null;
          store = true;
        } else {
          if (!this.singleSampledTextureForResolveTextureID[resolveTextureOutputID]) {
            var desc = assertExists(graph.renderTargetDescriptions[renderTargetID]);
            this.singleSampledTextureForResolveTextureID[resolveTextureOutputID] = this.acquireSingleSampledTextureForDescription(desc);
            this.device.setResourceName(this.singleSampledTextureForResolveTextureID[resolveTextureOutputID].texture, "".concat(renderTarget.debugName, " (Resolve ").concat(resolveTextureOutputID, ")"));
          }
          resolveTo = this.singleSampledTextureForResolveTextureID[resolveTextureOutputID].texture;
        }
      } else if (hasExternalTexture) {
        resolveTo = externalTexture;
        level = pass.resolveTextureOutputExternalTextureLevel[slot];
      } else {
        resolveTo = null;
      }
      return {
        resolveTo: resolveTo,
        store: store,
        level: level
      };
    }
  }, {
    key: "schedulePass",
    value: function schedulePass(graph, pass) {
      var depthStencilRenderTargetID = pass.renderTargetIDs[RGAttachmentSlot.DepthStencil];
      for (var slot = RGAttachmentSlot.Color0; slot <= RGAttachmentSlot.ColorMax; slot++) {
        var colorRenderTargetID = pass.renderTargetIDs[slot];
        var colorRenderTarget = this.acquireRenderTargetForID(graph, colorRenderTargetID);
        pass.renderTargets[slot] = colorRenderTarget;
        pass.descriptor.colorAttachment[slot] = colorRenderTarget !== null ? colorRenderTarget.attachment : null;
        pass.descriptor.colorAttachmentLevel[slot] = pass.renderTargetLevels[slot];
        var _this$determineResolv = this.determineResolveParam(graph, pass, slot),
          _resolveTo = _this$determineResolv.resolveTo,
          _store = _this$determineResolv.store,
          level = _this$determineResolv.level;
        pass.descriptor.colorResolveTo[slot] = _resolveTo;
        pass.descriptor.colorResolveToLevel[slot] = level;
        pass.descriptor.colorStore[slot] = _store;
        pass.descriptor.colorClearColor[slot] = colorRenderTarget !== null && colorRenderTarget.needsClear ? graph.renderTargetDescriptions[colorRenderTargetID].colorClearColor : 'load';
      }
      var depthStencilRenderTarget = this.acquireRenderTargetForID(graph, depthStencilRenderTargetID);
      pass.renderTargets[RGAttachmentSlot.DepthStencil] = depthStencilRenderTarget;
      pass.descriptor.depthStencilAttachment = depthStencilRenderTarget !== null ? depthStencilRenderTarget.attachment : null;
      var _this$determineResolv2 = this.determineResolveParam(graph, pass, RGAttachmentSlot.DepthStencil),
        resolveTo = _this$determineResolv2.resolveTo,
        store = _this$determineResolv2.store;
      pass.descriptor.depthStencilResolveTo = resolveTo;
      pass.descriptor.depthStencilStore = store;
      pass.descriptor.depthClearValue = depthStencilRenderTarget !== null && depthStencilRenderTarget.needsClear ? graph.renderTargetDescriptions[depthStencilRenderTargetID].depthClearValue : 'load';
      pass.descriptor.stencilClearValue = depthStencilRenderTarget !== null && depthStencilRenderTarget.needsClear ? graph.renderTargetDescriptions[depthStencilRenderTargetID].stencilClearValue : 'load';
      var rtWidth = 0;
      var rtHeight = 0;
      var rtSampleCount = 0;
      for (var i = 0; i < pass.renderTargets.length; i++) {
        var renderTarget = pass.renderTargets[i];
        if (!renderTarget) continue;
        var width = renderTarget.width >>> pass.renderTargetLevels[i];
        var height = renderTarget.height >>> pass.renderTargetLevels[i];
        if (rtWidth === 0) {
          rtWidth = width;
          rtHeight = height;
          rtSampleCount = renderTarget.sampleCount;
        }
        assert(width === rtWidth);
        assert(height === rtHeight);
        assert(renderTarget.sampleCount === rtSampleCount);
        renderTarget.needsClear = false;
      }
      if (rtWidth > 0 && rtHeight > 0) {
        pass.viewportX *= rtWidth;
        pass.viewportY *= rtHeight;
        pass.viewportW *= rtWidth;
        pass.viewportH *= rtHeight;
      }
      for (var _i = 0; _i < pass.resolveTextureInputIDs.length; _i++) {
        var resolveTextureID = pass.resolveTextureInputIDs[_i];
        pass.resolveTextureInputTextures[_i] = this.acquireResolveTextureInputTextureForID(graph, resolveTextureID);
      }
      for (var _i2 = 0; _i2 < pass.renderTargetIDs.length; _i2++) this.releaseRenderTargetForID(pass.renderTargetIDs[_i2], true);
      for (var _slot = 0; _slot < pass.renderTargetExtraRefs.length; _slot++) if (pass.renderTargetExtraRefs[_slot]) this.releaseRenderTargetForID(pass.renderTargetIDs[_slot], true);
    }
  }, {
    key: "scheduleGraph",
    value: function scheduleGraph(graph) {
      assert(this.renderTargetOutputCount.length === 0);
      assert(this.renderTargetResolveCount.length === 0);
      assert(this.resolveTextureUseCount.length === 0);

      // Go through and increment the age of everything in our dead pools to mark that it's old.
      for (var i = 0; i < this.renderTargetDeadPool.length; i++) this.renderTargetDeadPool[i].age++;
      for (var _i3 = 0; _i3 < this.singleSampledTextureDeadPool.length; _i3++) this.singleSampledTextureDeadPool[_i3].age++;

      // Schedule our resources -- first, count up all uses of resources, then hand them out.

      // Initialize our accumulators.
      fillArray(this.renderTargetOutputCount, graph.renderTargetDescriptions.length, 0);
      fillArray(this.renderTargetResolveCount, graph.renderTargetDescriptions.length, 0);
      fillArray(this.resolveTextureUseCount, graph.resolveTextureRenderTargetIDs.length, 0);

      // Count.
      for (var _i4 = 0; _i4 < graph.passes.length; _i4++) this.scheduleAddUseCount(graph, graph.passes[_i4]);

      // Now hand out resources.
      for (var _i5 = 0; _i5 < graph.passes.length; _i5++) this.schedulePass(graph, graph.passes[_i5]);

      // Double-check that all resources were handed out.
      for (var _i6 = 0; _i6 < this.renderTargetOutputCount.length; _i6++) assert(this.renderTargetOutputCount[_i6] === 0);
      for (var _i7 = 0; _i7 < this.renderTargetResolveCount.length; _i7++) assert(this.renderTargetResolveCount[_i7] === 0);
      for (var _i8 = 0; _i8 < this.resolveTextureUseCount.length; _i8++) assert(this.resolveTextureUseCount[_i8] === 0);
      for (var _i9 = 0; _i9 < this.renderTargetAliveForID.length; _i9++) assert(this.renderTargetAliveForID[_i9] === undefined);

      // Now go through and kill anything that's over our age threshold (hasn't been used in a bit)
      var ageThreshold = 1;
      for (var _i0 = 0; _i0 < this.renderTargetDeadPool.length; _i0++) {
        if (this.renderTargetDeadPool[_i0].age >= ageThreshold) {
          this.renderTargetDeadPool[_i0].destroy();
          this.renderTargetDeadPool.splice(_i0--, 1);
        }
      }
      for (var _i1 = 0; _i1 < this.singleSampledTextureDeadPool.length; _i1++) {
        if (this.singleSampledTextureDeadPool[_i1].age >= ageThreshold) {
          this.singleSampledTextureDeadPool[_i1].destroy();
          this.singleSampledTextureDeadPool.splice(_i1--, 1);
        }
      }

      // Clear out our transient scheduling state.
      this.renderTargetResolveCount.length = 0;
      this.renderTargetOutputCount.length = 0;
      this.resolveTextureUseCount.length = 0;
    }
    // #endregion

    // #region Execution
  }, {
    key: "execPass",
    value: function execPass(pass) {
      assert(this.currentPass === null);
      this.currentPass = pass;
      var renderPass = this.device.createRenderPass(pass.descriptor);
      renderPass.pushDebugGroup(pass.debugName);
      renderPass.setViewport(pass.viewportX, pass.viewportY, pass.viewportW, pass.viewportH);
      if (pass.execFunc !== null) pass.execFunc(renderPass, this);
      renderPass.popDebugGroup();
      this.device.submitPass(renderPass);
      if (pass.postFunc !== null) pass.postFunc(this);
      this.currentPass = null;
    }
  }, {
    key: "execGraph",
    value: function execGraph(graph) {
      var _this = this;
      this.scheduleGraph(graph);
      this.device.beginFrame();
      graph.passes.forEach(function (pass) {
        _this.execPass(pass);
      });
      this.device.endFrame();
      // Clear our transient scope state.
      this.singleSampledTextureForResolveTextureID.length = 0;
    }
  }, {
    key: "execute",
    value: function execute() {
      var graph = assertExists(this.currentGraph);
      this.execGraph(graph);
      this.currentGraph = null;
    }
  }, {
    key: "getDebug",
    value: function getDebug() {
      return this;
    }
    // #endregion

    // #region GfxrGraphBuilderDebug
  }, {
    key: "getPasses",
    value: function getPasses() {
      return this.currentGraph.passes;
    }
  }, {
    key: "getPassDebugThumbnails",
    value: function getPassDebugThumbnails(pass) {
      return pass.debugThumbnails;
    }
  }, {
    key: "getPassRenderTargetID",
    value: function getPassRenderTargetID(pass, slot) {
      return pass.renderTargetIDs[slot];
    }
  }, {
    key: "getRenderTargetIDDebugName",
    value: function getRenderTargetIDDebugName(renderTargetID) {
      return this.currentGraph.renderTargetDebugNames[renderTargetID];
    }
    // #endregion

    // #region GfxrPassScope
  }, {
    key: "getResolveTextureForID",
    value: function getResolveTextureForID(resolveTextureID) {
      var currentGraphPass = this.currentPass;
      var i = currentGraphPass.resolveTextureInputIDs.indexOf(resolveTextureID);
      assert(i >= 0);
      return assertExists(currentGraphPass.resolveTextureInputTextures[i]);
    }
  }, {
    key: "getRenderTargetAttachment",
    value: function getRenderTargetAttachment(slot) {
      var currentGraphPass = this.currentPass;
      var renderTarget = currentGraphPass.renderTargets[slot];
      if (!renderTarget) return null;
      return renderTarget.attachment;
    }
  }, {
    key: "getRenderTargetTexture",
    value: function getRenderTargetTexture(slot) {
      var currentGraphPass = this.currentPass;
      var renderTarget = currentGraphPass.renderTargets[slot];
      if (!renderTarget) return null;
      return renderTarget.texture;
    }
    // #endregion
  }, {
    key: "newGraphBuilder",
    value: function newGraphBuilder() {
      this.beginGraphBuilder();
      return this;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      // At the time this is called, we shouldn't have anything alive.
      for (var i = 0; i < this.renderTargetAliveForID.length; i++) assert(this.renderTargetAliveForID[i] === undefined);
      for (var _i10 = 0; _i10 < this.singleSampledTextureForResolveTextureID.length; _i10++) assert(this.singleSampledTextureForResolveTextureID[_i10] === undefined);
      for (var _i11 = 0; _i11 < this.renderTargetDeadPool.length; _i11++) this.renderTargetDeadPool[_i11].destroy();
      for (var _i12 = 0; _i12 < this.singleSampledTextureDeadPool.length; _i12++) this.singleSampledTextureDeadPool[_i12].destroy();
    }
  }]);
}();

// Suggested values for the "layer" of makeSortKey. These are rough groups, and you can define your own
// ordering within the rough groups (e.g. you might use BACKGROUND + 1, or BACKGROUND + 2).
// TRANSLUCENT is meant to be used as a bitflag. It's special as it changes the behavior of the generic sort key

// functions like makeSortKey and setSortKeyDepth.
var RendererLayer = /*#__PURE__*/function (RendererLayer) {
  RendererLayer[RendererLayer["BACKGROUND"] = 0] = "BACKGROUND";
  RendererLayer[RendererLayer["ALPHA_TEST"] = 16] = "ALPHA_TEST";
  RendererLayer[RendererLayer["OPAQUE"] = 32] = "OPAQUE";
  RendererLayer[RendererLayer["TRANSLUCENT"] = 128] = "TRANSLUCENT";
  return RendererLayer;
}({});
var MAX_DEPTH = 0x10000;
var DEPTH_BITS = 16;
function makeDepthKey(depth, flipDepth) {
  var maxDepth = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : MAX_DEPTH;
  // Input depth here is: 0 is the closest to the camera, positive values are further away. Negative values (behind camera) are clamped to 0.
  // normalizedDepth: 0.0 is closest to camera, 1.0 is farthest from camera.
  // These values are flipped if flipDepth is set.
  var normalizedDepth = clamp(depth, 0, maxDepth) / maxDepth;
  if (flipDepth) normalizedDepth = 1.0 - normalizedDepth;
  var depthKey = normalizedDepth * ((1 << DEPTH_BITS) - 1);
  return depthKey & 0xffff;
}

// Common sort key kinds.
// Indexed:     TLLLLLLL IIIIIIII IIIIIIII IIIIIIII
// Opaque:      0LLLLLLL PPPPPPPP PPPPPPPP DDDDDDDD
// Translucent: 1LLLLLLL DDDDDDDD DDDDDDDD BBBBBBBB

function getSortKeyLayer(sortKey) {
  return sortKey >>> 24 & 0xff;
}
function setSortKeyLayer(sortKey, layer) {
  return (sortKey & 0x00ffffff | (layer & 0xff) << 24) >>> 0;
}
function setSortKeyProgramKey(sortKey, programKey) {
  var isTransparent = !!(sortKey >>> 31 & 1);
  if (isTransparent) return sortKey;
  return (sortKey & 0xff0000ff | (programKey & 0xffff) << 8) >>> 0;
}
function setSortKeyBias(sortKey, bias) {
  var isTransparent = !!(sortKey >>> 31 & 1);
  if (isTransparent) return (sortKey & 0xffffff00 | bias & 0xff) >>> 0;
  return sortKey;
}
function makeSortKeyOpaque(layer, programKey) {
  return setSortKeyLayer(setSortKeyProgramKey(0, programKey), layer);
}
function setSortKeyOpaqueDepth(sortKey, depthKey) {
  assert(depthKey >= 0);
  return (sortKey & 0xffffff00 | depthKey >>> 8 & 0xff) >>> 0;
}
function makeSortKeyTranslucent(layer) {
  return setSortKeyLayer(0, layer);
}
function setSortKeyTranslucentDepth(sortKey, depthKey) {
  assert(depthKey >= 0);
  return (sortKey & 0xff0000ff | depthKey << 8) >>> 0;
}
function makeSortKey(layer) {
  var programKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  if (layer & RendererLayer.TRANSLUCENT) return makeSortKeyTranslucent(layer);
  return makeSortKeyOpaque(layer, programKey);
}
function setSortKeyDepthKey(sortKey, depthKey) {
  var isTranslucent = !!(sortKey >>> 31 & 1);
  return isTranslucent ? setSortKeyTranslucentDepth(sortKey, depthKey) : setSortKeyOpaqueDepth(sortKey, depthKey);
}
function setSortKeyDepth(sortKey, depth) {
  var maxDepth = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : MAX_DEPTH;
  var isTranslucent = !!(sortKey >>> 31 & 1);
  var depthKey = makeDepthKey(depth, isTranslucent, maxDepth);
  return isTranslucent ? setSortKeyTranslucentDepth(sortKey, depthKey) : setSortKeyOpaqueDepth(sortKey, depthKey);
}
function getSortKeyDepth(sortKey) {
  var isTranslucent = !!(sortKey >>> 31 & 1);
  if (isTranslucent) return sortKey >>> 8 & 0xffff;
  return sortKey >>> 8 & 0xfffc | sortKey & 0x03;
}

var mtxOpenGLFromD3D = mat4.fromValues(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 2, 0, 0, 0, -1, 1);

// Converts a projection matrix from D3D-style Z range [0, 1] to OpenGL-style Z range [-1, 1]
function projectionMatrixOpenGLFromD3D(m) {
  mat4.mul(m, mtxOpenGLFromD3D, m);
}
var mtxD3DFromOpenGL = mat4.fromValues(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0.5, 0, 0, 0, 0.5, 1);

// Converts a projection matrix from OpenGL-style Z range [-1, 1] to D3D-style Z range [0, 1]
function projectionMatrixD3DFromOpenGL(m) {
  mat4.mul(m, mtxD3DFromOpenGL, m);
}

/**
 * Convert a projection matrix {@param m} between differing clip spaces.
 *
 * There are two kinds of clip-space conventions in active use in graphics APIs, differing in the
 * range of the Z axis: OpenGL (and thus GL ES and WebGL) use a Z range of [-1, 1] which matches
 * the X and Y axis ranges. Direct3D, Vulkan, Metal, and WebGPU all use a Z range of [0, 1], which
 * differs from the X and Y axis ranges, but makes sense from the perspective of a camera: a camera
 * can see to the left and right of it, above and below it, but only in front and not behind it.
 *
 * The [0, 1] convention for Z range also has better characteristics for "reversed depth". Since
 * floating point numbers have higher precision around 0 than around 1. We then get to choose where
 * to put the extra precise bits: close to the near plane, or close to the far plane.
 *
 * With OpenGL's [-1, 1] convention, both -1 and 1 have similar amounts of precision, so we don't
 * get to make the same choice, and our higher precision around 0 is stuck in the middle of the
 * scene, which doesn't particularly help.
 *
 * The gl-matrix library has two different kinds of entry points: {@method mat4.perpsective} will
 * generate a matrix with a [-1, 1] clip space, corresponding to {@see ClipSpaceNearZ.NegativeOne},
 * but {@method mat4.perspectiveZO} will generate a matrix with a [0, 1] clip space, corresponding
 * to {@see ClipSpaceNearZ.Zero}.
 *
 * This function does nothing if {@param dst} and {@param src} are the same.
 */
function projectionMatrixConvertClipSpaceNearZ(m, dst, src) {
  if (dst === src) return;
  if (dst === ClipSpaceNearZ.NEGATIVE_ONE) projectionMatrixOpenGLFromD3D(m);else if (dst === ClipSpaceNearZ.ZERO) projectionMatrixD3DFromOpenGL(m);
}

// Helpers to fill vertex buffers.

// import { ReadonlyMat2d } from 'gl-matrix';

function fillVec3v(d, offs, v) {
  var v3 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  d[offs + 0] = v[0];
  d[offs + 1] = v[1];
  d[offs + 2] = v[2];
  d[offs + 3] = v3;
  return 4;
}
function fillVec4(d, offs, v0) {
  var v1 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var v2 = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
  var v3 = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 0;
  d[offs + 0] = v0;
  d[offs + 1] = v1;
  d[offs + 2] = v2;
  d[offs + 3] = v3;
  return 4;
}
function fillVec4v(d, offs, v) {
  d[offs + 0] = v[0];
  d[offs + 1] = v[1];
  d[offs + 2] = v[2];
  d[offs + 3] = v[3];
  return 4;
}
function fillColor(d, offs, c) {
  var a = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : c.a;
  d[offs + 0] = c.r;
  d[offs + 1] = c.g;
  d[offs + 2] = c.b;
  d[offs + 3] = a;
  return 4;
}

// All of our matrices are row-major.
function fillMatrix4x4(d, offs, m) {
  // d[offs + 0] = m[0];
  // d[offs + 1] = m[4];
  // d[offs + 2] = m[8];
  // d[offs + 3] = m[12];
  // d[offs + 4] = m[1];
  // d[offs + 5] = m[5];
  // d[offs + 6] = m[9];
  // d[offs + 7] = m[13];
  // d[offs + 8] = m[2];
  // d[offs + 9] = m[6];
  // d[offs + 10] = m[10];
  // d[offs + 11] = m[14];
  // d[offs + 12] = m[3];
  // d[offs + 13] = m[7];
  // d[offs + 14] = m[11];
  // d[offs + 15] = m[15];
  d[offs + 0] = m[0];
  d[offs + 1] = m[1];
  d[offs + 2] = m[2];
  d[offs + 3] = m[3];
  d[offs + 4] = m[4];
  d[offs + 5] = m[5];
  d[offs + 6] = m[6];
  d[offs + 7] = m[7];
  d[offs + 8] = m[8];
  d[offs + 9] = m[9];
  d[offs + 10] = m[10];
  d[offs + 11] = m[11];
  d[offs + 12] = m[12];
  d[offs + 13] = m[13];
  d[offs + 14] = m[14];
  d[offs + 15] = m[15];
  return 4 * 4;
}

// export function fillMatrix4x3(d: Float32Array, offs: number, m: ReadonlyMat4): number {
//   d[offs + 0] = m[0];
//   d[offs + 1] = m[4];
//   d[offs + 2] = m[8];
//   d[offs + 3] = m[12];
//   d[offs + 4] = m[1];
//   d[offs + 5] = m[5];
//   d[offs + 6] = m[9];
//   d[offs + 7] = m[13];
//   d[offs + 8] = m[2];
//   d[offs + 9] = m[6];
//   d[offs + 10] = m[10];
//   d[offs + 11] = m[14];
//   return 4 * 3;
// }

// export function fillMatrix3x2(d: Float32Array, offs: number, m: ReadonlyMat2d): number {
//   // 3x2 matrices are actually sent across as 4x2.
//   const ma = m[0],
//     mb = m[1];
//   const mc = m[2],
//     md = m[3];
//   const mx = m[4],
//     my = m[5];
//   d[offs + 0] = ma;
//   d[offs + 1] = mc;
//   d[offs + 2] = mx;
//   d[offs + 3] = 0;
//   d[offs + 4] = mb;
//   d[offs + 5] = md;
//   d[offs + 6] = my;
//   d[offs + 7] = 0;
//   return 4 * 2;
// }

// export function fillMatrix4x2(d: Float32Array, offs: number, m: ReadonlyMat4): number {
//   // The bottom two rows are basically just ignored in a 4x2.
//   d[offs + 0] = m[0];
//   d[offs + 1] = m[4];
//   d[offs + 2] = m[8];
//   d[offs + 3] = m[12];
//   d[offs + 4] = m[1];
//   d[offs + 5] = m[5];
//   d[offs + 6] = m[9];
//   d[offs + 7] = m[13];
//   return 4 * 2;
// }

var RenderInstFlags = /*#__PURE__*/function (RenderInstFlags) {
  RenderInstFlags[RenderInstFlags["None"] = 0] = "None";
  RenderInstFlags[RenderInstFlags["Indexed"] = 1] = "Indexed";
  RenderInstFlags[RenderInstFlags["AllowSkippingIfPipelineNotReady"] = 2] = "AllowSkippingIfPipelineNotReady";
  // Mostly for error checking.
  RenderInstFlags[RenderInstFlags["Template"] = 4] = "Template";
  RenderInstFlags[RenderInstFlags["Draw"] = 8] = "Draw";
  // Which flags are inherited from templates...
  RenderInstFlags[RenderInstFlags["InheritedFlags"] = 3] = "InheritedFlags";
  return RenderInstFlags;
}({});
var RenderInst = /*#__PURE__*/function () {
  function RenderInst() {
    _classCallCheck(this, RenderInst);
    this.sortKey = 0;
    // Debugging pointer for whomever wants it...
    this.debug = null;
    this.uniforms = [];
    this.bindingDescriptors = nArray(1, function () {
      return {
        bindingLayout: null,
        samplerBindings: [],
        uniformBufferBindings: []
      };
    });
    this.dynamicUniformBufferByteOffsets = nArray(4, function () {
      return 0;
    });
    this.flags = 0;
    this.vertexBuffers = null;
    this.indexBuffer = null;
    this.drawStart = 0;
    this.drawCount = 0;
    this.drawInstanceCount = 0;
    this.renderPipelineDescriptor = {
      inputLayout: null,
      megaStateDescriptor: copyMegaState(defaultMegaState),
      program: null,
      topology: PrimitiveTopology.TRIANGLES,
      colorAttachmentFormats: [],
      depthStencilAttachmentFormat: null,
      sampleCount: 1
    };
    this.reset();
  }

  /**
   * Resets a render inst to be boring, so it can re-enter the pool.
   * Normally, you should not need to call this.
   */
  return _createClass(RenderInst, [{
    key: "reset",
    value: function reset() {
      this.sortKey = 0;
      this.flags = RenderInstFlags.AllowSkippingIfPipelineNotReady;
      this.vertexBuffers = null;
      this.indexBuffer = null;
      this.renderPipelineDescriptor.inputLayout = null;
    }

    /**
     * Copies the fields from another render inst {@param o} to this render inst.
     * Normally, you should not need to call this.
     */
  }, {
    key: "setFromTemplate",
    value: function setFromTemplate(o) {
      var _obd$samplerBindings, _obd$uniformBufferBin;
      setMegaStateFlags(this.renderPipelineDescriptor.megaStateDescriptor, o.renderPipelineDescriptor.megaStateDescriptor);
      this.renderPipelineDescriptor.program = o.renderPipelineDescriptor.program;
      this.renderPipelineDescriptor.inputLayout = o.renderPipelineDescriptor.inputLayout;
      this.renderPipelineDescriptor.topology = o.renderPipelineDescriptor.topology;
      this.renderPipelineDescriptor.colorAttachmentFormats.length = Math.max(this.renderPipelineDescriptor.colorAttachmentFormats.length, o.renderPipelineDescriptor.colorAttachmentFormats.length);
      for (var i = 0; i < o.renderPipelineDescriptor.colorAttachmentFormats.length; i++) this.renderPipelineDescriptor.colorAttachmentFormats[i] = o.renderPipelineDescriptor.colorAttachmentFormats[i];
      this.renderPipelineDescriptor.depthStencilAttachmentFormat = o.renderPipelineDescriptor.depthStencilAttachmentFormat;
      this.renderPipelineDescriptor.sampleCount = o.renderPipelineDescriptor.sampleCount;
      this.uniformBuffer = o.uniformBuffer;
      this.uniforms = _toConsumableArray(o.uniforms);
      this.drawCount = o.drawCount;
      this.drawStart = o.drawStart;
      this.drawInstanceCount = o.drawInstanceCount;
      this.vertexBuffers = o.vertexBuffers;
      this.indexBuffer = o.indexBuffer;
      this.flags = this.flags & ~RenderInstFlags.InheritedFlags | o.flags & RenderInstFlags.InheritedFlags;
      this.sortKey = o.sortKey;
      var tbd = this.bindingDescriptors[0];
      var obd = o.bindingDescriptors[0];
      this.setBindingLayout({
        numSamplers: (_obd$samplerBindings = obd.samplerBindings) === null || _obd$samplerBindings === void 0 ? void 0 : _obd$samplerBindings.length,
        numUniformBuffers: (_obd$uniformBufferBin = obd.uniformBufferBindings) === null || _obd$uniformBufferBin === void 0 ? void 0 : _obd$uniformBufferBin.length
      });
      for (var _i = 0; _i < Math.min(tbd.uniformBufferBindings.length, obd.uniformBufferBindings.length); _i++) tbd.uniformBufferBindings[_i].size = o.bindingDescriptors[0].uniformBufferBindings[_i].size;
      this.setSamplerBindingsFromTextureMappings(obd.samplerBindings);
      for (var _i2 = 0; _i2 < o.dynamicUniformBufferByteOffsets.length; _i2++) this.dynamicUniformBufferByteOffsets[_i2] = o.dynamicUniformBufferByteOffsets[_i2];
    }
  }, {
    key: "validate",
    value: function validate() {
      // Validate uniform buffer bindings.
      for (var i = 0; i < this.bindingDescriptors.length; i++) {
        var bd = this.bindingDescriptors[i];
        for (var j = 0; j < ((_bd$uniformBufferBind = bd.uniformBufferBindings) === null || _bd$uniformBufferBind === void 0 ? void 0 : _bd$uniformBufferBind.length); j++) {
          var _bd$uniformBufferBind;
          assert(bd.uniformBufferBindings[j].size > 0);
        }
      }
      assert(this.drawCount > 0);
    }

    /**
     * Set the {@see Program} that this render inst will render with. This is part of the automatic
     * pipeline building facilities. At render time, a pipeline will be automatically and constructed from
     * the pipeline parameters.
     */
  }, {
    key: "setProgram",
    value: function setProgram(program) {
      this.renderPipelineDescriptor.program = program;
    }

    /**
     * Set the {@see MegaStateDescriptor} that this render inst will render with. This is part of the automatic
     * pipeline building facilities. At render time, a pipeline will be automatically and constructed from
     * the pipeline parameters.
     */
  }, {
    key: "setMegaStateFlags",
    value: function setMegaStateFlags$1(r) {
      setMegaStateFlags(this.renderPipelineDescriptor.megaStateDescriptor, r);
      return this.renderPipelineDescriptor.megaStateDescriptor;
    }

    /**
     * Retrieve the {@see MegaStateDescriptor} property bag that this will render with. This is similar to
     * {@see setMegaStateFlags} but allows you to set fields directly on the internal property bag, rather than
     * merge them. This can be slightly more efficient.
     */
  }, {
    key: "getMegaStateFlags",
    value: function getMegaStateFlags() {
      return this.renderPipelineDescriptor.megaStateDescriptor;
    }

    /**
     * Sets the vertex input configuration to be used by this render instance.
     * The {@see InputLayout} is used to construct the pipeline as part of the automatic pipeline building
     * facilities, while the {@see VertexBufferDescriptor} and {@see IndexBufferDescriptor} is used for the render.
     */
  }, {
    key: "setVertexInput",
    value: function setVertexInput(inputLayout, vertexBuffers, indexBuffer) {
      this.vertexBuffers = vertexBuffers;
      this.indexBuffer = indexBuffer;
      this.renderPipelineDescriptor.inputLayout = inputLayout;
    }
  }, {
    key: "setBindingLayout",
    value: function setBindingLayout(bindingLayout) {
      assert(bindingLayout.numUniformBuffers < this.dynamicUniformBufferByteOffsets.length);
      for (var i = this.bindingDescriptors[0].uniformBufferBindings.length; i < bindingLayout.numUniformBuffers; i++) this.bindingDescriptors[0].uniformBufferBindings.push({
        binding: i,
        buffer: null,
        size: 0
      });
      for (var _i3 = this.bindingDescriptors[0].samplerBindings.length; _i3 < bindingLayout.numSamplers; _i3++) this.bindingDescriptors[0].samplerBindings.push({
        sampler: null,
        texture: null
      });
    }
  }, {
    key: "drawIndexes",
    value: function drawIndexes(indexCount) {
      var indexStart = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      this.flags = setBitFlagEnabled(this.flags, RenderInstFlags.Indexed, true);
      this.drawCount = indexCount;
      this.drawStart = indexStart;
      this.drawInstanceCount = 1;
    }
  }, {
    key: "drawIndexesInstanced",
    value: function drawIndexesInstanced(indexCount, instanceCount) {
      var indexStart = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
      this.flags = setBitFlagEnabled(this.flags, RenderInstFlags.Indexed, true);
      this.drawCount = indexCount;
      this.drawStart = indexStart;
      this.drawInstanceCount = instanceCount;
    }
  }, {
    key: "drawPrimitives",
    value: function drawPrimitives(primitiveCount) {
      var primitiveStart = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      this.flags = setBitFlagEnabled(this.flags, RenderInstFlags.Indexed, false);
      this.drawCount = primitiveCount;
      this.drawStart = primitiveStart;
      this.drawInstanceCount = 1;
    }

    /**
     * account for WebGL1
     */
  }, {
    key: "setUniforms",
    value: function setUniforms(bufferIndex, uniforms) {
      if (uniforms.length === 0) {
        return;
      }

      // use later in WebGL1
      this.uniforms[bufferIndex] = uniforms;

      // calc buffer size
      var offset = 0;
      var uboBuffer = [];
      uniforms.forEach(function (uniform) {
        var value = uniform.value;

        // number | number[] | Float32Array
        if (isNumber(value) || Array.isArray(value) || value instanceof Float32Array) {
          var array = isNumber(value) ? [value] : value;
          var formatByteSize = array.length > 4 ? 4 : array.length;

          // std140 UBO layout
          var _emptySpace = 4 - offset % 4;
          if (_emptySpace !== 4) {
            if (_emptySpace >= formatByteSize) ; else {
              offset += _emptySpace;
              for (var j = 0; j < _emptySpace; j++) {
                uboBuffer.push(0); // padding
              }
            }
          }
          offset += array.length;
          uboBuffer.push.apply(uboBuffer, _toConsumableArray(array));
        }
      });

      // padding
      var emptySpace = 4 - uboBuffer.length % 4;
      if (emptySpace !== 4) {
        for (var j = 0; j < emptySpace; j++) {
          uboBuffer.push(0);
        }
      }

      // upload UBO
      var offs = this.allocateUniformBuffer(bufferIndex, uboBuffer.length);
      var d = this.mapUniformBufferF32(bufferIndex);
      for (var i = 0; i < uboBuffer.length; i += 4) {
        offs += fillVec4(d, offs, uboBuffer[i], uboBuffer[i + 1], uboBuffer[i + 2], uboBuffer[i + 3]);
      }
    }
  }, {
    key: "setUniformBuffer",
    value: function setUniformBuffer(uniformBuffer) {
      this.uniformBuffer = uniformBuffer;
    }

    /**
     * Allocates {@param wordCount} words from the uniform buffer and assigns it to the buffer
     * slot at index {@param bufferIndex}. As a convenience, this also directly returns the same
     * offset into the uniform buffer, in words, that would be returned by a subsequent call to
     * {@see getUniformBufferOffset}.
     */
  }, {
    key: "allocateUniformBuffer",
    value: function allocateUniformBuffer(bufferIndex, wordCount) {
      var _this$bindingDescript;
      assert(((_this$bindingDescript = this.bindingDescriptors[0].uniformBufferBindings) === null || _this$bindingDescript === void 0 ? void 0 : _this$bindingDescript.length) < this.dynamicUniformBufferByteOffsets.length);
      this.dynamicUniformBufferByteOffsets[bufferIndex] = this.uniformBuffer.allocateChunk(wordCount) << 2;
      var dst = this.bindingDescriptors[0].uniformBufferBindings[bufferIndex];
      dst.size = wordCount << 2;
      return this.getUniformBufferOffset(bufferIndex);
    }

    /**
     * Returns the offset into the uniform buffer, in words, that is assigned to the buffer slot
     * at index {@param bufferIndex}, to be used with e.g. {@see mapUniformBufferF32}.
     */
  }, {
    key: "getUniformBufferOffset",
    value: function getUniformBufferOffset(bufferIndex) {
      var wordOffset = this.dynamicUniformBufferByteOffsets[bufferIndex] >>> 2;
      return wordOffset;
    }

    /**
     * This is a convenience wrapper for {@see RenderDynamicUniformBuffer.mapBufferF32}, but uses
     * the values previously assigned for the uniform buffer slot at index {@param bufferIndex}.
     * Like {@see RenderDynamicUniformBuffer.mapBufferF32}, this does not return a slice for the
     * buffer; you need to write to it with the correct uniform buffer offset; this will usually be
     * returned by {@see allocateUniformBuffer}.
     */
  }, {
    key: "mapUniformBufferF32",
    value: function mapUniformBufferF32(bufferIndex) {
      return this.uniformBuffer.mapBufferF32();
    }

    /**
     * Retrieve the {@see RenderDynamicUniformBuffer} that this render inst will use to allocate.
     */
  }, {
    key: "getUniformBuffer",
    value: function getUniformBuffer() {
      return this.uniformBuffer;
    }

    /**
     * Sets the {@param SamplerBinding}s in use by this render instance.
     *
     * Note that {@see RenderInst} has a method of doing late binding, intended to solve cases where live render
     * targets are used, which can have difficult control flow consequences for users. Pass a string instead of a
     * SamplerBinding to record that it can be resolved later, and use {@see RenderInst.resolveLateSamplerBinding}
     * or equivalent to fill it in later.
     */
  }, {
    key: "setSamplerBindingsFromTextureMappings",
    value: function setSamplerBindingsFromTextureMappings(mappings) {
      mappings = mappings.filter(function (m) {
        return m;
      });
      for (var i = 0; i < this.bindingDescriptors[0].samplerBindings.length; i++) {
        var dst = this.bindingDescriptors[0].samplerBindings[i];
        var binding = mappings[i];
        if (binding === undefined || binding === null) {
          dst.texture = null;
          dst.sampler = null;
          continue;
        }
        dst.texture = binding.texture;
        dst.sampler = binding.sampler;
      }
    }

    /**
     * Sets whether this render inst should be skipped if the render pipeline isn't ready.
     *
     * Some draws of objects can be skipped if the pipelines aren't ready. Others are more
     * crucial to draw, and so this can be set to force for the pipeline to become available.
     *
     * By default, this is true.
     */
  }, {
    key: "setAllowSkippingIfPipelineNotReady",
    value: function setAllowSkippingIfPipelineNotReady(v) {
      this.flags = setBitFlagEnabled(this.flags, RenderInstFlags.AllowSkippingIfPipelineNotReady, v);
    }
  }, {
    key: "setAttachmentFormatsFromRenderPass",
    value: function setAttachmentFormatsFromRenderPass(device, passRenderer) {
      var passDescriptor = device.queryRenderPass(passRenderer);
      var sampleCount = -1;
      for (var i = 0; i < passDescriptor.colorAttachment.length; i++) {
        var colorAttachmentDescriptor = passDescriptor.colorAttachment[i] !== null ? device.queryRenderTarget(passDescriptor.colorAttachment[i]) : null;
        this.renderPipelineDescriptor.colorAttachmentFormats[i] = colorAttachmentDescriptor !== null ? colorAttachmentDescriptor.format : null;
        if (colorAttachmentDescriptor !== null) {
          if (sampleCount === -1) sampleCount = colorAttachmentDescriptor.sampleCount;else assert(sampleCount === colorAttachmentDescriptor.sampleCount);
        }
      }
      var depthStencilAttachmentDescriptor = passDescriptor.depthStencilAttachment !== null ? device.queryRenderTarget(passDescriptor.depthStencilAttachment) : null;
      this.renderPipelineDescriptor.depthStencilAttachmentFormat = depthStencilAttachmentDescriptor !== null ? depthStencilAttachmentDescriptor.format : null;
      if (depthStencilAttachmentDescriptor !== null) {
        if (sampleCount === -1) sampleCount = depthStencilAttachmentDescriptor.sampleCount;else assert(sampleCount === depthStencilAttachmentDescriptor.sampleCount);
      }
      assert(sampleCount > 0);
      this.renderPipelineDescriptor.sampleCount = sampleCount;
    }
  }, {
    key: "drawOnPass",
    value: function drawOnPass(cache, passRenderer) {
      var _this = this;
      var device = cache.device;
      this.setAttachmentFormatsFromRenderPass(device, passRenderer);
      var gfxPipeline = cache.createRenderPipeline(this.renderPipelineDescriptor);
      var pipelineReady = device.pipelineQueryReady(gfxPipeline);
      if (!pipelineReady) {
        if (this.flags & RenderInstFlags.AllowSkippingIfPipelineNotReady) {
          return false;
        }
        device.pipelineForceReady(gfxPipeline);
      }
      passRenderer.setPipeline(gfxPipeline);
      passRenderer.setVertexInput(this.renderPipelineDescriptor.inputLayout, this.vertexBuffers, this.indexBuffer);

      // upload uniforms
      for (var i = 0; i < this.bindingDescriptors[0].uniformBufferBindings.length; i++) {
        this.bindingDescriptors[0].uniformBufferBindings[i].buffer = assertExists(this.uniformBuffer.buffer);
        this.bindingDescriptors[0].uniformBufferBindings[i].offset = this.dynamicUniformBufferByteOffsets[i];
      }
      if (this.renderPipelineDescriptor.program.gl_program) {
        this.uniforms.forEach(function (uniforms) {
          var uniformsMap = {};
          uniforms.forEach(function (_ref) {
            var name = _ref.name,
              value = _ref.value;
            uniformsMap[name] = value;
          });
          _this.renderPipelineDescriptor.program.setUniformsLegacy(uniformsMap);
        });
      }

      // TODO: Support multiple binding descriptors.
      var gfxBindings = cache.createBindings(_objectSpread(_objectSpread({}, this.bindingDescriptors[0]), {}, {
        pipeline: gfxPipeline
      }));
      passRenderer.setBindings(gfxBindings);
      if (this.flags & RenderInstFlags.Indexed) {
        passRenderer.drawIndexed(this.drawCount, this.drawInstanceCount, this.drawStart, 0, 0);
      } else {
        passRenderer.draw(this.drawCount, this.drawInstanceCount, this.drawStart, 0);
      }
      return true;
    }
  }]);
}();

var renderInstCompareNone = null;
function renderInstCompareSortKey(a, b) {
  return a.sortKey - b.sortKey;
}
var RenderInstExecutionOrder = /*#__PURE__*/function (RenderInstExecutionOrder) {
  RenderInstExecutionOrder[RenderInstExecutionOrder["Forwards"] = 0] = "Forwards";
  RenderInstExecutionOrder[RenderInstExecutionOrder["Backwards"] = 1] = "Backwards";
  return RenderInstExecutionOrder;
}({});
var RenderInstList = /*#__PURE__*/function () {
  function RenderInstList() {
    var compareFunction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : renderInstCompareSortKey;
    var executionOrder = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : RenderInstExecutionOrder.Forwards;
    _classCallCheck(this, RenderInstList);
    this.renderInsts = [];
    this.usePostSort = false;
    this.compareFunction = compareFunction;
    this.executionOrder = executionOrder;
  }

  /**
   * Determine whether to use post-sorting, based on some heuristics.
   */
  return _createClass(RenderInstList, [{
    key: "checkUsePostSort",
    value: function checkUsePostSort() {
      // Over a certain threshold, it's faster to push and then sort than insort directly...
      this.usePostSort = this.compareFunction !== null && this.renderInsts.length >= 500;
    }

    /**
     * Insert a render inst to the list. This directly inserts the render inst to
     * the position specified by the compare function, so the render inst must be
     * fully constructed at this point.
     */
  }, {
    key: "insertSorted",
    value: function insertSorted(renderInst) {
      if (this.compareFunction === null) {
        this.renderInsts.push(renderInst);
      } else if (this.usePostSort) {
        this.renderInsts.push(renderInst);
      } else {
        spliceBisectRight(this.renderInsts, renderInst, this.compareFunction);
      }
      this.checkUsePostSort();
    }
  }, {
    key: "submitRenderInst",
    value: function submitRenderInst(renderInst) {
      // TODO: drawCount = 0
      // renderInst.validate();
      renderInst.flags |= RenderInstFlags.Draw;
      this.insertSorted(renderInst);
    }
  }, {
    key: "ensureSorted",
    value: function ensureSorted() {
      if (this.usePostSort) {
        if (this.renderInsts.length !== 0) this.renderInsts.sort(this.compareFunction);
        this.usePostSort = false;
      }
    }
  }, {
    key: "drawOnPassRendererNoReset",
    value: function drawOnPassRendererNoReset(cache, passRenderer) {
      this.ensureSorted();
      if (this.executionOrder === RenderInstExecutionOrder.Forwards) {
        for (var i = 0; i < this.renderInsts.length; i++) this.renderInsts[i].drawOnPass(cache, passRenderer);
      } else {
        for (var _i = this.renderInsts.length - 1; _i >= 0; _i--) this.renderInsts[_i].drawOnPass(cache, passRenderer);
      }
    }
  }, {
    key: "reset",
    value: function reset() {
      this.renderInsts.length = 0;
    }
  }, {
    key: "drawOnPassRenderer",
    value: function drawOnPassRenderer(cache, passRenderer) {
      this.drawOnPassRendererNoReset(cache, passRenderer);
      this.reset();
    }
  }]);
}();

var RenderInstPool = /*#__PURE__*/function () {
  function RenderInstPool() {
    _classCallCheck(this, RenderInstPool);
    // The pool contains all render insts that we've ever created.
    this.pool = [];
    // The number of render insts currently allocated out to the user.
    this.allocCount = 0;
  }
  return _createClass(RenderInstPool, [{
    key: "allocRenderInstIndex",
    value: function allocRenderInstIndex() {
      this.allocCount++;
      if (this.allocCount > this.pool.length) {
        this.pool.push(new RenderInst());
      }
      return this.allocCount - 1;
    }
  }, {
    key: "popRenderInst",
    value: function popRenderInst() {
      this.allocCount--;
    }
  }, {
    key: "reset",
    value: function reset() {
      for (var i = 0; i < this.pool.length; i++) {
        this.pool[i].reset();
      }
      this.allocCount = 0;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      this.pool.length = 0;
      this.allocCount = 0;
    }
  }]);
}();

var RenderInstManager = /*#__PURE__*/function () {
  function RenderInstManager(renderCache) {
    _classCallCheck(this, RenderInstManager);
    this.instPool = new RenderInstPool();
    this.templatePool = new RenderInstPool();
    this.simpleRenderInstList = new RenderInstList();
    this.currentRenderInstList = this.simpleRenderInstList;
    this.renderCache = renderCache;
  }

  /**
   * Creates a new RenderInst object and returns it. If there is a template
   * pushed onto the template stack, then its values will be used as a base for this
   * render inst.
   */
  return _createClass(RenderInstManager, [{
    key: "newRenderInst",
    value: function newRenderInst() {
      var templateIndex = this.templatePool.allocCount - 1;
      var renderInstIndex = this.instPool.allocRenderInstIndex();
      var renderInst = this.instPool.pool[renderInstIndex];
      renderInst.debug = null;
      if (templateIndex >= 0) renderInst.setFromTemplate(this.templatePool.pool[templateIndex]);
      return renderInst;
    }

    /**
     * Submits RenderInst to the current render inst list. Note that
     * this assumes the render inst was fully filled in, so do not modify it
     * after submitting it.
     */
  }, {
    key: "submitRenderInst",
    value: function submitRenderInst(renderInst) {
      var list = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.currentRenderInstList;
      list.submitRenderInst(renderInst);
    }

    /**
     * Sets the currently active render inst list. This is the list that will
     * be used by @param submitRenderInst}. If you use this function, please
     * make sure to call {@see disableSimpleMode} when the RenderInstManager
     * is created, to ensure that nobody uses the "legacy" APIs. Failure to do
     * so might cause memory leaks or other problems.
     */
  }, {
    key: "setCurrentRenderInstList",
    value: function setCurrentRenderInstList(list) {
      assert(this.simpleRenderInstList === null);
      this.currentRenderInstList = list;
    }

    /**
     * Pushes a new template render inst to the template stack. All properties set
     * on the topmost template on the template stack will be the defaults for both
     * for any future render insts created. Once done with a template, call
     * {@param popTemplateRenderInst} to pop it off the template stack.
     */
  }, {
    key: "pushTemplateRenderInst",
    value: function pushTemplateRenderInst() {
      var templateIndex = this.templatePool.allocCount - 1;
      var newTemplateIndex = this.templatePool.allocRenderInstIndex();
      var newTemplate = this.templatePool.pool[newTemplateIndex];
      if (templateIndex >= 0) newTemplate.setFromTemplate(this.templatePool.pool[templateIndex]);
      newTemplate.flags |= RenderInstFlags.Template;
      return newTemplate;
    }
  }, {
    key: "popTemplateRenderInst",
    value: function popTemplateRenderInst() {
      this.templatePool.popRenderInst();
    }

    /**
     * Retrieves the current template render inst on the top of the template stack.
     */
  }, {
    key: "getTemplateRenderInst",
    value: function getTemplateRenderInst() {
      var templateIndex = this.templatePool.allocCount - 1;
      return this.templatePool.pool[templateIndex];
    }

    /**
     * Reset all allocated render insts. This should be called at the end of the frame,
     * once done with all of the allocated render insts and render inst lists.
     */
  }, {
    key: "resetRenderInsts",
    value: function resetRenderInsts() {
      // Retire the existing render insts.
      this.instPool.reset();
      if (this.simpleRenderInstList !== null) this.simpleRenderInstList.reset();
      // Ensure we aren't leaking templates.
      assert(this.templatePool.allocCount === 0);
    }
  }, {
    key: "destroy",
    value: function destroy() {
      this.instPool.destroy();
      this.renderCache.destroy();
    }

    /**
     * Disables the "simple" render inst list management API.
     */
  }, {
    key: "disableSimpleMode",
    value: function disableSimpleMode() {
      // This is a one-way street!
      this.simpleRenderInstList = null;
    }

    // /**
    //  * Execute all scheduled render insts in {@param list} onto the {@param RenderPass},
    //  * using {@param device} and {@param cache} to create any device-specific resources
    //  * necessary to complete the draws.
    //  */
    // drawListOnPassRenderer(list: RenderInstList, passRenderer: RenderPass): void {
    //   list.drawOnPassRenderer(this.renderCache, passRenderer);
    // }
  }, {
    key: "drawOnPassRenderer",
    value: function drawOnPassRenderer(passRenderer) {
      var list = assertExists(this.simpleRenderInstList);
      list.drawOnPassRenderer(this.renderCache, passRenderer);
    }
  }, {
    key: "drawOnPassRendererNoReset",
    value: function drawOnPassRendererNoReset(passRenderer) {
      var list = assertExists(this.simpleRenderInstList);
      list.drawOnPassRendererNoReset(this.renderCache, passRenderer);
    }
  }]);
}();

var RenderHelper = /*#__PURE__*/function () {
  // debugThumbnails: DebugThumbnailDrawer;

  function RenderHelper(parameters) {
    _classCallCheck(this, RenderHelper);
    this.parameters = parameters;
  }
  return _createClass(RenderHelper, [{
    key: "getDevice",
    value: function getDevice() {
      return this.device;
    }
  }, {
    key: "setDevice",
    value: function setDevice(device) {
      this.device = device;
      this.renderCache = new RenderCache(device);
      this.renderGraph = new RenderGraph(this.device);
      this.renderInstManager = new RenderInstManager(this.renderCache);
      this.uniformBuffer = new DynamicUniformBuffer(this.device);
      // this.debugThumbnails = new DebugThumbnailDrawer(this);
    }
  }, {
    key: "pushTemplateRenderInst",
    value: function pushTemplateRenderInst() {
      var template = this.renderInstManager.pushTemplateRenderInst();
      template.setUniformBuffer(this.uniformBuffer);
      return template;
    }
  }, {
    key: "prepareToRender",
    value: function prepareToRender() {
      this.uniformBuffer.prepareToRender();
    }
  }, {
    key: "destroy",
    value: function destroy() {
      if (this.uniformBuffer) {
        this.uniformBuffer.destroy();
      }
      if (this.renderInstManager) {
        this.renderInstManager.destroy();
      }
      if (this.renderCache) {
        this.renderCache.destroy();
      }
      if (this.renderGraph) {
        this.renderGraph.destroy();
      }
    }

    // getDebugTextDrawer(): TextDrawer | null {
    //   return null;
    // }
  }, {
    key: "getCache",
    value: function getCache() {
      return this.renderCache;
    }
  }, {
    key: "getDefines",
    value: function getDefines() {
      var _this$parameters, _this$parameters2, _this$parameters3;
      return {
        USE_TONEMAPPING: ((_this$parameters = this.parameters) === null || _this$parameters === void 0 ? void 0 : _this$parameters.toneMapping) && ((_this$parameters2 = this.parameters) === null || _this$parameters2 === void 0 ? void 0 : _this$parameters2.toneMapping) !== ToneMapping.NONE,
        toneMapping: (_this$parameters3 = this.parameters) === null || _this$parameters3 === void 0 ? void 0 : _this$parameters3.toneMapping
      };
    }
  }]);
}();

var RGRenderTargetDescription = /*#__PURE__*/function () {
  function RGRenderTargetDescription(format) {
    _classCallCheck(this, RGRenderTargetDescription);
    this.width = 0;
    this.height = 0;
    this.sampleCount = 0;
    this.colorClearColor = 'load';
    this.depthClearValue = 'load';
    this.stencilClearValue = 'load';
    this.format = format;
  }

  /**
   * Set the dimensions of a render target description.
   */
  return _createClass(RGRenderTargetDescription, [{
    key: "setDimensions",
    value: function setDimensions(width, height, sampleCount) {
      this.width = width;
      this.height = height;
      this.sampleCount = sampleCount;
    }
  }, {
    key: "copyDimensions",
    value: function copyDimensions(desc) {
      this.width = desc.width;
      this.height = desc.height;
      this.sampleCount = desc.sampleCount;
    }
  }]);
}();

function makeAttachmentClearDescriptor(clearColor) {
  return {
    colorClearColor: clearColor,
    // depthClearValue: reverseDepthForClearValue(1.0),
    depthClearValue: 1,
    stencilClearValue: 0.0
  };
}
var standardFullClearRenderPassDescriptor = makeAttachmentClearDescriptor(colorNewFromRGBA(0.88, 0.88, 0.88, 1.0));
var opaqueBlackFullClearRenderPassDescriptor = makeAttachmentClearDescriptor(OpaqueBlack);
var opaqueWhiteFullClearRenderPassDescriptor = makeAttachmentClearDescriptor(OpaqueWhite);
var AntialiasingMode = /*#__PURE__*/function (AntialiasingMode) {
  AntialiasingMode[AntialiasingMode["None"] = 0] = "None";
  AntialiasingMode[AntialiasingMode["FXAA"] = 1] = "FXAA";
  AntialiasingMode[AntialiasingMode["MSAAx4"] = 2] = "MSAAx4";
  return AntialiasingMode;
}({});
function selectFormatSimple(slot) {
  if (slot === RGAttachmentSlot.Color0) {
    return Format.U8_RGBA_RT;
  }
  if (slot === RGAttachmentSlot.DepthStencil) {
    return Format.D24_S8;
  }
  throw new Error('whoops');
}
function selectSampleCount(renderInput) {
  if (renderInput.antialiasingMode === AntialiasingMode.MSAAx4) {
    return 4;
  }
  return 1;
}
function setBackbufferDescSimple(desc, renderInput) {
  var sampleCount = selectSampleCount(renderInput);
  desc.setDimensions(renderInput.backbufferWidth, renderInput.backbufferHeight, sampleCount);
}
function makeBackbufferDescSimple(slot, renderInput, clearDescriptor) {
  var pixelFormat = selectFormatSimple(slot);
  var desc = new RGRenderTargetDescription(pixelFormat);
  setBackbufferDescSimple(desc, renderInput);
  if (clearDescriptor !== null) {
    desc.colorClearColor = clearDescriptor.colorClearColor;
    desc.depthClearValue = clearDescriptor.depthClearValue;
    desc.stencilClearValue = clearDescriptor.stencilClearValue;
  }
  return desc;
}

var TextureMapping = /*#__PURE__*/function () {
  function TextureMapping() {
    _classCallCheck(this, TextureMapping);
    this.texture = null;
    this.sampler = null;
    this.width = 0;
    this.height = 0;
    this.lodBias = 0;
  }
  return _createClass(TextureMapping, [{
    key: "reset",
    value:
    // GL sucks. This is a convenience when building texture matrices.
    // The core renderer does not use this code at all.
    // flipY: boolean = false;

    function reset() {
      this.texture = null;
      this.sampler = null;
      this.width = 0;
      this.height = 0;
      this.lodBias = 0;
      // this.flipY = false;
    }
  }, {
    key: "copy",
    value: function copy(other) {
      this.texture = other.texture;
      this.sampler = other.sampler;
      this.width = other.width;
      this.height = other.height;
      this.lodBias = other.lodBias;
      // this.flipY = other.flipY;
    }
  }]);
}();

// Public API for saving off copies of images for temporal-style effects.
var TemporalTexture = /*#__PURE__*/function () {
  function TemporalTexture() {
    _classCallCheck(this, TemporalTexture);
    // These names might be a bit confusing, but they're named relative to the graph.
    // outputTexture is the target of a resolve, inputTexture is the source for sampling.
    this.inputTexture = null;
    this.outputTexture = null;
  }
  return _createClass(TemporalTexture, [{
    key: "setDescription",
    value: function setDescription(device, desc) {
      // Updating the description will happen at the start of the frame,
      // so we need to keep the inputTexture alive (the previous frame's texture),
      // and create a new outputTexture.

      if (this.inputTexture !== this.outputTexture) {
        if (this.inputTexture !== null) this.inputTexture.destroy();

        // Set the input texture to our old output texture.
        this.inputTexture = this.outputTexture;
      }
      assert(this.inputTexture === this.outputTexture);
      if (this.outputTexture !== null && this.outputTexture.matchesDescription(desc)) return;
      this.outputTexture = new SingleSampledTexture(device, desc);
      if (this.inputTexture === null) this.inputTexture = this.outputTexture;
    }
  }, {
    key: "getTextureForSampling",
    value: function getTextureForSampling() {
      return this.inputTexture !== null ? this.inputTexture.texture : null;
    }
  }, {
    key: "getTextureForResolving",
    value: function getTextureForResolving() {
      var _this$outputTexture;
      return (_this$outputTexture = this.outputTexture) === null || _this$outputTexture === void 0 ? void 0 : _this$outputTexture.texture;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      if (this.outputTexture !== null && this.outputTexture !== this.inputTexture) {
        this.outputTexture.destroy();
        this.outputTexture = null;
      }
      if (this.inputTexture !== null) {
        this.inputTexture.destroy();
        this.inputTexture = null;
      }
    }
  }]);
}();

var _excluded$1 = ["style"];
var Light = /*#__PURE__*/function (_DisplayObject) {
  function Light() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      style = _ref.style,
      rest = _objectWithoutProperties(_ref, _excluded$1);
    _classCallCheck(this, Light);
    return _callSuper(this, Light, [_objectSpread({
      type: Light.tag,
      style: _objectSpread({
        intensity: Math.PI
      }, style)
    }, rest)]);
  }
  _inherits(Light, _DisplayObject);
  return _createClass(Light);
}(DisplayObject);
Light.PARSED_STYLE_LIST = new Set([].concat(_toConsumableArray(DisplayObject.PARSED_STYLE_LIST), ['intensity']));
Light.tag = 'light';

var _excluded = ["style"];
var FogType = /*#__PURE__*/function (FogType) {
  FogType[FogType["NONE"] = 0] = "NONE";
  FogType[FogType["EXP"] = 1] = "EXP";
  FogType[FogType["EXP2"] = 2] = "EXP2";
  FogType[FogType["LINEAR"] = 3] = "LINEAR";
  return FogType;
}({});
var Fog = /*#__PURE__*/function (_DisplayObject) {
  function Fog() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      style = _ref.style,
      rest = _objectWithoutProperties(_ref, _excluded);
    _classCallCheck(this, Fog);
    return _callSuper(this, Fog, [_objectSpread({
      type: Fog.tag,
      style: _objectSpread({
        type: FogType.NONE,
        fill: 'black',
        start: 1,
        end: 1000,
        density: 0
      }, style)
    }, rest)]);
  }

  // abstract getUniformWordCount(): number;
  _inherits(Fog, _DisplayObject);
  return _createClass(Fog);
}(DisplayObject);
Fog.PARSED_STYLE_LIST = new Set([].concat(_toConsumableArray(DisplayObject.PARSED_STYLE_LIST), ['type', 'density', 'start', 'end']));
Fog.tag = 'fog';

function makeDataBuffer(device, usage, data) {
  var hint = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : BufferFrequencyHint.STATIC;
  var buffer = device.createBuffer({
    viewOrSize: data.byteLength,
    usage: usage,
    hint: hint
  });
  buffer.setSubData(0, new Uint8Array(data));
  return buffer;
}
var GeometryEvent = /*#__PURE__*/function (GeometryEvent) {
  GeometryEvent["CHANGED"] = "changed";
  return GeometryEvent;
}({});

/**
 * just hold descriptors of buffers & indices, won't use underlying GPU resources
 */
var BufferGeometry = /*#__PURE__*/function (_EventEmitter) {
  function BufferGeometry(device) {
    var _this;
    var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    _classCallCheck(this, BufferGeometry);
    _this = _callSuper(this, BufferGeometry);
    _this.drawMode = PrimitiveTopology.TRIANGLES;
    _this.vertexBuffers = [];
    _this.vertices = [];
    _this.inputLayoutDescriptor = {
      vertexBufferDescriptors: [],
      indexBufferFormat: null,
      program: null
    };
    _this.vertexCount = 0;
    _this.instancedCount = 0;
    _this.indexStart = 0;
    _this.primitiveStart = 0;
    _this.dirty = true;
    _this.meshes = [];
    _this.device = device;
    _this.props = props;
    return _this;
  }
  _inherits(BufferGeometry, _EventEmitter);
  return _createClass(BufferGeometry, [{
    key: "validate",
    value: function validate(mesh) {
      return true;
    }
  }, {
    key: "build",
    value: function build(meshes) {}
  }, {
    key: "computeBoundingBox",
    value: function computeBoundingBox() {
      return new AABB();
    }
  }, {
    key: "setIndexBuffer",
    value: function setIndexBuffer(indices) {
      if (this.indexBuffer) {
        this.indexBuffer.destroy();
      }
      this.indexBuffer = makeDataBuffer(this.device, BufferUsage.INDEX, new Uint32Array(ArrayBuffer.isView(indices) ? indices.buffer : indices).buffer);
      this.indices = indices;
      this.inputLayoutDescriptor.indexBufferFormat = Format.U32_R;
      return this;
    }
  }, {
    key: "setVertexBuffer",
    value: function setVertexBuffer(descriptor) {
      var _this2 = this;
      var bufferIndex = descriptor.bufferIndex,
        byteStride = descriptor.byteStride,
        stepMode = descriptor.stepMode,
        attributes = descriptor.attributes,
        data = descriptor.data;
      this.inputLayoutDescriptor.vertexBufferDescriptors[bufferIndex] = {
        arrayStride: byteStride,
        stepMode: stepMode,
        attributes: []
      };
      this.vertices[bufferIndex] = data;
      attributes.forEach(function (_ref) {
        var format = _ref.format,
          bufferByteOffset = _ref.bufferByteOffset,
          location = _ref.location,
          divisor = _ref.divisor;
          _ref.byteStride;
        var existed = _this2.inputLayoutDescriptor.vertexBufferDescriptors[bufferIndex].attributes.find(function (e) {
          return e.shaderLocation === location;
        });
        if (existed) {
          existed.format = format;
          existed.offset = bufferByteOffset;
          existed.divisor = divisor;
        } else {
          _this2.inputLayoutDescriptor.vertexBufferDescriptors[bufferIndex].attributes.push({
            format: format,
            offset: bufferByteOffset,
            shaderLocation: location,
            divisor: divisor
          });
        }
      });

      // create GPUBuffer
      if (this.vertexBuffers[bufferIndex]) {
        this.vertexBuffers[bufferIndex].destroy();
      }
      var buffer = makeDataBuffer(this.device, BufferUsage.VERTEX, data.buffer, BufferFrequencyHint.DYNAMIC);
      this.vertexBuffers[bufferIndex] = buffer;
      return this;
    }
  }, {
    key: "getVertexBuffer",
    value: function getVertexBuffer(bufferIndex) {
      return this.vertexBuffers[bufferIndex];
    }
  }, {
    key: "updateVertexBuffer",
    value: function updateVertexBuffer(bufferIndex, location, index, data) {
      var bufferDescriptor = this.inputLayoutDescriptor.vertexBufferDescriptors[bufferIndex];
      if (!bufferDescriptor) {
        return;
      }
      var arrayStride = bufferDescriptor.arrayStride;
      var descriptor = this.inputLayoutDescriptor.vertexBufferDescriptors[bufferIndex].attributes.find(function (d) {
        return d.shaderLocation === location;
      });
      if (descriptor) {
        var vertexBuffer = this.getVertexBuffer(bufferIndex);
        var offset = index * arrayStride;
        vertexBuffer.setSubData(descriptor.offset + offset, data);

        // TODO: update vertices
        // this.vertices[bufferIndex] = data;
      }
      this.emit(GeometryEvent.CHANGED);
    }
  }, {
    key: "updateIndices",
    value: function updateIndices(indices) {
      var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      if (this.indexBuffer) {
        this.indexBuffer.setSubData(offset, new Uint8Array(ArrayBuffer.isView(indices) ? indices : new Uint32Array(indices)));
      }
      return this;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      this.vertexBuffers.forEach(function (buffer) {
        if (buffer) {
          buffer.destroy();
        }
      });
      if (this.indexBuffer) {
        this.indexBuffer.destroy();
      }
      this.inputLayoutDescriptor.vertexBufferDescriptors = [];
      this.indexBuffer = undefined;
      this.vertexBuffers = [];
      this.indices = undefined;
      this.vertices = [];
      this.vertexCount = 0;
      this.instancedCount = 0;
    }
  }]);
}(EventEmitter);

var vert$5 = "#define GLSLIFY 1\nlayout(location = 0) in vec2 a_Position;\n\nout vec2 v_TexCoord;\n\nvoid main() {\n  v_TexCoord = 0.5 * (a_Position + 1.0);\n  gl_Position = vec4(a_Position, 0., 1.);\n\n  #ifdef VIEWPORT_ORIGIN_TL\n    v_TexCoord.y = 1.0 - v_TexCoord.y;\n  #endif\n}"; // eslint-disable-line

var frag$5 = "#define GLSLIFY 1\nuniform sampler2D u_Texture;\nin vec2 v_TexCoord;\n\nout vec4 outputColor;\n\nfloat MonochromeNTSC(vec3 t_Color) {\n  // NTSC primaries.\n  return dot(t_Color.rgb, vec3(0.299, 0.587, 0.114));\n}\n\nvec4 FXAA(PD_SAMPLER_2D(t_Texture), in vec2 t_PixelCenter, in vec2 t_InvResolution) {\n  // FXAA v2, based on implementations:\n  // http://www.geeks3d.com/20110405/fxaa-fast-approximate-anti-aliasing-demo-glsl-opengl-test-radeon-geforce/\n  // https://github.com/mitsuhiko/webgl-meincraft\n\n  float lumaMM = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy).rgb);\n\n  #if 1\n    vec2 t_PixelTopLeft = t_PixelCenter.xy - t_InvResolution.xy * 0.5;\n    float lumaNW = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelTopLeft.xy)             .rgb);\n    float lumaNE = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelTopLeft.xy + vec2(1.0, 0.0)).rgb);\n    float lumaSW = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelTopLeft.xy + vec2(0.0, 1.0)).rgb);\n    float lumaSE = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelTopLeft.xy + vec2(1.0, 1.0)).rgb);\n  #else\n    // We're at the pixel center -- pixel edges are 0.5 units away.\n    // NOTE(jstpierre): mitsuhiko's port seems to get this wrong?\n    vec2 t_PixelSize = t_InvResolution.xy * 0.5;\n\n    float lumaNW = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy + t_PixelSize * vec2(-1.0, -1.0)).rgb);\n    float lumaNE = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy + t_PixelSize * vec2( 1.0, -1.0)).rgb);\n    float lumaSW = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy + t_PixelSize * vec2(-1.0,  1.0)).rgb);\n    float lumaSE = MonochromeNTSC(texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy + t_PixelSize * vec2( 1.0,  1.0)).rgb);\n  #endif\n\n  vec2 dir; \n  dir.x = -((lumaNW + lumaNE) - (lumaSW + lumaSE));\n  dir.y =  ((lumaNW + lumaSW) - (lumaNE + lumaSE));\n\n  const float FXAA_REDUCE_MIN = 1.0/128.0;\n  const float FXAA_REDUCE_MUL = 1.0/8.0;\n  const float FXAA_SPAN_MAX = 8.0;\n\n  float dirReduce = max(\n      (lumaNW + lumaNE + lumaSW + lumaSE) * (0.25 * FXAA_REDUCE_MUL),\n      FXAA_REDUCE_MIN);\n\n  float rcpDirMin = 1.0/(min(abs(dir.x), abs(dir.y)) + dirReduce);\n  dir = min(vec2( FXAA_SPAN_MAX,  FXAA_SPAN_MAX), max(vec2(-FXAA_SPAN_MAX, -FXAA_SPAN_MAX), dir * rcpDirMin)) * u_InvResolution.xy;\n\n  float lumaMin = min(lumaMM, min(min(lumaNW, lumaNE), min(lumaSW, lumaSE)));\n  float lumaMax = max(lumaMM, max(max(lumaNW, lumaNE), max(lumaSW, lumaSE)));\n\n  vec4 rgbA = (1.0/2.0) * (\n      texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy + dir * (1.0/3.0 - 0.5)) +\n      texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy + dir * (2.0/3.0 - 0.5)));\n  vec4 rgbB = rgbA * (1.0/2.0) + (1.0/4.0) * (\n      texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy + dir * (0.0/3.0 - 0.5)) +\n      texture(PU_SAMPLER_2D(t_Texture), t_PixelCenter.xy + dir * (3.0/3.0 - 0.5)));\n  float lumaB = MonochromeNTSC(rgbB.rgb);\n\n  vec4 rgbOutput = ((lumaB < lumaMin) || (lumaB > lumaMax)) ? rgbA : rgbB;\n  return rgbOutput;\n}\n\nvoid main() {\n  outputColor = FXAA(PP_SAMPLER_2D(u_Texture), v_TexCoord.xy, u_InvResolution.xy);\n}"; // eslint-disable-line

var FXAAProgram = /*#__PURE__*/function (_DeviceProgram) {
  function FXAAProgram() {
    var _this;
    _classCallCheck(this, FXAAProgram);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _callSuper(this, FXAAProgram, [].concat(args));
    _this.features = {};
    _this.both = "\nlayout(std140) uniform ub_Params {\n    vec4 u_Misc[1];\n};\n#define u_InvResolution (u_Misc[0].xy)\n";
    _this.vert = vert$5;
    _this.frag = frag$5;
    return _this;
  }
  _inherits(FXAAProgram, _DeviceProgram);
  return _createClass(FXAAProgram);
}(DeviceProgram);
var textureMapping = nArray(1, function () {
  return new TextureMapping();
});
var geometry;
var inputLayout;
function pushFXAAPass(builder, renderHelper, renderInput, mainColorTargetID) {
  builder.pushPass(function (pass) {
    pass.setDebugName('FXAA');
    pass.attachRenderTargetID(RGAttachmentSlot.Color0, mainColorTargetID);
    var mainColorResolveTextureID = builder.resolveRenderTarget(mainColorTargetID);
    pass.attachResolveTexture(mainColorResolveTextureID);
    var renderInst = renderHelper.renderInstManager.newRenderInst();
    renderInst.setUniformBuffer(renderHelper.uniformBuffer);
    renderInst.setAllowSkippingIfPipelineNotReady(false);
    renderInst.setMegaStateFlags(fullscreenMegaState);
    renderInst.setBindingLayout({
      numUniformBuffers: 1,
      numSamplers: 1
    });
    renderInst.drawPrimitives(3);

    // since gl_VertexID is not available in GLSL 100, we need to use a geometry
    var offs = renderInst.allocateUniformBuffer(0, 4);
    var d = renderInst.mapUniformBufferF32(0);
    fillVec4(d, offs, 1.0 / renderInput.backbufferWidth, 1.0 / renderInput.backbufferHeight);
    var fxaaProgram = new FXAAProgram();
    var program = renderHelper.renderCache.createProgramSimple(fxaaProgram);
    renderInst.setProgram(program);
    if (!geometry) {
      geometry = new BufferGeometry(renderHelper.getDevice());
      geometry.setVertexBuffer({
        bufferIndex: 0,
        byteStride: 4 * 2,
        stepMode: VertexStepMode.VERTEX,
        attributes: [{
          format: Format.F32_RG,
          bufferByteOffset: 4 * 0,
          location: 0
        }],
        // rendering a fullscreen triangle instead of quad
        // @see https://www.saschawillems.de/blog/2016/08/13/vulkan-tutorial-on-rendering-a-fullscreen-quad-without-buffers/
        data: new Float32Array([1, 3, -3, -1, 1, -1])
      });
      geometry.vertexCount = 3;
      inputLayout = renderHelper.getCache().createInputLayout(geometry.inputLayoutDescriptor);
    }
    pass.exec(function (passRenderer, scope) {
      textureMapping[0].texture = scope.getResolveTextureForID(mainColorResolveTextureID);
      renderInst.setSamplerBindingsFromTextureMappings(textureMapping);
      renderInst.setVertexInput(inputLayout, geometry.vertexBuffers.map(function (buffer) {
        return {
          buffer: buffer,
          byteOffset: 0
        };
      }), null);
      renderInst.drawOnPass(renderHelper.renderCache, passRenderer);
    });
  });
}

// scene uniform block index
var SceneUniformBufferIndex = 0;

// uniforms in scene level
var SceneUniform = /*#__PURE__*/function (SceneUniform) {
  SceneUniform["PROJECTION_MATRIX"] = "u_ProjectionMatrix";
  SceneUniform["VIEW_MATRIX"] = "u_ViewMatrix";
  SceneUniform["CAMERA_POSITION"] = "u_CameraPosition";
  SceneUniform["DEVICE_PIXEL_RATIO"] = "u_DevicePixelRatio";
  SceneUniform["VIEWPORT"] = "u_Viewport";
  SceneUniform["IS_ORTHO"] = "u_IsOrtho";
  SceneUniform["IS_PICKING"] = "u_IsPicking";
  return SceneUniform;
}({});
var RenderGraphPlugin = /*#__PURE__*/function () {
  function RenderGraphPlugin(renderHelper, lightPool, texturePool, batchManager, options) {
    _classCallCheck(this, RenderGraphPlugin);
    this.renderLists = {
      /**
       * used in main forward rendering pass
       */
      leftEye: new RenderInstList(),
      /**
       * right eye in VR session.
       */
      rightEye: new RenderInstList(),
      /**
       * used in picking pass, should disable blending
       */
      picking: new RenderInstList()
    };
    /**
     * An array of sub cameras used in VR scenes.
     * @see https://threejs.org/docs/#api/en/cameras/ArrayCamera
     */
    this.cameras = [];
    this.renderHelper = renderHelper;
    this.lightPool = lightPool;
    this.texturePool = texturePool;
    this.batchManager = batchManager;
    this.options = options;
  }
  return _createClass(RenderGraphPlugin, [{
    key: "getDevice",
    value: function getDevice() {
      return this.device;
    }
  }, {
    key: "getSwapChain",
    value: function getSwapChain() {
      return this.swapChain;
    }
  }, {
    key: "getRenderLists",
    value: function getRenderLists() {
      return this.renderLists;
    }
  }, {
    key: "apply",
    value: function apply(context) {
      var _this = this;
      this.context = context;
      var renderingService = context.renderingService,
        renderingContext = context.renderingContext,
        config = context.config;
      var canvas = renderingContext.root.ownerDocument.defaultView;
      config.disableRenderHooks = true;
      var handleMounted = function handleMounted(e) {
        var object = e.target;

        // collect lights
        if (object.nodeName === Light.tag) {
          _this.lightPool.addLight(object);
          return;
        }
        if (object.nodeName === Fog.tag) {
          _this.lightPool.addFog(object);
          return;
        }

        // @ts-ignore
        if (!object.renderable3D) {
          // @ts-ignore
          object.renderable3D = new Renderable3D();
        }
        _this.batchManager.add(object);
      };
      var handleUnmounted = function handleUnmounted(e) {
        var object = e.target;
        if (object.nodeName === Light.tag) {
          _this.lightPool.removeLight(object);
          return;
        }
        if (object.nodeName === Fog.tag) {
          _this.lightPool.removeFog(object);
          return;
        }
        if (object.nodeName === Shape.MESH) {
          var _object$style$geometr, _object$style$materia;
          if ((_object$style$geometr = object.style.geometry) !== null && _object$style$geometr !== void 0 && _object$style$geometr.meshes) {
            var index = object.style.geometry.meshes.indexOf(object);
            if (index > -1) {
              object.style.geometry.meshes.splice(index, 1);
            }
          }
          if ((_object$style$materia = object.style.material) !== null && _object$style$materia !== void 0 && _object$style$materia.meshes) {
            var _index = object.style.material.meshes.indexOf(object);
            if (_index > -1) {
              object.style.material.meshes.splice(_index, 1);
            }
          }
        }
        if (_this.swapChain) {
          _this.batchManager.remove(object);
        }

        // @ts-ignore
        delete object.renderable3D;

        // entity.removeComponent(Geometry3D, true);
        // entity.removeComponent(Material3D, true);
        // entity.removeComponent(Renderable3D, true);
      };
      var handleAttributeChanged = function handleAttributeChanged(e) {
        if (_this.swapChain) {
          var object = e.target;
          var attrName = e.attrName,
            newValue = e.newValue;
          if (attrName === 'zIndex') {
            object.parentNode.forEach(function (child) {
              _this.batchManager.changeRenderOrder(child, child.sortable.renderOrder);
            });
          } else {
            _this.batchManager.updateAttribute(object, attrName, newValue);
          }
        }
      };
      var handleBoundsChanged = function handleBoundsChanged(e) {
        if (_this.swapChain) {
          var records = e.detail;
          for (var i = 0; i < records.length; i++) {
            var record = records[i];
            var object = record.target;
            var nodes = object.nodeName === Shape.FRAGMENT ? object.childNodes : [object];
            nodes.forEach(function (node) {
              _this.batchManager.updateAttribute(node, 'modelMatrix', null);
            });
          }
        }
      };
      renderingService.hooks.initAsync.tapPromise(RenderGraphPlugin.tag, /*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        var $canvas, _this$context$config, width, height;
        return _regeneratorRuntime().wrap(function (_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              canvas.addEventListener(ElementEvent.MOUNTED, handleMounted);
              canvas.addEventListener(ElementEvent.UNMOUNTED, handleUnmounted);
              canvas.addEventListener(ElementEvent.ATTR_MODIFIED, handleAttributeChanged);
              canvas.addEventListener(ElementEvent.BOUNDS_CHANGED, handleBoundsChanged);
              _this.context.config.renderer.getConfig().enableDirtyRectangleRendering = false;
              $canvas = _this.context.contextService.getDomElement();
              _this$context$config = _this.context.config, width = _this$context$config.width, height = _this$context$config.height;
              _this.context.contextService.resize(width, height);

              // create swap chain and get device
              // @ts-ignore
              _context.next = 1;
              return _this.context.deviceContribution.createSwapChain($canvas);
            case 1:
              _this.swapChain = _context.sent;
              _this.device = _this.swapChain.getDevice();
              _this.renderHelper.setDevice(_this.device);
              _this.renderHelper.renderInstManager.disableSimpleMode();
              _this.swapChain.configureSwapChain($canvas.width, $canvas.height);
              canvas.addEventListener(CanvasEvent.RESIZE, function () {
                _this.swapChain.configureSwapChain($canvas.width, $canvas.height);
              });
              _this.batchManager.attach(_objectSpread({
                device: _this.device
              }, context));
            case 2:
            case "end":
              return _context.stop();
          }
        }, _callee);
      })));
      renderingService.hooks.destroy.tap(RenderGraphPlugin.tag, function () {
        _this.renderHelper.destroy();
        _this.batchManager.destroy();
        _this.texturePool.destroy();
        canvas.removeEventListener(ElementEvent.MOUNTED, handleMounted);
        canvas.removeEventListener(ElementEvent.UNMOUNTED, handleUnmounted);
        canvas.removeEventListener(ElementEvent.ATTR_MODIFIED, handleAttributeChanged);
        canvas.removeEventListener(ElementEvent.BOUNDS_CHANGED, handleBoundsChanged);
        _this.device.destroy();
        _this.device.checkForLeaks();
        config.disableRenderHooks = false;
      });

      /**
       * build frame graph at the beginning of each frame
       */
      renderingService.hooks.beginFrame.tap(RenderGraphPlugin.tag, function (frame) {
        var _this$options;
        var session = frame === null || frame === void 0 ? void 0 : frame.session;
        var _this$context$config2 = _this.context.config,
          width = _this$context$config2.width,
          height = _this$context$config2.height;
        if (session) {
          // Assumed to be a XRWebGLLayer for now.
          var layer = session.renderState.baseLayer;
          if (!layer) {
            layer = session.renderState.layers[0];
          }
          _this.swapChain.configureSwapChain(layer.framebufferWidth, layer.framebufferHeight, layer.framebuffer);

          // @ts-ignore
          var referenceSpace = session.referenceSpace;
          // Retrieve the pose of the device.
          // XRFrame.getViewerPose can return null while the session attempts to establish tracking.
          var pose = frame.getViewerPose(referenceSpace);
          if (pose) {
            // In mobile AR, we only have one view.
            pose.views.forEach(function (view, i) {
              var viewport = session.renderState.baseLayer.getViewport(view);

              // @ts-ignore
              var cameraMatrix = mat4.fromValues.apply(mat4, _toConsumableArray(view.transform.matrix));
              cameraMatrix[12] *= width;
              cameraMatrix[13] *= height;
              cameraMatrix[14] *= 500;
              cameraMatrix[12] += width / 2;
              cameraMatrix[13] -= height / 2;
              cameraMatrix[14] += 500 / 2;

              // Use this matrix without modification or decomposition
              // @see https://immersive-web.github.io/webxr/#dom-xrview-projectionmatrix
              var projectionMatrix = mat4.fromValues.apply(mat4, _toConsumableArray(view.projectionMatrix));
              var viewMatrix = mat4.invert(mat4.create(), cameraMatrix);
              mat4.scale(viewMatrix, viewMatrix, vec3.fromValues(1, -1, 1));

              // @see https://github.com/immersive-web/webxr-samples/blob/main/js/render/core/renderer.js#L651
              var _view$transform$posit = view.transform.position,
                x = _view$transform$posit.x,
                y = _view$transform$posit.y,
                z = _view$transform$posit.z;
              _this.cameras[i] = {
                viewport: {
                  x: viewport.x / layer.framebufferWidth,
                  y: viewport.y / layer.framebufferHeight,
                  width: viewport.width / layer.framebufferWidth,
                  height: viewport.height / layer.framebufferHeight
                },
                projectionMatrix: projectionMatrix,
                viewMatrix: viewMatrix,
                cameraPosition: [x, y, z],
                isOrtho: false
              };
            });
          }
        } else {
          var camera = _this.context.camera;
          _this.cameras = [{
            viewport: {
              x: 0,
              y: 0,
              width: 1,
              height: 1
            },
            projectionMatrix: camera.getPerspective(),
            viewMatrix: camera.getViewTransform(),
            cameraPosition: camera.getPosition(),
            isOrtho: camera.isOrtho()
          }];
        }
        var canvas = _this.swapChain.getCanvas();
        var renderInstManager = _this.renderHelper.renderInstManager;
        _this.builder = _this.renderHelper.renderGraph.newGraphBuilder();
        var clearColor;
        if (_this.context.config.background === 'transparent') {
          clearColor = TransparentBlack;
        } else {
          // use canvas.background
          var backgroundColor = parseColor(_this.context.config.background);
          clearColor = _this.context.config.background ?
          // use premultipliedAlpha
          // @see https://canvatechblog.com/alpha-blending-and-webgl-99feb392779e
          colorNewFromRGBA(Number(backgroundColor.r) / 255 * Number(backgroundColor.alpha), Number(backgroundColor.g) / 255 * Number(backgroundColor.alpha), Number(backgroundColor.b) / 255 * Number(backgroundColor.alpha), Number(backgroundColor.alpha)) : TransparentWhite;
        }

        // retrieve at each frame since canvas may resize
        var renderInput = {
          backbufferWidth: canvas.width,
          backbufferHeight: canvas.height,
          antialiasingMode: AntialiasingMode.None
        };
        // create main Color RT
        var mainRenderDesc = makeBackbufferDescSimple(RGAttachmentSlot.Color0, renderInput, makeAttachmentClearDescriptor(clearColor));
        // create main Depth RT
        var mainDepthDesc = makeBackbufferDescSimple(RGAttachmentSlot.DepthStencil, renderInput, opaqueWhiteFullClearRenderPassDescriptor);
        var mainColorTargetID = _this.builder.createRenderTargetID(mainRenderDesc, 'Main Color');
        var mainDepthTargetID = _this.builder.createRenderTargetID(mainDepthDesc, 'Main Depth');

        // main render pass
        _this.builder.pushPass(function (pass) {
          pass.setDebugName('Main Render Pass');
          pass.attachRenderTargetID(RGAttachmentSlot.Color0, mainColorTargetID);
          pass.attachRenderTargetID(RGAttachmentSlot.DepthStencil, mainDepthTargetID);
          pass.exec(function (passRenderer, scope) {
            _this.cameras.forEach(function (_ref2, i) {
              var viewport = _ref2.viewport;
              var x = viewport.x,
                y = viewport.y,
                width = viewport.width,
                height = viewport.height;
              var _scope$currentPass = scope.currentPass,
                viewportW = _scope$currentPass.viewportW,
                viewportH = _scope$currentPass.viewportH;

              // @see https://github.com/immersive-web/webxr-samples/blob/main/js/render/core/renderer.js#L757
              passRenderer.setViewport(x * viewportW, y * viewportH, width * viewportW, height * viewportH);
              (i === 0 ? _this.renderLists.leftEye : _this.renderLists.rightEye).drawOnPassRenderer(renderInstManager.renderCache, passRenderer);
            });
          });
        });

        // TODO: other post-processing passes
        if ((_this$options = _this.options) !== null && _this$options !== void 0 && _this$options.enableFXAA) {
          // FXAA
          pushFXAAPass(_this.builder, _this.renderHelper, renderInput, mainColorTargetID);
        }

        // output to screen
        _this.builder.resolveRenderTargetToExternalTexture(mainColorTargetID, _this.swapChain.getOnscreenTexture());
      });
      renderingService.hooks.endFrame.tap(RenderGraphPlugin.tag, function (frame) {
        var renderInstManager = _this.renderHelper.renderInstManager;
        var _this$context$config3 = _this.context.config,
          width = _this$context$config3.width,
          height = _this$context$config3.height;
        _this.cameras.forEach(function (_ref3, i) {
          var viewport = _ref3.viewport,
            cameraPosition = _ref3.cameraPosition,
            viewMatrix = _ref3.viewMatrix,
            projectionMatrix = _ref3.projectionMatrix,
            isOrtho = _ref3.isOrtho;
          var normalizedW = viewport.width,
            normalizedH = viewport.height;

          // Push our outer template, which contains the dynamic UBO bindings...
          var template = _this.renderHelper.pushTemplateRenderInst();
          // SceneParams: binding = 0, ObjectParams: binding = 1
          template.setBindingLayout({
            numUniformBuffers: 2,
            numSamplers: 0
          });
          template.setMegaStateFlags(setAttachmentStateSimple({
            depthWrite: true,
            blendConstant: TransparentBlack
          }, {
            rgbBlendMode: BlendMode.ADD,
            alphaBlendMode: BlendMode.ADD,
            rgbBlendSrcFactor: BlendFactor.SRC_ALPHA,
            alphaBlendSrcFactor: BlendFactor.ONE,
            rgbBlendDstFactor: BlendFactor.ONE_MINUS_SRC_ALPHA,
            alphaBlendDstFactor: BlendFactor.ONE_MINUS_SRC_ALPHA
          }));
          template.setUniforms(SceneUniformBufferIndex, [{
            name: SceneUniform.PROJECTION_MATRIX,
            value: projectionMatrix
          }, {
            name: SceneUniform.VIEW_MATRIX,
            value: viewMatrix
          }, {
            name: SceneUniform.CAMERA_POSITION,
            value: cameraPosition
          }, {
            name: SceneUniform.DEVICE_PIXEL_RATIO,
            value: _this.context.contextService.getDPR()
          }, {
            name: SceneUniform.VIEWPORT,
            value: [width * normalizedW, height * normalizedH]
          }, {
            name: SceneUniform.IS_ORTHO,
            value: isOrtho ? 1 : 0
          }, {
            name: SceneUniform.IS_PICKING,
            value: 0
          }]);
          _this.batchManager.render(i === 0 ? _this.renderLists.leftEye : _this.renderLists.rightEye);
          renderInstManager.popTemplateRenderInst();
        });
        _this.renderHelper.prepareToRender();
        _this.renderHelper.renderGraph.execute();
        renderInstManager.resetRenderInsts();

        // capture here since we don't preserve drawing buffer
        if (_this.enableCapture && _this.resolveCapturePromise) {
          var _this$captureOptions = _this.captureOptions,
            type = _this$captureOptions.type,
            encoderOptions = _this$captureOptions.encoderOptions;
          var dataURL = _this.context.contextService.getDomElement().toDataURL(type, encoderOptions);
          _this.resolveCapturePromise(dataURL);
          _this.enableCapture = false;
          _this.captureOptions = undefined;
          _this.resolveCapturePromise = undefined;
        }
      });
    }

    /**
     * load texture in an async way and render when loaded
     */
  }, {
    key: "loadTexture",
    value: function loadTexture(src, descriptor, successCallback) {
      return this.texturePool.getOrCreateTexture(this.device, src, descriptor, function (t) {
        if (successCallback) {
          successCallback(t);
        }
      });
    }
  }, {
    key: "toDataURL",
    value: function () {
      var _toDataURL = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee2(options) {
        var _this2 = this;
        return _regeneratorRuntime().wrap(function (_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              // trigger re-render
              this.enableCapture = true;
              this.captureOptions = options;
              this.capturePromise = new Promise(function (resolve) {
                _this2.resolveCapturePromise = function (dataURL) {
                  resolve(dataURL);
                };
              });
              return _context2.abrupt("return", this.capturePromise);
            case 1:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this);
      }));
      function toDataURL(_x) {
        return _toDataURL.apply(this, arguments);
      }
      return toDataURL;
    }()
  }]);
}();
RenderGraphPlugin.tag = 'RenderGraph';

/**
 * max depth when doing multi-layer picking
 */
var MAX_PICKING_DEPTH = 100;

/**
 * Use color-based picking in GPU
 */
var PickingPlugin = /*#__PURE__*/function () {
  function PickingPlugin(renderHelper, renderGraphPlugin, pickingIdGenerator, batchManager) {
    _classCallCheck(this, PickingPlugin);
    this.renderHelper = renderHelper;
    this.renderGraphPlugin = renderGraphPlugin;
    this.pickingIdGenerator = pickingIdGenerator;
    this.batchManager = batchManager;
  }
  return _createClass(PickingPlugin, [{
    key: "apply",
    value: function apply(context) {
      var _this = this;
      this.context = context;
      var renderingService = context.renderingService,
        renderingContext = context.renderingContext;
      var canvas = renderingContext.root.ownerDocument.defaultView;
      var handleMounted = function handleMounted(e) {
        var object = e.target;

        // @ts-ignore
        if (!object.renderable3D) {
          // @ts-ignore
          object.renderable3D = new Renderable3D();
        }

        // @ts-ignore
        var renderable3D = object.renderable3D;
        // generate picking id for later use
        var pickingId = _this.pickingIdGenerator.getId(object);
        renderable3D.pickingId = pickingId;
        renderable3D.encodedPickingColor = _this.pickingIdGenerator.encodePickingColor(pickingId);
      };
      var handleUnmounted = function handleUnmounted(e) {
        var object = e.target;

        // @ts-ignore
        var renderable3D = object.renderable3D;
        if (renderable3D) {
          _this.pickingIdGenerator.deleteById(renderable3D.pickingId);
        }
      };
      renderingService.hooks.init.tap(PickingPlugin.tag, function () {
        canvas.addEventListener(ElementEvent.MOUNTED, handleMounted);
        canvas.addEventListener(ElementEvent.UNMOUNTED, handleUnmounted);
      });
      renderingService.hooks.destroy.tap(PickingPlugin.tag, function () {
        canvas.removeEventListener(ElementEvent.MOUNTED, handleMounted);
        canvas.removeEventListener(ElementEvent.UNMOUNTED, handleUnmounted);
        _this.pickingIdGenerator.reset();
      });

      /**
       * Sync version is not implemented.
       */
      renderingService.hooks.pickSync.tap(PickingPlugin.tag, function (result) {
        return _this.pick(result);
      });
      renderingService.hooks.pick.tapPromise(PickingPlugin.tag, /*#__PURE__*/function () {
        var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee(result) {
          return _regeneratorRuntime().wrap(function (_context) {
            while (1) switch (_context.prev = _context.next) {
              case 0:
                return _context.abrupt("return", _this.pick(result));
              case 1:
              case "end":
                return _context.stop();
            }
          }, _callee);
        }));
        return function (_x) {
          return _ref.apply(this, arguments);
        };
      }());
    }
  }, {
    key: "pick",
    value: function pick(result) {
      var topmost = result.topmost,
        position = result.position;

      // use viewportX/Y
      var x = position.viewportX,
        y = position.viewportY;
      var dpr = this.context.contextService.getDPR();
      var width = this.context.config.width * dpr;
      var height = this.context.config.height * dpr;
      var xInDevicePixel = x * dpr;
      var yInDevicePixel = y * dpr;
      if (!this.renderHelper.renderGraph || xInDevicePixel > width || xInDevicePixel < 0 || yInDevicePixel > height || yInDevicePixel < 0) {
        result.picked = [];
        return result;
      }

      // implements multi-layer picking
      // @see https://github.com/antvis/g/issues/948
      var pickedDisplayObjects = this.pickByRectangleInDepth(new Rectangle(clamp(Math.round(xInDevicePixel), 0, width - 1), clamp(Math.round(yInDevicePixel), 0, height - 1), 1, 1), topmost ? 1 : MAX_PICKING_DEPTH);
      result.picked = pickedDisplayObjects;
      return result;
    }
  }, {
    key: "pickByRectangleInDepth",
    value: function pickByRectangleInDepth(rect) {
      var depth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MAX_PICKING_DEPTH;
      var picked = null;
      var counter = 1;
      var targets = [];
      do {
        picked = this.pickByRectangle(rect, picked);
        if (picked) {
          counter++;
          targets.push(picked);
        } else {
          break;
        }
      } while (picked && counter <= depth);
      if (depth > 1) {
        // restore encoded picking color
        this.restorePickingColor(targets);
      }
      return targets;
    }
  }, {
    key: "restorePickingColor",
    value: function restorePickingColor(displayObjects) {
      var _this2 = this;
      displayObjects.forEach(function (picked) {
        _this2.batchManager.updateAttribute(picked, 'pointerEvents', true, true);
      });
    }

    /**
     * return displayobjects in target rectangle
     */
  }, {
    key: "pickByRectangle",
    value: function pickByRectangle(rect, picked) {
      var _this3 = this;
      var device = this.renderGraphPlugin.getDevice();
      var renderLists = this.renderGraphPlugin.getRenderLists();
      var renderInstManager = this.renderHelper.renderInstManager;
      var builder = this.renderHelper.renderGraph.newGraphBuilder();
      var clearColor = TransparentBlack;
      var camera = this.context.camera;

      // retrieve at each frame since canvas may resize
      var x = rect.x,
        y = rect.y,
        width = rect.width,
        height = rect.height;

      // use a small picking area(like 1x1) instead of a fullscreen rt
      var renderInput = {
        backbufferWidth: width,
        backbufferHeight: height,
        antialiasingMode: AntialiasingMode.None
      };
      var mainPickingDesc = makeBackbufferDescSimple(RGAttachmentSlot.Color0, renderInput, makeAttachmentClearDescriptor(clearColor));
      var pickingColorTargetID = builder.createRenderTargetID(mainPickingDesc, 'Picking Color');
      // create main Depth RT
      var mainDepthDesc = makeBackbufferDescSimple(RGAttachmentSlot.DepthStencil, renderInput, opaqueWhiteFullClearRenderPassDescriptor);
      var mainDepthTargetID = builder.createRenderTargetID(mainDepthDesc, 'Picking Depth');

      // account for current view offset
      var currentView = _objectSpread({}, camera.getView());

      // prevent unused RTs like main color being destroyed
      this.renderHelper.renderGraph.renderTargetDeadPool.forEach(function (rt) {
        rt.age = -1;
      });

      // picking pass
      var target;
      builder.pushPass(function (pass) {
        pass.setDebugName('Picking Pass');
        pass.attachRenderTargetID(RGAttachmentSlot.Color0, pickingColorTargetID);
        pass.attachRenderTargetID(RGAttachmentSlot.DepthStencil, mainDepthTargetID);
        pass.exec(function (passRenderer) {
          renderLists.picking.drawOnPassRenderer(renderInstManager.renderCache, passRenderer);
        });
        pass.post(function (scope) {
          var texture = scope.getRenderTargetTexture(RGAttachmentSlot.Color0);
          var readback = device.createReadback();

          // restore previous view
          if (currentView && currentView.enabled) {
            camera.setViewOffset(currentView.fullWidth, currentView.fullHeight, currentView.offsetX, currentView.offsetY, currentView.width, currentView.height);
          } else {
            camera.clearViewOffset();
          }
          camera.setEnableUpdate(true);
          var pickedColors;
          try {
            pickedColors = readback.readTextureSync(texture, 0, 0, width, height, new Uint8Array(width * height * 4));
          } catch (_unused) {}
          var pickedFeatureIdx = -1;
          if (pickedColors && (pickedColors[0] !== 0 || pickedColors[1] !== 0 || pickedColors[2] !== 0)) {
            pickedFeatureIdx = _this3.pickingIdGenerator.decodePickingColor(pickedColors);
          }
          if (pickedFeatureIdx > -1) {
            var pickedDisplayObject = _this3.pickingIdGenerator.getById(pickedFeatureIdx);
            if (pickedDisplayObject && pickedDisplayObject.isVisible() && pickedDisplayObject.isInteractive()) {
              target = pickedDisplayObject;
            }
          }
          readback.destroy();
        });
      });

      // Push our outer template, which contains the dynamic UBO bindings...
      var template = this.renderHelper.pushTemplateRenderInst();
      // SceneParams: binding = 0, ObjectParams: binding = 1
      template.setBindingLayout({
        numUniformBuffers: 2,
        numSamplers: 0
      });
      template.setMegaStateFlags(setAttachmentStateSimple({
        depthWrite: true
      }, {
        rgbBlendMode: BlendMode.ADD,
        rgbBlendSrcFactor: BlendFactor.ONE,
        rgbBlendDstFactor: BlendFactor.ZERO,
        alphaBlendMode: BlendMode.ADD,
        alphaBlendSrcFactor: BlendFactor.ONE,
        alphaBlendDstFactor: BlendFactor.ZERO
      }));

      // Update Scene Params
      var _this$context$config = this.context.config,
        canvasWidth = _this$context$config.width,
        canvasHeight = _this$context$config.height;
      var dpr = this.context.contextService.getDPR();
      camera.setEnableUpdate(false);
      camera.setViewOffset(canvasWidth * dpr, canvasHeight * dpr, x,
      // fix https://github.com/antvis/G/issues/1747
      canvasHeight * dpr - y, width, height);
      template.setUniforms(SceneUniformBufferIndex, [{
        name: SceneUniform.PROJECTION_MATRIX,
        value: camera.getPerspective()
      }, {
        name: SceneUniform.VIEW_MATRIX,
        value: camera.getViewTransform()
      }, {
        name: SceneUniform.CAMERA_POSITION,
        value: camera.getPosition()
      }, {
        name: SceneUniform.DEVICE_PIXEL_RATIO,
        value: this.context.contextService.getDPR()
      }, {
        name: SceneUniform.VIEWPORT,
        value: [width, height]
      }, {
        name: SceneUniform.IS_ORTHO,
        value: camera.isOrtho() ? 1 : 0
      }, {
        name: SceneUniform.IS_PICKING,
        value: 1
      }]);
      if (picked) {
        this.batchManager.updateAttribute(picked, 'pointerEvents', false, true);
      }
      this.batchManager.render(renderLists.picking, true);
      renderInstManager.popTemplateRenderInst();
      this.renderHelper.prepareToRender();
      this.renderHelper.renderGraph.execute();
      renderInstManager.resetRenderInsts();
      return target;
    }
  }]);
}();
PickingPlugin.tag = 'WebGLPicker';

/**
 * render order start from 0, our default camera's Z is 500
 */
var RENDER_ORDER_SCALE = 500 / 1000000;

/**
 * A container for multiple display objects with the same `style`,
 * eg. 1000 Circles with the same stroke color, but their position, radius can be different
 */
var Batch = /*#__PURE__*/function () {
  function Batch() {
    _classCallCheck(this, Batch);
    this.clipPathMeshCreated = false;
  }
  return _createClass(Batch, [{
    key: "beforeUploadUBO",
    value: function beforeUploadUBO(renderInst, mesh) {}
  }, {
    key: "beforeInitMesh",
    value: function beforeInitMesh(mesh) {}
  }, {
    key: "afterInitMesh",
    value: function afterInitMesh(mesh) {}

    // private findClipPath(): DisplayObject | null {
    //   let node = this.instance;
    //   while (node && node.style) {
    //     if (node.style.clipPath) {
    //       return node.style.clipPath;
    //     }
    //     node = node.parentNode as DisplayObject;
    //   }
    //   return null;
    // }

    // private applyClipPath() {
    //   // find clipPath
    //   const clipPathShape = this.findClipPath();
    //   if (clipPathShape && !this.clipPathMeshCreated) {
    //     if (this.batchMeshList.length === 0) {
    //       return;
    //     }

    //     const clipPathMesh = this.meshFactory(clipPathShape.nodeName);
    //     clipPathMesh.clipPathTarget = this.instance;

    //     // draw clipPath first
    //     this.batchMeshList.unshift(clipPathMesh);
    //     this.clipPathMeshCreated = true;

    //     this.batchMeshList.forEach((mesh, i) => {
    //       mesh.clipPath = clipPathShape;

    //       if (!mesh.material) {
    //         mesh.material = new ShaderMaterial(this.device);
    //       }
    //       mesh.material.stencilRef = this.batchManager.getStencilRef(clipPathShape);
    //     });
    //   }

    //   // remove clipPath from render queue
    //   if (!clipPathShape) {
    //     if (this.batchMeshList.length && this.batchMeshList[0].clipPathTarget) {
    //       this.batchMeshList.shift();
    //     }
    //   }
    // }
  }]);
}();

function isTexture(t) {
  return !!(t && t.type);
}
var MaterialEvent = /*#__PURE__*/function (MaterialEvent) {
  MaterialEvent["CHANGED"] = "changed";
  return MaterialEvent;
}({});

/**
 * an encapsulation on top of shaders
 * @see https://doc.babylonjs.com/divingDeeper/materials/using/materials_introduction
 */
var Material = /*#__PURE__*/function (_EventEmitter) {
  function Material(device, props) {
    var _this;
    _classCallCheck(this, Material);
    _this = _callSuper(this, Material);
    _this.props = {};
    /**
     * relative meshes
     */
    _this.meshes = [];
    // USE_XXX
    _this.defines = {};
    _this.uniforms = {};
    _this.uboBuffer = [];
    _this.textures = {};
    _this.samplers = [];
    /**
     * need re-compiling like vs/fs changed
     */
    _this.programDirty = true;
    /**
     * need re-upload textures
     */
    _this.textureDirty = true;
    /**
     * inform geometry to rebuild, eg. wireframe
     */
    _this.geometryDirty = true;
    var _copyMegaState = copyMegaState(defaultMegaState),
      cullMode = _copyMegaState.cullMode,
      depthCompare = _copyMegaState.depthCompare,
      depthWrite = _copyMegaState.depthWrite,
      stencilFront = _copyMegaState.stencilFront,
      stencilBack = _copyMegaState.stencilBack,
      stencilWrite = _copyMegaState.stencilWrite,
      frontFace = _copyMegaState.frontFace,
      polygonOffset = _copyMegaState.polygonOffset,
      attachmentsState = _copyMegaState.attachmentsState;
    _this.device = device;

    // @ts-ignore
    _this.props = _objectSpread({
      cullMode: cullMode,
      depthTest: true,
      depthCompare: depthCompare,
      depthWrite: depthWrite,
      stencilFront: stencilFront,
      stencilBack: stencilBack,
      stencilWrite: stencilWrite,
      frontFace: frontFace,
      polygonOffset: polygonOffset,
      attachmentsState: attachmentsState,
      dithering: false,
      wireframe: false,
      wireframeColor: 'black',
      wireframeLineWidth: 1,
      vertexShader: '',
      fragmentShader: ''
    }, props);
    _this.compile();
    return _this;
  }
  _inherits(Material, _EventEmitter);
  return _createClass(Material, [{
    key: "cullMode",
    get:
    /**
     * cullFace
     */
    function get() {
      return this.props.cullMode;
    },
    set: function set(value) {
      this.props.cullMode = value;
    }
  }, {
    key: "frontFace",
    get: function get() {
      return this.props.frontFace;
    },
    set: function set(value) {
      this.props.frontFace = value;
    }

    /**
     * Blending state
     */
  }, {
    key: "blendConstant",
    get: function get() {
      return this.props.blendConstant;
    },
    set: function set(value) {
      this.props.blendConstant = value;
    }
  }, {
    key: "blendEquation",
    get: function get() {
      return this.props.blendEquation;
    },
    set: function set(value) {
      this.props.blendEquation = value;
    }
  }, {
    key: "blendEquationAlpha",
    get: function get() {
      return this.props.blendEquationAlpha;
    },
    set: function set(value) {
      this.props.blendEquationAlpha = value;
    }
  }, {
    key: "blendSrc",
    get: function get() {
      return this.props.blendSrc;
    },
    set: function set(value) {
      this.props.blendSrc = value;
    }
  }, {
    key: "blendDst",
    get: function get() {
      return this.props.blendDst;
    },
    set: function set(value) {
      this.props.blendDst = value;
    }
  }, {
    key: "blendSrcAlpha",
    get: function get() {
      return this.props.blendSrcAlpha;
    },
    set: function set(value) {
      this.props.blendSrcAlpha = value;
    }
  }, {
    key: "blendDstAlpha",
    get: function get() {
      return this.props.blendDstAlpha;
    },
    set: function set(value) {
      this.props.blendDstAlpha = value;
    }
  }, {
    key: "depthCompare",
    get: function get() {
      return this.props.depthCompare;
    },
    set: function set(value) {
      this.props.depthCompare = value;
    }
  }, {
    key: "depthTest",
    get: function get() {
      return this.props.depthTest;
    },
    set: function set(value) {
      this.props.depthTest = value;
    }
  }, {
    key: "depthWrite",
    get: function get() {
      return this.props.depthWrite;
    },
    set: function set(value) {
      this.props.depthWrite = value;
    }
  }, {
    key: "stencilFront",
    get: function get() {
      return this.props.stencilFront;
    },
    set: function set(value) {
      this.props.stencilFront = value;
    }
  }, {
    key: "stencilBack",
    get: function get() {
      return this.props.stencilBack;
    },
    set: function set(value) {
      this.props.stencilBack = value;
    }
  }, {
    key: "stencilWrite",
    get: function get() {
      return this.props.stencilWrite;
    },
    set: function set(value) {
      this.props.stencilWrite = value;
    }
  }, {
    key: "stencilRef",
    get: function get() {
      return this.props.stencilRef;
    },
    set: function set(value) {
      this.props.stencilRef = value;
    }

    // @see https://developer.mozilla.org/zh-CN/docs/Web/API/WebGLRenderingContext/polygonOffset
  }, {
    key: "polygonOffset",
    get: function get() {
      return this.props.polygonOffset;
    },
    set: function set(value) {
      this.props.polygonOffset = value;
    }

    // gl.DITHER
  }, {
    key: "dithering",
    get: function get() {
      return this.props.dithering;
    },
    set: function set(value) {
      this.props.dithering = value;
    }

    // @see https://doc.babylonjs.com/divingDeeper/materials/using/materials_introduction#wireframe
  }, {
    key: "wireframe",
    get: function get() {
      return this.props.wireframe;
    },
    set: function set(value) {
      if (this.props.wireframe !== value) {
        // need re-generate geometry
        this.geometryDirty = true;
        this.programDirty = true;
        this.props.wireframe = value;
        this.dispatchMutationEvent();
      }
      this.defines.USE_WIREFRAME = !!value;
    }
  }, {
    key: "wireframeColor",
    get: function get() {
      return this.props.wireframeColor;
    },
    set: function set(value) {
      this.props.wireframeColor = value;
    }
  }, {
    key: "wireframeLineWidth",
    get: function get() {
      return this.props.wireframeLineWidth;
    },
    set: function set(value) {
      this.props.wireframeLineWidth = value;
    }

    // shader pairs
  }, {
    key: "vertexShader",
    get: function get() {
      return this.props.vertexShader;
    },
    set: function set(value) {
      if (this.props.vertexShader !== value) {
        this.programDirty = true;
        this.props.vertexShader = value;
        this.compile();
      }
    }
  }, {
    key: "fragmentShader",
    get: function get() {
      return this.props.fragmentShader;
    },
    set: function set(value) {
      if (this.props.fragmentShader !== value) {
        this.programDirty = true;
        this.props.fragmentShader = value;
        this.compile();
      }
    }
  }, {
    key: "compile",
    value: function compile() {
      var _this2 = this;
      // uniform sampler2D u_Texture0;
      this.props.fragmentShader.replace(/^\s*uniform\s*sampler2D\s*(.*)\s*;$/gm, function (_, name) {
        _this2.samplers.push(name);
        return '';
      });

      /**
       * extract from uniform buffer object, should account for struct & pre-defines, eg.
       * layout(std140) uniform ub_ObjectParams {
       *   mat4 u_ModelMatrix;
       *   vec4 u_Color;
       *   vec4 u_StrokeColor;
       *   #ifdef NUM_DIR_LIGHTS
       *     DirectionalLight directionalLights[ NUM_DIR_LIGHTS ];
       *   #endif
       * }
       */
      this.uniformNames = getUniforms(this.props.fragmentShader);
    }

    /**
     * @example
     * material.setUniforms({
     *   u_ModelMatrix: [1, 2, 3, 4],
     *   u_Time: 1,
     *   u_Map: texture,
     * })
     */
  }, {
    key: "setUniforms",
    value: function setUniforms(uniforms) {
      var _this3 = this;
      var shoudDispatchMutationEvent = false;
      Object.keys(uniforms).forEach(function (key) {
        var value = uniforms[key];
        var existedTexture = _this3.textures[key];
        if (existedTexture && existedTexture !== value) {
          // existedTexture.destroy();
          _this3.textureDirty = true;
        }
        if (isTexture(value)) {
          _this3.textures[key] = value;
          _this3.textureDirty = true;
          value.on(TextureEvent.LOADED, function () {
            _this3.dispatchMutationEvent();
          });
        } else {
          _this3.uniforms[key] = value;
          shoudDispatchMutationEvent = true;
        }
        if (isNil(uniforms[key])) {
          delete _this3.textures[key];
          delete _this3.uniforms[key];
        }
      });
      if (shoudDispatchMutationEvent) {
        this.dispatchMutationEvent();
      }
    }
  }, {
    key: "dispatchMutationEvent",
    value: function dispatchMutationEvent() {
      this.emit(MaterialEvent.CHANGED);
    }
  }]);
}(EventEmitter);

var ShaderMaterial = /*#__PURE__*/function (_Material) {
  function ShaderMaterial(device, props) {
    var _this;
    _classCallCheck(this, ShaderMaterial);
    _this = _callSuper(this, ShaderMaterial, [device, _objectSpread({}, props)]);
    _this.defines = _objectSpread(_objectSpread({}, _this.defines), {}, {
      USE_UV: false,
      USE_MAP: false,
      USE_WIREFRAME: false,
      USE_FOG: false,
      USE_LIGHT: false
    });
    return _this;
  }
  _inherits(ShaderMaterial, _Material);
  return _createClass(ShaderMaterial);
}(Material);

function enumToObject(enumObject) {
  var result = {};
  Object.keys(enumObject).forEach(function (key) {
    if (typeof enumObject[key] === 'number') {
      result[key] = enumObject[key];
    }
  });
  return result;
}
function compareDefines(d1, d2) {
  var d1Keys = Object.keys(d1);
  var d2Keys = Object.keys(d2);
  if (d1Keys.length !== d2Keys.length) {
    return false;
  }
  return d1Keys.every(function (key) {
    return d1[key] === d2[key];
  });
}
var definedProps = function definedProps(obj) {
  return Object.fromEntries(Object.entries(obj).filter(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2);
      _ref2[0];
      var v = _ref2[1];
    return v !== undefined;
  }));
};

function packUint8ToFloat(a, b) {
  a = clamp(Math.floor(a), 0, 255);
  b = clamp(Math.floor(b), 0, 255);
  return 256 * a + b;
}

var counter = 1;
var FILL_TEXTURE_MAPPING = 'FillTextureMapping';

/**
 * WebGPU has max vertex attribute num(8)
 */
var VertexAttributeBufferIndex = /*#__PURE__*/function (VertexAttributeBufferIndex) {
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["MODEL_MATRIX"] = 0] = "MODEL_MATRIX";
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["PACKED_COLOR"] = 1] = "PACKED_COLOR";
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["PACKED_STYLE"] = 2] = "PACKED_STYLE";
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["PICKING_COLOR"] = 3] = "PICKING_COLOR";
  // built-in
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["POSITION"] = 4] = "POSITION";
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["NORMAL"] = 5] = "NORMAL";
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["UV"] = 6] = "UV";
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["BARYCENTRIC"] = 7] = "BARYCENTRIC";
  VertexAttributeBufferIndex[VertexAttributeBufferIndex["MAX"] = 8] = "MAX";
  return VertexAttributeBufferIndex;
}({});

/**
 * GL.MAX_VERTEX_ATTRIBS
 */
var VertexAttributeLocation = /*#__PURE__*/function (VertexAttributeLocation) {
  // TODO: bind mat4 in WebGL2 instead of decomposed 4 * vec4?
  // @see https://stackoverflow.com/questions/38853096/webgl-how-to-bind-values-to-a-mat4-attribute/38853623#38853623
  VertexAttributeLocation[VertexAttributeLocation["MODEL_MATRIX0"] = 0] = "MODEL_MATRIX0";
  VertexAttributeLocation[VertexAttributeLocation["MODEL_MATRIX1"] = 1] = "MODEL_MATRIX1";
  VertexAttributeLocation[VertexAttributeLocation["MODEL_MATRIX2"] = 2] = "MODEL_MATRIX2";
  VertexAttributeLocation[VertexAttributeLocation["MODEL_MATRIX3"] = 3] = "MODEL_MATRIX3";
  VertexAttributeLocation[VertexAttributeLocation["PACKED_COLOR"] = 4] = "PACKED_COLOR";
  // fill & stroke
  VertexAttributeLocation[VertexAttributeLocation["PACKED_STYLE1"] = 5] = "PACKED_STYLE1";
  // opacity fillOpacity strokeOpacity lineWidth
  VertexAttributeLocation[VertexAttributeLocation["PACKED_STYLE2"] = 6] = "PACKED_STYLE2";
  // visibility anchorX anchorY increasedLineWidthForHitTesting
  VertexAttributeLocation[VertexAttributeLocation["PICKING_COLOR"] = 7] = "PICKING_COLOR";
  VertexAttributeLocation[VertexAttributeLocation["POSITION"] = 8] = "POSITION";
  VertexAttributeLocation[VertexAttributeLocation["NORMAL"] = 9] = "NORMAL";
  VertexAttributeLocation[VertexAttributeLocation["UV"] = 10] = "UV";
  VertexAttributeLocation[VertexAttributeLocation["BARYCENTRIC"] = 11] = "BARYCENTRIC";
  VertexAttributeLocation[VertexAttributeLocation["MAX"] = 12] = "MAX";
  return VertexAttributeLocation;
}({});

/**
 * Draw call.
 */
var Instanced = /*#__PURE__*/function () {
  function Instanced(renderHelper, texturePool, lightPool, object,
  /**
   * All drawcall constructors.
   */
  drawcallCtors) {
    var index = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : -1;
    var context = arguments.length > 6 ? arguments[6] : undefined;
    _classCallCheck(this, Instanced);
    /**
     * unique ID
     */
    this.id = counter++;
    /**
     * attribute name used for gradient or pattern
     */
    this.gradientAttributeName = 'fill';
    /**
     * instances
     */
    this.objects = [];
    this.program = new DeviceProgram();
    this.geometryDirty = true;
    /**
     * the same material maybe shared between different canvases
     */
    this.materialDirty = true;
    /**
     * texture mappings
     */
    this.textureMappings = [];
    /**
     * Divisor of instanced array.
     */
    this.divisor = 1;
    /**
     * Account for anchor and merge it into modelMatrix.
     */
    this.mergeXYZIntoModelMatrix = false;
    this.checkNodeName = true;
    /**
     * Create a new batch if the number of instances exceeds.
     */
    this.maxInstances = Infinity;
    this.inited = false;
    this.renderHelper = renderHelper;
    this.texturePool = texturePool;
    this.lightPool = lightPool;
    this.drawcallCtors = drawcallCtors;
    this.index = index;
    this.context = context;
  }
  return _createClass(Instanced, [{
    key: "instance",
    get: function get() {
      return this.objects[0];
    }
  }, {
    key: "init",
    value: function init() {
      if (this.inited) {
        return;
      }
      this.renderer.beforeInitMesh(this);
      this.material = new ShaderMaterial(this.context.device);
      this.material.defines = _objectSpread(_objectSpread({}, enumToObject(VertexAttributeLocation)), this.material.defines);
      this.geometry = new BufferGeometry(this.context.device);

      // make refs so that we can trigger MutationEvent on every object
      this.geometry.meshes = this.objects;
      this.material.meshes = this.objects;
      this.observeGeometryChanged();
      this.observeMaterialChanged();
      this.inited = true;
      this.renderer.afterInitMesh(this);
    }
  }, {
    key: "observeGeometryChanged",
    value: function observeGeometryChanged() {
      var _this = this;
      this.geometry.on(GeometryEvent.CHANGED, function () {
        _this.geometry.meshes.forEach(function (mesh) {
          mesh.renderable.dirty = true;
        });
        _this.context.renderingService.dirty();
      });
    }
  }, {
    key: "observeMaterialChanged",
    value: function observeMaterialChanged() {
      var _this2 = this;
      this.material.on(MaterialEvent.CHANGED, function () {
        _this2.material.meshes.forEach(function (mesh) {
          mesh.renderable.dirty = true;
        });
        _this2.context.renderingService.dirty();
      });
    }
  }, {
    key: "shouldMergeColor",
    value: function shouldMergeColor(o1, o2, name) {
      // c1: CSSRGB | CSSGradientValue[] | Pattern, c2: CSSRGB | CSSGradientValue[] | Pattern
      // can't be merged if gradients & pattern used
      var source = o1.parsedStyle[name];
      var target = o2.parsedStyle[name];
      if (!source && !target) {
        return true;
      }

      // constant color value
      if (isCSSRGB(source) && isCSSRGB(target)) {
        return true;
      }

      // pattern
      if (isPattern(source) && isPattern(target) && source.image === target.image) {
        return true;
      }

      // gradients
      if (Array.isArray(source) && Array.isArray(target) && o1.style[name] === o2.style[name]) {
        return true;
      }
      return false;
    }

    /**
     * should be merged into current InstancedMesh
     */
  }, {
    key: "shouldMerge",
    value: function shouldMerge(object, index) {
      if (!this.instance) {
        return true;
      }

      // Path / Polyline could be rendered as Line
      if (this.checkNodeName && this.instance.nodeName !== object.nodeName) {
        return false;
      }

      // can't be merged when using clipPath
      if (object.parsedStyle.clipPath) {
        return false;
      }
      if (!this.shouldMergeColor(this.instance, object, 'fill') || !this.shouldMergeColor(this.instance, object, 'stroke')) {
        return false;
      }
      return true;
    }
  }, {
    key: "createGeometry",
    value: function createGeometry(objects) {
      var _objects$,
        _objects$2,
        _this3 = this;
      var modelMatrix = mat4.create();
      var modelViewMatrix = mat4.create();
      // const normalMatrix = mat3.create();
      var packedModelMatrix = [];
      var packedFillStroke = [];
      var packedStyle = [];
      var packedPicking = [];
      var divisor = this.divisor;
      var anchorOffset = ((_objects$ = objects[0]) === null || _objects$ === void 0 ? void 0 : _objects$.nodeName) === Shape.CIRCLE || ((_objects$2 = objects[0]) === null || _objects$2 === void 0 ? void 0 : _objects$2.nodeName) === Shape.ELLIPSE ? 0.5 : 0;

      // const useNormal = this.material.defines.NORMAL;

      objects.forEach(function (object) {
        var _object$renderable3D;
        var _object$parsedStyle = object.parsedStyle,
          fill = _object$parsedStyle.fill,
          stroke = _object$parsedStyle.stroke,
          _object$parsedStyle$o = _object$parsedStyle.opacity,
          opacity = _object$parsedStyle$o === void 0 ? 1 : _object$parsedStyle$o,
          _object$parsedStyle$f = _object$parsedStyle.fillOpacity,
          fillOpacity = _object$parsedStyle$f === void 0 ? 1 : _object$parsedStyle$f,
          _object$parsedStyle$s = _object$parsedStyle.strokeOpacity,
          strokeOpacity = _object$parsedStyle$s === void 0 ? 1 : _object$parsedStyle$s,
          _object$parsedStyle$l = _object$parsedStyle.lineWidth,
          lineWidth = _object$parsedStyle$l === void 0 ? 1 : _object$parsedStyle$l,
          visibility = _object$parsedStyle.visibility,
          _object$parsedStyle$i = _object$parsedStyle.increasedLineWidthForHitTesting,
          increasedLineWidthForHitTesting = _object$parsedStyle$i === void 0 ? 0 : _object$parsedStyle$i;
        var fillColor = [0, 0, 0, 0];
        if (isCSSRGB(fill)) {
          fillColor = [Number(fill.r), Number(fill.g), Number(fill.b), Number(fill.alpha) * 255];
        }
        var strokeColor = [0, 0, 0, 0];
        if (isCSSRGB(stroke)) {
          strokeColor = [Number(stroke.r), Number(stroke.g), Number(stroke.b), Number(stroke.alpha) * 255];
        }

        // if (this.clipPathTarget) {
        //   // account for target's rts
        //   mat4.copy(modelMatrix, object.getLocalTransform());
        //   fillColor = [255, 255, 255, 255];
        //   mat4.mul(
        //     modelMatrix,
        //     this.clipPathTarget.getWorldTransform(),
        //     modelMatrix,
        //   );
        // } else {
        //   mat4.copy(modelMatrix, object.getWorldTransform());
        // }
        mat4.mul(modelViewMatrix, _this3.context.camera.getViewTransform(), modelMatrix);
        var encodedPickingColor = object.isInteractive() && (// @ts-ignore
        (_object$renderable3D = object.renderable3D) === null || _object$renderable3D === void 0 ? void 0 : _object$renderable3D.encodedPickingColor) || [0, 0, 0];
        if (_this3.mergeXYZIntoModelMatrix) {
          var _ref = object.parsedStyle,
            x = _ref.x,
            y = _ref.y,
            z = _ref.z;
          mat4.mul(modelMatrix, object.getWorldTransform(),
          // apply anchor
          mat4.fromTranslation(modelMatrix, vec3.fromValues(x, y, z)));
        } else {
          mat4.copy(modelMatrix, object.getWorldTransform());
        }
        packedModelMatrix.push.apply(packedModelMatrix, _toConsumableArray(modelMatrix));
        packedFillStroke.push(packUint8ToFloat(fillColor[0], fillColor[1]), packUint8ToFloat(fillColor[2], fillColor[3]), packUint8ToFloat(strokeColor[0], strokeColor[1]), packUint8ToFloat(strokeColor[2], strokeColor[3]));
        packedStyle.push(opacity, fillOpacity, strokeOpacity, lineWidth, visibility !== 'hidden' ? 1 : 0, anchorOffset, anchorOffset, increasedLineWidthForHitTesting);
        packedPicking.push.apply(packedPicking, _toConsumableArray(encodedPickingColor).concat([object.sortable.renderOrder * RENDER_ORDER_SCALE]));

        // if (useNormal) {
        //   // should not calc normal matrix in shader, mat3.invert is not cheap
        //   // @see https://stackoverflow.com/a/21079741
        //   mat3.fromMat4(normalMatrix, modelViewMatrix);
        //   mat3.invert(normalMatrix, normalMatrix);
        //   mat3.transpose(normalMatrix, normalMatrix);

        //   const { NORMAL_MATRIX0, NORMAL_MATRIX1, NORMAL_MATRIX2 } = this.material.defines;
        //   this.bufferGeometry.setVertexBuffer({
        //     bufferIndex: 4,
        //     byteStride: 4 * (3 * 3),
        //     stepMode: VertexStepMode.INSTANCE,
        //     attributes: [
        //       {
        //         format: Format.F32_RGB,
        //         bufferByteOffset: 4 * 0,
        //         location: Number(NORMAL_MATRIX0),
        //         divisor
        //       },
        //       {
        //         format: Format.F32_RGB,
        //         bufferByteOffset: 4 * 3,
        //         location: Number(NORMAL_MATRIX1),
        //         divisor
        //       },
        //       {
        //         format: Format.F32_RGB,
        //         bufferByteOffset: 4 * 6,
        //         location: Number(NORMAL_MATRIX2),
        //         divisor
        //       },
        //     ],
        //     data: new Float32Array(normalMatrix),
        //   });
        // }
      });
      this.geometry.instancedCount = objects.length;
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.MODEL_MATRIX,
        byteStride: 4 * (4 * 4),
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.MODEL_MATRIX0,
          divisor: divisor
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 4,
          location: VertexAttributeLocation.MODEL_MATRIX1,
          divisor: divisor
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 8,
          location: VertexAttributeLocation.MODEL_MATRIX2,
          divisor: divisor
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 12,
          location: VertexAttributeLocation.MODEL_MATRIX3,
          divisor: divisor
        }],
        data: new Float32Array(packedModelMatrix)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.PACKED_COLOR,
        byteStride: 4 * 4,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.PACKED_COLOR,
          divisor: divisor
        }],
        data: new Float32Array(packedFillStroke)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.PACKED_STYLE,
        byteStride: 4 * 8,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.PACKED_STYLE1,
          divisor: divisor
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 4,
          location: VertexAttributeLocation.PACKED_STYLE2,
          divisor: divisor
        }],
        data: new Float32Array(packedStyle)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.PICKING_COLOR,
        byteStride: 4 * 4,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.PICKING_COLOR,
          divisor: divisor
        }],
        data: new Float32Array(packedPicking)
      });
    }
  }, {
    key: "destroy",
    value: function destroy() {
      if (this.geometry) {
        this.geometry.destroy();
      }
      // if (this.textureMappings) {
      //   this.textureMappings.forEach((mapping) => {
      //     mapping.texture.destroy();
      //   });
      //   this.textureMappings = [];
      // }
    }
  }, {
    key: "applyRenderInst",
    value: function applyRenderInst(renderInst, objects) {
      var _this4 = this;
      // detect if scene changed, eg. lights & fog
      var fog = this.lightPool.getFog();
      var useFog = !!fog;
      if (this.clipPathTarget || this.clipPath) {
        if (this.clipPathTarget) {
          this.material.stencilWrite = true;
          // @see https://open.gl/depthstencils
          this.material.depthWrite = false;
          this.material.stencilFront = {
            compare: CompareFunction.ALWAYS,
            passOp: StencilOp.REPLACE
          };
          this.material.stencilBack = {
            compare: CompareFunction.ALWAYS,
            passOp: StencilOp.REPLACE
          };
        } else {
          this.material.stencilWrite = false;
          this.material.depthWrite = true;
          this.material.stencilFront = {
            compare: CompareFunction.EQUAL,
            passOp: StencilOp.KEEP
          };
          this.material.stencilBack = {
            compare: CompareFunction.EQUAL,
            passOp: StencilOp.KEEP
          };
        }
      } else {
        this.material.stencilWrite = false;
      }
      if (this.materialDirty || this.material.programDirty) {
        this.createMaterial(objects);
      }
      var oldDefines = _objectSpread({}, this.material.defines);
      this.material.defines.USE_FOG = useFog;
      this.material.defines = _objectSpread(_objectSpread(_objectSpread({}, this.lightPool.getDefines()), this.material.defines), this.renderHelper.getDefines());

      // re-upload textures
      if (this.material.textureDirty) {
        this.textureMappings = [];

        // set texture mappings
        var fillTextureMapping = this.createFillGradientTextureMapping(objects);
        if (fillTextureMapping) {
          this.textureMappings.push(fillTextureMapping);
        }
        Object.keys(this.material.textures).sort(function (a, b) {
          return _this4.material.samplers.indexOf(a) - _this4.material.samplers.indexOf(b);
        }).forEach(function (key) {
          var mapping = new TextureMapping();
          mapping.name = key;
          mapping.texture = _this4.material.textures[key];
          _this4.context.device.setResourceName(mapping.texture, "Material Texture ".concat(key));
          mapping.sampler = _this4.renderHelper.getCache().createSampler({
            addressModeU: AddressMode.CLAMP_TO_EDGE,
            addressModeV: AddressMode.CLAMP_TO_EDGE,
            minFilter: FilterMode.POINT,
            magFilter: FilterMode.BILINEAR,
            mipmapFilter: MipmapFilterMode.LINEAR,
            lodMinClamp: 0,
            lodMaxClamp: 0
          });
          _this4.textureMappings.push(mapping);
        });
        if (this.textureMappings.length) {
          this.material.defines.USE_UV = true;
          this.material.defines.USE_MAP = true;
        } else {
          this.material.defines.USE_UV = false;
          this.material.defines.USE_MAP = false;
        }
        this.material.textureDirty = false;
      }
      var needRecompileProgram = !compareDefines(oldDefines, this.material.defines);

      // re-compile program, eg. DEFINE changed
      if (needRecompileProgram || this.material.programDirty || this.materialDirty) {
        // set defines
        this.material.defines = _objectSpread(_objectSpread({}, this.material.defines), enumToObject(VertexAttributeLocation));
        Object.keys(this.material.defines).forEach(function (key) {
          var value = _this4.material.defines[key];
          if (typeof value === 'boolean') {
            _this4.program.setDefineBool(key, value);
          } else {
            _this4.program.setDefineString(key, "".concat(value));
          }
        });

        // build shaders
        this.program.vert = this.material.vertexShader;
        this.program.frag = this.material.fragmentShader;
        this.material.programDirty = false;
        this.materialDirty = false;
      }
      if (this.material.geometryDirty) {
        // wireframe 需要额外生成 geometry 重心坐标
        this.geometryDirty = true;
        this.material.geometryDirty = false;
      }
      if (this.geometryDirty || this.geometry.dirty) {
        // destroy first
        if (this.geometry) {
          this.geometry.destroy();
        }
        // re-create buffer geometry
        this.createGeometry(objects);

        // generate wireframe
        if (this.material.wireframe) {
          this.generateWireframe(this.geometry);
        }

        // sync to internal Geometry
        this.geometryDirty = false;
        this.geometry.dirty = false;
      }

      // cached input layout
      var program = this.renderHelper.getCache().createProgramSimple(this.program);
      var inputLayout = this.renderHelper.getCache().createInputLayout(_objectSpread(_objectSpread({}, this.geometry.inputLayoutDescriptor), {}, {
        program: program
      }));
      var useIndexes = !!this.geometry.indexBuffer;
      renderInst.renderPipelineDescriptor.topology = this.geometry.drawMode;
      renderInst.setProgram(program);
      renderInst.setVertexInput(inputLayout, this.geometry.vertexBuffers.filter(function (b) {
        return !!b;
      }).map(function (buffer) {
        return {
          buffer: buffer,
          byteOffset: 0
        };
      }), useIndexes ? {
        buffer: this.geometry.indexBuffer,
        offset: 0
      } : null);
      this.renderer.beforeUploadUBO(renderInst, this);
      // upload uniform buffer object
      this.uploadUBO(renderInst);
      if (useIndexes) {
        // drawElements
        renderInst.drawIndexesInstanced(this.geometry.vertexCount, this.geometry.instancedCount, this.geometry.indexStart);
      } else {
        // drawArrays
        renderInst.drawPrimitives(this.geometry.vertexCount, this.geometry.primitiveStart);
      }
      // FIXME: 暂时都当作非透明物体，按照创建顺序排序
      renderInst.sortKey = makeSortKeyOpaque(RendererLayer.OPAQUE, objects[0].sortable.renderOrder);
    }

    /**
     * update a continuous GPU buffer
     */
  }, {
    key: "updateBatchedAttribute",
    value: function updateBatchedAttribute(objects, startIndex, name, value) {
      var _this5 = this;
      if (objects.length === 0) {
        return;
      }
      var stylePacked = ['opacity', 'fillOpacity', 'strokeOpacity', 'lineWidth', 'visibility', 'anchor', 'increasedLineWidthForHitTesting'];
      if (name === 'fill' || name === 'stroke') {
        var packedFillStroke = [];
        objects.forEach(function (object) {
          var _object$parsedStyle2 = object.parsedStyle,
            fill = _object$parsedStyle2.fill,
            stroke = _object$parsedStyle2.stroke;
          var fillColor = [0, 0, 0, 0];
          if (isCSSRGB(fill)) {
            fillColor = [Number(fill.r), Number(fill.g), Number(fill.b), Number(fill.alpha) * 255];
          }
          var strokeColor = [0, 0, 0, 0];
          if (isCSSRGB(stroke)) {
            strokeColor = [Number(stroke.r), Number(stroke.g), Number(stroke.b), Number(stroke.alpha) * 255];
          }
          packedFillStroke.push(packUint8ToFloat(fillColor[0], fillColor[1]), packUint8ToFloat(fillColor[2], fillColor[3]), packUint8ToFloat(strokeColor[0], strokeColor[1]), packUint8ToFloat(strokeColor[2], strokeColor[3]));
        });
        this.geometry.updateVertexBuffer(VertexAttributeBufferIndex.PACKED_COLOR, VertexAttributeLocation.PACKED_COLOR, startIndex, new Uint8Array(new Float32Array(packedFillStroke).buffer));
        var fill = this.instance.parsedStyle.fill;
        var i = this.textureMappings.findIndex(function (m) {
          return m.name === FILL_TEXTURE_MAPPING;
        });
        if (isCSSRGB(fill)) {
          if (i >= 0) {
            // remove original fill texture mapping
            this.textureMappings.splice(i, -1);
            this.material.textureDirty = true;
          }
        } else {
          var fillTextureMapping = this.createFillGradientTextureMapping([this.instance]);
          if (i >= 0) {
            this.textureMappings.splice(i, 1, fillTextureMapping);
          }
          this.material.textureDirty = true;
        }
      } else if (stylePacked.indexOf(name) > -1) {
        var _objects$3, _objects$4;
        var packed = [];
        var anchorOffset = ((_objects$3 = objects[0]) === null || _objects$3 === void 0 ? void 0 : _objects$3.nodeName) === Shape.CIRCLE || ((_objects$4 = objects[0]) === null || _objects$4 === void 0 ? void 0 : _objects$4.nodeName) === Shape.ELLIPSE ? 0.5 : 0;
        objects.forEach(function (object) {
          var _object$parsedStyle3 = object.parsedStyle,
            _object$parsedStyle3$ = _object$parsedStyle3.opacity,
            opacity = _object$parsedStyle3$ === void 0 ? 1 : _object$parsedStyle3$,
            _object$parsedStyle3$2 = _object$parsedStyle3.fillOpacity,
            fillOpacity = _object$parsedStyle3$2 === void 0 ? 1 : _object$parsedStyle3$2,
            _object$parsedStyle3$3 = _object$parsedStyle3.strokeOpacity,
            strokeOpacity = _object$parsedStyle3$3 === void 0 ? 1 : _object$parsedStyle3$3,
            _object$parsedStyle3$4 = _object$parsedStyle3.lineWidth,
            lineWidth = _object$parsedStyle3$4 === void 0 ? 1 : _object$parsedStyle3$4,
            visibility = _object$parsedStyle3.visibility,
            _object$parsedStyle3$5 = _object$parsedStyle3.increasedLineWidthForHitTesting,
            increasedLineWidthForHitTesting = _object$parsedStyle3$5 === void 0 ? 0 : _object$parsedStyle3$5;
          packed.push(opacity, fillOpacity, strokeOpacity, lineWidth, visibility !== 'hidden' ? 1 : 0, anchorOffset, anchorOffset, increasedLineWidthForHitTesting);
        });
        this.geometry.updateVertexBuffer(VertexAttributeBufferIndex.PACKED_STYLE, VertexAttributeLocation.PACKED_STYLE1, startIndex, new Uint8Array(new Float32Array(packed).buffer));
      } else if (name === 'modelMatrix' || this.mergeXYZIntoModelMatrix && (name === 'x' || name === 'y' || name === 'z')) {
        var _packed = [];
        var modelMatrix = mat4.create();
        objects.forEach(function (object) {
          if (_this5.mergeXYZIntoModelMatrix) {
            var _ref2 = object.parsedStyle,
              x = _ref2.x,
              y = _ref2.y,
              z = _ref2.z;
            mat4.mul(modelMatrix, object.getWorldTransform(),
            // apply anchor
            mat4.fromTranslation(modelMatrix, vec3.fromValues(x, y, z)));
          } else {
            mat4.copy(modelMatrix, object.getWorldTransform());
          }
          _packed.push.apply(_packed, _toConsumableArray(modelMatrix));
        });
        this.geometry.updateVertexBuffer(VertexAttributeBufferIndex.MODEL_MATRIX, VertexAttributeLocation.MODEL_MATRIX0, startIndex, new Uint8Array(new Float32Array(_packed).buffer));
      } else if (name === 'pointerEvents') {
        var _packed2 = [];
        objects.forEach(function (object) {
          var _object$renderable3D2;
          var encodedPickingColor = value && object.isInteractive() && (// @ts-ignore
          (_object$renderable3D2 = object.renderable3D) === null || _object$renderable3D2 === void 0 ? void 0 : _object$renderable3D2.encodedPickingColor) || [0, 0, 0];
          _packed2.push.apply(_packed2, _toConsumableArray(encodedPickingColor).concat([object.sortable.renderOrder * RENDER_ORDER_SCALE]));
        });
        this.geometry.updateVertexBuffer(VertexAttributeBufferIndex.PICKING_COLOR, VertexAttributeLocation.PICKING_COLOR, startIndex, new Uint8Array(new Float32Array(_packed2).buffer));
      }
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(objects, startIndex, name, value) {
      if (name === 'clipPath') {
        if (this.clipPath) {
          this.geometryDirty = true;
        }
      }

      // fix https://github.com/antvis/G/issues/1768
      if (this.geometryDirty === true && this.objects.length !== 0) {
        if (this.geometry) {
          this.geometry.destroy();
        }
        this.createGeometry(this.objects);
        this.geometryDirty = false;
      }
    }
  }, {
    key: "changeRenderOrder",
    value: function changeRenderOrder(object, renderOrder) {
      var _object$renderable3D3;
      var index = this.objects.indexOf(object);
      var encodedPickingColor = object.isInteractive() && (// @ts-ignore
      (_object$renderable3D3 = object.renderable3D) === null || _object$renderable3D3 === void 0 ? void 0 : _object$renderable3D3.encodedPickingColor) || [0, 0, 0];
      this.geometry.updateVertexBuffer(VertexAttributeBufferIndex.PICKING_COLOR, VertexAttributeLocation.PICKING_COLOR, index, new Uint8Array(new Float32Array([].concat(_toConsumableArray(encodedPickingColor), [renderOrder * RENDER_ORDER_SCALE])).buffer));
    }
  }, {
    key: "generateWireframe",
    value: function generateWireframe(geometry) {
      // need generate barycentric coordinates
      var indices = geometry.indices;
      var indiceNum = geometry.indices.length;
      var originalVertexBuffers = geometry.vertices.map(function (buffer) {
        // @ts-ignore
        return buffer.slice();
      });
      for (var i = VertexAttributeBufferIndex.PICKING_COLOR; i < geometry.vertexBuffers.length; i++) {
        var arrayStride = geometry.inputLayoutDescriptor.vertexBufferDescriptors[i].arrayStride;
        geometry.vertices[i] = new Float32Array(arrayStride / 4 * indiceNum);
      }
      // reallocate attribute data
      var cursor = 0;
      var uniqueIndices = new Uint32Array(indiceNum);
      for (var _i = 0; _i < indiceNum; _i++) {
        var ii = indices[_i];
        for (var j = 1; j < geometry.vertices.length; j++) {
          var _arrayStride = geometry.inputLayoutDescriptor.vertexBufferDescriptors[j].arrayStride;
          var size = _arrayStride / 4;
          for (var k = 0; k < size; k++) {
            geometry.vertices[j][cursor * size + k] = originalVertexBuffers[j][ii * size + k];
          }
        }
        uniqueIndices[_i] = cursor;
        cursor++;
      }
      for (var _i2 = VertexAttributeBufferIndex.PICKING_COLOR + 1; _i2 < geometry.vertexBuffers.length; _i2++) {
        // if (i === 3) {
        //   continue;
        // }
        var _geometry$inputLayout = geometry.inputLayoutDescriptor.vertexBufferDescriptors[_i2],
          stepMode = _geometry$inputLayout.stepMode,
          _arrayStride2 = _geometry$inputLayout.arrayStride;
        var descriptor = geometry.inputLayoutDescriptor.vertexBufferDescriptors[_i2].attributes[0];
        if (descriptor) {
          var location = descriptor.shaderLocation,
            bufferByteOffset = descriptor.offset,
            format = descriptor.format,
            divisor = descriptor.divisor;
          geometry.setVertexBuffer({
            bufferIndex: _i2,
            byteStride: _arrayStride2,
            stepMode: stepMode,
            attributes: [{
              format: format,
              bufferByteOffset: bufferByteOffset,
              location: location,
              divisor: divisor
            }],
            data: geometry.vertices[_i2]
          });
        }
      }
      // create barycentric attributes
      var barycentricBuffer = new Float32Array(indiceNum * 3);
      for (var _i3 = 0; _i3 < indiceNum;) {
        for (var _j = 0; _j < 3; _j++) {
          var _ii = uniqueIndices[_i3++];
          barycentricBuffer[_ii * 3 + _j] = 1;
        }
      }
      geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.BARYCENTRIC,
        byteStride: 4 * 3,
        stepMode: VertexStepMode.VERTEX,
        attributes: [{
          format: Format.F32_RGB,
          bufferByteOffset: 4 * 0,
          location: Number(VertexAttributeLocation.BARYCENTRIC)
        }],
        data: barycentricBuffer
      });
      geometry.setIndexBuffer(uniqueIndices);
    }
  }, {
    key: "beforeUploadUBO",
    value: function beforeUploadUBO(renderInst, objects) {}
  }, {
    key: "uploadUBO",
    value: function uploadUBO(renderInst) {
      var _material$defines$USE,
        _this6 = this;
      var numUniformBuffers = 1; // Scene UBO
      var material = this.material;
      var lights = this.lightPool.getAllLights();
      var fog = this.lightPool.getFog();
      var useFog = !!fog;
      var useLight = (_material$defines$USE = material.defines.USE_LIGHT) !== null && _material$defines$USE !== void 0 ? _material$defines$USE : !!lights.length;
      var useWireframe = material.defines.USE_WIREFRAME;

      // collect uniforms
      var uniforms = [];
      if (useWireframe) {
        var wireframeColor = parseColor(material.wireframeColor);
        uniforms.push({
          name: 'u_WireframeLineColor',
          value: [Number(wireframeColor.r) / 255, Number(wireframeColor.g) / 255, Number(wireframeColor.b) / 255]
        });
        uniforms.push({
          name: 'u_WireframeLineWidth',
          value: material.wireframeLineWidth
        });
      }
      if (useFog) {
        this.uploadFog(uniforms, fog);
      }
      this.uploadMaterial(uniforms, material);
      if (useLight) {
        var _counter = {};
        lights.forEach(function (light) {
          if (!_counter[light.define]) {
            _counter[light.define] = -1;
          }
          _counter[light.define]++;
          light.uploadUBO(uniforms, _counter[light.define]);
        });
      }
      uniforms.sort(function (a, b) {
        return _this6.material.uniformNames.indexOf(a.name) - _this6.material.uniformNames.indexOf(b.name);
      });

      // TODO: should not upload all uniforms if no change
      renderInst.setUniforms(numUniformBuffers, uniforms);
      var depthCompare = material.depthCompare,
        depthWrite = material.depthWrite,
        stencilFront = material.stencilFront,
        stencilBack = material.stencilBack,
        stencilWrite = material.stencilWrite,
        stencilRef = material.stencilRef,
        cullMode = material.cullMode,
        frontFace = material.frontFace,
        polygonOffset = material.polygonOffset,
        blendConstant = material.blendConstant,
        blendEquation = material.blendEquation,
        blendEquationAlpha = material.blendEquationAlpha,
        blendSrc = material.blendSrc,
        blendDst = material.blendDst,
        blendSrcAlpha = material.blendSrcAlpha,
        blendDstAlpha = material.blendDstAlpha;
      var materialMegaState = definedProps({
        blendConstant: blendConstant,
        depthCompare: depthCompare,
        depthWrite: depthWrite,
        stencilFront: stencilFront,
        stencilBack: stencilBack,
        stencilWrite: stencilWrite,
        stencilRef: stencilRef,
        cullMode: cullMode,
        frontFace: frontFace,
        polygonOffset: polygonOffset
      });
      var currentAttachmentsState = renderInst.getMegaStateFlags().attachmentsState[0];
      renderInst.setMegaStateFlags(_objectSpread({
        attachmentsState: [{
          // should not affect color buffer when drawing stencil
          channelWriteMask: this.material.stencilWrite ? ChannelWriteMask.NONE : ChannelWriteMask.ALL,
          // channelWriteMask: ChannelWriteMask.AllChannels,
          rgbBlendState: _objectSpread(_objectSpread({}, currentAttachmentsState.rgbBlendState), definedProps({
            blendMode: blendEquation,
            blendSrcFactor: blendSrc,
            blendDstFactor: blendDst
          })),
          alphaBlendState: _objectSpread(_objectSpread({}, currentAttachmentsState.alphaBlendState), definedProps({
            blendMode: blendEquationAlpha,
            blendSrcFactor: blendSrcAlpha,
            blendDstFactor: blendDstAlpha
          }))
        }]
      }, materialMegaState));
      renderInst.setBindingLayout({
        numUniformBuffers: numUniformBuffers,
        numSamplers: this.textureMappings.length
      });
      renderInst.setSamplerBindingsFromTextureMappings(this.textureMappings);
    }
  }, {
    key: "uploadFog",
    value: function uploadFog(uniforms, fog) {
      var _fog$parsedStyle = fog.parsedStyle,
        type = _fog$parsedStyle.type,
        fill = _fog$parsedStyle.fill,
        start = _fog$parsedStyle.start,
        end = _fog$parsedStyle.end,
        density = _fog$parsedStyle.density;
      if (isCSSRGB(fill)) {
        var fillColor = [Number(fill.r) / 255, Number(fill.g) / 255, Number(fill.b) / 255, Number(fill.alpha)];
        uniforms.push({
          name: 'u_FogInfos',
          value: [type, start, end, density]
        });
        uniforms.push({
          name: 'u_FogColor',
          value: fillColor
        });
      }
    }
  }, {
    key: "uploadMaterial",
    value: function uploadMaterial(uniforms, material) {
      // sort
      var materialUniforms = Object.keys(material.uniforms).map(function (name) {
        return {
          name: name,
          value: material.uniforms[name]
        };
      });
      uniforms.push.apply(uniforms, _toConsumableArray(materialUniforms));
    }
  }, {
    key: "createFillGradientTextureMapping",
    value: function createFillGradientTextureMapping(objects) {
      var _this7 = this;
      var instance = objects[0];

      // should account for Line, Path, Polyline and Polyline
      var fill = instance.parsedStyle[this.gradientAttributeName];
      var texImageSource;

      // use pattern & gradient
      if (fill && (isPattern(fill) || Array.isArray(fill))) {
        if (Array.isArray(fill)) {
          this.program.setDefineBool('USE_PATTERN', false);
          this.texturePool.getOrCreateGradient({
            gradients: fill,
            width: 128,
            height: 128,
            instance: instance
          });
        } else if (isPattern(fill)) {
          this.program.setDefineBool('USE_PATTERN', true);
          this.texturePool.getOrCreatePattern(fill, instance, function () {
            // need re-render
            objects.forEach(function (object) {
              object.renderable.dirty = true;
            });
            _this7.material.textureDirty = true;
          });
        }
        texImageSource = this.texturePool.getOrCreateCanvas();
        var texture = this.texturePool.getOrCreateTexture(this.context.device, texImageSource, makeTextureDescriptor2D(Format.U8_RGBA_NORM, 1, 1, 1));
        if (texture) {
          var fillMapping = new TextureMapping();
          fillMapping.name = FILL_TEXTURE_MAPPING;
          fillMapping.texture = texture;
          fillMapping.texture.on('loaded', function () {
            // need re-render
            objects.forEach(function (object) {
              object.renderable.dirty = true;
            });
            _this7.material.textureDirty = true;
          });
          this.context.device.setResourceName(fillMapping.texture, "Fill Texture".concat(this.id));
          fillMapping.sampler = this.renderHelper.getCache().createSampler({
            addressModeU: AddressMode.CLAMP_TO_EDGE,
            addressModeV: AddressMode.CLAMP_TO_EDGE,
            minFilter: FilterMode.POINT,
            magFilter: FilterMode.BILINEAR,
            mipmapFilter: MipmapFilterMode.LINEAR,
            lodMinClamp: 0,
            lodMaxClamp: 0
          });
          return fillMapping;
        }
      }
      return null;
    }
  }]);
}();

var frag$4 = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\n\nin vec4 v_PickingResult;\nin vec4 v_Color;\nin vec4 v_StrokeColor;\nin vec4 v_StylePacked1;\nin vec4 v_StylePacked2;\n#ifdef USE_UV\n  in vec2 v_Uv;\n#endif\n#ifdef USE_MAP\n  uniform sampler2D u_Map;\n#endif\n\nin vec2 v_Data;\nin vec2 v_Radius;\nin vec3 v_StylePacked3;\n\nout vec4 outputColor;\nfloat epsilon = 0.000001;\n\n/**\n * 2D signed distance field functions\n * @see http://www.iquilezles.org/www/articles/distfunctions2d/distfunctions2d.htm\n */\n\nfloat sdCircle(vec2 p, float r) {\n  return length(p) - r;\n}\n\n// @see http://www.iquilezles.org/www/articles/ellipsoids/ellipsoids.htm\nfloat sdEllipsoidApproximated(vec2 p, vec2 r) {\n  float k0 = length(p / r);\n  float k1 = length(p / (r * r));\n  return k0 * (k0 - 1.0) / k1;\n}\n\n// @see https://www.shadertoy.com/view/4llXD7\nfloat sdRoundedBox(vec2 p, vec2 b, float r) {\n  p = abs(p) - b + r;\n  return length(max(p, 0.0)) + min(max(p.x, p.y), 0.0) - r;\n}\n\nvoid main() {\n  int shape = int(floor(v_StylePacked3.x + 0.5));\n\n  vec4 u_Color = v_Color;\nvec4 u_StrokeColor = v_StrokeColor;\nfloat u_Opacity = v_StylePacked1.x;\nfloat u_FillOpacity = v_StylePacked1.y;\nfloat u_StrokeOpacity = v_StylePacked1.z;\nfloat u_StrokeWidth = v_StylePacked1.w;\nfloat u_Visible = v_StylePacked2.x;\nvec3 u_PickingColor = v_PickingResult.xyz;\n\nif (u_Visible < 0.5) {\n    discard;\n}\n  #ifdef USE_MAP\n  #ifdef USE_PATTERN\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #else\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #endif\n#endif\n\n  bool omitStroke = v_StylePacked3.z == 1.0;\n\n  vec2 r = (v_Radius - (omitStroke ? 0.0 : u_StrokeWidth)) / v_Radius.y;\n  float wh = v_Radius.x / v_Radius.y;\n\n  float dist = length(v_Data);\n  float antialiased_blur = -fwidth(dist);\n\n  float outer_df;\n  float inner_df;\n  // 'circle', 'ellipse', 'rect'\n  if (shape == 0) {\n    outer_df = sdCircle(v_Data, 1.0);\n    inner_df = sdCircle(v_Data, r.x);\n  } else if (shape == 1) {\n    outer_df = sdEllipsoidApproximated(v_Data, vec2(wh, 1.0));\n    inner_df = sdEllipsoidApproximated(v_Data, r);\n  } else if (shape == 2) {\n    bool useRadius = v_StylePacked3.y > epsilon;\n    outer_df = sdRoundedBox(v_Data, vec2(wh, 1.0), useRadius ? (v_StylePacked3.y + u_StrokeWidth / 2.0) / v_Radius.y : 0.0);\n    inner_df = sdRoundedBox(v_Data, r, useRadius ? (v_StylePacked3.y - u_StrokeWidth / 2.0) / v_Radius.y : 0.0);\n  }\n\n  float opacity_t = smoothstep(0.0, antialiased_blur, outer_df);\n\n  float color_t = u_StrokeWidth < 0.01 ? 0.0 : smoothstep(\n    antialiased_blur,\n    0.0,\n    inner_df\n  );\n\n  vec4 diffuseColor;\n  vec4 strokeColor;\n  if (u_IsPicking > 0.5) {\n    diffuseColor = vec4(u_PickingColor, 1.0);\n    strokeColor = vec4(u_PickingColor, 1.0);\n  } else {\n    diffuseColor = u_Color;\n    strokeColor = (u_StrokeColor == vec4(0) || omitStroke) ? vec4(0.0) : u_StrokeColor;\n  }\n\n  outputColor = mix(vec4(diffuseColor.rgb, diffuseColor.a * u_FillOpacity), strokeColor * u_StrokeOpacity, color_t);\n  outputColor.a = outputColor.a * u_Opacity * opacity_t;\n\n  if (outputColor.a < epsilon)\n    discard;\n\n  if (u_IsPicking > 0.5) {\n    if (u_PickingColor.x == 0.0 && u_PickingColor.y == 0.0 && u_PickingColor.z == 0.0) {\n      discard;\n    }\n  }\n}"; // eslint-disable-line

var vert$4 = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\n\nlayout(location = MODEL_MATRIX0) in vec4 a_ModelMatrix0;\nlayout(location = MODEL_MATRIX1) in vec4 a_ModelMatrix1;\nlayout(location = MODEL_MATRIX2) in vec4 a_ModelMatrix2;\nlayout(location = MODEL_MATRIX3) in vec4 a_ModelMatrix3;\nlayout(location = PACKED_COLOR) in vec4 a_PackedColor;\nlayout(location = PACKED_STYLE1) in vec4 a_StylePacked1;\nlayout(location = PACKED_STYLE2) in vec4 a_StylePacked2;\nlayout(location = PICKING_COLOR) in vec4 a_PickingColor;\n\nout vec4 v_PickingResult;\nout vec4 v_Color;\nout vec4 v_StrokeColor;\nout vec4 v_StylePacked1;\nout vec4 v_StylePacked2;\n\n#define COLOR_SCALE 1. / 255.\nvoid setPickingColor(vec3 pickingColor) {\n  v_PickingResult.rgb = pickingColor * COLOR_SCALE;\n}\n\nvec2 unpack_float(const float packedValue) {\n  int packedIntValue = int(packedValue);\n  int v0 = packedIntValue / 256;\n  return vec2(v0, packedIntValue - v0 * 256);\n}\nvec4 decode_color(const vec2 encodedColor) {\n  return vec4(\n    unpack_float(encodedColor[0]) / 255.0,\n    unpack_float(encodedColor[1]) / 255.0\n  );\n}\nvec4 project(vec4 pos, mat4 pm, mat4 vm, mat4 mm) {\n  return pm * vm * mm * pos;\n}\n\nbool isPerspectiveMatrix(mat4 m) {\n  return m[2][3] == -1.0;\n}\n\nvec4 billboard(vec2 offset, float rotation, bool isSizeAttenuation, mat4 pm, mat4 vm, mat4 mm, vec3 position) {\n  vec4 mvPosition = vm * mm * vec4(position, 1.0);\n  vec2 scale;\n  scale.x = length(vec3(mm[0][0], mm[0][1], mm[0][2]));\n  scale.y = length(vec3(mm[1][0], mm[1][1], mm[1][2]));\n\n  if (isSizeAttenuation) {\n    bool isPerspective = isPerspectiveMatrix(pm);\n    if (isPerspective) {\n      scale *= -mvPosition.z / 250.0;\n    }\n  }\n\n  vec2 alignedPosition = offset * scale;\n  vec2 rotatedPosition;\n  rotatedPosition.x = cos(rotation) * alignedPosition.x - sin(rotation) * alignedPosition.y;\n  rotatedPosition.y = sin(rotation) * alignedPosition.x + cos(rotation) * alignedPosition.y;\n\n  mvPosition.xy += rotatedPosition;\n  return pm * mvPosition;\n}\n\nlayout(location = POSITION) in vec3 a_Position;\nlayout(location = EXTRUDE) in vec2 a_Extrude;\n// shape, radius, omitStroke, isBillboard\nlayout(location = PACKED_STYLE3) in vec3 a_StylePacked3;\nlayout(location = SIZE) in vec4 a_Size;\n#ifdef USE_UV\n  layout(location = UV) in vec2 a_Uv;\n  out vec2 v_Uv;\n#endif\n\nout vec2 v_Data;\nout vec2 v_Radius;\nout vec3 v_StylePacked3;\n\nvoid main() {\n  vec4 a_Color = decode_color(a_PackedColor.xy);\nvec4 a_StrokeColor = decode_color(a_PackedColor.zw);\n\nmat4 u_ModelMatrix = mat4(a_ModelMatrix0, a_ModelMatrix1, a_ModelMatrix2, a_ModelMatrix3);\nvec4 u_StrokeColor = a_StrokeColor;\nfloat u_Opacity = a_StylePacked1.x;\nfloat u_FillOpacity = a_StylePacked1.y;\nfloat u_StrokeOpacity = a_StylePacked1.z;\nfloat u_StrokeWidth = a_StylePacked1.w;\nfloat u_ZIndex = a_PickingColor.w;\nvec2 u_Anchor = a_StylePacked2.yz;\nfloat u_IncreasedLineWidthForHitTesting = a_StylePacked2.w;\n\nsetPickingColor(a_PickingColor.xyz);\n\nv_Color = a_Color;\nv_StrokeColor = a_StrokeColor;\nv_StylePacked1 = a_StylePacked1;\nv_StylePacked2 = a_StylePacked2;\n\n// #ifdef CLIPSPACE_NEAR_ZERO\n//     gl_Position.z = (gl_Position.z + gl_Position.w) * 0.5;\n// #endif\n  #ifdef USE_UV\n  v_Uv = a_Uv;\n#endif\n\n  float strokeWidth;\n  if (u_IsPicking > 0.5) {\n    strokeWidth = u_IncreasedLineWidthForHitTesting + u_StrokeWidth;\n  } else {\n    strokeWidth = u_StrokeWidth;\n  }\n\n  bool omitStroke = a_StylePacked3.z == 1.0;\n  vec2 radius = a_Size.xy + vec2(omitStroke ? 0.0 : strokeWidth / 2.0);\n  vec2 offset = (a_Extrude + vec2(1.0) - 2.0 * u_Anchor.xy) * a_Size.xy + a_Extrude * vec2(omitStroke ? 0.0 : strokeWidth / 2.0);\n\n  bool isBillboard = a_Size.z > 0.5;\n  if (isBillboard) {\n    float rotation = 0.0;\n    bool isSizeAttenuation = a_Size.w > 0.5;\n    gl_Position = billboard(offset, rotation, isSizeAttenuation, u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix, a_Position);\n  } else {\n    gl_Position = project(vec4(a_Position.xy + offset, u_ZIndex, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n  }\n  \n  v_Radius = radius;\n  v_Data = vec2(a_Extrude * radius / radius.y);\n  v_StylePacked3 = a_StylePacked3;\n}"; // eslint-disable-line

var SDFVertexAttributeBufferIndex = function (SDFVertexAttributeBufferIndex) {
  SDFVertexAttributeBufferIndex[SDFVertexAttributeBufferIndex["PACKED_STYLE"] = VertexAttributeBufferIndex.POSITION + 1] = "PACKED_STYLE";
  return SDFVertexAttributeBufferIndex;
}(SDFVertexAttributeBufferIndex || {});
var SDFVertexAttributeLocation = function (SDFVertexAttributeLocation) {
  SDFVertexAttributeLocation[SDFVertexAttributeLocation["PACKED_STYLE3"] = VertexAttributeLocation.MAX] = "PACKED_STYLE3";
  SDFVertexAttributeLocation[SDFVertexAttributeLocation["EXTRUDE"] = 1 + SDFVertexAttributeLocation["PACKED_STYLE3"]] = "EXTRUDE";
  SDFVertexAttributeLocation[SDFVertexAttributeLocation["SIZE"] = 1 + SDFVertexAttributeLocation["EXTRUDE"]] = "SIZE";
  return SDFVertexAttributeLocation;
}(SDFVertexAttributeLocation || {});
var SDF_Shape = [Shape.CIRCLE, Shape.ELLIPSE, Shape.RECT];

/**
 * Use SDF to render 2D shapes, eg. circle, ellipse.
 * Use less triangles(2) and vertices compared with normal triangulation.
 */
var SDFDrawcall = /*#__PURE__*/function (_Instanced) {
  function SDFDrawcall() {
    _classCallCheck(this, SDFDrawcall);
    return _callSuper(this, SDFDrawcall, arguments);
  }
  _inherits(SDFDrawcall, _Instanced);
  return _createClass(SDFDrawcall, [{
    key: "shouldMerge",
    value: function shouldMerge(object, index) {
      var shouldMerge = _superPropGet(SDFDrawcall, "shouldMerge", this, 3)([object, index]);
      if (!shouldMerge) {
        return false;
      }

      // if (
      //   this.needDrawStrokeSeparately(object.parsedStyle) ||
      //   this.needDrawStrokeSeparately(this.instance.parsedStyle)
      // ) {
      //   return false;
      // }

      // const { fill: instanceFill } = this.instance
      //   .parsedStyle as ParsedBaseStyleProps;
      // const { fill } = object.parsedStyle as ParsedBaseStyleProps;
      // if ((instanceFill as CSSRGB).isNone !== (fill as CSSRGB).isNone) {
      //   return false;
      // }

      return true;
    }
  }, {
    key: "createMaterial",
    value: function createMaterial(objects) {
      this.material.vertexShader = vert$4;
      this.material.fragmentShader = frag$4;
      this.material.defines = _objectSpread(_objectSpread({}, this.material.defines), enumToObject(SDFVertexAttributeLocation));
    }
  }, {
    key: "createGeometry",
    value: function createGeometry(objects) {
      var _this = this;
      // use default common attributes
      _superPropGet(SDFDrawcall, "createGeometry", this, 3)([objects]);
      var instanced = [];
      var positions = [];
      objects.forEach(function (object, i) {
        var circle = object;
        // @ts-ignore
        var radius = circle.parsedStyle.radius;
        var omitStroke = _this.shouldOmitStroke(circle.parsedStyle);
        var size = _this.getSize(object.parsedStyle, circle.nodeName);
        var position = _this.getPosition(object.parsedStyle, circle.nodeName);
        instanced.push.apply(instanced, _toConsumableArray(size).concat([circle.parsedStyle.isBillboard ? 1 : 0, circle.parsedStyle.isSizeAttenuation ? 1 : 0, SDF_Shape.indexOf(circle.nodeName), radius && radius[0] || 0, omitStroke ? 1 : 0]));
        positions.push.apply(positions, _toConsumableArray(position));
      });
      this.geometry.setIndexBuffer(new Uint32Array([0, 2, 1, 0, 3, 2]));
      this.geometry.vertexCount = 6;
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.UV,
        byteStride: 4 * 4,
        stepMode: VertexStepMode.VERTEX,
        attributes: [{
          format: Format.F32_RG,
          bufferByteOffset: 4 * 0,
          location: SDFVertexAttributeLocation.EXTRUDE
        }, {
          format: Format.F32_RG,
          bufferByteOffset: 4 * 2,
          location: VertexAttributeLocation.UV
        }],
        data: new Float32Array([-1, -1, 0, 0, 1, -1, 1, 0, 1, 1, 1, 1, -1, 1, 0, 1])
      });
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.POSITION,
        byteStride: 4 * 3,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGB,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.POSITION
        }],
        data: new Float32Array(positions)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: SDFVertexAttributeBufferIndex.PACKED_STYLE,
        byteStride: 4 * 7,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 0,
          location: SDFVertexAttributeLocation.SIZE,
          divisor: 1
        }, {
          format: Format.F32_RGB,
          bufferByteOffset: 4 * 4,
          location: SDFVertexAttributeLocation.PACKED_STYLE3,
          divisor: 1
        }],
        data: new Float32Array(instanced)
      });
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(objects, startIndex, name, value) {
      var _this2 = this;
      if (objects.length === 0) {
        return;
      }
      _superPropGet(SDFDrawcall, "updateAttribute", this, 3)([objects, startIndex, name, value]);
      this.updateBatchedAttribute(objects, startIndex, name, value);
      if (name === 'r' || name === 'rx' || name === 'ry' || name === 'width' || name === 'height' || name === 'lineWidth' || name === 'stroke' || name === 'lineDash' || name === 'strokeOpacity' || name === 'radius' || name === 'isBillboard' || name === 'isSizeAttenuation') {
        var packed = [];
        objects.forEach(function (object) {
          var circle = object;
          var omitStroke = _this2.shouldOmitStroke(circle.parsedStyle);
          var _this2$getSize = _this2.getSize(object.parsedStyle, object.nodeName),
            _this2$getSize2 = _slicedToArray(_this2$getSize, 2),
            halfWidth = _this2$getSize2[0],
            halfHeight = _this2$getSize2[1];
          var size = [halfWidth, halfHeight];
          packed.push.apply(packed, size.concat([circle.parsedStyle.isBillboard ? 1 : 0, circle.parsedStyle.isSizeAttenuation ? 1 : 0, SDF_Shape.indexOf(object.nodeName), object.parsedStyle.radius && object.parsedStyle.radius[0] || 0, omitStroke ? 1 : 0]));
        });
        this.geometry.updateVertexBuffer(SDFVertexAttributeBufferIndex.PACKED_STYLE, SDFVertexAttributeLocation.SIZE, startIndex, new Uint8Array(new Float32Array(packed).buffer));
      } else if (name === 'cx' || name === 'cy' || name === 'x' || name === 'y') {
        var _packed = [];
        objects.forEach(function (object) {
          var _this2$getPosition = _this2.getPosition(object.parsedStyle, object.nodeName),
            _this2$getPosition2 = _slicedToArray(_this2$getPosition, 2),
            x = _this2$getPosition2[0],
            y = _this2$getPosition2[1];
          _packed.push(x, y);
        });
        this.geometry.updateVertexBuffer(VertexAttributeBufferIndex.POSITION, VertexAttributeLocation.POSITION, startIndex, new Uint8Array(new Float32Array(_packed).buffer));
      }
    }
  }, {
    key: "getPosition",
    value: function getPosition(parsed, tagName) {
      var size = [0, 0, 0];
      if (tagName === Shape.CIRCLE || tagName === Shape.ELLIPSE) {
        var _ref = parsed,
          _ref$cx = _ref.cx,
          cx = _ref$cx === void 0 ? 0 : _ref$cx,
          _ref$cy = _ref.cy,
          cy = _ref$cy === void 0 ? 0 : _ref$cy,
          _ref$cz = _ref.cz,
          cz = _ref$cz === void 0 ? 0 : _ref$cz;
        size = [cx, cy, cz];
      } else if (tagName === Shape.RECT) {
        var _ref2 = parsed,
          _ref2$x = _ref2.x,
          x = _ref2$x === void 0 ? 0 : _ref2$x,
          _ref2$y = _ref2.y,
          y = _ref2$y === void 0 ? 0 : _ref2$y,
          _ref2$z = _ref2.z,
          z = _ref2$z === void 0 ? 0 : _ref2$z;
        size = [x, y, z];
      }
      return size;
    }
  }, {
    key: "getSize",
    value: function getSize(parsed, tagName) {
      var size = [0, 0];
      if (tagName === Shape.CIRCLE) {
        var _ref3 = parsed,
          r = _ref3.r;
        size = [r, r];
      } else if (tagName === Shape.ELLIPSE) {
        var _ref4 = parsed,
          rx = _ref4.rx,
          ry = _ref4.ry;
        size = [rx, ry];
      } else if (tagName === Shape.RECT) {
        var _ref5 = parsed,
          width = _ref5.width,
          height = _ref5.height;
        size = [width / 2, height / 2];
      }
      return size;
    }
  }, {
    key: "shouldOmitStroke",
    value: function shouldOmitStroke(parsedStyle) {
      var lineDash = parsedStyle.lineDash,
        stroke = parsedStyle.stroke,
        strokeOpacity = parsedStyle.strokeOpacity;
      var hasStroke = stroke && !stroke.isNone;
      var hasLineDash = lineDash && lineDash.length && lineDash.every(function (item) {
        return item !== 0;
      });
      var hasStrokeOpacity = strokeOpacity < 1;
      return !hasStroke || hasStroke && (hasLineDash || hasStrokeOpacity);
    }
  }]);
}(Instanced);

var frag$3 = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\n\nin vec4 v_PickingResult;\nin vec4 v_Color;\nin vec4 v_StrokeColor;\nin vec4 v_StylePacked1;\nin vec4 v_StylePacked2;\n#ifdef USE_UV\n  in vec2 v_Uv;\n#endif\n#ifdef USE_MAP\n  uniform sampler2D u_Map;\n#endif\n\nin vec4 v_Dash;\nin vec2 v_Distance;\n\nout vec4 outputColor;\nfloat epsilon = 0.000001;\n\nvoid main() {\n  vec4 u_Color = v_Color;\nvec4 u_StrokeColor = v_StrokeColor;\nfloat u_Opacity = v_StylePacked1.x;\nfloat u_FillOpacity = v_StylePacked1.y;\nfloat u_StrokeOpacity = v_StylePacked1.z;\nfloat u_StrokeWidth = v_StylePacked1.w;\nfloat u_Visible = v_StylePacked2.x;\nvec3 u_PickingColor = v_PickingResult.xyz;\n\nif (u_Visible < 0.5) {\n    discard;\n}\n  #ifdef USE_MAP\n  #ifdef USE_PATTERN\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #else\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #endif\n#endif\n\n  if (u_IsPicking > 0.5) {\n    if (u_PickingColor.x == 0.0 && u_PickingColor.y == 0.0 && u_PickingColor.z == 0.0) {\n      discard;\n    }\n    outputColor = vec4(u_PickingColor, 1.0);\n  } else {\n    outputColor = u_StrokeColor;\n    #ifdef USE_MAP\n      outputColor = u_Color;\n    #endif\n\n    float blur;\n    if (v_Distance.y < 1.0) {\n      blur = smoothstep(0.0, v_Distance.y, 1.0 - abs(v_Distance.x));\n    } else {\n      blur = 1.0 / v_Distance.y;\n    }\n    float u_dash_offset = v_Dash.y;\n    float u_dash_array = v_Dash.z;\n    float u_dash_ratio = v_Dash.w;\n\n    outputColor.a = outputColor.a\n      * max(blur, 0.5)\n      * u_Opacity * u_StrokeOpacity\n      * (u_dash_array < 1.0 ? (ceil((u_dash_array * u_dash_ratio) - mod(v_Dash.x + u_dash_offset, u_dash_array))) : 1.0);\n\n    if (outputColor.a < epsilon) {\n      discard;\n    }\n  }\n}"; // eslint-disable-line

var vert$3 = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\n\nlayout(location = MODEL_MATRIX0) in vec4 a_ModelMatrix0;\nlayout(location = MODEL_MATRIX1) in vec4 a_ModelMatrix1;\nlayout(location = MODEL_MATRIX2) in vec4 a_ModelMatrix2;\nlayout(location = MODEL_MATRIX3) in vec4 a_ModelMatrix3;\nlayout(location = PACKED_COLOR) in vec4 a_PackedColor;\nlayout(location = PACKED_STYLE1) in vec4 a_StylePacked1;\nlayout(location = PACKED_STYLE2) in vec4 a_StylePacked2;\nlayout(location = PICKING_COLOR) in vec4 a_PickingColor;\n\nout vec4 v_PickingResult;\nout vec4 v_Color;\nout vec4 v_StrokeColor;\nout vec4 v_StylePacked1;\nout vec4 v_StylePacked2;\n\n#define COLOR_SCALE 1. / 255.\nvoid setPickingColor(vec3 pickingColor) {\n  v_PickingResult.rgb = pickingColor * COLOR_SCALE;\n}\n\nvec2 unpack_float(const float packedValue) {\n  int packedIntValue = int(packedValue);\n  int v0 = packedIntValue / 256;\n  return vec2(v0, packedIntValue - v0 * 256);\n}\nvec4 decode_color(const vec2 encodedColor) {\n  return vec4(\n    unpack_float(encodedColor[0]) / 255.0,\n    unpack_float(encodedColor[1]) / 255.0\n  );\n}\nvec4 project(vec4 pos, mat4 pm, mat4 vm, mat4 mm) {\n  return pm * vm * mm * pos;\n}\n\nlayout(location = POSITION) in vec3 a_Position;\nlayout(location = POINTA) in vec3 a_PointA;\nlayout(location = POINTB) in vec3 a_PointB;\nlayout(location = CAP) in float a_Cap;\n#ifdef USE_UV\n  layout(location = UV) in vec2 a_Uv;\n  out vec2 v_Uv;\n#endif\nlayout(location = DASH) in vec4 a_Dash;\n\nout vec4 v_Dash;\nout vec2 v_Distance;\n\nvoid main() {\n  vec4 a_Color = decode_color(a_PackedColor.xy);\nvec4 a_StrokeColor = decode_color(a_PackedColor.zw);\n\nmat4 u_ModelMatrix = mat4(a_ModelMatrix0, a_ModelMatrix1, a_ModelMatrix2, a_ModelMatrix3);\nvec4 u_StrokeColor = a_StrokeColor;\nfloat u_Opacity = a_StylePacked1.x;\nfloat u_FillOpacity = a_StylePacked1.y;\nfloat u_StrokeOpacity = a_StylePacked1.z;\nfloat u_StrokeWidth = a_StylePacked1.w;\nfloat u_ZIndex = a_PickingColor.w;\nvec2 u_Anchor = a_StylePacked2.yz;\nfloat u_IncreasedLineWidthForHitTesting = a_StylePacked2.w;\n\nsetPickingColor(a_PickingColor.xyz);\n\nv_Color = a_Color;\nv_StrokeColor = a_StrokeColor;\nv_StylePacked1 = a_StylePacked1;\nv_StylePacked2 = a_StylePacked2;\n\n// #ifdef CLIPSPACE_NEAR_ZERO\n//     gl_Position.z = (gl_Position.z + gl_Position.w) * 0.5;\n// #endif\n  #ifdef USE_UV\n  v_Uv = a_Uv;\n#endif\n\n  float strokeWidth;\n  if (u_IsPicking > 0.5) {\n    strokeWidth = u_IncreasedLineWidthForHitTesting + u_StrokeWidth;\n  } else {\n    strokeWidth = u_StrokeWidth;\n  }\n  float clampedStrokeWidth = max(strokeWidth, 1.0);\n\n  bool isSizeAttenuation = a_Dash.w > 0.5;\n  if (isSizeAttenuation) {\n    vec4 clip0 = project(vec4(a_PointA, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n    vec4 clip1 = project(vec4(a_PointB, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n    // screen space\n    vec2 screen0 = u_Viewport * (0.5 * clip0.xy / clip0.w + 0.5);\n    vec2 screen1 = u_Viewport * (0.5 * clip1.xy / clip1.w + 0.5);\n    vec2 xBasis = normalize(screen1 - screen0);\n    vec2 yBasis = vec2(-xBasis.y, xBasis.x);\n    vec2 pt0 = screen0 + clampedStrokeWidth * (a_Position.x * xBasis + a_Position.y * yBasis);\n    vec2 pt1 = screen1 + clampedStrokeWidth * (a_Position.x * xBasis + a_Position.y * yBasis);\n    vec2 pt = mix(pt0, pt1, a_Position.z);\n    vec4 clip = mix(clip0, clip1, a_Position.z);\n    gl_Position = vec4(clip.w * (2.0 * pt / u_Viewport - 1.0), clip.z, clip.w);\n  } else {\n    vec2 xBasis = a_PointB.xy - a_PointA.xy;\n    vec2 yBasis = normalize(vec2(-xBasis.y, xBasis.x));\n\n    vec2 point = a_PointA.xy + xBasis * a_Position.x + yBasis * clampedStrokeWidth * a_Position.y;\n    point = point - u_Anchor.xy * abs(xBasis);\n\n    // round & square\n    if (a_Cap > 1.0) {\n      point += sign(a_Position.x - 0.5) * normalize(xBasis) * vec2(clampedStrokeWidth / 2.0);\n    }\n    gl_Position = project(vec4(point, u_ZIndex, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n  }\n\n  float antialiasblur = 1.0 / strokeWidth;\n  v_Distance = vec2(a_Position.y * 2.0, antialiasblur);\n  v_Dash = vec4(a_Position.x, a_Dash.xyz);\n}"; // eslint-disable-line

var meshFrag = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\n\nin vec4 v_PickingResult;\nin vec4 v_Color;\nin vec4 v_StrokeColor;\nin vec4 v_StylePacked1;\nin vec4 v_StylePacked2;\n#ifdef USE_UV\n  in vec2 v_Uv;\n#endif\n#ifdef USE_MAP\n  uniform sampler2D u_Map;\n#endif\n\nout vec4 outputColor;\n\nvoid main(){\n  vec4 u_Color = v_Color;\nvec4 u_StrokeColor = v_StrokeColor;\nfloat u_Opacity = v_StylePacked1.x;\nfloat u_FillOpacity = v_StylePacked1.y;\nfloat u_StrokeOpacity = v_StylePacked1.z;\nfloat u_StrokeWidth = v_StylePacked1.w;\nfloat u_Visible = v_StylePacked2.x;\nvec3 u_PickingColor = v_PickingResult.xyz;\n\nif (u_Visible < 0.5) {\n    discard;\n}\n  #ifdef USE_MAP\n  #ifdef USE_PATTERN\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #else\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #endif\n#endif\n\n  if (u_IsPicking > 0.5) {\n    if (u_PickingColor.x == 0.0 && u_PickingColor.y == 0.0 && u_PickingColor.z == 0.0) {\n      discard;\n    }\n    outputColor = vec4(u_PickingColor, 1.0);\n  } else {\n    outputColor = u_Color;\n\n    outputColor.a = outputColor.a * u_Opacity * u_FillOpacity;\n  }\n}"; // eslint-disable-line

var meshVert = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\nlayout(location = MODEL_MATRIX0) in vec4 a_ModelMatrix0;\nlayout(location = MODEL_MATRIX1) in vec4 a_ModelMatrix1;\nlayout(location = MODEL_MATRIX2) in vec4 a_ModelMatrix2;\nlayout(location = MODEL_MATRIX3) in vec4 a_ModelMatrix3;\nlayout(location = PACKED_COLOR) in vec4 a_PackedColor;\nlayout(location = PACKED_STYLE1) in vec4 a_StylePacked1;\nlayout(location = PACKED_STYLE2) in vec4 a_StylePacked2;\nlayout(location = PICKING_COLOR) in vec4 a_PickingColor;\n\nout vec4 v_PickingResult;\nout vec4 v_Color;\nout vec4 v_StrokeColor;\nout vec4 v_StylePacked1;\nout vec4 v_StylePacked2;\n\n#define COLOR_SCALE 1. / 255.\nvoid setPickingColor(vec3 pickingColor) {\n  v_PickingResult.rgb = pickingColor * COLOR_SCALE;\n}\n\nvec2 unpack_float(const float packedValue) {\n  int packedIntValue = int(packedValue);\n  int v0 = packedIntValue / 256;\n  return vec2(v0, packedIntValue - v0 * 256);\n}\nvec4 decode_color(const vec2 encodedColor) {\n  return vec4(\n    unpack_float(encodedColor[0]) / 255.0,\n    unpack_float(encodedColor[1]) / 255.0\n  );\n}\nvec4 project(vec4 pos, mat4 pm, mat4 vm, mat4 mm) {\n  return pm * vm * mm * pos;\n}\n\nbool isPerspectiveMatrix(mat4 m) {\n  return m[2][3] == -1.0;\n}\n\nvec4 billboard(vec2 offset, float rotation, bool isSizeAttenuation, mat4 pm, mat4 vm, mat4 mm, vec3 position) {\n  vec4 mvPosition = vm * mm * vec4(position, 1.0);\n  vec2 scale;\n  scale.x = length(vec3(mm[0][0], mm[0][1], mm[0][2]));\n  scale.y = length(vec3(mm[1][0], mm[1][1], mm[1][2]));\n\n  if (isSizeAttenuation) {\n    bool isPerspective = isPerspectiveMatrix(pm);\n    if (isPerspective) {\n      scale *= -mvPosition.z / 250.0;\n    }\n  }\n\n  vec2 alignedPosition = offset * scale;\n  vec2 rotatedPosition;\n  rotatedPosition.x = cos(rotation) * alignedPosition.x - sin(rotation) * alignedPosition.y;\n  rotatedPosition.y = sin(rotation) * alignedPosition.x + cos(rotation) * alignedPosition.y;\n\n  mvPosition.xy += rotatedPosition;\n  return pm * mvPosition;\n}\n\nlayout(location = POSITION) in vec3 a_Position;\nlayout(location = PACKED_STYLE3) in vec4 a_StylePacked3;\n\n#ifdef USE_UV\n  layout(location = UV) in vec2 a_Uv;\n  out vec2 v_Uv;\n#endif\n\nvoid main() {\n  vec4 a_Color = decode_color(a_PackedColor.xy);\nvec4 a_StrokeColor = decode_color(a_PackedColor.zw);\n\nmat4 u_ModelMatrix = mat4(a_ModelMatrix0, a_ModelMatrix1, a_ModelMatrix2, a_ModelMatrix3);\nvec4 u_StrokeColor = a_StrokeColor;\nfloat u_Opacity = a_StylePacked1.x;\nfloat u_FillOpacity = a_StylePacked1.y;\nfloat u_StrokeOpacity = a_StylePacked1.z;\nfloat u_StrokeWidth = a_StylePacked1.w;\nfloat u_ZIndex = a_PickingColor.w;\nvec2 u_Anchor = a_StylePacked2.yz;\nfloat u_IncreasedLineWidthForHitTesting = a_StylePacked2.w;\n\nsetPickingColor(a_PickingColor.xyz);\n\nv_Color = a_Color;\nv_StrokeColor = a_StrokeColor;\nv_StylePacked1 = a_StylePacked1;\nv_StylePacked2 = a_StylePacked2;\n\n// #ifdef CLIPSPACE_NEAR_ZERO\n//     gl_Position.z = (gl_Position.z + gl_Position.w) * 0.5;\n// #endif\n  #ifdef USE_UV\n  v_Uv = a_Uv;\n#endif\n\n  bool isBillboard = a_StylePacked3.x > 0.5;\n  if (isBillboard) {\n    float rotation = a_StylePacked3.y;\n    bool isSizeAttenuation = a_StylePacked3.z > 0.5;\n    gl_Position = billboard(a_Position.xy, rotation, isSizeAttenuation, u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix, vec3(0.0));\n  } else {\n    gl_Position = project(vec4(a_Position.xy, u_ZIndex, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n  }\n}"; // eslint-disable-line

var frag$2 = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\n\nin vec4 v_PickingResult;\nin vec4 v_Color;\nin vec4 v_StrokeColor;\nin vec4 v_StylePacked1;\nin vec4 v_StylePacked2;\n#ifdef USE_UV\n  in vec2 v_Uv;\n#endif\n#ifdef USE_MAP\n  uniform sampler2D u_Map;\n#endif\n\nin vec4 v_Dash;\n\nin vec4 v_Distance;\nin vec4 v_Arc;\nin float v_Type;\nin float v_Travel;\nin float v_ScalingFactor;\n\nout vec4 outputColor;\nfloat epsilon = 0.000001;\n\nvoid main(){\n  vec4 u_Color = v_Color;\nvec4 u_StrokeColor = v_StrokeColor;\nfloat u_Opacity = v_StylePacked1.x;\nfloat u_FillOpacity = v_StylePacked1.y;\nfloat u_StrokeOpacity = v_StylePacked1.z;\nfloat u_StrokeWidth = v_StylePacked1.w;\nfloat u_Visible = v_StylePacked2.x;\nvec3 u_PickingColor = v_PickingResult.xyz;\n\nif (u_Visible < 0.5) {\n    discard;\n}\n  #ifdef USE_MAP\n  #ifdef USE_PATTERN\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #else\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #endif\n#endif\n\n  float alpha = 1.0;\n  float lineWidth = v_Distance.w;\n  if (v_Type < 0.5) {\n    float left = max(v_Distance.x - 0.5, -v_Distance.w);\n    float right = min(v_Distance.x + 0.5, v_Distance.w);\n    float near = v_Distance.y - 0.5;\n    float far = min(v_Distance.y + 0.5, 0.0);\n    float top = v_Distance.z - 0.5;\n    float bottom = min(v_Distance.z + 0.5, 0.0);\n    alpha = max(right - left, 0.0) * max(bottom - top, 0.0) * max(far - near, 0.0);\n  } else if (v_Type < 1.5) {\n    float a1 = clamp(v_Distance.x + 0.5 - lineWidth, 0.0, 1.0);\n    float a2 = clamp(v_Distance.x + 0.5 + lineWidth, 0.0, 1.0);\n    float b1 = clamp(v_Distance.y + 0.5 - lineWidth, 0.0, 1.0);\n    float b2 = clamp(v_Distance.y + 0.5 + lineWidth, 0.0, 1.0);\n    alpha = a2 * b2 - a1 * b1;\n  } else if (v_Type < 2.5) {\n    alpha *= max(min(v_Distance.x + 0.5, 1.0), 0.0);\n    alpha *= max(min(v_Distance.y + 0.5, 1.0), 0.0);\n    alpha *= max(min(v_Distance.z + 0.5, 1.0), 0.0);\n  } else if (v_Type < 3.5) {\n    float a1 = clamp(v_Distance.x + 0.5 - lineWidth, 0.0, 1.0);\n    float a2 = clamp(v_Distance.x + 0.5 + lineWidth, 0.0, 1.0);\n    float b1 = clamp(v_Distance.y + 0.5 - lineWidth, 0.0, 1.0);\n    float b2 = clamp(v_Distance.y + 0.5 + lineWidth, 0.0, 1.0);\n    float alpha_miter = a2 * b2 - a1 * b1;\n    float alpha_plane = max(min(v_Distance.z + 0.5, 1.0), 0.0);\n    float d = length(v_Arc.xy);\n    float circle_hor = max(min(v_Arc.w, d + 0.5) - max(-v_Arc.w, d - 0.5), 0.0);\n    float circle_vert = min(v_Arc.w * 2.0, 1.0);\n    float alpha_circle = circle_hor * circle_vert;\n    alpha = min(alpha_miter, max(alpha_circle, alpha_plane));\n  } else {\n    float a1 = clamp(v_Distance.x + 0.5 - lineWidth, 0.0, 1.0);\n    float a2 = clamp(v_Distance.x + 0.5 + lineWidth, 0.0, 1.0);\n    float b1 = clamp(v_Distance.y + 0.5 - lineWidth, 0.0, 1.0);\n    float b2 = clamp(v_Distance.y + 0.5 + lineWidth, 0.0, 1.0);\n    alpha = a2 * b2 - a1 * b1;\n    alpha *= max(min(v_Distance.z + 0.5, 1.0), 0.0);\n  }\n\n  float u_Dash = v_Dash.x;\n  float u_Gap = v_Dash.y;\n  float u_DashOffset = v_Dash.z;\n  if (u_Dash + u_Gap > 1.0) {\n    float travel = mod(v_Travel + u_Gap * v_ScalingFactor * 0.5 + u_DashOffset, u_Dash * v_ScalingFactor + u_Gap * v_ScalingFactor) - (u_Gap * v_ScalingFactor * 0.5);\n    float left = max(travel - 0.5, -0.5);\n    float right = min(travel + 0.5, u_Gap * v_ScalingFactor + 0.5);\n    alpha *= max(0.0, right - left);\n  }\n\n  if (u_IsPicking > 0.5) {\n    vec3 pickingColor = u_PickingColor;\n    if (pickingColor.x == 0.0 && pickingColor.y == 0.0 && pickingColor.z == 0.0) {\n      discard;\n    }\n    outputColor = vec4(pickingColor, 1.0);\n  } else {\n    outputColor = u_StrokeColor;\n    #ifdef USE_MAP\n      outputColor = u_Color;\n    #endif\n\n    outputColor.a *= alpha * u_Opacity * u_StrokeOpacity;\n    if (outputColor.a < epsilon) {\n      discard;\n    }\n  }\n}"; // eslint-disable-line

var vert$2 = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\n\nlayout(location = MODEL_MATRIX0) in vec4 a_ModelMatrix0;\nlayout(location = MODEL_MATRIX1) in vec4 a_ModelMatrix1;\nlayout(location = MODEL_MATRIX2) in vec4 a_ModelMatrix2;\nlayout(location = MODEL_MATRIX3) in vec4 a_ModelMatrix3;\nlayout(location = PACKED_COLOR) in vec4 a_PackedColor;\nlayout(location = PACKED_STYLE1) in vec4 a_StylePacked1;\nlayout(location = PACKED_STYLE2) in vec4 a_StylePacked2;\nlayout(location = PICKING_COLOR) in vec4 a_PickingColor;\n\nout vec4 v_PickingResult;\nout vec4 v_Color;\nout vec4 v_StrokeColor;\nout vec4 v_StylePacked1;\nout vec4 v_StylePacked2;\n\n#define COLOR_SCALE 1. / 255.\nvoid setPickingColor(vec3 pickingColor) {\n  v_PickingResult.rgb = pickingColor * COLOR_SCALE;\n}\n\nvec2 unpack_float(const float packedValue) {\n  int packedIntValue = int(packedValue);\n  int v0 = packedIntValue / 256;\n  return vec2(v0, packedIntValue - v0 * 256);\n}\nvec4 decode_color(const vec2 encodedColor) {\n  return vec4(\n    unpack_float(encodedColor[0]) / 255.0,\n    unpack_float(encodedColor[1]) / 255.0\n  );\n}\nvec4 project(vec4 pos, mat4 pm, mat4 vm, mat4 mm) {\n  return pm * vm * mm * pos;\n}\n\nbool isPerspectiveMatrix(mat4 m) {\n  return m[2][3] == -1.0;\n}\n\nvec4 billboard(vec2 offset, float rotation, bool isSizeAttenuation, mat4 pm, mat4 vm, mat4 mm, vec3 position) {\n  vec4 mvPosition = vm * mm * vec4(position, 1.0);\n  vec2 scale;\n  scale.x = length(vec3(mm[0][0], mm[0][1], mm[0][2]));\n  scale.y = length(vec3(mm[1][0], mm[1][1], mm[1][2]));\n\n  if (isSizeAttenuation) {\n    bool isPerspective = isPerspectiveMatrix(pm);\n    if (isPerspective) {\n      scale *= -mvPosition.z / 250.0;\n    }\n  }\n\n  vec2 alignedPosition = offset * scale;\n  vec2 rotatedPosition;\n  rotatedPosition.x = cos(rotation) * alignedPosition.x - sin(rotation) * alignedPosition.y;\n  rotatedPosition.y = sin(rotation) * alignedPosition.x + cos(rotation) * alignedPosition.y;\n\n  mvPosition.xy += rotatedPosition;\n  return pm * mvPosition;\n}\n\nlayout(location = PREV) in vec3 a_Prev;\nlayout(location = POINT1) in vec3 a_Point1;\nlayout(location = POINT2) in vec3 a_Point2;\nlayout(location = NEXT) in vec3 a_Next;\nlayout(location = VERTEX_JOINT) in float a_VertexJoint;\nlayout(location = VERTEX_NUM) in float a_VertexNum;\nlayout(location = TRAVEL) in float a_Travel;\n#ifdef USE_UV\n  layout(location = UV) in vec2 a_Uv;\n  out vec2 v_Uv;\n#endif\nlayout(location = DASH) in vec4 a_Dash;\nout vec4 v_Dash;\n\nconst float FILL = 1.0;\nconst float BEVEL = 4.0;\nconst float MITER = 8.0;\nconst float ROUND = 12.0;\nconst float JOINT_CAP_BUTT = 16.0;\nconst float JOINT_CAP_SQUARE = 18.0;\nconst float JOINT_CAP_ROUND = 20.0;\nconst float FILL_EXPAND = 24.0;\nconst float CAP_BUTT = 1.0;\nconst float CAP_SQUARE = 2.0;\nconst float CAP_ROUND = 3.0;\nconst float CAP_BUTT2 = 4.0;\n\nconst float u_Expand = 1.0;\nconst float u_MiterLimit = 5.0;\nconst float u_ScaleMode = 1.0;\nconst float u_Alignment = 0.5;\n\nout vec4 v_Distance;\nout vec4 v_Arc;\nout float v_Type;\nout float v_Travel;\nout float v_ScalingFactor;\n\nvec2 doBisect(\n  vec2 norm, float len, vec2 norm2, float len2, float dy, float inner\n) {\n  vec2 bisect = (norm + norm2) / 2.0;\n  bisect /= dot(norm, bisect);\n  vec2 shift = dy * bisect;\n  if (inner > 0.5) {\n    if (len < len2) {\n      if (abs(dy * (bisect.x * norm.y - bisect.y * norm.x)) > len) {\n        return dy * norm;\n      }\n    } else {\n      if (abs(dy * (bisect.x * norm2.y - bisect.y * norm2.x)) > len2) {\n        return dy * norm;\n      }\n    }\n  }\n  return dy * bisect;\n}\n\nvec2 clip2ScreenSpace(vec4 clip) {\n  return u_Viewport * (0.5 * clip.xy / clip.w + 0.5);\n}\n\nvoid main() {\n  vec4 a_Color = decode_color(a_PackedColor.xy);\nvec4 a_StrokeColor = decode_color(a_PackedColor.zw);\n\nmat4 u_ModelMatrix = mat4(a_ModelMatrix0, a_ModelMatrix1, a_ModelMatrix2, a_ModelMatrix3);\nvec4 u_StrokeColor = a_StrokeColor;\nfloat u_Opacity = a_StylePacked1.x;\nfloat u_FillOpacity = a_StylePacked1.y;\nfloat u_StrokeOpacity = a_StylePacked1.z;\nfloat u_StrokeWidth = a_StylePacked1.w;\nfloat u_ZIndex = a_PickingColor.w;\nvec2 u_Anchor = a_StylePacked2.yz;\nfloat u_IncreasedLineWidthForHitTesting = a_StylePacked2.w;\n\nsetPickingColor(a_PickingColor.xyz);\n\nv_Color = a_Color;\nv_StrokeColor = a_StrokeColor;\nv_StylePacked1 = a_StylePacked1;\nv_StylePacked2 = a_StylePacked2;\n\n// #ifdef CLIPSPACE_NEAR_ZERO\n//     gl_Position.z = (gl_Position.z + gl_Position.w) * 0.5;\n// #endif\n  #ifdef USE_UV\n  v_Uv = a_Uv;\n#endif\n\n  v_Dash = a_Dash;\n\n  vec2 pointA;\n  vec2 pointB;\n  vec4 clip0;\n  vec4 clip1;\n\n  float compressed = a_Dash.w;\n  float is_billboard = floor(compressed / 4.0);\n  compressed -= is_billboard * 4.0;\n  float is_size_attenuation = floor(compressed / 2.0);\n  compressed -= is_size_attenuation * 2.0;\n  float is_3d_polyline = compressed;\n\n  bool isBillboard = is_billboard > 0.5;\n  bool isSizeAttenuation = is_size_attenuation > 0.5;\n  bool is3DPolyline = is_3d_polyline > 0.5;\n  if (isBillboard) {\n    clip0 = billboard(a_Point1.xy, 0.0, isSizeAttenuation, u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix, vec3(0.0));\n    clip1 = billboard(a_Point2.xy, 0.0, isSizeAttenuation, u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix, vec3(0.0));\n  } else if (is3DPolyline) {\n    clip0 = project(vec4(a_Point1, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n    clip1 = project(vec4(a_Point2, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n  }\n\n  if (isBillboard || is3DPolyline) {\n    pointA = clip2ScreenSpace(clip0);\n    pointB = clip2ScreenSpace(clip1);\n  } else {\n    pointA = (u_ModelMatrix * vec4(a_Point1, 1.0)).xy;\n    pointB = (u_ModelMatrix * vec4(a_Point2, 1.0)).xy;\n  }\n\n  vec2 xBasis = pointB - pointA;\n  float len = length(xBasis);\n  vec2 forward = xBasis / len;\n  vec2 norm = vec2(forward.y, -forward.x);\n\n  float type = a_VertexJoint;\n\n  float lineWidth;\n  if (u_IsPicking > 0.5) {\n    lineWidth = u_IncreasedLineWidthForHitTesting + u_StrokeWidth;\n  } else {\n    lineWidth = u_StrokeWidth;\n  }\n\n  if (u_ScaleMode > 2.5) {\n    lineWidth *= length(u_ModelMatrix * vec4(1.0, 0.0, 0.0, 0.0));\n  } else if (u_ScaleMode > 1.5) {\n    lineWidth *= length(u_ModelMatrix * vec4(0.0, 1.0, 0.0, 0.0));\n  } else if (u_ScaleMode > 0.5) {\n    vec2 avgDiag = (u_ModelMatrix * vec4(1.0, 1.0, 0.0, 0.0)).xy;\n    lineWidth *= sqrt(dot(avgDiag, avgDiag) * 0.5);\n  }\n  float capType = floor(type / 32.0);\n  type -= capType * 32.0;\n  v_Arc = vec4(0.0);\n  lineWidth *= 0.5;\n  float lineAlignment = 2.0 * u_Alignment - 1.0;\n\n  vec2 pos;\n\n  if (capType == CAP_ROUND) {\n    if (a_VertexNum < 3.5) {\n      gl_Position = vec4(0.0, 0.0, 0.0, 1.0);\n      return;\n    }\n    type = JOINT_CAP_ROUND;\n    capType = 0.0;\n  }\n\n  if (type >= BEVEL) {\n    float dy = lineWidth + u_Expand;\n    float inner = 0.0;\n    if (a_VertexNum >= 1.5) {\n      dy = -dy;\n      inner = 1.0;\n    }\n\n    vec2 base, next, xBasis2, bisect;\n    float flag = 0.0;\n    float sign2 = 1.0;\n    if (a_VertexNum < 0.5 || a_VertexNum > 2.5 && a_VertexNum < 3.5) {\n      if (isBillboard) {\n        next = clip2ScreenSpace(billboard(a_Prev.xy, 0.0, isSizeAttenuation, u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix, vec3(0.0)));\n      } else if (is3DPolyline) {\n        next = clip2ScreenSpace(project(vec4(a_Prev, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix));\n      } else {\n        next = (u_ModelMatrix * vec4(a_Prev, 1.0)).xy;\n      }\n\n      base = pointA;\n      flag = type - floor(type / 2.0) * 2.0;\n      sign2 = -1.0;\n    } else {\n      if (isBillboard) {\n        next = clip2ScreenSpace(billboard(a_Next.xy, 0.0, isSizeAttenuation, u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix, vec3(0.0)));\n      } else if (is3DPolyline) {\n        next = clip2ScreenSpace(project(vec4(a_Next, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix));\n      } else {\n        next = (u_ModelMatrix * vec4(a_Next, 1.0)).xy;\n      }\n      \n      base = pointB;\n      if (type >= MITER && type < MITER + 3.5) {\n        flag = step(MITER + 1.5, type);\n        // check miter limit here?\n      }\n    }\n    xBasis2 = next - base;\n    float len2 = length(xBasis2);\n    vec2 norm2 = vec2(xBasis2.y, -xBasis2.x) / len2;\n    float D = norm.x * norm2.y - norm.y * norm2.x;\n    if (D < 0.0) {\n      inner = 1.0 - inner;\n    }\n    norm2 *= sign2;\n\n    if (abs(lineAlignment) > 0.01) {\n      float shift = lineWidth * lineAlignment;\n      pointA += norm * shift;\n      pointB += norm * shift;\n      if (abs(D) < 0.01) {\n        base += norm * shift;\n      } else {\n        base += doBisect(norm, len, norm2, len2, shift, 0.0);\n      }\n    }\n\n    float collinear = step(0.0, dot(norm, norm2));\n    v_Type = 0.0;\n    float dy2 = -1000.0;\n    float dy3 = -1000.0;\n    if (abs(D) < 0.01 && collinear < 0.5) {\n      if (type >= ROUND && type < ROUND + 1.5) {\n        type = JOINT_CAP_ROUND;\n      }\n      // TODO: BUTT here too\n    }\n\n    if (a_VertexNum < 3.5) {\n      if (abs(D) < 0.01) {\n        pos = dy * norm;\n      } else {\n        if (flag < 0.5 && inner < 0.5) {\n          pos = dy * norm;\n        } else {\n          pos = doBisect(norm, len, norm2, len2, dy, inner);\n        }\n      }\n      if (capType >= CAP_BUTT && capType < CAP_ROUND) {\n        float extra = step(CAP_SQUARE, capType) * lineWidth;\n        vec2 back = -forward;\n        if (a_VertexNum < 0.5 || a_VertexNum > 2.5) {\n          pos += back * (u_Expand + extra);\n          dy2 = u_Expand;\n        } else {\n          dy2 = dot(pos + base - pointA, back) - extra;\n        }\n      }\n      if (type >= JOINT_CAP_BUTT && type < JOINT_CAP_SQUARE + 0.5) {\n        float extra = step(JOINT_CAP_SQUARE, type) * lineWidth;\n        if (a_VertexNum < 0.5 || a_VertexNum > 2.5) {\n          dy3 = dot(pos + base - pointB, forward) - extra;\n        } else {\n          pos += forward * (u_Expand + extra);\n          dy3 = u_Expand;\n          if (capType >= CAP_BUTT) {\n            dy2 -= u_Expand + extra;\n          }\n        }\n      }\n    } else if (type >= JOINT_CAP_ROUND && type < JOINT_CAP_ROUND + 1.5) {\n      if (inner > 0.5) {\n        dy = -dy;\n        inner = 0.0;\n      }\n      vec2 d2 = abs(dy) * forward;\n      if (a_VertexNum < 4.5) {\n        dy = -dy;\n        pos = dy * norm;\n      } else if (a_VertexNum < 5.5) {\n        pos = dy * norm;\n      } else if (a_VertexNum < 6.5) {\n        pos = dy * norm + d2;\n        v_Arc.x = abs(dy);\n      } else {\n        dy = -dy;\n        pos = dy * norm + d2;\n        v_Arc.x = abs(dy);\n      }\n      dy2 = 0.0;\n      v_Arc.y = dy;\n      v_Arc.z = 0.0;\n      v_Arc.w = lineWidth;\n      v_Type = 3.0;\n    } else if (abs(D) < 0.01) {\n      pos = dy * norm;\n    } else {\n      if (type >= ROUND && type < ROUND + 1.5) {\n        if (inner > 0.5) {\n          dy = -dy;\n          inner = 0.0;\n        }\n        if (a_VertexNum < 4.5) {\n          pos = doBisect(norm, len, norm2, len2, -dy, 1.0);\n        } else if (a_VertexNum < 5.5) {\n          pos = dy * norm;\n        } else if (a_VertexNum > 7.5) {\n          pos = dy * norm2;\n        } else {\n          pos = doBisect(norm, len, norm2, len2, dy, 0.0);\n          float d2 = abs(dy);\n          if (length(pos) > abs(dy) * 1.5) {\n            if (a_VertexNum < 6.5) {\n              pos.x = dy * norm.x - d2 * norm.y;\n              pos.y = dy * norm.y + d2 * norm.x;\n            } else {\n              pos.x = dy * norm2.x + d2 * norm2.y;\n              pos.y = dy * norm2.y - d2 * norm2.x;\n            }\n          }\n        }\n        vec2 norm3 = normalize(norm + norm2);\n        float sign = step(0.0, dy) * 2.0 - 1.0;\n        v_Arc.x = sign * dot(pos, norm3);\n        v_Arc.y = pos.x * norm3.y - pos.y * norm3.x;\n        v_Arc.z = dot(norm, norm3) * lineWidth;\n        v_Arc.w = lineWidth;\n        dy = -sign * dot(pos, norm);\n        dy2 = -sign * dot(pos, norm2);\n        dy3 = v_Arc.z - v_Arc.x;\n        v_Type = 3.0;\n      } else {\n        float hit = 0.0;\n        if (type >= BEVEL && type < BEVEL + 1.5) {\n          if (dot(norm, norm2) > 0.0) {\n            type = MITER;\n          }\n        }\n        if (type >= MITER && type < MITER + 3.5) {\n          if (inner > 0.5) {\n            dy = -dy;\n            inner = 0.0;\n          }\n          float sign = step(0.0, dy) * 2.0 - 1.0;\n          pos = doBisect(norm, len, norm2, len2, dy, 0.0);\n          if (length(pos) > abs(dy) * u_MiterLimit) {\n            type = BEVEL;\n          } else {\n            if (a_VertexNum < 4.5) {\n              dy = -dy;\n              pos = doBisect(norm, len, norm2, len2, dy, 1.0);\n            } else if (a_VertexNum < 5.5) {\n              pos = dy * norm;\n            } else if (a_VertexNum > 6.5) {\n              pos = dy * norm2;\n            }\n            v_Type = 1.0;\n            dy = -sign * dot(pos, norm);\n            dy2 = -sign * dot(pos, norm2);\n            hit = 1.0;\n          }\n        }\n        if (type >= BEVEL && type < BEVEL + 1.5) {\n          if (inner > 0.5) {\n            dy = -dy;\n            inner = 0.0;\n          }\n          float d2 = abs(dy);\n          vec2 pos3 = vec2(dy * norm.x - d2 * norm.y, dy * norm.y + d2 * norm.x);\n          vec2 pos4 = vec2(dy * norm2.x + d2 * norm2.y, dy * norm2.y - d2 * norm2.x);\n          if (a_VertexNum < 4.5) {\n            pos = doBisect(norm, len, norm2, len2, -dy, 1.0);\n          } else if (a_VertexNum < 5.5) {\n            pos = dy * norm;\n          } else if (a_VertexNum > 7.5) {\n            pos = dy * norm2;\n          } else {\n            if (a_VertexNum < 6.5) {\n              pos = pos3;\n            } else {\n              pos = pos4;\n            }\n          }\n          vec2 norm3 = normalize(norm + norm2);\n          float sign = step(0.0, dy) * 2.0 - 1.0;\n          dy = -sign * dot(pos, norm);\n          dy2 = -sign * dot(pos, norm2);\n          dy3 = (-sign * dot(pos, norm3)) + lineWidth;\n          v_Type = 4.0;\n          hit = 1.0;\n        }\n        if (hit < 0.5) {\n          gl_Position = vec4(0.0, 0.0, 0.0, 1.0);\n          return;\n        }\n      }\n    }\n    pos += base;\n    v_Distance = vec4(dy, dy2, dy3, lineWidth) * u_DevicePixelRatio;\n    v_Arc = v_Arc * u_DevicePixelRatio;\n    v_Travel = a_Travel + dot(pos - pointA, vec2(-norm.y, norm.x));\n  }\n\n  v_ScalingFactor = sqrt(u_ModelMatrix[0][0] * u_ModelMatrix[0][0] + u_ModelMatrix[0][1] * u_ModelMatrix[0][1] + u_ModelMatrix[0][2] * u_ModelMatrix[0][2]);\n\n  if (isBillboard || is3DPolyline) {\n    vec4 clip = mix(clip0, clip1, 0.5);\n    gl_Position = vec4(clip.w * (2.0 * pos / u_Viewport - 1.0), clip.z, clip.w);\n  } else {\n    gl_Position = u_ProjectionMatrix * u_ViewMatrix * vec4(pos, u_ZIndex, 1.0);\n  }\n}"; // eslint-disable-line

var SEGMENT_LENGTH = 10;
var MIN_SEGMENT_NUM = 8;
var MAX_SEGMENT_NUM = 100;
function quadCurveTo(cpX, cpY, toX, toY, points, segmentNum) {
  var fromX = points[points.length - 3];
  var fromY = points[points.length - 2];
  var n = segmentNum !== null && segmentNum !== void 0 ? segmentNum : clamp(quadLength(fromX, fromY, cpX, cpY, toX, toY) / SEGMENT_LENGTH, MIN_SEGMENT_NUM, MAX_SEGMENT_NUM);
  var xa = 0;
  var ya = 0;
  for (var i = 1; i <= n; ++i) {
    var j = i / n;
    xa = fromX + (cpX - fromX) * j;
    ya = fromY + (cpY - fromY) * j;
    points.push(xa + (cpX + (toX - cpX) * j - xa) * j, ya + (cpY + (toY - cpY) * j - ya) * j, 0);
  }
}
function bezierCurveTo(cpX, cpY, cpX2, cpY2, toX, toY, points, segmentNum) {
  var fromX = points[points.length - 3];
  var fromY = points[points.length - 2];
  points.length -= 3;
  var n = segmentNum !== null && segmentNum !== void 0 ? segmentNum : clamp(cubicLength(fromX, fromY, cpX, cpY, cpX2, cpY2, toX, toY) / SEGMENT_LENGTH, MIN_SEGMENT_NUM, MAX_SEGMENT_NUM);
  var dt = 0;
  var dt2 = 0;
  var dt3 = 0;
  var t2 = 0;
  var t3 = 0;
  points.push(fromX, fromY, 0);
  for (var i = 1, j = 0; i <= n; ++i) {
    j = i / n;
    dt = 1 - j;
    dt2 = dt * dt;
    dt3 = dt2 * dt;
    t2 = j * j;
    t3 = t2 * j;
    points.push(dt3 * fromX + 3 * dt2 * j * cpX + 3 * dt * t2 * cpX2 + t3 * toX, dt3 * fromY + 3 * dt2 * j * cpY + 3 * dt * t2 * cpY2 + t3 * toY, 0);
  }
}

/**
 * Borrow from https://github.com/mapbox/tiny-sdf
 */

var INF = 1e20;
var TinySDF = /*#__PURE__*/function () {
  function TinySDF(options, runtime) {
    _classCallCheck(this, TinySDF);
    var _options$fontSize = options.fontSize,
      fontSize = _options$fontSize === void 0 ? 24 : _options$fontSize,
      _options$buffer = options.buffer,
      buffer = _options$buffer === void 0 ? 3 : _options$buffer,
      _options$radius = options.radius,
      radius = _options$radius === void 0 ? 8 : _options$radius,
      _options$cutoff = options.cutoff,
      cutoff = _options$cutoff === void 0 ? 0.25 : _options$cutoff,
      _options$fontFamily = options.fontFamily,
      fontFamily = _options$fontFamily === void 0 ? 'sans-serif' : _options$fontFamily,
      _options$fontWeight = options.fontWeight,
      fontWeight = _options$fontWeight === void 0 ? 'normal' : _options$fontWeight,
      _options$fontStyle = options.fontStyle,
      fontStyle = _options$fontStyle === void 0 ? 'normal' : _options$fontStyle,
      canvas = options.canvas;
    this.buffer = buffer;
    this.cutoff = cutoff;
    this.radius = radius;

    // make the canvas size big enough to both have the specified buffer around the glyph
    // for "halo", and account for some glyphs possibly being larger than their font size
    var size = this.size = fontSize + buffer * 4;
    var $offscreenCanvas = runtime.offscreenCanvasCreator.getOrCreateCanvas(canvas);

    // canvas.width = canvas.height = size;
    // $offscreenCanvas.width = width * dpr;
    // $offscreenCanvas.height = height * dpr;
    $offscreenCanvas.width = size;
    $offscreenCanvas.height = size;
    var ctx = runtime.offscreenCanvasCreator.getOrCreateContext(canvas, {
      willReadFrequently: true
    });
    this.ctx = ctx;

    // const ctx = (this.ctx = canvas.getContext('2d', {
    //   willReadFrequently: true,
    // }));
    ctx.font = "".concat(fontStyle, " ").concat(fontWeight, " ").concat(fontSize, "px ").concat(fontFamily);
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left'; // Necessary so that RTL text doesn't have different alignment
    ctx.fillStyle = 'black';

    // temporary arrays for the distance transform
    this.gridOuter = new Float64Array(size * size);
    this.gridInner = new Float64Array(size * size);
    this.f = new Float64Array(size);
    this.z = new Float64Array(size + 1);
    this.v = new Uint16Array(size);
  }

  // _createCanvas(size) {
  //   const canvas = document.createElement('canvas');
  //   canvas.width = canvas.height = size;
  //   return canvas;
  // }
  return _createClass(TinySDF, [{
    key: "draw",
    value: function draw(_char) {
      var _this$ctx$measureText = this.ctx.measureText(_char),
        glyphAdvance = _this$ctx$measureText.width,
        actualBoundingBoxAscent = _this$ctx$measureText.actualBoundingBoxAscent,
        actualBoundingBoxDescent = _this$ctx$measureText.actualBoundingBoxDescent,
        actualBoundingBoxLeft = _this$ctx$measureText.actualBoundingBoxLeft,
        actualBoundingBoxRight = _this$ctx$measureText.actualBoundingBoxRight;

      // The integer/pixel part of the top alignment is encoded in metrics.glyphTop
      // The remainder is implicitly encoded in the rasterization
      var glyphTop = Math.ceil(actualBoundingBoxAscent);
      var glyphLeft = 0;

      // If the glyph overflows the canvas size, it will be clipped at the bottom/right
      var glyphWidth = Math.max(0, Math.min(this.size - this.buffer, Math.ceil(actualBoundingBoxRight - actualBoundingBoxLeft)));
      var glyphHeight = Math.min(this.size - this.buffer, glyphTop + Math.ceil(actualBoundingBoxDescent));
      var width = glyphWidth + 2 * this.buffer;
      var height = glyphHeight + 2 * this.buffer;
      var len = Math.max(width * height, 0);
      var data = new Uint8ClampedArray(len);
      var glyph = {
        data: data,
        width: width,
        height: height,
        glyphWidth: glyphWidth,
        glyphHeight: glyphHeight,
        glyphTop: glyphTop,
        glyphLeft: glyphLeft,
        glyphAdvance: glyphAdvance
      };
      if (glyphWidth === 0 || glyphHeight === 0) return glyph;
      var ctx = this.ctx,
        buffer = this.buffer,
        gridInner = this.gridInner,
        gridOuter = this.gridOuter;
      ctx.clearRect(buffer, buffer, glyphWidth, glyphHeight);
      ctx.fillText(_char, buffer, buffer + glyphTop);
      var imgData = ctx.getImageData(buffer, buffer, glyphWidth, glyphHeight);

      // Initialize grids outside the glyph range to alpha 0
      gridOuter.fill(INF, 0, len);
      gridInner.fill(0, 0, len);
      for (var y = 0; y < glyphHeight; y++) {
        for (var x = 0; x < glyphWidth; x++) {
          var a = imgData.data[4 * (y * glyphWidth + x) + 3] / 255; // alpha value
          if (a === 0) continue; // empty pixels

          var j = (y + buffer) * width + x + buffer;
          if (a === 1) {
            // fully drawn pixels
            gridOuter[j] = 0;
            gridInner[j] = INF;
          } else {
            // aliased pixels
            var d = 0.5 - a;
            gridOuter[j] = d > 0 ? d * d : 0;
            gridInner[j] = d < 0 ? d * d : 0;
          }
        }
      }
      edt(gridOuter, 0, 0, width, height, width, this.f, this.v, this.z);
      edt(gridInner, buffer, buffer, glyphWidth, glyphHeight, width, this.f, this.v, this.z);
      for (var i = 0; i < len; i++) {
        var _d = Math.sqrt(gridOuter[i]) - Math.sqrt(gridInner[i]);
        data[i] = Math.round(255 - 255 * (_d / this.radius + this.cutoff));
      }
      return glyph;
    }
  }]);
}();

// 2D Euclidean squared distance transform by Felzenszwalb & Huttenlocher https://cs.brown.edu/~pff/papers/dt-final.pdf
function edt(data, x0, y0, width, height, gridSize, f, v, z) {
  for (var x = x0; x < x0 + width; x++) edt1d(data, y0 * gridSize + x, gridSize, height, f, v, z);
  for (var y = y0; y < y0 + height; y++) edt1d(data, y * gridSize + x0, 1, width, f, v, z);
}

// 1D squared distance transform
function edt1d(grid, offset, stride, length, f, v, z) {
  v[0] = 0;
  z[0] = -INF;
  z[1] = INF;
  f[0] = grid[offset];
  for (var q = 1, k = 0, s = 0; q < length; q++) {
    f[q] = grid[offset + q * stride];
    var q2 = q * q;
    do {
      var r = v[k];
      s = (f[q] - f[r] + q2 - r * r) / (q - r) / 2;
    } while (s <= z[k] && --k > -1);
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = INF;
  }
  for (var _q = 0, _k = 0; _q < length; _q++) {
    while (z[_k + 1] < _q) _k++;
    var _r = v[_k];
    var qr = _q - _r;
    grid[offset + _q * stride] = f[_r] + qr * qr;
  }
}

var LineVertexAttributeBufferIndex = function (LineVertexAttributeBufferIndex) {
  LineVertexAttributeBufferIndex[LineVertexAttributeBufferIndex["PACKED"] = VertexAttributeBufferIndex.POSITION + 1] = "PACKED";
  LineVertexAttributeBufferIndex[LineVertexAttributeBufferIndex["VERTEX_NUM"] = 1 + LineVertexAttributeBufferIndex["PACKED"]] = "VERTEX_NUM";
  LineVertexAttributeBufferIndex[LineVertexAttributeBufferIndex["TRAVEL"] = 1 + LineVertexAttributeBufferIndex["VERTEX_NUM"]] = "TRAVEL";
  LineVertexAttributeBufferIndex[LineVertexAttributeBufferIndex["DASH"] = 1 + LineVertexAttributeBufferIndex["TRAVEL"]] = "DASH";
  return LineVertexAttributeBufferIndex;
}(LineVertexAttributeBufferIndex || {});
var LineVertexAttributeLocation = function (LineVertexAttributeLocation) {
  LineVertexAttributeLocation[LineVertexAttributeLocation["PREV"] = VertexAttributeLocation.POSITION] = "PREV";
  LineVertexAttributeLocation[LineVertexAttributeLocation["POINT1"] = 1 + LineVertexAttributeLocation["PREV"]] = "POINT1";
  LineVertexAttributeLocation[LineVertexAttributeLocation["POINT2"] = 1 + LineVertexAttributeLocation["POINT1"]] = "POINT2";
  LineVertexAttributeLocation[LineVertexAttributeLocation["NEXT"] = 1 + LineVertexAttributeLocation["POINT2"]] = "NEXT";
  LineVertexAttributeLocation[LineVertexAttributeLocation["VERTEX_JOINT"] = 1 + LineVertexAttributeLocation["NEXT"]] = "VERTEX_JOINT";
  LineVertexAttributeLocation[LineVertexAttributeLocation["VERTEX_NUM"] = 1 + LineVertexAttributeLocation["VERTEX_JOINT"]] = "VERTEX_NUM";
  LineVertexAttributeLocation[LineVertexAttributeLocation["TRAVEL"] = 1 + LineVertexAttributeLocation["VERTEX_NUM"]] = "TRAVEL";
  LineVertexAttributeLocation[LineVertexAttributeLocation["DASH"] = 1 + LineVertexAttributeLocation["TRAVEL"]] = "DASH";
  return LineVertexAttributeLocation;
}(LineVertexAttributeLocation || {});
var SEGMENT_NUM$1 = 12;
function packBoolean(a, b, c) {
  return (a ? 1 : 0) * 4 + (b ? 1 : 0) * 2 + (c ? 1 : 0);
}
function is3DPolyline(object) {
  var isPolyline = object.nodeName === Shape.POLYLINE;
  if (!isPolyline) {
    return false;
  }
  // Polyline supports 3 dimensions so that each point is shaped like [x, y, z].
  var polylineControlPoints = object.parsedStyle.points.points;
  var length = polylineControlPoints.length;
  return length && !isNil(polylineControlPoints[0][2]);
}

/**
 * Used for Curve only contains 2 commands, e.g. [[M], [C | Q | A]]
 */
var InstancedPathDrawcall = /*#__PURE__*/function (_Instanced) {
  function InstancedPathDrawcall(renderHelper, texturePool, lightPool, object, drawcallCtors, index, context) {
    var _this;
    _classCallCheck(this, InstancedPathDrawcall);
    _this = _callSuper(this, InstancedPathDrawcall, [renderHelper, texturePool, lightPool, object, drawcallCtors, index, context]);
    // protected mergeAnchorIntoModelMatrix = true;
    _this.segmentNum = -1;
    _this.renderHelper = renderHelper;
    _this.texturePool = texturePool;
    _this.lightPool = lightPool;
    _this.segmentNum = _this.calcSegmentNum(object);
    _this.gradientAttributeName = 'stroke';
    return _this;
  }
  _inherits(InstancedPathDrawcall, _Instanced);
  return _createClass(InstancedPathDrawcall, [{
    key: "calcSegmentNum",
    value: function calcSegmentNum(object) {
      // FIXME: only need to collect instanced count
      var _updateBuffer = updateBuffer(object, false, SEGMENT_NUM$1, this.calcSubpathIndex(object)),
        instancedCount = _updateBuffer.instancedCount;
      return instancedCount;
    }
  }, {
    key: "calcSubpathIndex",
    value: function calcSubpathIndex(object) {
      if (object.nodeName === Shape.PATH) {
        var fillDrawcallCount = this.drawcallCtors.filter(function (ctor) {
          return ctor === InstancedFillDrawcall;
        }).length;
        return this.index - fillDrawcallCount;
      }
      return 0;
    }

    /**
     * Paths with the same number of vertices should be merged.
     */
  }, {
    key: "shouldMerge",
    value: function shouldMerge(object, index) {
      var shouldMerge = _superPropGet(InstancedPathDrawcall, "shouldMerge", this, 3)([object, index]);
      if (!shouldMerge) {
        return false;
      }
      if (this.index !== index) {
        return false;
      }
      var segmentNum = this.calcSegmentNum(object);
      return this.segmentNum === segmentNum;
    }
  }, {
    key: "createMaterial",
    value: function createMaterial(objects) {
      this.material.vertexShader = vert$2;
      this.material.fragmentShader = frag$2;
      this.material.defines = _objectSpread(_objectSpread({}, this.material.defines), enumToObject(LineVertexAttributeLocation));
    }
  }, {
    key: "createGeometry",
    value: function createGeometry(objects) {
      var _this2 = this;
      var indices = [];
      var pointsBuffer = [];
      var travelBuffer = [];
      var packedDash = [];
      var instancedCount = 0;
      var offset = 0;
      objects.forEach(function (object) {
        var _updateBuffer2 = updateBuffer(object, false, SEGMENT_NUM$1, _this2.calcSubpathIndex(object)),
          pBuffer = _updateBuffer2.pointsBuffer,
          tBuffer = _updateBuffer2.travelBuffer,
          count = _updateBuffer2.instancedCount;
        var _parsedStyle = object.parsedStyle,
          lineDash = _parsedStyle.lineDash,
          lineDashOffset = _parsedStyle.lineDashOffset,
          isBillboard = _parsedStyle.isBillboard,
          isSizeAttenuation = _parsedStyle.isSizeAttenuation;
        packedDash.push(lineDash && lineDash[0] || 0,
        // DASH
        lineDash && lineDash[1] || 0,
        // GAP
        lineDashOffset || 0, packBoolean(isBillboard, isSizeAttenuation, is3DPolyline(object)));
        instancedCount += count;

        // Can't use interleaved buffer here, we should spread them like:
        // | prev - pointA - pointB - next |. This will allocate ~4x buffer memory space.
        for (var i = 0; i < pBuffer.length - 3 * 4; i += 4) {
          pointsBuffer.push(pBuffer[i], pBuffer[i + 1], pBuffer[i + 2], pBuffer[i + 3], pBuffer[i + 4], pBuffer[i + 5], pBuffer[i + 6], pBuffer[i + 7], pBuffer[i + 8], pBuffer[i + 9], pBuffer[i + 10], pBuffer[i + 11], pBuffer[i + 12], pBuffer[i + 13], pBuffer[i + 14], pBuffer[i + 15]);
        }
        travelBuffer.push.apply(travelBuffer, _toConsumableArray(tBuffer));
        indices.push(0 + offset, 2 + offset, 1 + offset, 0 + offset, 3 + offset, 2 + offset, 4 + offset, 6 + offset, 5 + offset, 4 + offset, 7 + offset, 6 + offset, 4 + offset, 7 + offset, 8 + offset);
        offset += 9;
      });
      if (pointsBuffer.length) {
        this.geometry.setVertexBuffer({
          bufferIndex: LineVertexAttributeBufferIndex.PACKED,
          byteStride: 4 * (4 + 4 + 4 + 4),
          stepMode: VertexStepMode.INSTANCE,
          attributes: [{
            format: Format.F32_RGB,
            bufferByteOffset: 4 * 0,
            location: LineVertexAttributeLocation.PREV,
            divisor: 1
          }, {
            format: Format.F32_RGB,
            bufferByteOffset: 4 * 4,
            location: LineVertexAttributeLocation.POINT1,
            divisor: 1
          }, {
            format: Format.F32_R,
            bufferByteOffset: 4 * 7,
            location: LineVertexAttributeLocation.VERTEX_JOINT,
            divisor: 1
          }, {
            format: Format.F32_RGB,
            bufferByteOffset: 4 * 8,
            location: LineVertexAttributeLocation.POINT2,
            divisor: 1
          }, {
            format: Format.F32_RGB,
            bufferByteOffset: 4 * 12,
            location: LineVertexAttributeLocation.NEXT,
            divisor: 1
          }],
          data: new Float32Array(pointsBuffer)
        });
        this.geometry.setVertexBuffer({
          bufferIndex: LineVertexAttributeBufferIndex.VERTEX_NUM,
          byteStride: 4 * 1,
          stepMode: VertexStepMode.INSTANCE,
          attributes: [{
            format: Format.F32_R,
            bufferByteOffset: 4 * 0,
            byteStride: 4 * 1,
            location: LineVertexAttributeLocation.VERTEX_NUM,
            divisor: 0
          }],
          data: new Float32Array([0, 1, 2, 3, 4, 5, 6, 7, 8])
        });
        this.geometry.setVertexBuffer({
          bufferIndex: LineVertexAttributeBufferIndex.TRAVEL,
          byteStride: 4 * 1,
          stepMode: VertexStepMode.INSTANCE,
          attributes: [{
            format: Format.F32_R,
            bufferByteOffset: 4 * 0,
            byteStride: 4 * 1,
            location: LineVertexAttributeLocation.TRAVEL,
            divisor: 1
          }],
          data: new Float32Array(travelBuffer)
        });

        // this attribute only changes for each n instance
        this.divisor = instancedCount / objects.length;
        this.geometry.setVertexBuffer({
          bufferIndex: LineVertexAttributeBufferIndex.DASH,
          byteStride: 4 * 4,
          stepMode: VertexStepMode.INSTANCE,
          attributes: [{
            format: Format.F32_RGBA,
            bufferByteOffset: 4 * 0,
            location: LineVertexAttributeLocation.DASH,
            divisor: this.divisor
          }],
          data: new Float32Array(packedDash)
        });

        // use default common attributes
        _superPropGet(InstancedPathDrawcall, "createGeometry", this, 3)([objects]);
        this.geometry.vertexCount = 15;
        this.geometry.instancedCount = instancedCount;
        this.geometry.setIndexBuffer(new Uint32Array(indices));
      }
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(objects, startIndex, name, value) {
      var _this3 = this;
      if (objects.length === 0) {
        return;
      }
      _superPropGet(InstancedPathDrawcall, "updateAttribute", this, 3)([objects, startIndex, name, value]);
      this.updateBatchedAttribute(objects, startIndex, name, value);
      if (name === 'r' || name === 'rx' || name === 'ry' || name === 'width' || name === 'height' || name === 'radius' || name === 'x1' || name === 'y1' || name === 'x2' || name === 'y2' || name === 'points' || name === 'path' || name === 'd' || name === 'lineJoin' || name === 'lineCap' || name === 'markerStartOffset' || name === 'markerEndOffset' || name === 'markerStart' || name === 'markerEnd') {
        var pointsBuffer = [];
        var travelBuffer = [];
        var instancedCount = 0;
        objects.forEach(function (object) {
          var _updateBuffer3 = updateBuffer(object, false, SEGMENT_NUM$1, _this3.calcSubpathIndex(object)),
            pBuffer = _updateBuffer3.pointsBuffer,
            tBuffer = _updateBuffer3.travelBuffer,
            iCount = _updateBuffer3.instancedCount;
          instancedCount = iCount;

          // Can't use interleaved buffer here, we should spread them like:
          // | prev - pointA - pointB - next |. This will allocate ~4x buffer memory space.
          for (var i = 0; i < pBuffer.length - 3 * 4; i += 4) {
            pointsBuffer.push(pBuffer[i], pBuffer[i + 1], pBuffer[i + 2], pBuffer[i + 3], pBuffer[i + 4], pBuffer[i + 5], pBuffer[i + 6], pBuffer[i + 7], pBuffer[i + 8], pBuffer[i + 9], pBuffer[i + 10], pBuffer[i + 11], pBuffer[i + 12], pBuffer[i + 13], pBuffer[i + 14], pBuffer[i + 15]);
          }
          travelBuffer.push.apply(travelBuffer, _toConsumableArray(tBuffer));
        });
        this.geometry.updateVertexBuffer(LineVertexAttributeBufferIndex.PACKED, LineVertexAttributeLocation.PREV, startIndex * instancedCount, new Uint8Array(new Float32Array(pointsBuffer).buffer));
        this.geometry.updateVertexBuffer(LineVertexAttributeBufferIndex.TRAVEL, LineVertexAttributeLocation.TRAVEL, startIndex, new Uint8Array(new Float32Array(travelBuffer).buffer));
      } else if (name === 'lineDashOffset' || name === 'lineDash' || name === 'isBillboard' || name === 'isSizeAttenuation') {
        var packedDash = [];
        objects.forEach(function (object) {
          var _parsedStyle2 = object.parsedStyle,
            lineDash = _parsedStyle2.lineDash,
            lineDashOffset = _parsedStyle2.lineDashOffset,
            isBillboard = _parsedStyle2.isBillboard,
            isSizeAttenuation = _parsedStyle2.isSizeAttenuation;
          packedDash.push(lineDash && lineDash[0] || 0,
          // DASH
          lineDash && lineDash[1] || 0,
          // GAP
          lineDashOffset || 0, packBoolean(isBillboard, isSizeAttenuation, is3DPolyline(object)));
        });
        this.geometry.updateVertexBuffer(LineVertexAttributeBufferIndex.DASH, LineVertexAttributeLocation.DASH, startIndex, new Uint8Array(new Float32Array(packedDash).buffer));
      }
    }
  }], [{
    key: "calcSubpathNum",
    value: function calcSubpathNum(object) {
      if (object.nodeName === Shape.PATH) {
        var _ref = object.parsedStyle,
          absolutePath = _ref.d.absolutePath;
        return absolutePath.filter(function (d) {
          return d[0] === 'M';
        }).length;
      }
      return 1;
    }
  }]);
}(Instanced);
var JOINT_TYPE = /*#__PURE__*/function (JOINT_TYPE) {
  JOINT_TYPE[JOINT_TYPE["NONE"] = 0] = "NONE";
  JOINT_TYPE[JOINT_TYPE["FILL"] = 1] = "FILL";
  JOINT_TYPE[JOINT_TYPE["JOINT_BEVEL"] = 4] = "JOINT_BEVEL";
  JOINT_TYPE[JOINT_TYPE["JOINT_MITER"] = 8] = "JOINT_MITER";
  JOINT_TYPE[JOINT_TYPE["JOINT_ROUND"] = 12] = "JOINT_ROUND";
  JOINT_TYPE[JOINT_TYPE["JOINT_CAP_BUTT"] = 16] = "JOINT_CAP_BUTT";
  JOINT_TYPE[JOINT_TYPE["JOINT_CAP_SQUARE"] = 18] = "JOINT_CAP_SQUARE";
  JOINT_TYPE[JOINT_TYPE["JOINT_CAP_ROUND"] = 20] = "JOINT_CAP_ROUND";
  JOINT_TYPE[JOINT_TYPE["FILL_EXPAND"] = 24] = "FILL_EXPAND";
  JOINT_TYPE[JOINT_TYPE["CAP_BUTT"] = 32] = "CAP_BUTT";
  JOINT_TYPE[JOINT_TYPE["CAP_SQUARE"] = 64] = "CAP_SQUARE";
  JOINT_TYPE[JOINT_TYPE["CAP_ROUND"] = 96] = "CAP_ROUND";
  JOINT_TYPE[JOINT_TYPE["CAP_BUTT2"] = 128] = "CAP_BUTT2";
  return JOINT_TYPE;
}({});
var stridePoints = 3;
var strideFloats = 4;
function updateBuffer(object) {
  var needEarcut = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var segmentNum = arguments.length > 2 ? arguments[2] : undefined;
  var subPathIndex = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var _object$parsedStyle = object.parsedStyle,
    lineCap = _object$parsedStyle.lineCap,
    lineJoin = _object$parsedStyle.lineJoin;
  var zIndex = object.sortable.renderOrder * RENDER_ORDER_SCALE;
  var defX = 0;
  var defY = 0;
  var _ref2 = object.parsedStyle,
    markerStart = _ref2.markerStart,
    markerEnd = _ref2.markerEnd,
    markerStartOffset = _ref2.markerStartOffset,
    markerEndOffset = _ref2.markerEndOffset;
  var points = [];
  var triangles = [];
  if (object.nodeName === Shape.POLYLINE || object.nodeName === Shape.POLYGON) {
    var polylineControlPoints = object.parsedStyle.points.points;
    var length = polylineControlPoints.length;
    var startOffsetX = 0;
    var startOffsetY = 0;
    var endOffsetX = 0;
    var endOffsetY = 0;
    var rad = 0;
    var x;
    var y;
    if (markerStart && isDisplayObject(markerStart) && markerStartOffset) {
      x = polylineControlPoints[1][0] - polylineControlPoints[0][0];
      y = polylineControlPoints[1][1] - polylineControlPoints[0][1];
      rad = Math.atan2(y, x);
      startOffsetX = Math.cos(rad) * (markerStartOffset || 0);
      startOffsetY = Math.sin(rad) * (markerStartOffset || 0);
    }
    if (markerEnd && isDisplayObject(markerEnd) && markerEndOffset) {
      x = polylineControlPoints[length - 2][0] - polylineControlPoints[length - 1][0];
      y = polylineControlPoints[length - 2][1] - polylineControlPoints[length - 1][1];
      rad = Math.atan2(y, x);
      endOffsetX = Math.cos(rad) * (markerEndOffset || 0);
      endOffsetY = Math.sin(rad) * (markerEndOffset || 0);
    }
    var isPolyline = object.nodeName === Shape.POLYLINE;
    points[0] = polylineControlPoints.reduce(function (prev, cur, i) {
      var offsetX = 0;
      var offsetY = 0;
      if (i === 0) {
        offsetX = startOffsetX;
        offsetY = startOffsetY;
      } else if (i === length - 1) {
        offsetX = endOffsetX;
        offsetY = endOffsetY;
      }
      prev.push(cur[0] + offsetX, cur[1] + offsetY, isPolyline ? cur[2] || 0 : zIndex);
      return prev;
    }, []);

    // close polygon, dealing with extra joint
    if (object.nodeName === Shape.POLYGON) {
      var _points$;
      if (needEarcut) {
        // use earcut for triangulation
        triangles = earcut(points[0], [], 3);
        return {
          pointsBuffer: points[0],
          travelBuffer: [],
          triangles: triangles,
          instancedCount: Math.round(points[0].length / stridePoints)
        };
      }
      points[0].push(points[0][0], points[0][1], points[0][2] || zIndex);
      (_points$ = points[0]).push.apply(_points$, _toConsumableArray(addTailSegment(points[0][0], points[0][1], points[0][2] || zIndex, points[0][3], points[0][4], points[0][5] || zIndex)));
    }
  } else if (object.nodeName === Shape.PATH || object.nodeName === Shape.CIRCLE || object.nodeName === Shape.ELLIPSE || object.nodeName === Shape.RECT) {
    var path;
    if (object.nodeName !== Shape.PATH) {
      path = parsePath(convertToPath(object, mat4.identity(mat4.create())));

      // support negative width/height of Rect
      if (object.nodeName === Shape.RECT) {
        var _object$parsedStyle2 = object.parsedStyle,
          width = _object$parsedStyle2.width,
          height = _object$parsedStyle2.height;
        if (width < 0) {
          defX += path.rect.width;
        }
        if (height < 0) {
          defY += path.rect.height;
        }
      }
    } else {
      path = object.parsedStyle.d;
    }
    var _path = path,
      absolutePath = _path.absolutePath,
      segments = _path.segments;
    var _startOffsetX = 0;
    var _startOffsetY = 0;
    var _endOffsetX = 0;
    var _endOffsetY = 0;
    var _rad = 0;
    var _x;
    var _y;
    if (markerStart && markerStart.parentNode && isDisplayObject(markerStart) && markerStartOffset) {
      var _getStartTangent = markerStart.parentNode.getStartTangent(),
        _getStartTangent2 = _slicedToArray(_getStartTangent, 2),
        p1 = _getStartTangent2[0],
        p2 = _getStartTangent2[1];
      _x = p1[0] - p2[0];
      _y = p1[1] - p2[1];
      _rad = Math.atan2(_y, _x);
      _startOffsetX = Math.cos(_rad) * (markerStartOffset || 0);
      _startOffsetY = Math.sin(_rad) * (markerStartOffset || 0);
    }
    if (markerEnd && markerEnd.parentNode && isDisplayObject(markerEnd) && markerEndOffset) {
      var _getEndTangent = markerEnd.parentNode.getEndTangent(),
        _getEndTangent2 = _slicedToArray(_getEndTangent, 2),
        _p = _getEndTangent2[0],
        _p2 = _getEndTangent2[1];
      _x = _p[0] - _p2[0];
      _y = _p[1] - _p2[1];
      _rad = Math.atan2(_y, _x);
      _endOffsetX = Math.cos(_rad) * (markerEndOffset || 0);
      _endOffsetY = Math.sin(_rad) * (markerEndOffset || 0);
    }
    var startPointIndex = -1;
    var mCommandsNum = -1;
    absolutePath.forEach(function (_ref3, i) {
      var _ref4 = _toArray(_ref3),
        command = _ref4[0],
        params = _arrayLikeToArray(_ref4).slice(1);
      var nextSegment = absolutePath[i + 1];
      var useStartOffset = i === 0 && (_startOffsetX !== 0 || _startOffsetY !== 0);
      var useEndOffset = (i === absolutePath.length - 1 || nextSegment && (nextSegment[0] === 'M' || nextSegment[0] === 'Z')) && _endOffsetX !== 0 && _endOffsetY !== 0;
      if (command === 'M') {
        mCommandsNum++;
        points[mCommandsNum] = [];
        startPointIndex = points[mCommandsNum].length;
        if (useStartOffset) {
          points[mCommandsNum].push(params[0] - defX + _startOffsetX, params[1] - defY + _startOffsetY, zIndex, params[0] - defX, params[1] - defY, zIndex);
        } else {
          points[mCommandsNum].push(params[0] - defX, params[1] - defY, zIndex);
        }
      } else if (command === 'L') {
        if (useEndOffset) {
          points[mCommandsNum].push(params[0] - defX + _endOffsetX, params[1] - defY + _endOffsetY, zIndex);
        } else {
          points[mCommandsNum].push(params[0] - defX, params[1] - defY, zIndex);
        }
      } else if (command === 'Q') {
        quadCurveTo(params[0] - defX, params[1] - defY, params[2] - defX, params[3] - defY, points[mCommandsNum], segmentNum);
        if (useEndOffset) {
          points[mCommandsNum].push(params[2] - defX + _endOffsetX, params[3] - defY + _endOffsetY, zIndex);
        }
      } else if (command === 'A') {
        // convert Arc to Cubic

        var _segments$i$prePoint = _slicedToArray(segments[i].prePoint, 2),
          px1 = _segments$i$prePoint[0],
          py1 = _segments$i$prePoint[1];
        var args = arcToCubic(px1, py1, params[0], params[1], params[2], params[3], params[4], params[5], params[6], undefined);

        // fixArc
        for (var _i = 0; _i < args.length; _i += 6) {
          bezierCurveTo(args[_i] - defX, args[_i + 1] - defY, args[_i + 2] - defX, args[_i + 3] - defY, args[_i + 4] - defX, args[_i + 5] - defY, points[mCommandsNum], segmentNum);
        }
        if (useEndOffset) {
          points[mCommandsNum].push(params[5] - defX + _endOffsetX, params[6] - defY + _endOffsetY, zIndex);
        }
      } else if (command === 'C') {
        bezierCurveTo(params[0] - defX, params[1] - defY, params[2] - defX, params[3] - defY, params[4] - defX, params[5] - defY, points[mCommandsNum], segmentNum);
        if (useEndOffset) {
          points[mCommandsNum].push(params[4] - defX + _endOffsetX, params[5] - defY + _endOffsetY, zIndex);
        }
      } else if (command === 'Z' && (object.nodeName === Shape.PATH || object.nodeName === Shape.RECT)) {
        var _points$mCommandsNum;
        var epsilon = 0.0001;
        // skip if already closed
        if (Math.abs(points[mCommandsNum][points[mCommandsNum].length - 2] - points[mCommandsNum][startPointIndex]) > epsilon || Math.abs(points[mCommandsNum][points[mCommandsNum].length - 1] - points[mCommandsNum][startPointIndex + 1]) > epsilon) {
          points[mCommandsNum].push(points[mCommandsNum][startPointIndex], points[mCommandsNum][startPointIndex + 1], zIndex);
        }
        (_points$mCommandsNum = points[mCommandsNum]).push.apply(_points$mCommandsNum, _toConsumableArray(addTailSegment(points[mCommandsNum][startPointIndex], points[mCommandsNum][startPointIndex + 1], points[mCommandsNum][startPointIndex + 2], points[mCommandsNum][startPointIndex + 3], points[mCommandsNum][startPointIndex + 4], points[mCommandsNum][startPointIndex + 5])));
      }
    });
    if (needEarcut) {
      var pointsBuffer = points[subPathIndex];
      // use earcut for triangulation
      triangles = earcut(pointsBuffer, [], 3);
      return {
        pointsBuffer: pointsBuffer,
        travelBuffer: [],
        triangles: triangles,
        instancedCount: Math.round(pointsBuffer.length / stridePoints)
      };
    }
  }
  var jointType = getJointType(lineJoin);
  var capType = getCapType(lineCap);
  var endJoint = capType;
  if (capType === JOINT_TYPE.CAP_ROUND) {
    endJoint = JOINT_TYPE.JOINT_CAP_ROUND;
  }
  if (capType === JOINT_TYPE.CAP_BUTT) {
    endJoint = JOINT_TYPE.JOINT_CAP_BUTT;
  }
  if (capType === JOINT_TYPE.CAP_SQUARE) {
    endJoint = JOINT_TYPE.JOINT_CAP_SQUARE;
  }
  var subPath = points[subPathIndex];
  {
    var _points = subPath;
    var j = (Math.round(0 / stridePoints) + 2) * strideFloats;
    // const needDash = !isNil(lineDash);
    var dist = 0;
    var _pointsBuffer = [];
    var travelBuffer = [];
    for (var i = 0; i < _points.length; i += stridePoints) {
      // calc travel
      // if (needDash) {
      if (i > 1) {
        dist += Math.sqrt(Math.pow(_points[i] - _points[i - stridePoints], 2) + Math.pow(_points[i + 1] - _points[i + 1 - stridePoints], 2) + Math.pow(_points[i + 2] - _points[i + 2 - stridePoints], 2));
      }
      travelBuffer.push(dist);
      // } else {
      //   travelBuffer.push(0);
      // }

      _pointsBuffer[j++] = _points[i];
      _pointsBuffer[j++] = _points[i + 1];
      _pointsBuffer[j++] = _points[i + 2] || 0;
      _pointsBuffer[j] = jointType;
      if (i === 0 && capType !== JOINT_TYPE.CAP_ROUND) {
        _pointsBuffer[j] += capType;
      }
      if (i + stridePoints * 2 >= _points.length) {
        _pointsBuffer[j] += endJoint - jointType;
      } else if (i + stridePoints >= _points.length) {
        _pointsBuffer[j] = 0;
      }
      j++;
    }
    _pointsBuffer[j++] = _points[_points.length - 6];
    _pointsBuffer[j++] = _points[_points.length - 5];
    _pointsBuffer[j++] = _points[_points.length - 4] || zIndex;
    _pointsBuffer[j++] = 0;
    _pointsBuffer[0] = _points[0];
    _pointsBuffer[1] = _points[1];
    _pointsBuffer[2] = _points[2] || zIndex;
    _pointsBuffer[3] = 0;
    _pointsBuffer[4] = _points[3];
    _pointsBuffer[5] = _points[4];
    _pointsBuffer[6] = _points[5] || zIndex;
    _pointsBuffer[7] = capType === JOINT_TYPE.CAP_ROUND ? capType : 0;
    var instancedCount = Math.round(_points.length / stridePoints);
    return {
      pointsBuffer: _pointsBuffer,
      travelBuffer: travelBuffer,
      triangles: triangles,
      instancedCount: instancedCount
    };
  }
}
function getJointType(lineJoin) {
  var joint;
  switch (lineJoin) {
    case 'bevel':
      joint = JOINT_TYPE.JOINT_BEVEL;
      break;
    case 'round':
      joint = JOINT_TYPE.JOINT_ROUND;
      break;
    default:
      joint = JOINT_TYPE.JOINT_MITER;
      break;
  }
  return joint;
}
function getCapType(lineCap) {
  var cap;
  switch (lineCap) {
    case 'square':
      cap = JOINT_TYPE.CAP_SQUARE;
      break;
    case 'round':
      cap = JOINT_TYPE.CAP_ROUND;
      break;
    default:
      cap = JOINT_TYPE.CAP_BUTT;
      break;
  }
  return cap;
}
function addTailSegment(x1, y1, z1) {
  var x2 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : x1;
  var y2 = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : y1;
  var z2 = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : z1;
  var vec = [x2 - x1, y2 - y1, z2 - z1];
  var length = 0.01;
  return [x1 + vec[0] * length, y1 + vec[1] * length, z1 + vec[2] * length];
}

var SEGMENT_NUM = 12;
var FillVertexAttributeBufferIndex = function (FillVertexAttributeBufferIndex) {
  FillVertexAttributeBufferIndex[FillVertexAttributeBufferIndex["PACKED_STYLE"] = VertexAttributeBufferIndex.POSITION + 1] = "PACKED_STYLE";
  return FillVertexAttributeBufferIndex;
}(FillVertexAttributeBufferIndex || {});
var FillVertexAttributeLocation = function (FillVertexAttributeLocation) {
  FillVertexAttributeLocation[FillVertexAttributeLocation["PACKED_STYLE3"] = VertexAttributeLocation.MAX] = "PACKED_STYLE3";
  return FillVertexAttributeLocation;
}(FillVertexAttributeLocation || {});
var InstancedFillDrawcall = /*#__PURE__*/function (_Instanced) {
  // protected mergeAnchorIntoModelMatrix = true;

  function InstancedFillDrawcall(renderHelper, texturePool, lightPool, object, drawcallCtors, index, context) {
    var _this;
    _classCallCheck(this, InstancedFillDrawcall);
    _this = _callSuper(this, InstancedFillDrawcall, [renderHelper, texturePool, lightPool, object, drawcallCtors, index, context]);
    _this.trianglesHash = [[], []];
    _this.renderHelper = renderHelper;
    _this.texturePool = texturePool;
    _this.lightPool = lightPool;
    _this.trianglesHash = _this.calcSegmentNum(object);
    return _this;
  }
  _inherits(InstancedFillDrawcall, _Instanced);
  return _createClass(InstancedFillDrawcall, [{
    key: "calcSegmentNum",
    value: function calcSegmentNum(object) {
      var _updateBuffer = updateBuffer(object, true, SEGMENT_NUM, this.calcSubpathIndex(object)),
        triangles = _updateBuffer.triangles,
        pointsBuffer = _updateBuffer.pointsBuffer;
      return [triangles, pointsBuffer];
    }
  }, {
    key: "calcSubpathIndex",
    value: function calcSubpathIndex(object) {
      if (object.nodeName === Shape.PATH) {
        return this.index;
      }
      return 0;
    }
  }, {
    key: "compareTrianglesHash",
    value: function compareTrianglesHash(hash) {
      var _this$trianglesHash = _slicedToArray(this.trianglesHash, 2),
        triangles = _this$trianglesHash[0],
        points = _this$trianglesHash[1];
      var _hash = _slicedToArray(hash, 2),
        t = _hash[0],
        p = _hash[1];
      if (triangles.length !== t.length || points.length !== p.length) {
        return false;
      }
      if (triangles.some(function (n, i) {
        return n !== t[i];
      }) || points.some(function (n, i) {
        return n !== p[i];
      })) {
        return false;
      }
      return true;
    }
  }, {
    key: "shouldMerge",
    value: function shouldMerge(object, index) {
      var shouldMerge = _superPropGet(InstancedFillDrawcall, "shouldMerge", this, 3)([object, index]);
      if (!shouldMerge) {
        return false;
      }
      if (this.index !== index) {
        return false;
      }
      var trianglesHash = this.calcSegmentNum(object);
      return this.compareTrianglesHash(trianglesHash);
    }
  }, {
    key: "createGeometry",
    value: function createGeometry(objects) {
      var _this2 = this;
      var indices = [];
      var pointsBuffer = [];
      var uvsBuffer = [];
      var offset = 0;
      objects.forEach(function (object, i) {
        // use triangles for Polygon
        var _updateBuffer2 = updateBuffer(object, true, SEGMENT_NUM, _this2.calcSubpathIndex(object)),
          triangles = _updateBuffer2.triangles,
          pBuffer = _updateBuffer2.pointsBuffer;
        if (triangles.length) {
          var _object$getGeometryBo = object.getGeometryBounds(),
            halfExtents = _object$getGeometryBo.halfExtents;
          // pointsBuffer use 3D
          var uvBuffer = [];
          pBuffer.forEach(function (x, i) {
            if (i % 3 !== 2) {
              uvBuffer.push(x / halfExtents[i % 3] / 2);
            }
          });
          offset += pointsBuffer.length / 3;
          pointsBuffer.push.apply(pointsBuffer, _toConsumableArray(pBuffer));
          uvsBuffer.push.apply(uvsBuffer, uvBuffer);
          indices.push.apply(indices, _toConsumableArray(triangles.map(function (n) {
            return n + offset;
          })));
        }
      });
      if (pointsBuffer.length) {
        // use default common attributes
        _superPropGet(InstancedFillDrawcall, "createGeometry", this, 3)([objects]);
        var packedStyle = [];
        objects.forEach(function (object) {
          var _object$parsedStyle = object.parsedStyle,
            isBillboard = _object$parsedStyle.isBillboard,
            billboardRotation = _object$parsedStyle.billboardRotation,
            isSizeAttenuation = _object$parsedStyle.isSizeAttenuation;
          packedStyle.push(isBillboard ? 1 : 0, billboardRotation !== null && billboardRotation !== void 0 ? billboardRotation : 0, isSizeAttenuation ? 1 : 0, 0);
        });
        this.geometry.setVertexBuffer({
          bufferIndex: VertexAttributeBufferIndex.POSITION,
          byteStride: 4 * 3,
          stepMode: VertexStepMode.VERTEX,
          attributes: [{
            format: Format.F32_RGB,
            bufferByteOffset: 4 * 0,
            location: VertexAttributeLocation.POSITION
          }],
          data: new Float32Array(pointsBuffer)
        });
        this.geometry.setVertexBuffer({
          bufferIndex: FillVertexAttributeBufferIndex.PACKED_STYLE,
          byteStride: 4 * 4,
          stepMode: VertexStepMode.INSTANCE,
          attributes: [{
            format: Format.F32_RGBA,
            bufferByteOffset: 4 * 0,
            location: FillVertexAttributeLocation.PACKED_STYLE3,
            divisor: 1
          }],
          data: new Float32Array(packedStyle)
        });
        this.geometry.setVertexBuffer({
          bufferIndex: VertexAttributeBufferIndex.UV,
          byteStride: 4 * 2,
          stepMode: VertexStepMode.VERTEX,
          attributes: [{
            format: Format.F32_RG,
            bufferByteOffset: 4 * 0,
            location: VertexAttributeLocation.UV
          }],
          data: new Float32Array(uvsBuffer)
        });
        this.geometry.vertexCount = indices.length / objects.length;
        this.geometry.setIndexBuffer(new Uint32Array(indices));
      }
    }
  }, {
    key: "createMaterial",
    value: function createMaterial(objects) {
      this.material.vertexShader = meshVert;
      this.material.fragmentShader = meshFrag;
      this.material.defines = _objectSpread(_objectSpread(_objectSpread({}, this.material.defines), enumToObject(FillVertexAttributeLocation)), {}, {
        INSTANCED: true
      });
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(objects, startIndex, name, value) {
      if (objects.length === 0) {
        return;
      }
      _superPropGet(InstancedFillDrawcall, "updateAttribute", this, 3)([objects, startIndex, name, value]);
      this.updateBatchedAttribute(objects, startIndex, name, value);
      if (name === 'isBillboard' || name === 'billboardRotation' || name === 'isSizeAttenuation') {
        var packed = [];
        objects.forEach(function (object) {
          var _object$parsedStyle2 = object.parsedStyle,
            isBillboard = _object$parsedStyle2.isBillboard,
            billboardRotation = _object$parsedStyle2.billboardRotation,
            isSizeAttenuation = _object$parsedStyle2.isSizeAttenuation;
          packed.push(isBillboard ? 1 : 0, billboardRotation !== null && billboardRotation !== void 0 ? billboardRotation : 0, isSizeAttenuation ? 1 : 0, 0);
        });
        this.geometry.updateVertexBuffer(FillVertexAttributeBufferIndex.PACKED_STYLE, FillVertexAttributeLocation.PACKED_STYLE3, startIndex, new Uint8Array(new Float32Array(packed).buffer));
      }
    }
  }]);
}(Instanced);

var segmentInstanceGeometry = [0, -0.5, 0, 0, 0, 1, -0.5, 1, 1, 0, 1, 0.5, 1, 1, 1, 0, 0.5, 0, 0, 1];
var InstancedLineVertexAttributeBufferIndex = function (InstancedLineVertexAttributeBufferIndex) {
  InstancedLineVertexAttributeBufferIndex[InstancedLineVertexAttributeBufferIndex["POINT"] = VertexAttributeBufferIndex.POSITION + 1] = "POINT";
  InstancedLineVertexAttributeBufferIndex[InstancedLineVertexAttributeBufferIndex["CAP"] = 1 + InstancedLineVertexAttributeBufferIndex["POINT"]] = "CAP";
  InstancedLineVertexAttributeBufferIndex[InstancedLineVertexAttributeBufferIndex["DASH"] = 1 + InstancedLineVertexAttributeBufferIndex["CAP"]] = "DASH";
  return InstancedLineVertexAttributeBufferIndex;
}(InstancedLineVertexAttributeBufferIndex || {});
var InstancedLineVertexAttributeLocation = function (InstancedLineVertexAttributeLocation) {
  InstancedLineVertexAttributeLocation[InstancedLineVertexAttributeLocation["POSITION"] = VertexAttributeLocation.POSITION] = "POSITION";
  InstancedLineVertexAttributeLocation[InstancedLineVertexAttributeLocation["UV"] = VertexAttributeLocation.UV] = "UV";
  InstancedLineVertexAttributeLocation[InstancedLineVertexAttributeLocation["POINTA"] = VertexAttributeLocation.NORMAL] = "POINTA";
  InstancedLineVertexAttributeLocation[InstancedLineVertexAttributeLocation["POINTB"] = VertexAttributeLocation.BARYCENTRIC] = "POINTB";
  InstancedLineVertexAttributeLocation[InstancedLineVertexAttributeLocation["CAP"] = VertexAttributeLocation.MAX] = "CAP";
  InstancedLineVertexAttributeLocation[InstancedLineVertexAttributeLocation["DASH"] = VertexAttributeLocation.MAX + 1] = "DASH";
  return InstancedLineVertexAttributeLocation;
}(InstancedLineVertexAttributeLocation || {});
var LineCap_MAP = {
  butt: 1,
  round: 2,
  square: 3
};
var InstancedLineDrawcall = /*#__PURE__*/function (_Instanced) {
  function InstancedLineDrawcall(renderHelper, texturePool, lightPool, object, drawcallCtors, index, context) {
    var _this;
    _classCallCheck(this, InstancedLineDrawcall);
    _this = _callSuper(this, InstancedLineDrawcall, [renderHelper, texturePool, lightPool, object, drawcallCtors, index, context]);
    _this.renderHelper = renderHelper;
    _this.texturePool = texturePool;
    _this.lightPool = lightPool;
    _this.gradientAttributeName = 'stroke';
    return _this;
  }
  _inherits(InstancedLineDrawcall, _Instanced);
  return _createClass(InstancedLineDrawcall, [{
    key: "shouldMerge",
    value: function shouldMerge(object, index) {
      var shouldMerge = _superPropGet(InstancedLineDrawcall, "shouldMerge", this, 3)([object, index]);
      if (!shouldMerge) {
        return false;
      }
      return true;
    }
  }, {
    key: "createMaterial",
    value: function createMaterial(objects) {
      this.material.vertexShader = vert$3;
      this.material.fragmentShader = frag$3;
      this.material.defines = _objectSpread(_objectSpread({}, this.material.defines), enumToObject(InstancedLineVertexAttributeLocation));
    }
  }, {
    key: "calcSubpathIndex",
    value: function calcSubpathIndex(object) {
      if (object.nodeName === Shape.PATH) {
        var fillDrawcallCount = this.drawcallCtors.filter(function (ctor) {
          return ctor === InstancedFillDrawcall;
        }).length;
        return this.index - fillDrawcallCount;
      }
      return 0;
    }
  }, {
    key: "createGeometry",
    value: function createGeometry(objects) {
      var _this2 = this;
      // use default common attributes
      _superPropGet(InstancedLineDrawcall, "createGeometry", this, 3)([objects]);
      var interleaved = [];
      var packedCap = [];
      var packedDash = [];
      var indices = [];
      var offset = 0;
      objects.forEach(function (object) {
        var parsedLineStyleProps;
        var totalLength;
        if (object.nodeName === Shape.LINE) {
          parsedLineStyleProps = object.parsedStyle;
          totalLength = object.getTotalLength();
        } else if (object.nodeName === Shape.POLYLINE) {
          var _parsedStyle = object.parsedStyle,
            points = _parsedStyle.points.points,
            _lineCap = _parsedStyle.lineCap,
            lineDash = _parsedStyle.lineDash,
            lineDashOffset = _parsedStyle.lineDashOffset,
            markerStart = _parsedStyle.markerStart,
            markerEnd = _parsedStyle.markerEnd,
            markerStartOffset = _parsedStyle.markerStartOffset,
            markerEndOffset = _parsedStyle.markerEndOffset,
            _isBillboard = _parsedStyle.isBillboard,
            _isSizeAttenuation = _parsedStyle.isSizeAttenuation;
          parsedLineStyleProps = {
            x1: points[0][0],
            y1: points[0][1],
            x2: points[points.length - 1][0],
            y2: points[points.length - 1][1],
            z1: 0,
            z2: 0,
            lineCap: _lineCap,
            lineDash: lineDash,
            lineDashOffset: lineDashOffset,
            isBillboard: _isBillboard,
            isSizeAttenuation: _isSizeAttenuation,
            markerStart: markerStart,
            markerEnd: markerEnd,
            markerStartOffset: markerStartOffset,
            markerEndOffset: markerEndOffset
          };
          totalLength = object.getTotalLength();
        } else if (object.nodeName === Shape.PATH) {
          var _parsedStyle2 = object.parsedStyle,
            absolutePath = _parsedStyle2.d.absolutePath,
            _lineCap2 = _parsedStyle2.lineCap,
            _lineDash = _parsedStyle2.lineDash,
            _lineDashOffset = _parsedStyle2.lineDashOffset,
            _markerStart = _parsedStyle2.markerStart,
            _markerEnd = _parsedStyle2.markerEnd,
            _markerStartOffset = _parsedStyle2.markerStartOffset,
            _markerEndOffset = _parsedStyle2.markerEndOffset,
            _isBillboard2 = _parsedStyle2.isBillboard,
            _isSizeAttenuation2 = _parsedStyle2.isSizeAttenuation;
          var mSegmentCount = 0;
          var mCommandIndex = 0;
          var index = _this2.calcSubpathIndex(object);
          for (var i = 0; i < absolutePath.length; i++) {
            var segment = absolutePath[i];
            if (segment[0] === 'M') {
              if (mSegmentCount === index) {
                mCommandIndex = i;
                break;
              }
              mSegmentCount++;
            }
          }
          parsedLineStyleProps = {
            x1: absolutePath[mCommandIndex][1],
            y1: absolutePath[mCommandIndex][2],
            x2: absolutePath[mCommandIndex + 1][1],
            y2: absolutePath[mCommandIndex + 1][2],
            z1: 0,
            z2: 0,
            lineCap: _lineCap2,
            lineDash: _lineDash,
            lineDashOffset: _lineDashOffset,
            isBillboard: _isBillboard2,
            isSizeAttenuation: _isSizeAttenuation2,
            markerStart: _markerStart,
            markerEnd: _markerEnd,
            markerStartOffset: _markerStartOffset,
            markerEndOffset: _markerEndOffset
          };
          totalLength = object.getTotalLength();
        }
        var _parsedLineStyleProps = parsedLineStyleProps,
          x1 = _parsedLineStyleProps.x1,
          y1 = _parsedLineStyleProps.y1,
          x2 = _parsedLineStyleProps.x2,
          y2 = _parsedLineStyleProps.y2,
          z1 = _parsedLineStyleProps.z1,
          z2 = _parsedLineStyleProps.z2,
          lineCap = _parsedLineStyleProps.lineCap,
          isBillboard = _parsedLineStyleProps.isBillboard,
          isSizeAttenuation = _parsedLineStyleProps.isSizeAttenuation;
        var _this2$calcOffset = _this2.calcOffset(parsedLineStyleProps),
          startOffsetX = _this2$calcOffset.startOffsetX,
          startOffsetY = _this2$calcOffset.startOffsetY,
          endOffsetX = _this2$calcOffset.endOffsetX,
          endOffsetY = _this2$calcOffset.endOffsetY;
        var _this2$calcDash = _this2.calcDash(parsedLineStyleProps, totalLength),
          dashOffset = _this2$calcDash.dashOffset,
          dashSegmentPercent = _this2$calcDash.dashSegmentPercent,
          dashRatioInEachSegment = _this2$calcDash.dashRatioInEachSegment;
        packedCap.push(
        // caps
        LineCap_MAP[lineCap]);
        packedDash.push(dashOffset, dashSegmentPercent, dashRatioInEachSegment,
        // isSizeAttenuation
        isBillboard || isSizeAttenuation ? 1 : 0);
        interleaved.push(x1 + startOffsetX, y1 + startOffsetY, z1, x2 + endOffsetX, y2 + endOffsetY, z2);
        indices.push(0 + offset, 2 + offset, 1 + offset, 0 + offset, 3 + offset, 2 + offset);
        offset += 4;
      });
      this.geometry.setIndexBuffer(new Uint32Array(indices));
      this.geometry.vertexCount = 6;
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.POSITION,
        byteStride: 4 * 5,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGB,
          bufferByteOffset: 4 * 0,
          location: InstancedLineVertexAttributeLocation.POSITION,
          divisor: 0
        }, {
          format: Format.F32_RG,
          bufferByteOffset: 4 * 3,
          location: InstancedLineVertexAttributeLocation.UV,
          divisor: 0
        }],
        data: new Float32Array(segmentInstanceGeometry)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: InstancedLineVertexAttributeBufferIndex.POINT,
        byteStride: 4 * (3 + 3),
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGB,
          bufferByteOffset: 4 * 0,
          location: InstancedLineVertexAttributeLocation.POINTA,
          divisor: 1
        }, {
          format: Format.F32_RGB,
          bufferByteOffset: 4 * 3,
          location: InstancedLineVertexAttributeLocation.POINTB,
          divisor: 1
        }],
        data: new Float32Array(interleaved)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: InstancedLineVertexAttributeBufferIndex.CAP,
        byteStride: 4 * 1,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_R,
          bufferByteOffset: 4 * 0,
          location: InstancedLineVertexAttributeLocation.CAP,
          divisor: 1
        }],
        data: new Float32Array(packedCap)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: InstancedLineVertexAttributeBufferIndex.DASH,
        byteStride: 4 * 4,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 0,
          location: InstancedLineVertexAttributeLocation.DASH,
          divisor: 1
        }],
        data: new Float32Array(packedDash)
      });
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(objects, startIndex, name, value) {
      var _this3 = this;
      if (objects.length === 0) {
        return;
      }
      _superPropGet(InstancedLineDrawcall, "updateAttribute", this, 3)([objects, startIndex, name, value]);
      this.updateBatchedAttribute(objects, startIndex, name, value);
      if (name === 'x1' || name === 'y1' || name === 'x2' || name === 'y2' || name === 'z1' || name === 'z2' || name === 'markerStartOffset' || name === 'markerEndOffset' || name === 'markerStart' || name === 'markerEnd' || name === 'points' || name === 'd') {
        var packed = [];
        objects.forEach(function (object) {
          var parsedLineStyleProps;
          if (object.nodeName === Shape.LINE) {
            parsedLineStyleProps = object.parsedStyle;
          } else if (object.nodeName === Shape.POLYLINE) {
            var _parsedStyle3 = object.parsedStyle,
              points = _parsedStyle3.points.points,
              lineCap = _parsedStyle3.lineCap,
              markerStart = _parsedStyle3.markerStart,
              markerEnd = _parsedStyle3.markerEnd,
              markerStartOffset = _parsedStyle3.markerStartOffset,
              markerEndOffset = _parsedStyle3.markerEndOffset,
              isBillboard = _parsedStyle3.isBillboard,
              isSizeAttenuation = _parsedStyle3.isSizeAttenuation;
            parsedLineStyleProps = {
              x1: points[0][0],
              y1: points[0][1],
              x2: points[points.length - 1][0],
              y2: points[points.length - 1][1],
              z1: 0,
              z2: 0,
              lineCap: lineCap,
              isSizeAttenuation: isSizeAttenuation,
              isBillboard: isBillboard,
              markerStart: markerStart,
              markerEnd: markerEnd,
              markerStartOffset: markerStartOffset,
              markerEndOffset: markerEndOffset
            };
          } else if (object.nodeName === Shape.PATH) {
            var _parsedStyle4 = object.parsedStyle,
              absolutePath = _parsedStyle4.d.absolutePath,
              _lineCap3 = _parsedStyle4.lineCap,
              _markerStart2 = _parsedStyle4.markerStart,
              _markerEnd2 = _parsedStyle4.markerEnd,
              _markerStartOffset2 = _parsedStyle4.markerStartOffset,
              _markerEndOffset2 = _parsedStyle4.markerEndOffset,
              _isBillboard3 = _parsedStyle4.isBillboard,
              _isSizeAttenuation3 = _parsedStyle4.isSizeAttenuation;
            parsedLineStyleProps = {
              x1: absolutePath[0][1],
              y1: absolutePath[0][2],
              x2: absolutePath[1][1],
              y2: absolutePath[1][2],
              z1: 0,
              z2: 0,
              lineCap: _lineCap3,
              isBillboard: _isBillboard3,
              isSizeAttenuation: _isSizeAttenuation3,
              markerStart: _markerStart2,
              markerEnd: _markerEnd2,
              markerStartOffset: _markerStartOffset2,
              markerEndOffset: _markerEndOffset2
            };
          }
          var _parsedLineStyleProps2 = parsedLineStyleProps,
            x1 = _parsedLineStyleProps2.x1,
            y1 = _parsedLineStyleProps2.y1,
            x2 = _parsedLineStyleProps2.x2,
            y2 = _parsedLineStyleProps2.y2,
            z1 = _parsedLineStyleProps2.z1,
            z2 = _parsedLineStyleProps2.z2;
          var _this3$calcOffset = _this3.calcOffset(parsedLineStyleProps),
            startOffsetX = _this3$calcOffset.startOffsetX,
            startOffsetY = _this3$calcOffset.startOffsetY,
            endOffsetX = _this3$calcOffset.endOffsetX,
            endOffsetY = _this3$calcOffset.endOffsetY;
          packed.push(x1 + startOffsetX, y1 + startOffsetY, z1, x2 + endOffsetX, y2 + endOffsetY, z2);
        });
        this.geometry.updateVertexBuffer(InstancedLineVertexAttributeBufferIndex.POINT, InstancedLineVertexAttributeLocation.POINTA, startIndex, new Uint8Array(new Float32Array(packed).buffer));
      } else if (name === 'lineDashOffset' || name === 'lineDash' || name === 'isSizeAttenuation' || name === 'isBillboard') {
        var _packed = [];
        objects.forEach(function (object) {
          var totalLength = object.getTotalLength();
          var _this3$calcDash = _this3.calcDash(object.parsedStyle, totalLength),
            dashOffset = _this3$calcDash.dashOffset,
            dashSegmentPercent = _this3$calcDash.dashSegmentPercent,
            dashRatioInEachSegment = _this3$calcDash.dashRatioInEachSegment;
          _packed.push(dashOffset, dashSegmentPercent, dashRatioInEachSegment, object.parsedStyle.isBillboard || object.parsedStyle.isSizeAttenuation ? 1 : 0 // isSizeAttenuation
          );
        });
        this.geometry.updateVertexBuffer(InstancedLineVertexAttributeBufferIndex.DASH, InstancedLineVertexAttributeLocation.DASH, startIndex, new Uint8Array(new Float32Array(_packed).buffer));
      } else if (name === 'lineCap') {
        var _packed2 = [];
        objects.forEach(function (object) {
          var lineCap = object.parsedStyle.lineCap;
          _packed2.push(LineCap_MAP[lineCap]);
        });
        this.geometry.updateVertexBuffer(InstancedLineVertexAttributeBufferIndex.CAP, InstancedLineVertexAttributeLocation.CAP, startIndex, new Uint8Array(new Float32Array(_packed2).buffer));
      }
    }
  }, {
    key: "calcOffset",
    value: function calcOffset(parsedStyle) {
      var x1 = parsedStyle.x1,
        y1 = parsedStyle.y1,
        x2 = parsedStyle.x2,
        y2 = parsedStyle.y2,
        markerStart = parsedStyle.markerStart,
        markerEnd = parsedStyle.markerEnd,
        markerStartOffset = parsedStyle.markerStartOffset,
        markerEndOffset = parsedStyle.markerEndOffset;
      var startOffsetX = 0;
      var startOffsetY = 0;
      var endOffsetX = 0;
      var endOffsetY = 0;
      var rad = 0;
      var x;
      var y;
      if (markerStart && isDisplayObject(markerStart) && markerStartOffset) {
        x = x2 - x1;
        y = y2 - y1;
        rad = Math.atan2(y, x);
        startOffsetX = Math.cos(rad) * (markerStartOffset || 0);
        startOffsetY = Math.sin(rad) * (markerStartOffset || 0);
      }
      if (markerEnd && isDisplayObject(markerEnd) && markerEndOffset) {
        x = x1 - x2;
        y = y1 - y2;
        rad = Math.atan2(y, x);
        endOffsetX = Math.cos(rad) * (markerEndOffset || 0);
        endOffsetY = Math.sin(rad) * (markerEndOffset || 0);
      }
      return {
        startOffsetX: startOffsetX,
        startOffsetY: startOffsetY,
        endOffsetX: endOffsetX,
        endOffsetY: endOffsetY
      };
    }
  }, {
    key: "calcDash",
    value: function calcDash(parsedLineStyle, totalLength) {
      var lineDash = parsedLineStyle.lineDash,
        lineDashOffset = parsedLineStyle.lineDashOffset;
      var dashOffset = 0;
      var dashSegmentPercent = 1;
      var dashRatioInEachSegment = 0;
      if (lineDash && lineDash.length) {
        dashOffset = (lineDashOffset || 0) / totalLength;
        var segmentsLength = lineDash.reduce(function (cur, prev) {
          return cur + prev;
        }, 0);
        if (segmentsLength === 0) {
          dashSegmentPercent = 1;
          dashRatioInEachSegment = 0;
        } else {
          dashSegmentPercent = segmentsLength / totalLength;
          dashRatioInEachSegment = lineDash[1] / segmentsLength;
        }
      }
      return {
        dashOffset: dashOffset,
        dashSegmentPercent: dashSegmentPercent,
        dashRatioInEachSegment: dashRatioInEachSegment
      };
    }
  }], [{
    key: "isLine",
    value: function isLine(object, subpathIndex) {
      if (object.nodeName === Shape.PATH) {
        var _ref = object.parsedStyle,
          absolutePath = _ref.d.absolutePath;
        var mSegmentCount = 0;
        var mCommandIndex = 0;
        for (var i = 0; i < absolutePath.length; i++) {
          var segment = absolutePath[i];
          if (segment[0] === 'M') {
            if (mSegmentCount === subpathIndex) {
              mCommandIndex = i;
              break;
            }
            mSegmentCount++;
          }
        }

        // only contains M & L commands
        if (absolutePath[mCommandIndex][0] === 'M' && absolutePath[mCommandIndex + 1][0] === 'L' && (absolutePath[mCommandIndex + 2] === undefined || absolutePath[mCommandIndex + 2][0] === 'M')) {
          return true;
        }
      } else if (object.nodeName === Shape.POLYLINE) {
        var _ref2 = object.parsedStyle,
          points = _ref2.points.points;
        var tangent = (points[1][0] - points[1][1]) / (points[0][0] - points[0][1]);
        for (var _i = 1; _i < points.length - 1; _i++) {
          if ((points[_i + 1][0] - points[_i + 1][1]) / (points[_i][0] - points[_i][1]) !== tangent) {
            return false;
          }
        }
        return true;
      }
      return false;
    }
  }]);
}(Instanced);

var frag$1 = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\n\nin vec4 v_PickingResult;\nin vec4 v_Color;\nin vec4 v_StrokeColor;\nin vec4 v_StylePacked1;\nin vec4 v_StylePacked2;\n#ifdef USE_UV\n  in vec2 v_Uv;\n#endif\n#ifdef USE_MAP\n  uniform sampler2D u_Map;\n#endif\n\nout vec4 outputColor;\n\nfloat epsilon = 0.000001;\n\nvoid main() {\n  vec4 u_Color = v_Color;\nvec4 u_StrokeColor = v_StrokeColor;\nfloat u_Opacity = v_StylePacked1.x;\nfloat u_FillOpacity = v_StylePacked1.y;\nfloat u_StrokeOpacity = v_StylePacked1.z;\nfloat u_StrokeWidth = v_StylePacked1.w;\nfloat u_Visible = v_StylePacked2.x;\nvec3 u_PickingColor = v_PickingResult.xyz;\n\nif (u_Visible < 0.5) {\n    discard;\n}\n  #ifdef USE_MAP\n  #ifdef USE_PATTERN\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #else\n    vec4 texelColor = texture(SAMPLER_2D(u_Map), v_Uv);\n    u_Color = texelColor;\n  #endif\n#endif\n\n  if (u_IsPicking > 0.5) {\n    if (u_PickingColor.x == 0.0 && u_PickingColor.y == 0.0 && u_PickingColor.z == 0.0) {\n      discard;\n    }\n\n    // TODO: pointer-events: non-transparent-pixel\n    // if (u_Color.x == 0.0 && u_Color.y == 0.0 && u_Color.z == 0.0) {\n    //   discard;\n    // }\n    outputColor = vec4(u_PickingColor, 1.0);\n  } else {\n    outputColor = u_Color;\n    outputColor.a = outputColor.a * u_Opacity;\n\n    if (outputColor.a < epsilon) {\n      discard;\n    }\n  }\n}"; // eslint-disable-line

var vert$1 = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\nlayout(location = MODEL_MATRIX0) in vec4 a_ModelMatrix0;\nlayout(location = MODEL_MATRIX1) in vec4 a_ModelMatrix1;\nlayout(location = MODEL_MATRIX2) in vec4 a_ModelMatrix2;\nlayout(location = MODEL_MATRIX3) in vec4 a_ModelMatrix3;\nlayout(location = PACKED_COLOR) in vec4 a_PackedColor;\nlayout(location = PACKED_STYLE1) in vec4 a_StylePacked1;\nlayout(location = PACKED_STYLE2) in vec4 a_StylePacked2;\nlayout(location = PICKING_COLOR) in vec4 a_PickingColor;\n\nout vec4 v_PickingResult;\nout vec4 v_Color;\nout vec4 v_StrokeColor;\nout vec4 v_StylePacked1;\nout vec4 v_StylePacked2;\n\n#define COLOR_SCALE 1. / 255.\nvoid setPickingColor(vec3 pickingColor) {\n  v_PickingResult.rgb = pickingColor * COLOR_SCALE;\n}\n\nvec2 unpack_float(const float packedValue) {\n  int packedIntValue = int(packedValue);\n  int v0 = packedIntValue / 256;\n  return vec2(v0, packedIntValue - v0 * 256);\n}\nvec4 decode_color(const vec2 encodedColor) {\n  return vec4(\n    unpack_float(encodedColor[0]) / 255.0,\n    unpack_float(encodedColor[1]) / 255.0\n  );\n}\nvec4 project(vec4 pos, mat4 pm, mat4 vm, mat4 mm) {\n  return pm * vm * mm * pos;\n}\n\nbool isPerspectiveMatrix(mat4 m) {\n  return m[2][3] == -1.0;\n}\n\nvec4 billboard(vec2 offset, float rotation, bool isSizeAttenuation, mat4 pm, mat4 vm, mat4 mm, vec3 position) {\n  vec4 mvPosition = vm * mm * vec4(position, 1.0);\n  vec2 scale;\n  scale.x = length(vec3(mm[0][0], mm[0][1], mm[0][2]));\n  scale.y = length(vec3(mm[1][0], mm[1][1], mm[1][2]));\n\n  if (isSizeAttenuation) {\n    bool isPerspective = isPerspectiveMatrix(pm);\n    if (isPerspective) {\n      scale *= -mvPosition.z / 250.0;\n    }\n  }\n\n  vec2 alignedPosition = offset * scale;\n  vec2 rotatedPosition;\n  rotatedPosition.x = cos(rotation) * alignedPosition.x - sin(rotation) * alignedPosition.y;\n  rotatedPosition.y = sin(rotation) * alignedPosition.x + cos(rotation) * alignedPosition.y;\n\n  mvPosition.xy += rotatedPosition;\n  return pm * mvPosition;\n}\n\nlayout(location = POSITION) in vec3 a_Position;\nlayout(location = SIZE) in vec2 a_Size;\nlayout(location = PACKED_STYLE3) in vec4 a_StylePacked3;\n\n#ifdef USE_UV\n  layout(location = UV) in vec2 a_Uv;\n  out vec2 v_Uv;\n#endif\n\nvoid main() {\n  vec4 a_Color = decode_color(a_PackedColor.xy);\nvec4 a_StrokeColor = decode_color(a_PackedColor.zw);\n\nmat4 u_ModelMatrix = mat4(a_ModelMatrix0, a_ModelMatrix1, a_ModelMatrix2, a_ModelMatrix3);\nvec4 u_StrokeColor = a_StrokeColor;\nfloat u_Opacity = a_StylePacked1.x;\nfloat u_FillOpacity = a_StylePacked1.y;\nfloat u_StrokeOpacity = a_StylePacked1.z;\nfloat u_StrokeWidth = a_StylePacked1.w;\nfloat u_ZIndex = a_PickingColor.w;\nvec2 u_Anchor = a_StylePacked2.yz;\nfloat u_IncreasedLineWidthForHitTesting = a_StylePacked2.w;\n\nsetPickingColor(a_PickingColor.xyz);\n\nv_Color = a_Color;\nv_StrokeColor = a_StrokeColor;\nv_StylePacked1 = a_StylePacked1;\nv_StylePacked2 = a_StylePacked2;\n\n// #ifdef CLIPSPACE_NEAR_ZERO\n//     gl_Position.z = (gl_Position.z + gl_Position.w) * 0.5;\n// #endif\n\n  vec2 offset = (a_Uv - u_Anchor.xy) * a_Size;\n\n  bool isBillboard = a_StylePacked3.x > 0.5;\n  if (isBillboard) {\n    float rotation = a_StylePacked3.y;\n    bool isSizeAttenuation = a_StylePacked3.z > 0.5;\n    gl_Position = billboard(offset, rotation, isSizeAttenuation, u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix, a_Position);\n  } else {\n    gl_Position = project(vec4(a_Position.xy + offset, u_ZIndex, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n  }\n\n  #ifdef USE_UV\n  v_Uv = a_Uv;\n#endif\n\n}"; // eslint-disable-line

var ImageVertexAttributeBufferIndex = function (ImageVertexAttributeBufferIndex) {
  ImageVertexAttributeBufferIndex[ImageVertexAttributeBufferIndex["PACKED_STYLE"] = VertexAttributeBufferIndex.POSITION + 1] = "PACKED_STYLE";
  ImageVertexAttributeBufferIndex[ImageVertexAttributeBufferIndex["SIZE"] = VertexAttributeBufferIndex.MAX] = "SIZE";
  return ImageVertexAttributeBufferIndex;
}(ImageVertexAttributeBufferIndex || {});
var ImageVertexAttributeLocation = function (ImageVertexAttributeLocation) {
  ImageVertexAttributeLocation[ImageVertexAttributeLocation["PACKED_STYLE3"] = VertexAttributeLocation.MAX] = "PACKED_STYLE3";
  ImageVertexAttributeLocation[ImageVertexAttributeLocation["SIZE"] = 1 + ImageVertexAttributeLocation["PACKED_STYLE3"]] = "SIZE";
  return ImageVertexAttributeLocation;
}(ImageVertexAttributeLocation || {});
var ImageDrawcall = /*#__PURE__*/function (_Instanced) {
  function ImageDrawcall() {
    _classCallCheck(this, ImageDrawcall);
    return _callSuper(this, ImageDrawcall, arguments);
  }
  _inherits(ImageDrawcall, _Instanced);
  return _createClass(ImageDrawcall, [{
    key: "shouldMerge",
    value: function shouldMerge(object, index) {
      var shouldMerge = _superPropGet(ImageDrawcall, "shouldMerge", this, 3)([object, index]);
      if (!shouldMerge) {
        return false;
      }
      if (this.instance.parsedStyle.src !== object.parsedStyle.src) {
        return false;
      }
      return true;
    }

    // private calculateWithAspectRatio(
    //   object: Image,
    //   imageWidth: number,
    //   imageHeight: number,
    // ) {
    //   const { width, height } = object.parsedStyle;
    //   // if (width && !height) {
    //   //   object.setAttribute('height', (imageHeight / imageWidth) * width);
    //   // } else if (!width && height) {
    //   //   object.setAttribute('width', (imageWidth / imageHeight) * height);
    //   // }
    // }
  }, {
    key: "createMaterial",
    value: function createMaterial(objects) {
      var instance = objects[0];
      var src = instance.parsedStyle.src;
      this.material.defines = _objectSpread(_objectSpread({}, this.material.defines), enumToObject(ImageVertexAttributeLocation));
      this.material.vertexShader = vert$1;
      this.material.fragmentShader = frag$1;
      var map = this.texturePool.getOrCreateTexture(this.context.device, src, undefined
      // (texture, image) => {
      //   const { width, height } = image;
      //   objects.forEach((object) => {
      //     const image = object as ImageShape;
      //     const { keepAspectRatio } = image.parsedStyle;
      //     if (keepAspectRatio) {
      //       this.calculateWithAspectRatio(object, width, height);
      //       // set dirty rectangle flag
      //       object.renderable.dirty = true;
      //       this.context.renderingService.dirty();
      //     }
      //   });
      // },
      );
      this.material.setUniforms({
        u_Map: map
      });
    }
  }, {
    key: "createGeometry",
    value: function createGeometry(objects) {
      // use default common attributes
      _superPropGet(ImageDrawcall, "createGeometry", this, 3)([objects]);
      var positions = [];
      var sizes = [];
      var packedStyle = [];
      objects.forEach(function (object, i) {
        var image = object;
        var _image$parsedStyle = image.parsedStyle,
          _image$parsedStyle$x = _image$parsedStyle.x,
          x = _image$parsedStyle$x === void 0 ? 0 : _image$parsedStyle$x,
          _image$parsedStyle$y = _image$parsedStyle.y,
          y = _image$parsedStyle$y === void 0 ? 0 : _image$parsedStyle$y,
          _image$parsedStyle$z = _image$parsedStyle.z,
          z = _image$parsedStyle$z === void 0 ? 0 : _image$parsedStyle$z,
          width = _image$parsedStyle.width,
          height = _image$parsedStyle.height,
          isBillboard = _image$parsedStyle.isBillboard,
          billboardRotation = _image$parsedStyle.billboardRotation,
          isSizeAttenuation = _image$parsedStyle.isSizeAttenuation;
        positions.push(x, y, z);
        sizes.push(width, height);
        packedStyle.push(isBillboard ? 1 : 0, billboardRotation !== null && billboardRotation !== void 0 ? billboardRotation : 0, isSizeAttenuation ? 1 : 0, 0);
      });
      this.geometry.setIndexBuffer(new Uint32Array([0, 2, 1, 0, 3, 2]));
      this.geometry.vertexCount = 6;
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.POSITION,
        byteStride: 4 * 3,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGB,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.POSITION
        }],
        data: new Float32Array(positions)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: ImageVertexAttributeBufferIndex.SIZE,
        byteStride: 4 * 2,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RG,
          bufferByteOffset: 4 * 0,
          location: ImageVertexAttributeLocation.SIZE
        }],
        data: new Float32Array(sizes)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: ImageVertexAttributeBufferIndex.PACKED_STYLE,
        byteStride: 4 * 4,
        stepMode: VertexStepMode.INSTANCE,
        attributes: [{
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 0,
          location: ImageVertexAttributeLocation.PACKED_STYLE3,
          divisor: 1
        }],
        data: new Float32Array(packedStyle)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.UV,
        byteStride: 4 * 2,
        stepMode: VertexStepMode.VERTEX,
        attributes: [{
          format: Format.F32_RG,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.UV
        }],
        data: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1])
      });
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(objects, startIndex, name, value) {
      if (objects.length === 0) {
        return;
      }
      _superPropGet(ImageDrawcall, "updateAttribute", this, 3)([objects, startIndex, name, value]);
      this.updateBatchedAttribute(objects, startIndex, name, value);
      if (name === 'x' || name === 'y' || name === 'z') {
        var packed = [];
        objects.forEach(function (object) {
          var image = object;
          var _image$parsedStyle2 = image.parsedStyle,
            _image$parsedStyle2$x = _image$parsedStyle2.x,
            x = _image$parsedStyle2$x === void 0 ? 0 : _image$parsedStyle2$x,
            _image$parsedStyle2$y = _image$parsedStyle2.y,
            y = _image$parsedStyle2$y === void 0 ? 0 : _image$parsedStyle2$y,
            _image$parsedStyle2$z = _image$parsedStyle2.z,
            z = _image$parsedStyle2$z === void 0 ? 0 : _image$parsedStyle2$z;
          packed.push(x, y, z);
        });
        this.geometry.updateVertexBuffer(VertexAttributeBufferIndex.POSITION, VertexAttributeLocation.POSITION, startIndex, new Uint8Array(new Float32Array(packed).buffer));
      } else if (name === 'width' || name === 'height') {
        var _packed = [];
        objects.forEach(function (object) {
          var image = object;
          var _image$parsedStyle3 = image.parsedStyle,
            width = _image$parsedStyle3.width,
            height = _image$parsedStyle3.height;
          _packed.push(width, height);
        });
        this.geometry.updateVertexBuffer(ImageVertexAttributeBufferIndex.SIZE, ImageVertexAttributeLocation.SIZE, startIndex, new Uint8Array(new Float32Array(_packed).buffer));
      } else if (name === 'isBillboard' || name === 'billboardRotation' || name === 'isSizeAttenuation') {
        var _packed2 = [];
        objects.forEach(function (object) {
          var image = object;
          var _image$parsedStyle4 = image.parsedStyle,
            isBillboard = _image$parsedStyle4.isBillboard,
            billboardRotation = _image$parsedStyle4.billboardRotation,
            isSizeAttenuation = _image$parsedStyle4.isSizeAttenuation;
          _packed2.push(isBillboard ? 1 : 0, billboardRotation !== null && billboardRotation !== void 0 ? billboardRotation : 0, isSizeAttenuation ? 1 : 0, 0);
        });
        this.geometry.updateVertexBuffer(ImageVertexAttributeBufferIndex.PACKED_STYLE, ImageVertexAttributeLocation.PACKED_STYLE3, startIndex, new Uint8Array(new Float32Array(_packed2).buffer));
      } else if (name === 'src') {
        var map = this.texturePool.getOrCreateTexture(this.context.device, value);
        this.material.setUniforms({
          u_Map: map
        });
      }
    }
  }]);
}(Instanced);

var frag = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\nin vec4 v_PickingResult;\nin vec4 v_Color;\nin vec4 v_StrokeColor;\nin vec4 v_StylePacked1;\nin vec4 v_StylePacked2;\nlayout(std140) uniform ub_ObjectParams {\n  vec2 u_SDFMapSize;\n  float u_FontSize;\n  float u_GammaScale;\n  float u_StrokeBlur;\n  float u_HasStroke;\n};\n#ifdef USE_UV\n  in vec2 v_Uv;\n#endif\n\nuniform sampler2D u_SDFMap;\n\n#define SDF_PX 8.0\n\nout vec4 outputColor;\nfloat epsilon = 0.000001;\n\nvoid main() {\n  vec4 u_Color = v_Color;\nvec4 u_StrokeColor = v_StrokeColor;\nfloat u_Opacity = v_StylePacked1.x;\nfloat u_FillOpacity = v_StylePacked1.y;\nfloat u_StrokeOpacity = v_StylePacked1.z;\nfloat u_StrokeWidth = v_StylePacked1.w;\nfloat u_Visible = v_StylePacked2.x;\nvec3 u_PickingColor = v_PickingResult.xyz;\n\nif (u_Visible < 0.5) {\n    discard;\n}\n\n  float dist = texture(SAMPLER_2D(u_SDFMap), v_Uv).a;\n\n  float fontScale = u_FontSize / 24.0;\n  lowp vec4 color = u_Color;\n  lowp float buff = (256.0 - 64.0) / 256.0;\n  float opacity = u_FillOpacity;\n  if (u_HasStroke > 0.5 && u_StrokeWidth > 0.0) {\n    color = u_StrokeColor;\n    buff = (6.0 - u_StrokeWidth / fontScale / 2.0) / SDF_PX;\n    opacity = u_StrokeOpacity;\n  }\n\n  highp float gamma_scaled = fwidth(dist);\n  highp float alpha = smoothstep(buff - gamma_scaled, buff + gamma_scaled, dist);\n\n  opacity *= alpha * u_Opacity;\n\n  if (u_IsPicking > 0.5) {\n    if (u_PickingColor.x == 0.0 && u_PickingColor.y == 0.0 && u_PickingColor.z == 0.0) {\n      discard;\n    }\n    outputColor = vec4(u_PickingColor, 1.0);\n  } else {\n\n    if (opacity < epsilon) {\n      discard;\n    }\n\n    outputColor = color;\n    outputColor.a *= opacity;\n  }\n}"; // eslint-disable-line

var vert = "#define GLSLIFY 1\nlayout(std140) uniform ub_SceneParams {\n  mat4 u_ProjectionMatrix;\n  mat4 u_ViewMatrix;\n  vec3 u_CameraPosition;\n  float u_DevicePixelRatio;\n  vec2 u_Viewport;\n  float u_IsOrtho;\n  float u_IsPicking;\n};\nlayout(location = MODEL_MATRIX0) in vec4 a_ModelMatrix0;\nlayout(location = MODEL_MATRIX1) in vec4 a_ModelMatrix1;\nlayout(location = MODEL_MATRIX2) in vec4 a_ModelMatrix2;\nlayout(location = MODEL_MATRIX3) in vec4 a_ModelMatrix3;\nlayout(location = PACKED_COLOR) in vec4 a_PackedColor;\nlayout(location = PACKED_STYLE1) in vec4 a_StylePacked1;\nlayout(location = PACKED_STYLE2) in vec4 a_StylePacked2;\nlayout(location = PICKING_COLOR) in vec4 a_PickingColor;\n\nout vec4 v_PickingResult;\nout vec4 v_Color;\nout vec4 v_StrokeColor;\nout vec4 v_StylePacked1;\nout vec4 v_StylePacked2;\n\n#define COLOR_SCALE 1. / 255.\nvoid setPickingColor(vec3 pickingColor) {\n  v_PickingResult.rgb = pickingColor * COLOR_SCALE;\n}\n\nvec2 unpack_float(const float packedValue) {\n  int packedIntValue = int(packedValue);\n  int v0 = packedIntValue / 256;\n  return vec2(v0, packedIntValue - v0 * 256);\n}\nvec4 decode_color(const vec2 encodedColor) {\n  return vec4(\n    unpack_float(encodedColor[0]) / 255.0,\n    unpack_float(encodedColor[1]) / 255.0\n  );\n}\n\nlayout(std140) uniform ub_ObjectParams {\n  vec2 u_SDFMapSize;\n  float u_FontSize;\n  float u_GammaScale;\n  float u_StrokeBlur;\n  float u_HasStroke;\n};\nvec4 project(vec4 pos, mat4 pm, mat4 vm, mat4 mm) {\n  return pm * vm * mm * pos;\n}\n\nbool isPerspectiveMatrix(mat4 m) {\n  return m[2][3] == -1.0;\n}\n\nvec4 billboard(vec2 offset, float rotation, bool isSizeAttenuation, mat4 pm, mat4 vm, mat4 mm, vec3 position) {\n  vec4 mvPosition = vm * mm * vec4(position, 1.0);\n  vec2 scale;\n  scale.x = length(vec3(mm[0][0], mm[0][1], mm[0][2]));\n  scale.y = length(vec3(mm[1][0], mm[1][1], mm[1][2]));\n\n  if (isSizeAttenuation) {\n    bool isPerspective = isPerspectiveMatrix(pm);\n    if (isPerspective) {\n      scale *= -mvPosition.z / 250.0;\n    }\n  }\n\n  vec2 alignedPosition = offset * scale;\n  vec2 rotatedPosition;\n  rotatedPosition.x = cos(rotation) * alignedPosition.x - sin(rotation) * alignedPosition.y;\n  rotatedPosition.y = sin(rotation) * alignedPosition.x + cos(rotation) * alignedPosition.y;\n\n  mvPosition.xy += rotatedPosition;\n  return pm * mvPosition;\n}\n\nlayout(location = POSITION) in vec3 a_Position;\nlayout(location = TEX) in vec2 a_Tex;\nlayout(location = OFFSET) in vec2 a_Offset;\n\nout vec2 v_Uv;\n\nvoid main() {\n  vec4 a_Color = decode_color(a_PackedColor.xy);\nvec4 a_StrokeColor = decode_color(a_PackedColor.zw);\n\nmat4 u_ModelMatrix = mat4(a_ModelMatrix0, a_ModelMatrix1, a_ModelMatrix2, a_ModelMatrix3);\nvec4 u_StrokeColor = a_StrokeColor;\nfloat u_Opacity = a_StylePacked1.x;\nfloat u_FillOpacity = a_StylePacked1.y;\nfloat u_StrokeOpacity = a_StylePacked1.z;\nfloat u_StrokeWidth = a_StylePacked1.w;\nfloat u_ZIndex = a_PickingColor.w;\nvec2 u_Anchor = a_StylePacked2.yz;\nfloat u_IncreasedLineWidthForHitTesting = a_StylePacked2.w;\n\nsetPickingColor(a_PickingColor.xyz);\n\nv_Color = a_Color;\nv_StrokeColor = a_StrokeColor;\nv_StylePacked1 = a_StylePacked1;\nv_StylePacked2 = a_StylePacked2;\n\n// #ifdef CLIPSPACE_NEAR_ZERO\n//     gl_Position.z = (gl_Position.z + gl_Position.w) * 0.5;\n// #endif\n\n  v_Uv = a_Tex / u_SDFMapSize;\n  float fontScale = u_FontSize / 24.;\n\n  vec2 bufferOffset = vec2(0.7, 2.0);\n  vec2 offset = a_Offset * fontScale + + bufferOffset;\n\n  bool isBillboard = a_StylePacked2.y > 0.5;\n  if (isBillboard) {\n    float rotation =  a_StylePacked2.w;\n    bool isSizeAttenuation = a_StylePacked2.z > 0.5;\n    gl_Position = billboard(offset, rotation, isSizeAttenuation, u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix, a_Position );\n  } else {\n    gl_Position = project(vec4(a_Position.xy + offset, u_ZIndex, 1.0), u_ProjectionMatrix, u_ViewMatrix, u_ModelMatrix);\n  }\n}"; // eslint-disable-line

// borrow from mapbox/src/symbol

function createImage(image, _ref, channels, data) {
  var width = _ref.width,
    height = _ref.height;
  if (!data) {
    data = new Uint8Array(width * height * channels);
  } else if (data.length !== width * height * channels) {
    throw new RangeError('mismatched image size');
  }
  image.width = width;
  image.height = height;
  image.data = data;
  return image;
}
function resizeImage(image, _ref2, channels) {
  var width = _ref2.width,
    height = _ref2.height;
  if (width === image.width && height === image.height) {
    return;
  }
  var newImage = createImage({}, {
    width: width,
    height: height
  }, channels);
  copyImage(image, newImage, {
    x: 0,
    y: 0
  }, {
    x: 0,
    y: 0
  }, {
    width: Math.min(image.width, width),
    height: Math.min(image.height, height)
  }, channels);
  image.width = width;
  image.height = height;
  image.data = newImage.data;
}
function copyImage(srcImg, dstImg, srcPt, dstPt, size, channels) {
  if (size.width === 0 || size.height === 0) {
    return dstImg;
  }
  if (size.width > srcImg.width || size.height > srcImg.height || srcPt.x > srcImg.width - size.width || srcPt.y > srcImg.height - size.height) {
    throw new RangeError('out of range source coordinates for image copy');
  }
  if (size.width > dstImg.width || size.height > dstImg.height || dstPt.x > dstImg.width - size.width || dstPt.y > dstImg.height - size.height) {
    throw new RangeError('out of range destination coordinates for image copy');
  }
  var srcData = srcImg.data;
  var dstData = dstImg.data;
  for (var y = 0; y < size.height; y++) {
    var srcOffset = ((srcPt.y + y) * srcImg.width + srcPt.x) * channels;
    var dstOffset = ((dstPt.y + y) * dstImg.width + dstPt.x) * channels;
    for (var i = 0; i < size.width * channels; i++) {
      dstData[dstOffset + i] = srcData[srcOffset + i];
    }
  }
  return dstImg;
}
var AlphaImage = /*#__PURE__*/function () {
  function AlphaImage(size, data) {
    _classCallCheck(this, AlphaImage);
    createImage(this, size, 1, data);
  }
  return _createClass(AlphaImage, [{
    key: "resize",
    value: function resize(size) {
      resizeImage(this, size, 1);
    }
  }, {
    key: "clone",
    value: function clone() {
      return new AlphaImage({
        width: this.width,
        height: this.height
      }, new Uint8Array(this.data));
    }
  }], [{
    key: "copy",
    value: function copy(srcImg, dstImg, srcPt, dstPt, size) {
      copyImage(srcImg, dstImg, srcPt, dstPt, size, 1);
    }
  }]);
}();

// borrow from https://github.com/mapbox/potpack/blob/master/index.mjs
// @see https://github.com/antvis/g/issues/836
function potpack(boxes) {
  // calculate total box area and maximum box width
  var area = 0;
  var maxWidth = 0;
  var _iterator = _createForOfIteratorHelper(boxes),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var box = _step.value;
      area += box.w * box.h;
      maxWidth = Math.max(maxWidth, box.w);
    }

    // sort the boxes for insertion by height, descending
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  boxes.sort(function (a, b) {
    return b.h - a.h;
  });

  // aim for a squarish resulting container,
  // slightly adjusted for sub-100% space utilization
  var startWidth = Math.max(Math.ceil(Math.sqrt(area / 0.95)), maxWidth);

  // start with a single empty space, unbounded at the bottom
  var spaces = [{
    x: 0,
    y: 0,
    w: startWidth,
    h: Infinity
  }];
  var width = 0;
  var height = 0;
  var _iterator2 = _createForOfIteratorHelper(boxes),
    _step2;
  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var _box = _step2.value;
      // look through spaces backwards so that we check smaller spaces first
      for (var i = spaces.length - 1; i >= 0; i--) {
        var space = spaces[i];

        // look for empty spaces that can accommodate the current box
        if (_box.w > space.w || _box.h > space.h) continue;

        // found the space; add the box to its top-left corner
        // |-------|-------|
        // |  box  |       |
        // |_______|       |
        // |         space |
        // |_______________|
        _box.x = space.x;
        _box.y = space.y;
        height = Math.max(height, _box.y + _box.h);
        width = Math.max(width, _box.x + _box.w);
        if (_box.w === space.w && _box.h === space.h) {
          // space matches the box exactly; remove it
          var last = spaces.pop();
          if (i < spaces.length) spaces[i] = last;
        } else if (_box.h === space.h) {
          // space matches the box height; update it accordingly
          // |-------|---------------|
          // |  box  | updated space |
          // |_______|_______________|
          space.x += _box.w;
          space.w -= _box.w;
        } else if (_box.w === space.w) {
          // space matches the box width; update it accordingly
          // |---------------|
          // |      box      |
          // |_______________|
          // | updated space |
          // |_______________|
          space.y += _box.h;
          space.h -= _box.h;
        } else {
          // otherwise the box splits the space into two spaces
          // |-------|-----------|
          // |  box  | new space |
          // |_______|___________|
          // | updated space     |
          // |___________________|
          spaces.push({
            x: space.x + _box.w,
            y: space.y,
            w: space.w - _box.w,
            h: _box.h
          });
          space.y += _box.h;
          space.h -= _box.h;
        }
        break;
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }
  return {
    w: width,
    // container width
    h: height,
    // container height
    fill: area / (width * height) || 0 // space utilization
  };
}
var padding = 1;
/**
 * Merge SDFs into a large squared atlas with `potpack`,
 * because on WebGL1 context, all textures are resized to a power of two to produce the best quality.
 *
 * @see https://doc.babylonjs.com/advanced_topics/webGL2#power-of-two-textures
 */
var GlyphAtlas = /*#__PURE__*/_createClass(function GlyphAtlas(stacks) {
  _classCallCheck(this, GlyphAtlas);
  var positions = {};
  var bins = [];
  for (var stack in stacks) {
    var glyphs = stacks[stack];
    // @ts-ignore
    var stackPositions = positions[stack] = {};
    for (var id in glyphs) {
      var src = glyphs[+id];
      if (!src || src.bitmap.width === 0 || src.bitmap.height === 0) continue;
      var bin = {
        x: 0,
        y: 0,
        w: src.bitmap.width + 2 * padding,
        h: src.bitmap.height + 2 * padding
      };
      bins.push(bin);
      // @ts-ignore
      stackPositions[id] = {
        rect: bin,
        metrics: src.metrics
      };
    }
  }
  var _potpack = potpack(bins),
    w = _potpack.w,
    h = _potpack.h;
  var image = new AlphaImage({
    width: w || 1,
    height: h || 1
  });
  for (var _stack in stacks) {
    var _glyphs = stacks[_stack];
    for (var _id in _glyphs) {
      var _src = _glyphs[+_id];
      if (!_src || _src.bitmap.width === 0 || _src.bitmap.height === 0) continue;
      // @ts-ignore
      var _bin = positions[_stack][_id].rect;
      AlphaImage.copy(_src.bitmap, image, {
        x: 0,
        y: 0
      }, {
        x: _bin.x + padding,
        y: _bin.y + padding
      }, _src.bitmap);
    }
  }
  this.image = image;
  this.positions = positions;
});

var BASE_FONT_WIDTH = 24;
var BASE_FONT_BUFFER = 3;
var fontSize = BASE_FONT_WIDTH; // Font size in pixels
var buffer = BASE_FONT_BUFFER; // Whitespace buffer around a glyph in pixels
var radius = 8; // How many pixels around the glyph shape to use for encoding distance
var cutoff = 0.25; // How much of the radius (relative) is used for the inside part the glyph
function getDefaultCharacterSet() {
  var charSet = [];
  for (var i = 32; i < 128; i++) {
    charSet.push(String.fromCharCode(i));
  }
  return charSet;
}

/**
 * TODO: use one atlas for all fontstacks, each fontstack has one texture now
 */
var GlyphManager = /*#__PURE__*/function () {
  function GlyphManager(runtime) {
    _classCallCheck(this, GlyphManager);
    this.sdfGeneratorCache = {};
    this.textMetricsCache = {};
    this.glyphMap = {};
    this.runtime = runtime;
  }
  return _createClass(GlyphManager, [{
    key: "destroy",
    value: function destroy() {
      if (this.glyphAtlasTexture) {
        this.glyphAtlasTexture.destroy();
      }
    }
  }, {
    key: "getMap",
    value: function getMap() {
      return this.glyphMap;
    }
  }, {
    key: "getAtlas",
    value: function getAtlas() {
      return this.glyphAtlas;
    }
  }, {
    key: "getAtlasTexture",
    value: function getAtlasTexture() {
      return this.glyphAtlasTexture;
    }
  }, {
    key: "layout",
    value: function layout(lines, fontStack, lineHeight, textAlign, letterSpacing, offsetX, offsetY) {
      var _this = this;
      var positionedGlyphs = [];
      var x = offsetX;
      var y = offsetY;
      var justify =
      // eslint-disable-next-line no-nested-ternary
      textAlign === 'right' || textAlign === 'end' ? 1 : textAlign === 'left' || textAlign === 'start' ? 0 : 0.5;
      lines.forEach(function (line) {
        var lineStartIndex = positionedGlyphs.length;
        Array.from(line).forEach(function (_char2) {
          // fontStack
          var positions = _this.glyphMap[fontStack];
          var charCode = _char2.charCodeAt(0);
          var glyph = positions && positions[charCode];
          if (glyph) {
            positionedGlyphs.push({
              glyph: charCode,
              x: x,
              y: y,
              scale: 1,
              fontStack: fontStack
            });
            x += glyph.metrics.advance + letterSpacing;
          }
        });
        var lineWidth = x - letterSpacing;
        for (var i = lineStartIndex; i < positionedGlyphs.length; i++) {
          positionedGlyphs[i].x -= justify * lineWidth;
        }
        x = 0;
        y += lineHeight;
      });
      return positionedGlyphs;
    }
  }, {
    key: "generateAtlas",
    value: function generateAtlas(canvas) {
      var _this2 = this;
      var fontStack = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
      var fontFamily = arguments.length > 2 ? arguments[2] : undefined;
      var fontWeight = arguments.length > 3 ? arguments[3] : undefined;
      var fontStyle = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '';
      var text = arguments.length > 5 ? arguments[5] : undefined;
      var device = arguments.length > 6 ? arguments[6] : undefined;
      var newChars = [];
      if (!this.glyphMap[fontStack]) {
        newChars = getDefaultCharacterSet();
      }
      var existedChars = Object.keys(this.glyphMap[fontStack] || {});
      Array.from(new Set(text.split(''))).forEach(function (_char3) {
        if (existedChars.indexOf(_char3.charCodeAt(0).toString()) === -1) {
          newChars.push(_char3);
        }
      });
      if (newChars.length) {
        var glyphMap = newChars.map(function (_char4) {
          return _this2.generateSDF(canvas, fontStack, fontFamily, fontWeight, fontStyle, _char4);
        }).reduce(function (prev, cur) {
          // @ts-ignore
          prev[cur.id] = cur;
          return prev;
        }, {});
        this.glyphMap[fontStack] = _objectSpread(_objectSpread({}, this.glyphMap[fontStack]), glyphMap);
        this.glyphAtlas = new GlyphAtlas(this.glyphMap);
        var _this$glyphAtlas$imag = this.glyphAtlas.image,
          atlasWidth = _this$glyphAtlas$imag.width,
          atlasHeight = _this$glyphAtlas$imag.height,
          data = _this$glyphAtlas$imag.data;
        if (this.glyphAtlasTexture) {
          this.glyphAtlasTexture.destroy();
        }
        this.glyphAtlasTexture = device.createTexture(_objectSpread(_objectSpread({}, makeTextureDescriptor2D(Format.ALPHA, atlasWidth, atlasHeight, 1)), {}, {
          pixelStore: {
            unpackFlipY: false,
            unpackAlignment: 1
          }
        }));
        this.glyphAtlasTexture.setImageData([data]);
      }
    }
  }, {
    key: "generateSDF",
    value: function generateSDF(canvas) {
      var fontStack = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
      var fontFamily = arguments.length > 2 ? arguments[2] : undefined;
      var fontWeight = arguments.length > 3 ? arguments[3] : undefined;
      var fontStyle = arguments.length > 4 ? arguments[4] : undefined;
      var _char5 = arguments.length > 5 ? arguments[5] : undefined;
      var charCode = _char5.charCodeAt(0);
      var sdfGenerator = this.sdfGeneratorCache[fontStack];
      if (!sdfGenerator) {
        // 创建 SDF
        sdfGenerator = this.sdfGeneratorCache[fontStack] =
        // TODO: use OffscreenCanvas in TextService
        new TinySDF({
          canvas: canvas,
          fontSize: fontSize,
          fontFamily: fontFamily,
          fontWeight: fontWeight,
          fontStyle: fontStyle,
          buffer: buffer,
          radius: radius,
          cutoff: cutoff
        }, this.runtime);
      }
      if (!this.textMetricsCache[fontStack]) {
        this.textMetricsCache[fontStack] = {};
      }
      if (!this.textMetricsCache[fontStack][_char5]) {
        // 使用 mapbox/tiny-sdf 中的 context
        // @see https://stackoverflow.com/questions/46126565/how-to-get-font-glyphs-metrics-details-in-javascript
        this.textMetricsCache[fontStack][_char5] =
        // @ts-ignore
        sdfGenerator.ctx.measureText(_char5).width;
      }

      // use sdf 2.x @see https://github.com/mapbox/tiny-sdf
      var _sdfGenerator$draw = sdfGenerator.draw(_char5),
        data = _sdfGenerator$draw.data,
        width = _sdfGenerator$draw.width,
        height = _sdfGenerator$draw.height,
        glyphWidth = _sdfGenerator$draw.glyphWidth,
        glyphHeight = _sdfGenerator$draw.glyphHeight,
        glyphLeft = _sdfGenerator$draw.glyphLeft,
        glyphTop = _sdfGenerator$draw.glyphTop,
        glyphAdvance = _sdfGenerator$draw.glyphAdvance;
      return {
        id: charCode,
        // 在 canvas 中绘制字符，使用 Uint8Array 存储 30*30 sdf 数据
        bitmap: new AlphaImage({
          width: width,
          height: height
        }, data),
        metrics: {
          width: glyphWidth,
          height: glyphHeight,
          left: glyphLeft,
          top: glyphTop - BASE_FONT_WIDTH + BASE_FONT_BUFFER,
          advance: glyphAdvance
        }
      };
    }
  }]);
}();

/**
 * Create the quads used for rendering a text label.
 */
function getGlyphQuads(positionedGlyphs, positions) {
  var quads = [];
  for (var k = 0; k < positionedGlyphs.length; k++) {
    var positionedGlyph = positionedGlyphs[k];
    var glyphPositions = positions[positionedGlyph.fontStack];
    var glyph = glyphPositions && glyphPositions[positionedGlyph.glyph];
    if (!glyph) continue;
    var rect = glyph.rect;
    if (!rect) continue;

    // The rects have an addditional buffer that is not included in their size.
    var glyphPadding = 1.0;
    // const glyphPadding = 0.0;
    var rectBuffer = BASE_FONT_BUFFER + glyphPadding;
    var halfAdvance = glyph.metrics.advance * positionedGlyph.scale / 2;
    var glyphOffset = [0, 0];
    var builtInOffset = [positionedGlyph.x + halfAdvance, positionedGlyph.y];
    var x1 = (glyph.metrics.left - rectBuffer) * positionedGlyph.scale - halfAdvance + builtInOffset[0];
    var y1 = (-glyph.metrics.top - rectBuffer) * positionedGlyph.scale + builtInOffset[1];
    var x2 = x1 + rect.w * positionedGlyph.scale;
    var y2 = y1 + rect.h * positionedGlyph.scale;
    var tl = {
      x: x1,
      y: y1
    };
    var tr = {
      x: x2,
      y: y1
    };
    var bl = {
      x: x1,
      y: y2
    };
    var br = {
      x: x2,
      y: y2
    };
    quads.push({
      tl: tl,
      tr: tr,
      bl: bl,
      br: br,
      tex: rect,
      glyphOffset: glyphOffset
    });
  }
  return quads;
}

var TextVertexAttributeBufferIndex = function (TextVertexAttributeBufferIndex) {
  TextVertexAttributeBufferIndex[TextVertexAttributeBufferIndex["INSTANCED"] = VertexAttributeBufferIndex.POSITION + 1] = "INSTANCED";
  TextVertexAttributeBufferIndex[TextVertexAttributeBufferIndex["TEX"] = 1 + TextVertexAttributeBufferIndex["INSTANCED"]] = "TEX";
  return TextVertexAttributeBufferIndex;
}(TextVertexAttributeBufferIndex || {});
var TextVertexAttributeLocation = function (TextVertexAttributeLocation) {
  TextVertexAttributeLocation[TextVertexAttributeLocation["TEX"] = VertexAttributeLocation.MAX] = "TEX";
  TextVertexAttributeLocation[TextVertexAttributeLocation["OFFSET"] = 1 + TextVertexAttributeLocation["TEX"]] = "OFFSET";
  return TextVertexAttributeLocation;
}(TextVertexAttributeLocation || {});
var TextUniform = /*#__PURE__*/function (TextUniform) {
  TextUniform["SDF_MAP"] = "u_SDFMap";
  TextUniform["SDF_MAP_SIZE"] = "u_SDFMapSize";
  TextUniform["FONT_SIZE"] = "u_FontSize";
  TextUniform["GAMMA_SCALE"] = "u_GammaScale";
  TextUniform["STROKE_BLUR"] = "u_StrokeBlur";
  TextUniform["HAS_STROKE"] = "u_HasStroke";
  return TextUniform;
}({});
var TextDrawcall = /*#__PURE__*/function (_Instanced) {
  function TextDrawcall(renderHelper, texturePool, lightPool, object, drawcallCtors, index, context) {
    var _this;
    _classCallCheck(this, TextDrawcall);
    _this = _callSuper(this, TextDrawcall, [renderHelper, texturePool, lightPool, object, drawcallCtors, index, context]);
    _this.packedBufferObjectMap = new WeakMap();
    _this.tmpMat4 = mat4.create();
    _this.renderHelper = renderHelper;
    _this.texturePool = texturePool;
    _this.lightPool = lightPool;
    _this.fontHash = _this.calcFontHash(object);
    _this.glyphManager = new GlyphManager(_this.context);
    return _this;
  }
  _inherits(TextDrawcall, _Instanced);
  return _createClass(TextDrawcall, [{
    key: "calcFontHash",
    value: function calcFontHash(object) {
      // Trigger text geometry calculation.
      object.getBounds();
      var instancedAttributes = ['fontSize', 'fontFamily', 'fontWeight', 'textBaseline', 'letterSpacing'];
      return object.parsedStyle.metrics.font + instancedAttributes.reduce(function (prev, cur) {
        return prev + object.parsedStyle[cur];
      }, '');
    }
  }, {
    key: "shouldMerge",
    value: function shouldMerge(object, index) {
      var shouldMerge = _superPropGet(TextDrawcall, "shouldMerge", this, 3)([object, index]);
      if (!shouldMerge) {
        return false;
      }
      if (this.index !== index) {
        return false;
      }
      return this.fontHash === this.calcFontHash(object);
    }
  }, {
    key: "createGeometry",
    value: function createGeometry(objects) {
      var _this2 = this;
      var object = this.instance;
      var _object$parsedStyle = object.parsedStyle,
        _object$parsedStyle$f = _object$parsedStyle.fontSize,
        fontSize = _object$parsedStyle$f === void 0 ? 16 : _object$parsedStyle$f,
        _object$parsedStyle$l = _object$parsedStyle.letterSpacing,
        letterSpacing = _object$parsedStyle$l === void 0 ? 0 : _object$parsedStyle$l;
      var _object$parsedStyle$t = object.parsedStyle.textBaseline,
        textBaseline = _object$parsedStyle$t === void 0 ? 'alphabetic' : _object$parsedStyle$t;

      // scale current font size to base(24)
      var fontScale = BASE_FONT_WIDTH / fontSize;
      var indices = [];
      var positions = [];
      var uvOffsets = [];
      var packed = [];
      var indicesOff = 0;
      objects.forEach(function (object) {
        var _ref = object.parsedStyle,
          metrics = _ref.metrics,
          _ref$dx = _ref.dx,
          dx = _ref$dx === void 0 ? 0 : _ref$dx,
          _ref$dy = _ref.dy,
          dy = _ref$dy === void 0 ? 0 : _ref$dy;
        var font = metrics.font,
          lines = metrics.lines,
          height = metrics.height,
          lineHeight = metrics.lineHeight;

        // account for dx & dy
        var offsetX = dx;
        var offsetY = dy;
        if (textBaseline === 'alphabetic') {
          textBaseline = 'bottom';
        }
        var linePositionY = 0;
        // handle vertical text baseline
        if (textBaseline === 'middle') {
          linePositionY += -height / 2;
        } else if (textBaseline === 'bottom') {
          linePositionY += -height;
        } else if (textBaseline === 'top' || textBaseline === 'hanging') {
          linePositionY += 0;
        } else if (textBaseline === 'ideographic') {
          linePositionY += -height;
        }
        var glyphAtlas = _this2.glyphManager.getAtlas();
        var _this2$buildTextBuffe = _this2.buildTextBuffers({
            object: object,
            lines: lines,
            fontStack: font,
            lineHeight: fontScale * lineHeight,
            offsetX: fontScale * offsetX,
            offsetY: fontScale * (linePositionY + offsetY),
            letterSpacing: fontScale * letterSpacing,
            glyphAtlas: glyphAtlas,
            indicesOffset: indicesOff
          }),
          indicesOffset = _this2$buildTextBuffe.indicesOffset,
          indexBuffer = _this2$buildTextBuffe.indexBuffer,
          charUVOffsetBuffer = _this2$buildTextBuffe.charUVOffsetBuffer,
          charPositionsBuffer = _this2$buildTextBuffe.charPositionsBuffer,
          charPackedBuffer = _this2$buildTextBuffe.charPackedBuffer;
        indicesOff = indicesOffset;
        var start = packed.length;
        packed.push.apply(packed, _toConsumableArray(charPackedBuffer));
        var end = packed.length;
        _this2.packedBufferObjectMap.set(object, [start, end]);
        uvOffsets.push.apply(uvOffsets, _toConsumableArray(charUVOffsetBuffer));
        positions.push.apply(positions, _toConsumableArray(charPositionsBuffer));
        indices.push.apply(indices, _toConsumableArray(indexBuffer));
      });
      this.geometry.vertexCount = indices.length;
      this.geometry.setIndexBuffer(new Uint32Array(indices));
      this.geometry.setVertexBuffer({
        bufferIndex: TextVertexAttributeBufferIndex.INSTANCED,
        byteStride: 4 * (4 * 4 + 4 + 4 + 4 + 4),
        // 32
        stepMode: VertexStepMode.VERTEX,
        attributes: [{
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.MODEL_MATRIX0
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 4,
          location: VertexAttributeLocation.MODEL_MATRIX1
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 8,
          location: VertexAttributeLocation.MODEL_MATRIX2
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 12,
          location: VertexAttributeLocation.MODEL_MATRIX3
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 16,
          location: VertexAttributeLocation.PACKED_COLOR
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 20,
          location: VertexAttributeLocation.PACKED_STYLE1
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 24,
          location: VertexAttributeLocation.PACKED_STYLE2
        }, {
          format: Format.F32_RGBA,
          bufferByteOffset: 4 * 28,
          location: VertexAttributeLocation.PICKING_COLOR
        }],
        data: new Float32Array(packed)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: VertexAttributeBufferIndex.POSITION,
        byteStride: 4 * 3,
        stepMode: VertexStepMode.VERTEX,
        attributes: [{
          format: Format.F32_RGB,
          bufferByteOffset: 4 * 0,
          location: VertexAttributeLocation.POSITION
        }],
        data: new Float32Array(positions)
      });
      this.geometry.setVertexBuffer({
        bufferIndex: TextVertexAttributeBufferIndex.TEX,
        byteStride: 4 * (2 + 2),
        stepMode: VertexStepMode.VERTEX,
        attributes: [{
          format: Format.F32_RG,
          bufferByteOffset: 4 * 0,
          location: TextVertexAttributeLocation.TEX
        }, {
          format: Format.F32_RG,
          bufferByteOffset: 4 * 2,
          location: TextVertexAttributeLocation.OFFSET
        }],
        data: new Float32Array(uvOffsets)
      });
    }
  }, {
    key: "createMaterial",
    value: function createMaterial(objects) {
      this.material.vertexShader = vert;
      this.material.fragmentShader = frag;
      this.material.cullMode = CullMode.BACK;
      this.material.defines = _objectSpread(_objectSpread({}, this.material.defines), enumToObject(TextVertexAttributeLocation));
      var object = this.instance;
      var _object$parsedStyle2 = object.parsedStyle,
        _object$parsedStyle2$ = _object$parsedStyle2.fontSize,
        fontSize = _object$parsedStyle2$ === void 0 ? 16 : _object$parsedStyle2$,
        _object$parsedStyle2$2 = _object$parsedStyle2.fontFamily,
        fontFamily = _object$parsedStyle2$2 === void 0 ? 'sans-serif' : _object$parsedStyle2$2,
        _object$parsedStyle2$3 = _object$parsedStyle2.fontWeight,
        fontWeight = _object$parsedStyle2$3 === void 0 ? 'normal' : _object$parsedStyle2$3,
        _object$parsedStyle2$4 = _object$parsedStyle2.fontStyle,
        fontStyle = _object$parsedStyle2$4 === void 0 ? 'normal' : _object$parsedStyle2$4,
        metrics = _object$parsedStyle2.metrics;
      var font = metrics.font;
      var allText = objects.map(function (object) {
        return object.parsedStyle.text;
      }).join('');
      this.glyphManager.generateAtlas(this.texturePool.context.config.offscreenCanvas, font, fontFamily, fontWeight.toString(), fontStyle, allText, this.context.device);
      var glyphAtlasTexture = this.glyphManager.getAtlasTexture();
      var glyphAtlas = this.glyphManager.getAtlas();
      this.context.device.setResourceName(glyphAtlasTexture, 'TextSDF Texture');
      var _glyphAtlas$image = glyphAtlas.image,
        atlasWidth = _glyphAtlas$image.width,
        atlasHeight = _glyphAtlas$image.height;
      this.material.setUniforms(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, TextUniform.SDF_MAP, glyphAtlasTexture), TextUniform.SDF_MAP_SIZE, [atlasWidth, atlasHeight]), TextUniform.FONT_SIZE, fontSize), TextUniform.GAMMA_SCALE, 1), TextUniform.STROKE_BLUR, 0.2), TextUniform.HAS_STROKE, this.index));
    }
  }, {
    key: "changeRenderOrder",
    value: function changeRenderOrder(object, renderOrder) {
      var vertice = this.geometry.vertices[TextVertexAttributeBufferIndex.INSTANCED];
      var arrayStride = this.geometry.inputLayoutDescriptor.vertexBufferDescriptors[TextVertexAttributeBufferIndex.INSTANCED].arrayStride;
      var bytes = arrayStride / 4;
      var _this$packedBufferObj = this.packedBufferObjectMap.get(object),
        _this$packedBufferObj2 = _slicedToArray(_this$packedBufferObj, 2),
        start = _this$packedBufferObj2[0],
        end = _this$packedBufferObj2[1];
      var sliced = vertice.slice(start, end);
      for (var i = 0; i < end - start; i += bytes) {
        sliced[i + bytes - 1] = renderOrder * RENDER_ORDER_SCALE;
      }
      this.geometry.updateVertexBuffer(TextVertexAttributeBufferIndex.INSTANCED, VertexAttributeLocation.MODEL_MATRIX0, start / bytes, new Uint8Array(sliced.buffer));
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(objects, startIndex, name, value) {
      var _this3 = this;
      // fix https://github.com/antvis/G/issues/1755
      if (objects.length === 0) {
        return;
      }
      _superPropGet(TextDrawcall, "updateAttribute", this, 3)([objects, startIndex, name, value]);
      if (name === 'text' || name === 'fontFamily' || name === 'fontSize' || name === 'fontWeight' || name === 'fontStyle' || name === 'fontVariant' || name === 'textBaseline' || name === 'letterSpacing' || name === 'wordWrapWidth' || name === 'lineHeight' || name === 'wordWrap' || name === 'textAlign' || name === 'x' || name === 'y' || name === 'dx' || name === 'dy') {
        this.material.programDirty = true;
        this.material.geometryDirty = true;
        // need re-upload SDF texture
        this.material.textureDirty = true;
      } else if (name === 'modelMatrix' || name === 'fill' || name === 'fillOpacity' || name === 'stroke' || name === 'strokeOpacity' || name === 'opacity' || name === 'lineWidth' || name === 'visibility' || name === 'pointerEvents' || name === 'isBillboard' || name === 'billboardRotation' || name === 'isSizeAttenuation') {
        var vertice = this.geometry.vertices[TextVertexAttributeBufferIndex.INSTANCED];
        var arrayStride = this.geometry.inputLayoutDescriptor.vertexBufferDescriptors[TextVertexAttributeBufferIndex.INSTANCED].arrayStride;
        var bytes = arrayStride / 4;
        objects.forEach(function (object) {
          var _object$renderable3D;
          var _ref2 = object.parsedStyle,
            fill = _ref2.fill,
            stroke = _ref2.stroke,
            _ref2$opacity = _ref2.opacity,
            opacity = _ref2$opacity === void 0 ? 1 : _ref2$opacity,
            _ref2$fillOpacity = _ref2.fillOpacity,
            fillOpacity = _ref2$fillOpacity === void 0 ? 1 : _ref2$fillOpacity,
            _ref2$strokeOpacity = _ref2.strokeOpacity,
            strokeOpacity = _ref2$strokeOpacity === void 0 ? 1 : _ref2$strokeOpacity,
            _ref2$lineWidth = _ref2.lineWidth,
            lineWidth = _ref2$lineWidth === void 0 ? 1 : _ref2$lineWidth,
            visibility = _ref2.visibility,
            isBillboard = _ref2.isBillboard,
            billboardRotation = _ref2.billboardRotation,
            isSizeAttenuation = _ref2.isSizeAttenuation;
          var fillColor = [0, 0, 0, 0];
          if (isCSSRGB(fill)) {
            fillColor = [Number(fill.r), Number(fill.g), Number(fill.b), Number(fill.alpha) * 255];
          }
          var strokeColor = [0, 0, 0, 0];
          if (isCSSRGB(stroke)) {
            strokeColor = [Number(stroke.r), Number(stroke.g), Number(stroke.b), Number(stroke.alpha) * 255];
          }
          var encodedPickingColor = object.isInteractive() && (// @ts-ignore
          (_object$renderable3D = object.renderable3D) === null || _object$renderable3D === void 0 ? void 0 : _object$renderable3D.encodedPickingColor) || [0, 0, 0];
          var modelMatrix = mat4.copy(_this3.tmpMat4, object.getWorldTransform());
          var _this3$packedBufferOb = _this3.packedBufferObjectMap.get(object),
            _this3$packedBufferOb2 = _slicedToArray(_this3$packedBufferOb, 2),
            start = _this3$packedBufferOb2[0],
            end = _this3$packedBufferOb2[1];
          var sliced = vertice.slice(start, end);
          for (var i = 0; i < end - start; i += bytes) {
            // if (name === 'modelMatrix') {
            sliced[i + 0] = modelMatrix[0];
            sliced[i + 1] = modelMatrix[1];
            sliced[i + 2] = modelMatrix[2];
            sliced[i + 3] = modelMatrix[3];
            sliced[i + 4] = modelMatrix[4];
            sliced[i + 5] = modelMatrix[5];
            sliced[i + 6] = modelMatrix[6];
            sliced[i + 7] = modelMatrix[7];
            sliced[i + 8] = modelMatrix[8];
            sliced[i + 9] = modelMatrix[9];
            sliced[i + 10] = modelMatrix[10];
            sliced[i + 11] = modelMatrix[11];
            sliced[i + 12] = modelMatrix[12];
            sliced[i + 13] = modelMatrix[13];
            sliced[i + 14] = modelMatrix[14];
            sliced[i + 15] = modelMatrix[15];
            // } else if (name === 'fill') {
            sliced[i + 16] = packUint8ToFloat(fillColor[0], fillColor[1]);
            sliced[i + 17] = packUint8ToFloat(fillColor[2], fillColor[3]);
            // } else if (name === 'stroke') {
            sliced[i + 18] = packUint8ToFloat(strokeColor[0], strokeColor[1]);
            sliced[i + 19] = packUint8ToFloat(strokeColor[2], strokeColor[3]);
            // }
            sliced[i + 20] = opacity;
            sliced[i + 21] = fillOpacity;
            sliced[i + 22] = strokeOpacity;
            sliced[i + 23] = lineWidth;
            sliced[i + 24] = visibility !== 'hidden' ? 1 : 0;
            sliced[i + 25] = isBillboard ? 1 : 0;
            sliced[i + 26] = isSizeAttenuation ? 1 : 0;
            sliced[i + 27] = billboardRotation !== null && billboardRotation !== void 0 ? billboardRotation : 0;
            sliced[i + 28] = encodedPickingColor[0];
            sliced[i + 29] = encodedPickingColor[1];
            sliced[i + 30] = encodedPickingColor[2];
            // sliced[i + 31] = object.sortable.renderOrder * RENDER_ORDER_SCALE;
          }
          _this3.geometry.updateVertexBuffer(TextVertexAttributeBufferIndex.INSTANCED, VertexAttributeLocation.MODEL_MATRIX0, start / bytes, new Uint8Array(sliced.buffer));
        });
      }
    }
  }, {
    key: "buildTextBuffers",
    value: function buildTextBuffers(_ref3) {
      var _object$renderable3D2;
      var object = _ref3.object,
        lines = _ref3.lines,
        fontStack = _ref3.fontStack,
        lineHeight = _ref3.lineHeight,
        letterSpacing = _ref3.letterSpacing,
        offsetX = _ref3.offsetX,
        offsetY = _ref3.offsetY,
        glyphAtlas = _ref3.glyphAtlas,
        indicesOffset = _ref3.indicesOffset;
      var _ref4 = object.parsedStyle,
        _ref4$textAlign = _ref4.textAlign,
        textAlign = _ref4$textAlign === void 0 ? 'start' : _ref4$textAlign,
        fill = _ref4.fill,
        stroke = _ref4.stroke,
        _ref4$opacity = _ref4.opacity,
        opacity = _ref4$opacity === void 0 ? 1 : _ref4$opacity,
        _ref4$fillOpacity = _ref4.fillOpacity,
        fillOpacity = _ref4$fillOpacity === void 0 ? 1 : _ref4$fillOpacity,
        _ref4$strokeOpacity = _ref4.strokeOpacity,
        strokeOpacity = _ref4$strokeOpacity === void 0 ? 1 : _ref4$strokeOpacity,
        _ref4$lineWidth = _ref4.lineWidth,
        lineWidth = _ref4$lineWidth === void 0 ? 1 : _ref4$lineWidth,
        visibility = _ref4.visibility,
        isBillboard = _ref4.isBillboard,
        billboardRotation = _ref4.billboardRotation,
        isSizeAttenuation = _ref4.isSizeAttenuation,
        _ref4$x = _ref4.x,
        x = _ref4$x === void 0 ? 0 : _ref4$x,
        _ref4$y = _ref4.y,
        y = _ref4$y === void 0 ? 0 : _ref4$y,
        _ref4$z = _ref4.z,
        z = _ref4$z === void 0 ? 0 : _ref4$z;
      var fillColor = [0, 0, 0, 0];
      if (isCSSRGB(fill)) {
        fillColor = [Number(fill.r), Number(fill.g), Number(fill.b), Number(fill.alpha) * 255];
      }
      var strokeColor = [0, 0, 0, 0];
      if (isCSSRGB(stroke)) {
        strokeColor = [Number(stroke.r), Number(stroke.g), Number(stroke.b), Number(stroke.alpha) * 255];
      }
      var encodedPickingColor = object.isInteractive() && (// @ts-ignore
      (_object$renderable3D2 = object.renderable3D) === null || _object$renderable3D2 === void 0 ? void 0 : _object$renderable3D2.encodedPickingColor) || [0, 0, 0];
      var modelMatrix = mat4.copy(this.tmpMat4, object.getWorldTransform());
      var charPackedBuffer = [];
      var charUVOffsetBuffer = [];
      var charPositionsBuffer = [];
      var indexBuffer = [];
      var i = indicesOffset;
      var positionedGlyphs = this.glyphManager.layout(lines, fontStack, lineHeight, textAlign, letterSpacing, offsetX, offsetY);

      // 计算每个独立字符相对于锚点的位置信息
      var glyphQuads = getGlyphQuads(positionedGlyphs, glyphAtlas.positions);
      glyphQuads.forEach(function (quad) {
        // rollup will use `concat`
        var temp = [];
        temp.push.apply(temp, _toConsumableArray(modelMatrix));
        var packed = [].concat(temp, [packUint8ToFloat(fillColor[0], fillColor[1]), packUint8ToFloat(fillColor[2], fillColor[3]), packUint8ToFloat(strokeColor[0], strokeColor[1]), packUint8ToFloat(strokeColor[2], strokeColor[3]), opacity, fillOpacity, strokeOpacity, lineWidth, visibility !== 'hidden' ? 1 : 0, isBillboard ? 1 : 0, isSizeAttenuation ? 1 : 0, billboardRotation !== null && billboardRotation !== void 0 ? billboardRotation : 0], _toConsumableArray(encodedPickingColor), [object.sortable.renderOrder * RENDER_ORDER_SCALE]);
        // Can't use instanced here since the total number of each Text can be different.
        charPackedBuffer.push.apply(charPackedBuffer, _toConsumableArray(packed).concat(_toConsumableArray(packed), _toConsumableArray(packed), _toConsumableArray(packed)));

        // interleaved uv & offsets
        charUVOffsetBuffer.push(quad.tex.x, quad.tex.y, quad.tl.x, quad.tl.y);
        charUVOffsetBuffer.push(quad.tex.x + quad.tex.w, quad.tex.y, quad.tr.x, quad.tr.y);
        charUVOffsetBuffer.push(quad.tex.x + quad.tex.w, quad.tex.y + quad.tex.h, quad.br.x, quad.br.y);
        charUVOffsetBuffer.push(quad.tex.x, quad.tex.y + quad.tex.h, quad.bl.x, quad.bl.y);
        charPositionsBuffer.push(x, y, z, x, y, z, x, y, z, x, y, z);
        indexBuffer.push(0 + i, 2 + i, 1 + i);
        indexBuffer.push(2 + i, 0 + i, 3 + i);
        i += 4;
      });
      return {
        indexBuffer: indexBuffer,
        charUVOffsetBuffer: charUVOffsetBuffer,
        charPositionsBuffer: charPositionsBuffer,
        charPackedBuffer: charPackedBuffer,
        indicesOffset: i
      };
    }
  }, {
    key: "destroy",
    value: function destroy() {
      _superPropGet(TextDrawcall, "destroy", this, 3)([]);
      this.glyphManager.destroy();
    }
  }]);
}(Instanced);

var MeshDrawcall = /*#__PURE__*/function (_Instanced) {
  function MeshDrawcall() {
    var _this;
    _classCallCheck(this, MeshDrawcall);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _callSuper(this, MeshDrawcall, [].concat(args));
    _this.mergeXYZIntoModelMatrix = false;
    return _this;
  }
  _inherits(MeshDrawcall, _Instanced);
  return _createClass(MeshDrawcall, [{
    key: "shouldMerge",
    value: function shouldMerge(object, index) {
      var shouldMerge = _superPropGet(MeshDrawcall, "shouldMerge", this, 3)([object, index]);
      if (!shouldMerge) {
        return false;
      }
      if (this.instance.nodeName === Shape.MESH) {
        if (this.instance.parsedStyle.material !== object.parsedStyle.material || this.instance.parsedStyle.geometry !== object.parsedStyle.geometry) {
          return false;
        }
      }
      return true;
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(objects, startIndex, name, value) {
      if (objects.length === 0) {
        return;
      }
      _superPropGet(MeshDrawcall, "updateAttribute", this, 3)([objects, startIndex, name, value]);
      this.updateBatchedAttribute(objects, startIndex, name, value);
    }
  }, {
    key: "createMaterial",
    value: function createMaterial(objects) {
      var material = this.instance.parsedStyle.material;
      this.material = material;
      this.observeMaterialChanged();
    }
  }, {
    key: "createGeometry",
    value: function createGeometry(objects) {
      var geometry = this.instance.parsedStyle.geometry;
      this.geometry = geometry;

      // use default common attributes
      _superPropGet(MeshDrawcall, "createGeometry", this, 3)([objects]);
      this.geometry.build(objects);

      // TODO: clear dirty listener
      this.observeGeometryChanged();
    }
  }]);
}(Instanced);

/**
 * Use 2 meshes:
 * * SDF to draw fill & simple stroke if needed.
 * * InstancedPathDrawcall to draw stroke separately.
 */
var CircleRenderer = /*#__PURE__*/function (_Batch) {
  function CircleRenderer() {
    _classCallCheck(this, CircleRenderer);
    return _callSuper(this, CircleRenderer, arguments);
  }
  _inherits(CircleRenderer, _Batch);
  return _createClass(CircleRenderer, [{
    key: "getDrawcallCtors",
    value: function getDrawcallCtors(object) {
      var drawcalls = [];
      var _ref = object.parsedStyle,
        fill = _ref.fill;
      if (fill && !fill.isNone) {
        drawcalls.push(SDFDrawcall);
      }
      if (this.needDrawStrokeSeparately(object)) {
        drawcalls.push(InstancedPathDrawcall);
      }
      return drawcalls;
    }

    /**
     * need an additional mesh to draw stroke:
     * 1. strokeOpacity < 1
     * 2. lineDash used
     * 3. stroke is not 'none'
     */
  }, {
    key: "needDrawStrokeSeparately",
    value: function needDrawStrokeSeparately(object) {
      var _ref2 = object.parsedStyle,
        fill = _ref2.fill,
        stroke = _ref2.stroke,
        lineDash = _ref2.lineDash,
        lineWidth = _ref2.lineWidth,
        strokeOpacity = _ref2.strokeOpacity;
      var hasFill = fill && !fill.isNone;
      var hasStroke = stroke && !stroke.isNone;
      var hasDash = lineDash && lineDash.length && lineDash.every(function (item) {
        return item !== 0;
      });
      return !hasFill || hasStroke && lineWidth > 0 && (strokeOpacity < 1 || hasDash);
    }
  }]);
}(Batch);

/**
 * Use the following perf enhancements:
 * * Downgrading the "simple" Path / Polyline to {@link InstancedLineDrawcall}, e.g. 'M 0 0 L 100 0'
 * * Merge the Path into {@link InstancedPathDrawcall} which contains only one curve command, e.g 'M 0 0 Q 10 10 100 100'
 * @see https://github.com/antvis/G/issues/1113
 */
var PathRenderer = /*#__PURE__*/function (_Batch) {
  function PathRenderer() {
    _classCallCheck(this, PathRenderer);
    return _callSuper(this, PathRenderer, arguments);
  }
  _inherits(PathRenderer, _Batch);
  return _createClass(PathRenderer, [{
    key: "getDrawcallCtors",
    value:
    // meshes = [
    //   InstancedFillDrawcall ?,
    //   (InstancedLineDrawcall | InstancedPathDrawcall) *, // sub paths
    // ];

    function getDrawcallCtors(object) {
      var _ref = object.parsedStyle,
        fill = _ref.fill,
        stroke = _ref.stroke,
        opacity = _ref.opacity,
        strokeOpacity = _ref.strokeOpacity,
        lineWidth = _ref.lineWidth;
      var hasStroke = stroke && !stroke.isNone;
      var subpathNum = InstancedPathDrawcall.calcSubpathNum(object);
      var drawcalls = [];

      // Polyline don't need fill
      if (!(object.nodeName === Shape.POLYLINE || fill !== null && fill !== void 0 && fill.isNone)) {
        for (var i = 0; i < subpathNum; i++) {
          drawcalls.push(InstancedFillDrawcall);
        }
      }
      for (var _i = 0; _i < subpathNum; _i++) {
        if (!(strokeOpacity === 0 || opacity === 0 || lineWidth === 0 || !hasStroke)) {
          var isLine = InstancedLineDrawcall.isLine(object, _i);
          if (isLine) {
            drawcalls.push(InstancedLineDrawcall);
          } else {
            drawcalls.push(InstancedPathDrawcall);
          }
        }
      }
      return drawcalls;
    }
  }]);
}(Batch);

var ImageRenderer = /*#__PURE__*/function (_Batch) {
  function ImageRenderer() {
    _classCallCheck(this, ImageRenderer);
    return _callSuper(this, ImageRenderer, arguments);
  }
  _inherits(ImageRenderer, _Batch);
  return _createClass(ImageRenderer, [{
    key: "getDrawcallCtors",
    value: function getDrawcallCtors() {
      return [ImageDrawcall];
    }
  }]);
}(Batch);

var TextRenderer = /*#__PURE__*/function (_Batch) {
  function TextRenderer() {
    _classCallCheck(this, TextRenderer);
    return _callSuper(this, TextRenderer, arguments);
  }
  _inherits(TextRenderer, _Batch);
  return _createClass(TextRenderer, [{
    key: "getDrawcallCtors",
    value:
    /**
     * one for fill, one for stroke
     */
    function getDrawcallCtors(object) {
      var drawcalls = [];
      var _ref = object.parsedStyle,
        stroke = _ref.stroke,
        _ref$lineWidth = _ref.lineWidth,
        lineWidth = _ref$lineWidth === void 0 ? 1 : _ref$lineWidth;
      var hasStroke = !!(stroke && !stroke.isNone && lineWidth);
      if (hasStroke) {
        drawcalls.push(TextDrawcall);
      }
      drawcalls.push(TextDrawcall);
      return drawcalls;
    }
  }, {
    key: "beforeUploadUBO",
    value: function beforeUploadUBO(renderInst, mesh) {
      var drawcallNum = mesh.instance.renderable3D.drawcalls.length;
      mesh.material.setUniforms(_defineProperty({}, TextUniform.HAS_STROKE, drawcallNum === 1 ? 0 : 1 - mesh.index));
    }
  }]);
}(Batch);

/**
 * use instanced for each segment
 * @see https://blog.scottlogic.com/2019/11/18/drawing-lines-with-webgl.html
 *
 * support dash array
 * TODO: joint & cap
 */
var LineRenderer = /*#__PURE__*/function (_Batch) {
  function LineRenderer() {
    _classCallCheck(this, LineRenderer);
    return _callSuper(this, LineRenderer, arguments);
  }
  _inherits(LineRenderer, _Batch);
  return _createClass(LineRenderer, [{
    key: "getDrawcallCtors",
    value: function getDrawcallCtors() {
      return [InstancedLineDrawcall];
    }
  }]);
}(Batch);

var MeshRenderer = /*#__PURE__*/function (_Batch) {
  function MeshRenderer() {
    _classCallCheck(this, MeshRenderer);
    return _callSuper(this, MeshRenderer, arguments);
  }
  _inherits(MeshRenderer, _Batch);
  return _createClass(MeshRenderer, [{
    key: "getDrawcallCtors",
    value: function getDrawcallCtors() {
      return [MeshDrawcall];
    }
  }]);
}(Batch);

/**
 * Use 2 meshes:
 * * For simple Rect with fill & simple stroke, we use SDFDrawcall to draw which has a better performance.
 * * FillMesh & LineMesh to draw rounded rect with different radius.
 */
var RectRenderer = /*#__PURE__*/function (_Batch) {
  function RectRenderer() {
    _classCallCheck(this, RectRenderer);
    return _callSuper(this, RectRenderer, arguments);
  }
  _inherits(RectRenderer, _Batch);
  return _createClass(RectRenderer, [{
    key: "getDrawcallCtors",
    value: function getDrawcallCtors(object) {
      var drawcalls = [];
      var _ref = object.parsedStyle,
        fill = _ref.fill,
        radius = _ref.radius;
      var hasDifferentRadius = radius && radius.length && radius.some(function (r) {
        return r !== radius[0];
      });
      if (!(fill !== null && fill !== void 0 && fill.isNone || hasDifferentRadius)) {
        drawcalls.push(SDFDrawcall);
      }
      if (hasDifferentRadius) {
        drawcalls.push(InstancedFillDrawcall);
      }
      if (hasDifferentRadius || this.needDrawStrokeSeparately(object)) {
        drawcalls.push(InstancedPathDrawcall);
      }
      return drawcalls;
    }

    /**
     * need an additional mesh to draw stroke:
     * 1. strokeOpacity < 1
     * 2. lineDash used
     * 3. stroke is not 'none'
     */
  }, {
    key: "needDrawStrokeSeparately",
    value: function needDrawStrokeSeparately(object) {
      var _ref2 = object.parsedStyle,
        fill = _ref2.fill,
        stroke = _ref2.stroke,
        lineDash = _ref2.lineDash,
        lineWidth = _ref2.lineWidth,
        strokeOpacity = _ref2.strokeOpacity;
      var hasFill = fill && !fill.isNone;
      var hasStroke = stroke && !stroke.isNone;
      var hasDash = lineDash && lineDash.length && lineDash.every(function (item) {
        return item !== 0;
      });
      return !hasFill || hasStroke && lineWidth > 0 && (strokeOpacity < 1 || hasDash);
    }
  }]);
}(Batch);

var stencilRefCounter = 1;
var BatchManager = /*#__PURE__*/function () {
  function BatchManager(renderHelper, rendererFactory, texturePool, lightPool) {
    _classCallCheck(this, BatchManager);
    /**
     * draw calls
     */
    this.drawcalls = [];
    /**
     * update patches which can be merged before rendering
     */
    this.pendingUpdatePatches = {};
    this.stencilRefCache = {};
    this.renderHelper = renderHelper;
    this.rendererFactory = rendererFactory;
    this.texturePool = texturePool;
    this.lightPool = lightPool;
  }

  /**
   * attached when Device created
   */
  return _createClass(BatchManager, [{
    key: "destroy",
    value: function destroy() {
      this.drawcalls.forEach(function (drawcall) {
        drawcall.destroy();
      });
      this.drawcalls = [];
      this.pendingUpdatePatches = {};
    }
  }, {
    key: "render",
    value: function render(list) {
      var _this = this;
      var isPicking = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      if (!isPicking) {
        this.updatePendingPatches();
      }
      this.drawcalls.forEach(function (mesh) {
        // init rendering service, create geometry & material
        mesh.init();
        var objects = mesh.objects;
        if (mesh.clipPathTarget) {
          objects = [mesh.clipPath];
        }

        // new render instance
        var renderInst = _this.renderHelper.renderInstManager.newRenderInst();
        renderInst.setAllowSkippingIfPipelineNotReady(false);
        mesh.applyRenderInst(renderInst, objects);
        _this.renderHelper.renderInstManager.submitRenderInst(renderInst, list);

        // console.log('submit: ', mesh);

        if (!isPicking) {
          // finish rendering...
          mesh.objects.forEach(function (object) {
            object.renderable.dirty = false;
          });
        }
      });
    }

    /**
     * get called in RenderGraphPlugin
     */
  }, {
    key: "attach",
    value: function attach(context) {
      this.context = context;
    }
  }, {
    key: "add",
    value: function add(object) {
      var _this2 = this;
      var renderable3D = object.renderable3D;
      if (renderable3D && !renderable3D.drawcalls.length) {
        var renderer = this.rendererFactory[object.nodeName];
        if (renderer) {
          // A complex Path can be splitted into multple sub paths.
          renderer.getDrawcallCtors(object).forEach(function (DrawCallCtor, i, drawcallCtors) {
            var existedDrawcall = _this2.drawcalls.find(function (mesh) {
              return DrawCallCtor === mesh.constructor && mesh.index === i && mesh.objects.length < mesh.maxInstances && mesh.shouldMerge(object, i);
            });
            if (!existedDrawcall || existedDrawcall.key !== object.parsedStyle.batchKey) {
              existedDrawcall = new DrawCallCtor(_this2.renderHelper, _this2.texturePool, _this2.lightPool, object, drawcallCtors, i, _this2.context);
              existedDrawcall.renderer = renderer;
              _this2.drawcalls.push(existedDrawcall);
              if (object.parsedStyle.batchKey) {
                existedDrawcall.key = object.parsedStyle.batchKey;
              }
            }
            if (existedDrawcall) {
              existedDrawcall.objects.push(object);
              renderable3D.drawcalls[i] = existedDrawcall;
              existedDrawcall.geometryDirty = true;
            }
          });
        }
      }
    }
  }, {
    key: "remove",
    value: function remove(object) {
      var _this3 = this;
      // @ts-ignore
      var renderable3D = object.renderable3D;
      if (renderable3D) {
        renderable3D.drawcalls.forEach(function (mesh) {
          if (mesh) {
            // remove from mesh
            var index = mesh.objects.indexOf(object);
            if (index > -1) {
              mesh.objects.splice(index, 1);
              mesh.geometryDirty = true;
            }
            if (mesh.objects.length === 0) {
              var deletedDrawcalls = _this3.drawcalls.splice(_this3.drawcalls.indexOf(mesh), 1);
              deletedDrawcalls.forEach(function (deletedDrawcall) {
                deletedDrawcall.destroy();
              });
            }
          }
        });
        renderable3D.drawcalls = [];
      }
    }
  }, {
    key: "updateAttribute",
    value: function updateAttribute(object, attributeName, newValue) {
      var _this4 = this;
      var immediately = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
      var renderable3D = object.renderable3D;
      var renderer = this.rendererFactory[object.nodeName];
      if (renderer) {
        var drawcallCtors = renderer.getDrawcallCtors(object);
        drawcallCtors.forEach(function (DrawCallCtor, i, drawcallCtors) {
          var existedDrawcall = renderable3D.drawcalls.find(function (mesh) {
            return mesh && mesh.index === i && mesh.constructor === DrawCallCtor;
          });
          if (!existedDrawcall) {
            // Clear invalid drawcall.
            existedDrawcall = renderable3D.drawcalls[i];
            if (existedDrawcall) {
              // remove from mesh
              existedDrawcall.objects.splice(existedDrawcall.objects.indexOf(object), 1);
              existedDrawcall.geometryDirty = true;
              if (existedDrawcall.objects.length === 0) {
                _this4.drawcalls.splice(_this4.drawcalls.indexOf(existedDrawcall), 1);
              }
              renderable3D.drawcalls[renderable3D.drawcalls.indexOf(existedDrawcall)] = undefined;
            }

            // We should create a new drawcall from scratch.
            existedDrawcall = _this4.drawcalls.find(function (mesh) {
              return DrawCallCtor === mesh.constructor && mesh.index === i && mesh.objects.length < mesh.maxInstances && mesh.shouldMerge(object, i);
            });
            if (!existedDrawcall) {
              // @ts-ignore
              existedDrawcall = new DrawCallCtor(_this4.renderHelper, _this4.texturePool, _this4.lightPool, object, drawcallCtors, i, _this4.context);
              existedDrawcall.renderer = renderer;
              existedDrawcall.init();
              _this4.drawcalls.push(existedDrawcall);
            } else {
              existedDrawcall.geometryDirty = true;
            }
            existedDrawcall.objects.push(object);
            renderable3D.drawcalls[i] = existedDrawcall;
          }
          if (existedDrawcall.inited && !existedDrawcall.geometryDirty) {
            var shouldMerge = existedDrawcall.shouldMerge(object, i);
            if (shouldMerge) {
              var objectIdx = existedDrawcall.objects.indexOf(object);
              if (immediately) {
                object.parsedStyle[attributeName] = newValue;
                existedDrawcall.updateAttribute([object], objectIdx, attributeName, newValue);
              } else {
                var patchKey = existedDrawcall.id + attributeName;
                if (!_this4.pendingUpdatePatches[patchKey]) {
                  _this4.pendingUpdatePatches[patchKey] = {
                    instance: existedDrawcall,
                    objectIndices: [],
                    name: attributeName,
                    value: newValue
                  };
                }
                if (_this4.pendingUpdatePatches[patchKey].objectIndices.indexOf(objectIdx) === -1) {
                  _this4.pendingUpdatePatches[patchKey].objectIndices.push(objectIdx);
                }
              }
            } else {
              _this4.remove(object);
              _this4.add(object);
            }
          } else {
            _this4.remove(object);
            _this4.add(object);
          }
        });

        // Clear redundant drawcalls.
        if (renderable3D.drawcalls.length > drawcallCtors.length) {
          var drawcallNum = renderable3D.drawcalls.length;
          for (var i = drawcallNum - 1; i >= drawcallCtors.length; i--) {
            var existedDrawcall = renderable3D.drawcalls[i];

            // remove from mesh
            existedDrawcall.objects.splice(existedDrawcall.objects.indexOf(object), 1);
            existedDrawcall.geometryDirty = true;
            if (existedDrawcall.objects.length === 0) {
              this.drawcalls.splice(this.drawcalls.indexOf(existedDrawcall), 1);
            }
            renderable3D.drawcalls.pop();
          }
        }
      }
    }
  }, {
    key: "changeRenderOrder",
    value: function changeRenderOrder(object, renderOrder) {
      // @ts-ignore
      var renderable3D = object.renderable3D;
      if (renderable3D && renderable3D.drawcalls.length) {
        renderable3D.drawcalls.forEach(function (mesh) {
          if (mesh && mesh.inited && !mesh.geometryDirty) {
            if (mesh.inited) {
              mesh.changeRenderOrder(object, renderOrder);
            }
          }
        });
      }
    }
  }, {
    key: "getStencilRef",
    value: function getStencilRef(object) {
      if (!this.stencilRefCache[object.entity]) {
        this.stencilRefCache[object.entity] = stencilRefCounter++;
      }
      return this.stencilRefCache[object.entity];
    }
  }, {
    key: "updatePendingPatches",
    value: function updatePendingPatches() {
      var _this5 = this;
      // merge update patches to reduce `setSubData` calls
      Object.keys(this.pendingUpdatePatches).forEach(function (patchKey) {
        var _this5$pendingUpdateP = _this5.pendingUpdatePatches[patchKey],
          instance = _this5$pendingUpdateP.instance,
          objectIndices = _this5$pendingUpdateP.objectIndices,
          name = _this5$pendingUpdateP.name,
          value = _this5$pendingUpdateP.value;
        objectIndices.sort(function (a, b) {
          return a - b;
        });
        var updatePatches = [];
        objectIndices.forEach(function (i) {
          var lastUpdateBatch = updatePatches[updatePatches.length - 1];
          if (!lastUpdateBatch || i !== lastUpdateBatch[lastUpdateBatch.length - 1] + 1) {
            updatePatches.push([i]);
          } else {
            lastUpdateBatch.push(i);
          }
        });
        updatePatches.forEach(function (indices) {
          instance.updateAttribute(instance.objects.slice(indices[0], indices[0] + indices.length), indices[0], name, value);
        });
      });
      this.pendingUpdatePatches = {};
    }

    // private findClipPath(): DisplayObject | null {
    //   let node = this.instance;
    //   while (node && node.style) {
    //     if (node.style.clipPath) {
    //       return node.style.clipPath;
    //     }
    //     node = node.parentNode as DisplayObject;
    //   }
    //   return null;
    // }

    // private applyClipPath() {
    //   // find clipPath
    //   const clipPathShape = this.findClipPath();
    //   if (clipPathShape && !this.clipPathMeshCreated) {
    //     if (this.batchMeshList.length === 0) {
    //       return;
    //     }

    //     const clipPathMesh = this.meshFactory(clipPathShape.nodeName);
    //     clipPathMesh.clipPathTarget = this.instance;

    //     // draw clipPath first
    //     this.batchMeshList.unshift(clipPathMesh);
    //     this.clipPathMeshCreated = true;

    //     this.batchMeshList.forEach((mesh, i) => {
    //       mesh.clipPath = clipPathShape;

    //       // if (!mesh.material) {
    //       //   mesh.material = new ShaderMaterial(this.device);
    //       // }
    //       mesh.material.stencilRef = this.batchManager.getStencilRef(clipPathShape);
    //     });
    //   }

    //   // remove clipPath from render queue
    //   if (!clipPathShape) {
    //     if (this.batchMeshList.length && this.batchMeshList[0].clipPathTarget) {
    //       this.batchMeshList.shift();
    //     }
    //   }
    // }
  }]);
}();

var TexturePool = /*#__PURE__*/function () {
  function TexturePool(context, runtime) {
    _classCallCheck(this, TexturePool);
    this.textureCache = {};
    this.context = context;
    this.runtime = runtime;
  }
  return _createClass(TexturePool, [{
    key: "getOrCreateTexture",
    value: function getOrCreateTexture(device, src, descriptor, successCallback) {
      var _this = this;
      // use Image URL or src as cache key
      // @ts-ignore
      var id = typeof src === 'string' ? src : src.src || '';
      var texture;
      if (!id || !this.textureCache[id]) {
        texture = device.createTexture(_objectSpread({
          format: Format.U8_RGBA_NORM,
          width: 1,
          height: 1,
          depthOrArrayLayers: 1,
          mipLevelCount: 1,
          dimension: TextureDimension.TEXTURE_2D,
          usage: TextureUsage.SAMPLED,
          pixelStore: {
            unpackFlipY: false
          }
        }, descriptor));
        if (id) {
          this.textureCache[id] = texture;
        }
        if (!isString(src)) {
          texture.setImageData([src]);
          texture.emit(TextureEvent.LOADED);
          this.context.renderingService.dirty();
        } else {
          // @see https://github.com/antvis/g/issues/938
          var image = this.context.config.createImage();
          if (image) {
            image.onload = function () {
              var onSuccess = function onSuccess(bitmap) {
                _this.textureCache[id].setImageData([bitmap]);
                _this.textureCache[id].emit(TextureEvent.LOADED);
                _this.context.renderingService.dirty();
                if (successCallback) {
                  successCallback(_this.textureCache[id], bitmap);
                }
              };
              if (_this.runtime.globalThis.createImageBitmap) {
                _this.runtime.globalThis.createImageBitmap(image).then(function (bitmap) {
                  return onSuccess(bitmap);
                })["catch"](function () {
                  // Unhandled Rejection (InvalidStateError):
                  // Failed to execute 'createImageBitmap' on 'Window':
                  // The image element contains an SVG image without intrinsic dimensions,
                  // and no resize options or crop region are specified.
                  onSuccess(image);
                });
              } else {
                onSuccess(image);
              }
            };
            image.onerror = function () {};
            image.crossOrigin = 'Anonymous';
            image.src = src;
          }
        }
      } else {
        texture = this.textureCache[id];
        texture.emit(TextureEvent.LOADED);
      }
      return texture;
    }
  }, {
    key: "getOrCreateCanvas",
    value: function getOrCreateCanvas() {
      return this.runtime.offscreenCanvasCreator.getOrCreateCanvas(this.context.config.offscreenCanvas);
    }
  }, {
    key: "getOrCreateGradient",
    value: function getOrCreateGradient(params) {
      var instance = params.instance,
        gradients = params.gradients;
      var _instance$getGeometry = instance.getGeometryBounds(),
        halfExtents = _instance$getGeometry.halfExtents;
      var width = halfExtents[0] * 2 || 1;
      var height = halfExtents[1] * 2 || 1;
      var offscreenCanvas = this.context.config.offscreenCanvas;
      var canvas = this.runtime.offscreenCanvasCreator.getOrCreateCanvas(offscreenCanvas);
      var context = this.runtime.offscreenCanvasCreator.getOrCreateContext(offscreenCanvas);
      canvas.width = width;
      canvas.height = height;

      // @ts-ignore
      var imagePool = this.context.imagePool;
      gradients.forEach(function (g) {
        var gradient = imagePool.getOrCreateGradient(_objectSpread(_objectSpread({
          type: g.type
        }, g.value), {}, {
          width: width,
          height: height,
          min: [0, 0]
        }), context);

        // used as canvas' ID
        // @ts-ignore
        // canvas.src = key;

        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height);
      });
    }
  }, {
    key: "getOrCreatePattern",
    value: function getOrCreatePattern(pattern, instance, callback) {
      var image = pattern.image,
        repetition = pattern.repetition,
        transform = pattern.transform;
      var _instance$getGeometry2 = instance.getGeometryBounds(),
        halfExtents = _instance$getGeometry2.halfExtents;
      var width = halfExtents[0] * 2 || 1;
      var height = halfExtents[1] * 2 || 1;
      var offscreenCanvas = this.context.config.offscreenCanvas;
      var canvas = this.runtime.offscreenCanvasCreator.getOrCreateCanvas(offscreenCanvas);
      var context = this.runtime.offscreenCanvasCreator.getOrCreateContext(offscreenCanvas);
      canvas.width = width;
      canvas.height = height;
      var src;
      // Image URL
      if (isString(image)) {
        var imageCache = this.context.imagePool.getImageSync(image, instance, callback);
        src = imageCache === null || imageCache === void 0 ? void 0 : imageCache.img;
      } else {
        src = image;
      }
      var canvasPattern = src && context.createPattern(src, repetition);

      // @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasPattern/setTransform
      if (transform) {
        var mat = parsedTransformToMat4(parseTransform(transform), new DisplayObject({}));
        canvasPattern.setTransform({
          a: mat[0],
          b: mat[1],
          c: mat[4],
          d: mat[5],
          e: mat[12],
          f: mat[13]
        });
      }
      context.fillStyle = canvasPattern;
      context.fillRect(0, 0, width, height);
    }
  }, {
    key: "destroy",
    value: function destroy() {
      for (var key in this.textureCache) {
        this.textureCache[key].destroy();
      }
      this.textureCache = {};
    }
  }]);
}();

var Plugin = /*#__PURE__*/function (_AbstractRendererPlug) {
  function Plugin() {
    var _this;
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, Plugin);
    _this = _callSuper(this, Plugin);
    _this.name = 'device-renderer';
    _this.parameters = {
      /**
       * ToneMapping is a renderer-level parameter, it will affect all materials.
       * @see https://threejs.org/docs/#api/en/renderers/WebGLRenderer.toneMapping
       */
      toneMapping: ToneMapping.NONE,
      toneMappingExposure: 1
    };
    _this.options = options;
    return _this;
  }
  _inherits(Plugin, _AbstractRendererPlug);
  return _createClass(Plugin, [{
    key: "init",
    value: function init(runtime) {
      var _rendererFactory;
      runtime.geometryUpdaterFactory[Shape.MESH] = new MeshUpdater();
      var renderHelper = new RenderHelper(this.parameters);
      var lightPool = new LightPool();
      var texturePool = new TexturePool(this.context, runtime);
      var pickingIdGenerator = new PickingIdGenerator();
      var circleRenderer = new CircleRenderer();
      var pathRenderer = new PathRenderer();
      var rendererFactory = (_rendererFactory = {}, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_rendererFactory, Shape.CIRCLE, circleRenderer), Shape.ELLIPSE, circleRenderer), Shape.POLYLINE, pathRenderer), Shape.PATH, pathRenderer), Shape.POLYGON, pathRenderer), Shape.RECT, new RectRenderer()), Shape.IMAGE, new ImageRenderer()), Shape.LINE, new LineRenderer()), Shape.TEXT, new TextRenderer()), Shape.MESH, new MeshRenderer()), _defineProperty(_defineProperty(_rendererFactory, Shape.GROUP, undefined), Shape.HTML, undefined));
      var batchManager = new BatchManager(renderHelper, rendererFactory, texturePool, lightPool);
      var renderGraphPlugin = new RenderGraphPlugin(renderHelper, lightPool, texturePool, batchManager, this.options);
      this.addRenderingPlugin(renderGraphPlugin);
      this.addRenderingPlugin(new PickingPlugin(renderHelper, renderGraphPlugin, pickingIdGenerator, batchManager));
    }
  }, {
    key: "destroy",
    value: function destroy(runtime) {
      delete runtime.geometryUpdaterFactory[Shape.MESH];
    }
  }, {
    key: "getRenderGraphPlugin",
    value: function getRenderGraphPlugin() {
      return this.plugins[0];
    }
  }, {
    key: "getDevice",
    value: function getDevice() {
      return this.getRenderGraphPlugin().getDevice();
    }
  }, {
    key: "getSwapChain",
    value: function getSwapChain() {
      return this.getRenderGraphPlugin().getSwapChain();
    }
  }, {
    key: "loadTexture",
    value: function loadTexture(src, descriptor, successCallback) {
      return this.getRenderGraphPlugin().loadTexture(src, descriptor, successCallback);
    }
  }, {
    key: "toDataURL",
    value: function toDataURL(options) {
      return this.getRenderGraphPlugin().toDataURL(options);
    }
  }, {
    key: "setParameters",
    value: function setParameters(parameters) {
      this.parameters = _objectSpread(_objectSpread({}, this.parameters), parameters);
    }
  }]);
}(AbstractRendererPlugin);

export { AntialiasingMode, Batch, BufferGeometry, DeviceProgram, DynamicUniformBuffer, FILL_TEXTURE_MAPPING, Fog, FogType, GeometryEvent, HashMap, ImageDrawcall, Instanced, InstancedFillDrawcall, InstancedLineDrawcall, InstancedPathDrawcall, JOINT_TYPE, Light, Material, MaterialEvent, Mesh, MeshDrawcall, Plugin, RGAttachmentSlot, RenderCache, RenderGraphPlugin, RenderHelper, RenderInst, RenderInstExecutionOrder, RenderInstFlags, RenderInstList, RenderInstManager, Renderable3D, RendererLayer, SDFDrawcall, ShaderMaterial, SingleSampledTexture, TemporalTexture, TextDrawcall, TextUniform, TextureMapping, TexturePool, TinySDF, ToneMapping, VertexAttributeBufferIndex, VertexAttributeLocation, bezierCurveTo, compareDefines, definedProps, enumToObject, fillColor, fillMatrix4x4, fillVec3v, fillVec4, fillVec4v, getSortKeyDepth, getSortKeyLayer, hashCodeNumberFinish, hashCodeNumberUpdate, makeAttachmentClearDescriptor, makeBackbufferDescSimple, makeDataBuffer, makeDepthKey, makeSortKey, makeSortKeyOpaque, makeSortKeyTranslucent, nullHashFunc, opaqueBlackFullClearRenderPassDescriptor, opaqueWhiteFullClearRenderPassDescriptor, projectionMatrixConvertClipSpaceNearZ, pushFXAAPass, quadCurveTo, renderInstCompareNone, renderInstCompareSortKey, segmentInstanceGeometry, setBackbufferDescSimple, setSortKeyBias, setSortKeyDepth, setSortKeyDepthKey, setSortKeyLayer, setSortKeyOpaqueDepth, setSortKeyProgramKey, setSortKeyTranslucentDepth, standardFullClearRenderPassDescriptor, updateBuffer };
//# sourceMappingURL=index.esm.js.map
